import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import React from 'react';

// ─── DEMO MODE — Pure mock data, zero backend dependencies ───────────────────
// All API calls are no-ops. State lives entirely in React state.
const qtDownload = () => alert('Demo mode — downloads disabled.');
// These are kept as named stubs so legacy call-sites don't crash; they are
// never actually awaited in demo paths (all saves go directly to setState).
const apiFetch = async () => ({});
const qtFetch  = async () => ({});

// ── File attachment helpers ───────────────────────────────────────────────────
// Files are sent directly with the message as multipart/form-data (files[] field).
// No separate upload endpoint needed — multer on /messages and /messages/send handles it.

function fmtFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
}

function fileTypeIcon(mimeType = '') {
  if (mimeType.startsWith('image/'))       return '🖼️';
  if (mimeType.startsWith('video/'))       return '🎬';
  if (mimeType.startsWith('audio/'))       return '🎵';
  if (mimeType.includes('pdf'))            return '📄';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return '🗜️';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📊';
  return '📎';
}

// Renders one attachment — image inline, others as download card
// att shape from new backend: { id, fileName, fileSize, mimeType }
// att shape from old/optimistic: { url, name, size, mimeType }
const MsgAttachment = ({ att, isMe, tc, t }) => {
  const fileName = att.fileName || att.name || 'file';
  const fileSize = att.fileSize ?? att.size ?? 0;
  const mimeType = att.mimeType || '';
  const isImage  = mimeType.startsWith('image/');

  const doDownload = (e) => {
    e.preventDefault();
    if (att.url && !att.url.startsWith('data:')) { window.open(att.url, '_blank'); return; }
    alert('Demo mode — file attachments disabled.');
  };

  if (isImage && att.url && !att.url.startsWith('data:')) {
    return (
      <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ display:'block', marginBottom:4 }}>
        <img
          src={att.url} alt={fileName}
          style={{ maxWidth:'100%', maxHeight:220, borderRadius:8, objectFit:'cover', display:'block', cursor:'pointer' }}
          onError={e=>{ e.target.style.display='none'; }}
        />
        <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", marginTop:3, opacity:0.6, color:'inherit' }}>
          {fileName} {fileSize ? `· ${fmtFileSize(fileSize)}` : ''}
        </div>
      </a>
    );
  }
  return (
    <button onClick={doDownload} style={{ textDecoration:'none', display:'block', marginBottom:4, background:'none', border:'none', padding:0, cursor:'pointer', width:'100%', textAlign:'left' }}>
      <div style={{
        display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
        borderRadius:7,
        background: isMe ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.06)',
        border: `1px solid ${isMe ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
        cursor:'pointer',
      }}>
        <span style={{ fontSize:20, flexShrink:0 }}>{fileTypeIcon(mimeType)}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:12, fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, color:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fileName}</div>
          {fileSize > 0 && <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:'inherit', opacity:0.6, marginTop:1 }}>{fmtFileSize(fileSize)}</div>}
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink:0, opacity:0.6 }}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </div>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ─── THEMES ───────────────────────────────────────────────────────────────────
const DARK = {
  id:"dark",
  bg:"#050506",             // exact match with landing page --black
  surface:"#0d0d10",        // exact match with landing page --surface
  surface2:"#141418",       // exact match with landing page --surface-2
  surface3:"#1c1c22",       // exact match with landing page --surface-3
  border:"#252530",         // exact match with landing page --border
  borderBright:"#35353f",   // exact match with landing page --border-bright
  teal:"#00c9a7",           // brand — keep
  tealDim:"#007a65",        // exact match with landing page --teal-dim
  tealGlow:"rgba(0,201,167,0.12)", // exact match with landing page --teal-glow
  amber:"#f5a623",          // landing page amber — richer, warmer gold
  red:"#e05260",
  blue:"#5b8fd4",
  text:"#f0f0f5",           // landing page --white — slightly brighter, cooler
  textDim:"#8888a0",        // landing page --white-dim — cool blue-grey, not warm brown
  textDimmer:"#4a4a5a",     // landing page --white-dimmer — more legible metadata
  shadow:"0 4px 32px rgba(0,0,0,0.7)",
}
const LIGHT = {
  id:"light",
  // ── Backgrounds — warm ivory, not sterile grey ──
  bg:"#f0ede8",             // warm parchment — the old money base
  surface:"#faf8f5",        // warm near-white card surface
  surface2:"#f0ede8",       // matches bg — inset fields blend naturally
  surface3:"#e6e2db",       // subtle depth for hover states, dividers
  // ── Borders — visible, intentional ──
  border:"#d4cfc8",         // warm grey-beige — cards have clear edges
  borderBright:"#b8b2aa",   // stronger border for focus/active states
  // ── Brand — deeper, more authoritative on light ──
  teal:"#007a65",           // deep forest teal — commanding on ivory
  tealDim:"#005a4a",        // even deeper for hover
  tealGlow:"rgba(0,122,101,0.08)",
  // ── Accent colors — rich, not pastel ──
  amber:"#b8720a",          // deep cognac amber — aged whisky not highlighter
  red:"#b83050",            // deep wine red — serious, not alarming
  blue:"#2a5298",           // deep navy — authority
  // ── Typography — near-black on warm surface ──
  text:"#1a1714",           // warm near-black — not cold #141416
  textDim:"#5a5248",        // warm mid-tone — readable secondary text
  textDimmer:"#9a948c",     // warm muted — metadata, labels
  shadow:"0 2px 20px rgba(26,23,20,0.10)",
};
// ─── SVG LOGO ─────────────────────────────────────────────────────────────────
const LogoMark = ({ size=32 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size}>
    <rect width="200" height="200" rx="28" fill="#00c9a7"/>
    <text x="100" y="148" textAnchor="middle" fontFamily="Syne,'Arial Black',sans-serif"
      fontWeight="800" fontSize="130" letterSpacing="-6" fill="#050506">Q</text>
  </svg>
);

// INITIAL_TEAM — only used as local fallback if API is unreachable at login time.
// All real team members are managed via the backend and loaded on login.
const INITIAL_TEAM = [
  {id:1, username:"amon",    password:"demo1234", name:"Amon Cheruiyot",  role:"admin",     active:true, isActive:true, title:"Founder & Lead Developer",    joinedAt:"2024-01-01", email:"amon@qire.co.ke"},
  {id:2, username:"brian",   password:"demo1234", name:"Brian Ochieng",   role:"developer", active:true, isActive:true, title:"Full-Stack Developer",         joinedAt:"2024-03-15", email:"brian@qire.co.ke"},
  {id:3, username:"cynthia", password:"demo1234", name:"Cynthia Wanjiru", role:"sales",     active:true, isActive:true, title:"Sales & Partnerships Manager", joinedAt:"2024-05-01", email:"cynthia@qire.co.ke"},
  {id:4, username:"david",   password:"demo1234", name:"David Kamau",     role:"support",   active:true, isActive:true, title:"Customer Support Lead",        joinedAt:"2024-06-10", email:"david@qire.co.ke"},
  {id:5, username:"esther",  password:"demo1234", name:"Esther Njeri",    role:"marketing", active:true, isActive:true, title:"Digital Marketing Strategist", joinedAt:"2024-07-20", email:"esther@qire.co.ke"},
];
// ─── RICH DEMO SEED DATA ──────────────────────────────────────────────────────
const SEED = {
  clients: [
    {id:101, name:"James Mwangi",    company:"TechVault Ltd",       email:"james@techvault.co.ke",   phone:"+254 712 345 678", status:"Active",   deal:120000, currency:"USD", source:"Referral",      tags:["SaaS","Priority"],    notes:"Long-term retainer. Requires weekly progress updates.", createdAt:"2024-02-10"},
    {id:102, name:"Sara Omondi",     company:"GreenLeaf Organics",  email:"sara@greenleaf.co.ke",    phone:"+254 722 987 654", status:"Active",   deal:45000,  currency:"USD", source:"Website",       tags:["E-commerce"],         notes:"E-commerce redesign + SEO package.", createdAt:"2024-04-05"},
    {id:103, name:"Michael Kariuki", company:"SwiftLog Africa",     email:"michael@swiftlog.co.ke",  phone:"+254 733 111 222", status:"Active",   deal:85000,  currency:"USD", source:"LinkedIn",      tags:["Logistics","Mobile"], notes:"Mobile app for fleet management.", createdAt:"2024-05-18"},
    {id:104, name:"Patricia Chebet", company:"SunRise Schools",     email:"patti@sunriseschools.ke", phone:"+254 701 444 555", status:"Inactive", deal:18000,  currency:"KES", source:"Cold Outreach", tags:["EdTech"],             notes:"On hold — budget review Q3.", createdAt:"2024-07-01"},
    {id:105, name:"Kevin Rotich",    company:"Rotich & Associates", email:"kevin@rotich.law",        phone:"+254 790 333 666", status:"Lead",     deal:60000,  currency:"USD", source:"Event",         tags:["Legal","Enterprise"], notes:"Met at Nairobi Tech Summit. Follow up end of month.", createdAt:"2024-09-12"},
  ],
  leads: [
    {id:201, name:"Alice Nduta",    email:"alice@pixelstudio.co",   phone:"+254 711 222 333", company:"Pixel Studio",      product:"Web Design",    budget:15000,  currency:"USD", source:"Referral",      status:"NEW",       message:"Need a full brand + website redesign.",                createdAt:"2025-02-01", activities:[]},
    {id:207, name:"Naomi Wangari",  email:"naomi@safiratech.co.ke", phone:"+254 710 888 999", company:"Safira Tech",        product:"Custom Dev",    budget:55000,  currency:"USD", source:"Referral",      status:"NEW",       message:"Fintech app — M-Pesa wallet integration.",             createdAt:"2025-03-18", activities:[]},
    {id:211, name:"Rose Kamau",     email:"rose@bloomstudio.ke",    phone:"+254 711 300 400", company:"Bloom Studio",       product:"Web Design",    budget:9500,   currency:"USD", source:"Event",         status:"NEW",       message:"Portfolio site for a branding agency.",                createdAt:"2025-03-20", activities:[]},
    {id:204, name:"Peter Maina",    email:"peter@nairobi360.co",    phone:"+254 700 999 111", company:"Nairobi 360",        product:"SEO Package",   budget:8000,   currency:"USD", source:"Cold Outreach", status:"CONTACTED", message:"Wants to improve organic search ranking.",             createdAt:"2025-02-10", activities:[{id:1,type:"NOTE",body:"Sent intro email. Awaiting reply.",createdAt:"2025-02-12"}]},
    {id:208, name:"Brian Kipkoech", email:"brian@peaklogistics.ke", phone:"+254 726 111 222", company:"Peak Logistics",     product:"Mobile App",    budget:35000,  currency:"USD", source:"LinkedIn",      status:"CONTACTED", message:"Driver tracking app — similar scope to SwiftLog.",     createdAt:"2025-03-10", activities:[{id:1,type:"NOTE",body:"Sent intro deck. Following up Thursday.",createdAt:"2025-03-12"}]},
    {id:202, name:"George Otieno",  email:"george@agritech.ke",     phone:"+254 722 444 555", company:"AgriTech Kenya",     product:"Mobile App",    budget:40000,  currency:"USD", source:"LinkedIn",      status:"DISCOVERY", message:"Farm management app for smallholder farmers.",         createdAt:"2025-01-20", activities:[{id:1,type:"NOTE",body:"Intro call done — very interested. Scoping next week.",createdAt:"2025-01-22"}]},
    {id:209, name:"Zawadi Mwamba",  email:"zawadi@greenmile.co",    phone:"+254 733 500 600", company:"GreenMile Delivery", product:"Web Design",    budget:12000,  currency:"USD", source:"Website",       status:"DISCOVERY", message:"E-commerce site + rider dashboard.",                   createdAt:"2025-03-05", activities:[{id:1,type:"NOTE",body:"Scoping call booked for March 20.",createdAt:"2025-03-08"}]},
    {id:212, name:"Victor Ochieng", email:"victor@stackafrica.io",  phone:"+254 722 900 000", company:"Stack Africa",       product:"Custom Dev",    budget:120000, currency:"USD", source:"LinkedIn",      status:"DISCOVERY", message:"Pan-African developer community platform.",            createdAt:"2025-03-15", activities:[{id:1,type:"NOTE",body:"Big opportunity — assigned to Cynthia.",createdAt:"2025-03-16"}]},
    {id:203, name:"Fatuma Hassan",  email:"fatuma@coastmedia.co",   phone:"+254 733 777 888", company:"Coast Media",        product:"Q-Trader EA",   budget:5000,   currency:"USD", source:"Website",       status:"PROPOSED",  message:"Interested in algo trading EA license.",               createdAt:"2025-01-10", activities:[{id:1,type:"NOTE",body:"Demo call done. Awaiting decision.",createdAt:"2025-01-15"}]},
    {id:210, name:"Samuel Oduya",   email:"samuel@finedge.co.ke",   phone:"+254 798 700 800", company:"FinEdge Capital",    product:"ArbitragePlus", budget:99,     currency:"USD", source:"Twitter/X",     status:"PROPOSED",  message:"Interested in ArbitragePlus subscription.",            createdAt:"2025-02-28", activities:[{id:1,type:"NOTE",body:"Demo given. Proposal sent.",createdAt:"2025-03-02"}]},
    {id:205, name:"Linda Achieng",  email:"linda@sunwave.ke",       phone:"+254 799 555 666", company:"Sunwave Energy",     product:"Web Design",    budget:22000,  currency:"USD", source:"Event",         status:"WON",       message:"Solar energy company website.",                        createdAt:"2024-12-01", activities:[{id:1,type:"NOTE",body:"Contract signed! Starting Jan 2025.",createdAt:"2024-12-18"}]},
    {id:214, name:"Ernest Mutiso",  email:"ernest@cleancity.ke",    phone:"+254 700 444 555", company:"CleanCity Kenya",    product:"Mobile App",    budget:28000,  currency:"USD", source:"Referral",      status:"WON",       message:"Waste management booking app.",                        createdAt:"2025-01-08", activities:[{id:1,type:"NOTE",body:"Signed contract Feb 2. Kick-off next week.",createdAt:"2025-02-03"}]},
    {id:206, name:"Dennis Waweru",  email:"dennis@buildit.co.ke",   phone:"+254 756 321 987", company:"BuildIt Group",      product:"ERP System",    budget:90000,  currency:"USD", source:"Referral",      status:"LOST",      message:"Went with a competitor — pricing too high.",           createdAt:"2024-11-05", activities:[{id:1,type:"NOTE",body:"Lost to Andela. Consider revising pricing.",createdAt:"2024-12-01"}]},
    {id:213, name:"Hilda Njoroge",  email:"hilda@mediahub.co.ke",   phone:"+254 756 200 300", company:"MediaHub Africa",    product:"Partnership",   budget:0,      currency:"USD", source:"Cold Outreach", status:"JUNK",      message:"Partnership inquiry — no budget, no timeline.",        createdAt:"2025-01-15", activities:[{id:1,type:"NOTE",body:"Parked. No real intent.",createdAt:"2025-01-20"}]},
  ],
  projects: [
    {
      id:301, name:"TechVault Dashboard Rebuild", clientId:101, clientName:"TechVault Ltd",
      status:"In Progress", priority:"High", budget:120000, spent:68000, progress:58,
      deadline:"2025-04-30", startDate:"2025-01-10", assignee:2, assigneeName:"Brian Ochieng",
      description:"Full React dashboard with real-time analytics, KPI reporting, and role-based access control for TechVault's internal operations team.",
      chatParticipants:[1,2,101],
      tasks:[
        {id:1, title:"Auth module — JWT + role guards",          done:true},
        {id:2, title:"Dashboard layout & nav shell",             done:true},
        {id:3, title:"KPI cards & real-time data feeds",         done:true},
        {id:4, title:"Reports module — PDF export",              done:false},
        {id:5, title:"API integrations — CRM + accounting",      done:false},
        {id:6, title:"Dark mode + responsive polish",            done:false},
        {id:7, title:"QA — cross-browser & load testing",        done:false},
        {id:8, title:"Staging deploy & client UAT",              done:false},
        {id:9, title:"Production deployment",                    done:false},
      ],
      releases:[
        { id:"r301-1", version:"0.1.0", releaseType:"ALPHA", releasedAt:"2025-01-28T10:00:00Z", releaseNotes:"Initial internal build — auth system, navigation shell, and placeholder dashboard cards. Ready for internal review.", acknowledgedAt:"2025-01-29T14:00:00Z", changelog:{ features:["JWT auth with role-based routing","Responsive nav sidebar","Dashboard layout scaffold"], fixes:[], improvements:[], security:["Auth tokens expire after 24h"] } },
        { id:"r301-2", version:"0.3.0", releaseType:"BETA",  releasedAt:"2025-02-20T10:00:00Z", releaseNotes:"Beta build — KPI cards wired to live data, chart components complete, role-based visibility working. Pending reports module.", acknowledgedAt:"2025-02-22T09:00:00Z", changelog:{ features:["Live KPI data feeds","Bar, line & pie charts","User role visibility rules"], fixes:["Nav overflow on mobile","Token refresh loop"], improvements:["50% faster initial load","Skeleton loaders on data fetch"], security:[] } },
        { id:"r301-3", version:"0.5.0", releaseType:"MINOR", releasedAt:"2025-03-14T10:00:00Z", releaseNotes:"Sprint 3 delivery — full reports module with date-range filtering. PDF export in progress. API integrations scoped and started.", acknowledgedAt:null, changelog:{ features:["Reports module — date filter, table view","CSV export working"], fixes:["Chart tooltip z-index bug","Date picker locale issue"], improvements:["API response caching","Pagination on all tables"], security:[] } },
      ],
      bugs:[
        { id:"b301-1", title:"PDF export crashes on reports with >500 rows",     severity:"HIGH",     status:"In Progress",  createdAt:"2025-03-15T08:00:00Z", description:"Clicking Export PDF on any report with more than 500 data rows causes a browser tab crash. Reproducible on Chrome 122 and Edge.", stepsToRepro:"1. Open Reports → Revenue. 2. Set date range Jan–Mar 2025. 3. Click Export PDF. Tab crashes.", adminReply:"Reproduced. Root cause: rendering all rows into DOM before PDF capture. Fix: paginated PDF generation — ETA 3 days." },
        { id:"b301-2", title:"Date filter resets to today after page refresh",    severity:"MEDIUM",   status:"Resolved",     createdAt:"2025-03-08T11:00:00Z", description:"Any date filter set on the reports page is lost on refresh. State not persisted to URL params.", stepsToRepro:"1. Set custom date range. 2. Refresh page. Filter resets to today.", adminReply:"Fixed in v0.5.0 — date range now stored in URL query params." },
        { id:"b301-3", title:"Admin sees other users' private notes",             severity:"CRITICAL", status:"Resolved",     createdAt:"2025-02-25T09:00:00Z", description:"Users with admin role can view notes flagged as private by other users. This is a data visibility issue.", stepsToRepro:"1. Log in as admin. 2. Open any user profile. 3. Notes tab shows all notes including private ones.", adminReply:"Fixed immediately — private notes now filtered server-side. Deployed as hotfix v0.3.1." },
        { id:"b301-4", title:"Charts don't load in Safari 17",                    severity:"LOW",      status:"Open",         createdAt:"2025-03-18T14:00:00Z", description:"All chart components show blank white boxes on Safari 17 on macOS Sonoma. DevTools shows a canvas context error.", stepsToRepro:"1. Open dashboard on Safari 17. 2. All chart tiles show empty.", adminReply:"" },
      ],
      featureRequests:[
        { id:"fr301-1", title:"Add Slack notification when a KPI threshold is breached",    description:"Notify the ops channel in Slack whenever revenue drops >10% week-on-week or support SLA is missed.", votes:8, status:"Planned",      createdAt:"2025-02-10T00:00:00Z" },
        { id:"fr301-2", title:"Dark mode toggle per user preference",                       description:"Store theme preference in user profile so it persists across sessions and devices.",                  votes:5, status:"In Progress", createdAt:"2025-02-18T00:00:00Z" },
        { id:"fr301-3", title:"Export reports as Excel (.xlsx) in addition to CSV",         description:"Finance team needs Excel format for their existing reporting templates.",                             votes:4, status:"Under Review", createdAt:"2025-03-01T00:00:00Z" },
        { id:"fr301-4", title:"Multi-company / sub-account support",                        description:"TechVault has 3 subsidiaries — want one login to switch between them without re-auth.",             votes:2, status:"Under Review", createdAt:"2025-03-12T00:00:00Z" },
      ],
      clientTasks:[
        { id:"ct301-1", title:"Provide final logo assets and brand colour HEX codes",   description:"We need the updated logo SVG and the exact brand palette before we can finalise the header design.",         status:"Completed",  createdAt:"2025-01-15T00:00:00Z" },
        { id:"ct301-2", title:"Review and approve v0.3.0 Beta — fill UAT checklist",    description:"Please go through the 12-item UAT checklist shared in the project files and mark each item pass/fail.",     status:"Completed",  createdAt:"2025-02-20T00:00:00Z" },
        { id:"ct301-3", title:"Provide API credentials for CRM & accounting system",    description:"We need sandbox credentials for Zoho CRM and Quickbooks to build the integration module.",                 status:"In Progress",createdAt:"2025-03-14T00:00:00Z" },
        { id:"ct301-4", title:"Nominate 3 internal testers for UAT round 2",            description:"Please share names and emails of testers who will do final user acceptance testing before go-live.",        status:"Requested",  createdAt:"2025-03-20T00:00:00Z" },
      ],
      deliverables:[
        { id:"d301-1", name:"TechVault Dashboard — Figma Prototype v1",    fileName:"TechVault_Figma_v1.fig",    uploadedAt:"2025-01-18T00:00:00Z" },
        { id:"d301-2", name:"API Architecture Document",                   fileName:"API_Architecture_v1.pdf",   uploadedAt:"2025-01-25T00:00:00Z" },
        { id:"d301-3", name:"Sprint 2 — Build v0.3.0 Release Notes",       fileName:"Sprint2_Release_Notes.pdf", uploadedAt:"2025-02-20T00:00:00Z" },
        { id:"d301-4", name:"UAT Checklist — Round 1",                     fileName:"UAT_Checklist_R1.xlsx",     uploadedAt:"2025-02-20T00:00:00Z" },
        { id:"d301-5", name:"Sprint 3 — Build v0.5.0 Release Notes",       fileName:"Sprint3_Release_Notes.pdf", uploadedAt:"2025-03-14T00:00:00Z" },
      ],
    },
    {
      id:302, name:"GreenLeaf E-commerce Store", clientId:102, clientName:"GreenLeaf Organics",
      status:"Planning", priority:"Medium", budget:45000, spent:5200, progress:12,
      deadline:"2025-06-15", startDate:"2025-03-01", assignee:2, assigneeName:"Brian Ochieng",
      description:"Custom Shopify storefront with M-Pesa and Stripe payment rails, automated inventory sync, and a bespoke branded theme for GreenLeaf's organic products range.",
      chatParticipants:[1,2,102],
      tasks:[
        {id:1, title:"Discovery & requirements workshop",   done:true},
        {id:2, title:"Wireframes — mobile + desktop",       done:true},
        {id:3, title:"Figma design mockups",                done:false},
        {id:4, title:"Shopify theme development",           done:false},
        {id:5, title:"M-Pesa STK Push integration",         done:false},
        {id:6, title:"Stripe checkout integration",         done:false},
        {id:7, title:"Inventory sync — Google Sheets API",  done:false},
        {id:8, title:"QA — checkout flows & edge cases",    done:false},
        {id:9, title:"Launch & post-launch monitoring",     done:false},
      ],
      releases:[
        { id:"r302-1", version:"0.1.0", releaseType:"ALPHA", releasedAt:"2025-03-10T00:00:00Z", releaseNotes:"Wireframes complete and signed off. Design phase starting. Figma prototype shared for client review.", acknowledgedAt:"2025-03-12T00:00:00Z", changelog:{ features:["Mobile wireframes (12 screens)","Desktop wireframes (10 screens)","User flow diagrams"], fixes:[], improvements:[], security:[] } },
      ],
      bugs:[
        { id:"b302-1", title:"M-Pesa STK Push not triggering on test numbers", severity:"HIGH", status:"Investigating", createdAt:"2025-03-20T10:00:00Z", description:"During sandbox testing, M-Pesa STK Push callbacks are not received. Safaricom sandbox environment appears down or the callback URL is not resolving correctly.", stepsToRepro:"1. Add item to cart. 2. Select M-Pesa at checkout. 3. Enter Safaricom test number 0708374149. 4. No STK push received.", adminReply:"Raised with Safaricom sandbox team. Temporary workaround: using ngrok tunnel for callbacks." },
      ],
      featureRequests:[
        { id:"fr302-1", title:"Loyalty points system — earn points on every order",     description:"Sara wants repeat customers to earn points and redeem them as store credit. Common in organic/lifestyle brands.", votes:6, status:"Under Review", createdAt:"2025-03-05T00:00:00Z" },
        { id:"fr302-2", title:"WhatsApp order confirmation notifications",               description:"Send automated order confirmations and shipping updates via WhatsApp Business API instead of email.", votes:9, status:"Planned", createdAt:"2025-03-08T00:00:00Z" },
        { id:"fr302-3", title:"Subscription boxes — weekly/monthly recurring orders",   description:"Allow customers to subscribe to a weekly or monthly organic produce box at a discounted rate.", votes:7, status:"Under Review", createdAt:"2025-03-15T00:00:00Z" },
      ],
      clientTasks:[
        { id:"ct302-1", title:"Provide product catalogue — names, descriptions, prices, images", description:"We need the full product list in the shared Google Sheet before we can build the store pages. At least 80% of products needed to start.", status:"In Progress", createdAt:"2025-03-03T00:00:00Z" },
        { id:"ct302-2", title:"Review and approve Figma design mockups",                          description:"Please review the Figma link shared on March 10 and leave comments directly on the designs. Approval needed before development starts.", status:"Requested",  createdAt:"2025-03-10T00:00:00Z" },
        { id:"ct302-3", title:"Register Safaricom Daraja API account",                           description:"GreenLeaf needs their own Daraja account to process M-Pesa payments under their business name. We'll guide you through it.", status:"Requested",  createdAt:"2025-03-15T00:00:00Z" },
      ],
      deliverables:[
        { id:"d302-1", name:"Discovery Workshop Notes & Project Scope",  fileName:"Scope_Document_v1.pdf",       uploadedAt:"2025-03-05T00:00:00Z" },
        { id:"d302-2", name:"Wireframes — Mobile & Desktop (PDF)",       fileName:"GreenLeaf_Wireframes.pdf",    uploadedAt:"2025-03-10T00:00:00Z" },
        { id:"d302-3", name:"Figma Design Link (shared externally)",     fileName:null,                          uploadedAt:"2025-03-10T00:00:00Z" },
        { id:"d302-4", name:"Product Catalogue Template (Google Sheet)", fileName:"Product_Catalogue_TPL.xlsx",  uploadedAt:"2025-03-03T00:00:00Z" },
      ],
    },
    {
      id:303, name:"SwiftLog Fleet Mobile App", clientId:103, clientName:"SwiftLog Africa",
      status:"In Progress", priority:"High", budget:85000, spent:52000, progress:61,
      deadline:"2025-05-31", startDate:"2024-12-01", assignee:2, assigneeName:"Brian Ochieng",
      description:"React Native application for SwiftLog's field operations — driver-side app for job acceptance and GPS check-ins, dispatcher dashboard for fleet visibility and route assignment.",
      chatParticipants:[1,2,103],
      tasks:[
        {id:1, title:"Figma prototype — driver & dispatcher flows",      done:true},
        {id:2, title:"Auth system — OTP login for drivers",              done:true},
        {id:3, title:"Driver module — job list, accept/reject, status",  done:true},
        {id:4, title:"Push notifications — FCM setup",                   done:true},
        {id:5, title:"Dispatcher module — fleet map view",               done:false},
        {id:6, title:"Real-time GPS tracking — WebSocket feed",          done:false},
        {id:7, title:"Trip history & earnings summary",                  done:false},
        {id:8, title:"Android + iOS build & TestFlight upload",          done:false},
        {id:9, title:"App Store & Play Store submission",                done:false},
      ],
      releases:[
        { id:"r303-1", version:"0.2.0", releaseType:"ALPHA",  releasedAt:"2024-12-20T00:00:00Z", releaseNotes:"Driver app alpha — OTP login, job list, and accept/reject flow working. Internal build only, not distributed to drivers yet.", acknowledgedAt:"2024-12-22T00:00:00Z", changelog:{ features:["OTP phone auth","Driver job list","Accept / reject job actions","Basic profile screen"], fixes:[], improvements:[], security:["Phone numbers hashed at rest"] } },
        { id:"r303-2", version:"0.5.0", releaseType:"BETA",   releasedAt:"2025-01-30T00:00:00Z", releaseNotes:"Beta build distributed to 5 pilot drivers in Nairobi. Push notifications live. Dispatcher web dashboard at 40%.", acknowledgedAt:"2025-02-01T00:00:00Z", changelog:{ features:["FCM push notifications","Job status updates (En Route, Arrived, Completed)","Offline job queue","Pilot TestFlight build"], fixes:["OTP timeout on Airtel lines","Job list not refreshing after network loss"], improvements:["App launch time cut by 35%","Background sync every 90s"], security:[] } },
        { id:"r303-3", version:"0.8.0", releaseType:"MINOR",  releasedAt:"2025-03-05T00:00:00Z", releaseNotes:"Sprint 4 — dispatcher map view complete with live vehicle pins. GPS tracking architecture finalized. 15 pilot drivers onboarded.", acknowledgedAt:"2025-03-07T00:00:00Z", changelog:{ features:["Dispatcher fleet map — live vehicle pins","Driver GPS heartbeat every 30s","Route history playback (last 24h)"], fixes:["GPS drift on Tecno devices","Map tiles not loading on slow 3G"], improvements:["WebSocket reconnect on network drop","Battery usage optimised — reduced GPS wake frequency"], security:[] } },
        { id:"r303-4", version:"0.9.0", releaseType:"RC",     releasedAt:"2025-03-22T00:00:00Z", releaseNotes:"Release candidate — all core features complete. Trip history, earnings summary, and dispatcher analytics done. Pending final QA and store submission.", acknowledgedAt:null, changelog:{ features:["Trip history — full log with map replay","Driver earnings summary & monthly breakdown","Dispatcher analytics — utilisation, on-time rate"], fixes:["Earnings calculation rounding error","Map crash on 0 active drivers"], improvements:["Reduced APK size by 18%","Pagination on trip history"], security:["API keys rotated","Certificate pinning added"] } },
      ],
      bugs:[
        { id:"b303-1", title:"GPS drops to 0,0 coordinates on Tecno Spark devices",    severity:"HIGH",     status:"Resolved",    createdAt:"2025-02-10T00:00:00Z", description:"On Tecno Spark 10 and Spark 20 series, the GPS location occasionally snaps to lat:0, lng:0 (ocean south of Africa) causing drivers to disappear off the dispatcher map.", stepsToRepro:"1. Use Tecno Spark 10. 2. Start a job. 3. Drive for 5–10 mins. 4. GPS pin resets to 0,0 for ~30s.", adminReply:"Fixed in v0.8.0 — added GPS sanity check that discards coordinates outside Kenya bounding box." },
        { id:"b303-2", title:"Push notifications not delivered on Xiaomi MIUI 14",     severity:"MEDIUM",   status:"In Progress", createdAt:"2025-03-10T00:00:00Z", description:"FCM push notifications are silently blocked on Xiaomi devices running MIUI 14. MIUI's aggressive battery optimisation kills the background service.", stepsToRepro:"1. Install on Xiaomi Redmi Note 13. 2. Lock screen / background app. 3. Dispatch a job. 4. No notification received.", adminReply:"MIUI requires explicit battery whitelist. Added in-app prompt to guide drivers through whitelisting. FCM high-priority flag also set." },
        { id:"b303-3", title:"Trip earnings show negative values after job cancellation",severity:"MEDIUM",   status:"Open",        createdAt:"2025-03-20T00:00:00Z", description:"When a job is cancelled mid-trip, the earnings summary shows a negative deduction instead of zero for that trip.", stepsToRepro:"1. Start a job. 2. Cancel it after 2km. 3. Open earnings tab. 4. Negative value shown.", adminReply:"" },
        { id:"b303-4", title:"App not installable on Android 8 (API 26)",              severity:"LOW",      status:"Open",        createdAt:"2025-03-18T00:00:00Z", description:"Play Store shows 'Your device isn't compatible' for Android 8.0 Oreo. Min SDK may have been set too high.", stepsToRepro:"1. Try installing from Play Store on Android 8 device.", adminReply:"" },
      ],
      featureRequests:[
        { id:"fr303-1", title:"In-app chat between driver and dispatcher",        description:"Real-time text chat so dispatchers can send instructions to drivers without needing WhatsApp.", votes:12, status:"Planned",      createdAt:"2025-01-15T00:00:00Z" },
        { id:"fr303-2", title:"Driver rating system after each delivery",          description:"Customers and dispatchers should be able to rate drivers 1–5 stars. Display driver average rating on profile.", votes:7,  status:"Under Review", createdAt:"2025-02-01T00:00:00Z" },
        { id:"fr303-3", title:"Multi-stop route optimisation",                     description:"Instead of one pickup + one dropoff, allow routes with up to 5 stops and auto-optimise order using Google Maps.", votes:5,  status:"Under Review", createdAt:"2025-02-20T00:00:00Z" },
        { id:"fr303-4", title:"Fuel log — drivers record fuel spend per trip",     description:"Simple form for drivers to log fuel spend. Aggregated in dispatcher dashboard for cost-per-km reporting.", votes:3,  status:"Declined",     createdAt:"2025-03-01T00:00:00Z" },
      ],
      clientTasks:[
        { id:"ct303-1", title:"Onboard 15 pilot drivers — share phone numbers",        description:"We need the Kenyan phone numbers of the 15 pilot drivers to provision their accounts and send TestFlight/APK links.", status:"Completed",  createdAt:"2025-01-20T00:00:00Z" },
        { id:"ct303-2", title:"Provide Google Maps API key (billing-enabled)",         description:"SwiftLog needs their own Maps key to avoid shared quota limits. Follow the guide in the project files.", status:"Completed",  createdAt:"2025-01-25T00:00:00Z" },
        { id:"ct303-3", title:"Review RC v0.9.0 and complete QA sign-off sheet",       description:"Please test all 24 scenarios in the QA sheet and mark pass/fail. We need this before Play Store submission.", status:"In Progress",createdAt:"2025-03-22T00:00:00Z" },
        { id:"ct303-4", title:"Create Google Play Developer & Apple Developer accounts",description:"Both accounts are required for store submission. Takes 1–3 days for Apple. We have a guide in the project files.", status:"In Progress",createdAt:"2025-03-10T00:00:00Z" },
        { id:"ct303-5", title:"Sign off on app icon, splash screen, and store listing", description:"Final artwork for Play Store listing — screenshots, feature graphic, short description. Draft shared in Figma.", status:"Requested",  createdAt:"2025-03-22T00:00:00Z" },
      ],
      deliverables:[
        { id:"d303-1", name:"SwiftLog App — Figma Prototype",              fileName:"SwiftLog_Figma_v1.fig",         uploadedAt:"2024-12-05T00:00:00Z" },
        { id:"d303-2", name:"Alpha Build v0.2.0 — APK (internal)",         fileName:"SwiftLog_Alpha_v0.2.0.apk",     uploadedAt:"2024-12-20T00:00:00Z" },
        { id:"d303-3", name:"Beta Build v0.5.0 — TestFlight + APK",        fileName:"SwiftLog_Beta_v0.5.0.apk",      uploadedAt:"2025-01-30T00:00:00Z" },
        { id:"d303-4", name:"Sprint 4 Build v0.8.0 — APK",                 fileName:"SwiftLog_v0.8.0.apk",           uploadedAt:"2025-03-05T00:00:00Z" },
        { id:"d303-5", name:"RC Build v0.9.0 — APK + TestFlight",          fileName:"SwiftLog_RC_v0.9.0.apk",        uploadedAt:"2025-03-22T00:00:00Z" },
        { id:"d303-6", name:"API Integration Guide (Google Maps + FCM)",    fileName:"SwiftLog_API_Guide.pdf",        uploadedAt:"2025-01-28T00:00:00Z" },
        { id:"d303-7", name:"QA Sign-off Sheet — v0.9.0",                   fileName:"QA_SignOff_v0.9.0.xlsx",        uploadedAt:"2025-03-22T00:00:00Z" },
        { id:"d303-8", name:"Play Store & App Store Setup Guide",           fileName:"Store_Submission_Guide.pdf",    uploadedAt:"2025-03-10T00:00:00Z" },
      ],
    },
    {
      id:304, name:"Qire Agency Website v2", clientId:null, clientName:"Internal",
      status:"Completed", priority:"Low", budget:0, spent:0, progress:100,
      deadline:"2024-12-15", startDate:"2024-10-01", assignee:2, assigneeName:"Brian Ochieng",
      description:"Redesigned Qire Agency marketing site — new brand identity, case studies section, services pages, contact form with CRM integration, and SEO foundation.",
      chatParticipants:[1,2],
      tasks:[
        {id:1, title:"Brand refresh — colours, type, logo system",  done:true},
        {id:2, title:"Figma designs — all pages",                   done:true},
        {id:3, title:"React + Vite development",                    done:true},
        {id:4, title:"Case studies section",                        done:true},
        {id:5, title:"Contact form → CRM integration",              done:true},
        {id:6, title:"SEO setup — meta, sitemap, schema",           done:true},
        {id:7, title:"Deploy to Railway + Cloudflare CDN",          done:true},
      ],
      releases:[
        { id:"r304-1", version:"1.0.0", releaseType:"MAJOR", releasedAt:"2024-12-15T00:00:00Z", releaseNotes:"Production launch of Qire Agency Website v2. All pages live, CRM integration tested, Lighthouse score 96.", acknowledgedAt:"2024-12-15T12:00:00Z", changelog:{ features:["New brand identity system","6 service pages","3 case studies","Contact → CRM pipeline","Blog scaffold"], fixes:[], improvements:["96 Lighthouse performance score","Core Web Vitals all green"], security:["CSP headers set","Form honeypot anti-spam"] } },
        { id:"r304-2", version:"1.1.0", releaseType:"MINOR", releasedAt:"2025-01-10T00:00:00Z", releaseNotes:"Post-launch improvements — 2 new case studies, QireOS product page, and mobile nav fixes reported after launch.", acknowledgedAt:"2025-01-10T15:00:00Z", changelog:{ features:["QireOS product page","2 new case studies (SwiftLog, TechVault)"], fixes:["Mobile nav z-index issue","Form validation on Safari"], improvements:["Image lazy loading","Font subsetting — 30KB saved"], security:[] } },
      ],
      bugs:[
        { id:"b304-1", title:"Contact form submissions not tagged in CRM source",  severity:"LOW",    status:"Resolved", createdAt:"2024-12-20T00:00:00Z", description:"Leads from the website contact form were arriving in the CRM without a source tag, making pipeline attribution impossible.", stepsToRepro:"1. Submit contact form. 2. Check CRM lead. 3. Source field is blank.", adminReply:"Fixed — source field now hardcoded to 'Website' on form submission handler. Deployed Jan 3." },
        { id:"b304-2", title:"Blog page throws 404 on direct URL access",          severity:"MEDIUM", status:"Resolved", createdAt:"2025-01-12T00:00:00Z", description:"Navigating directly to /blog or any blog post URL returns a 404. Only works when navigating from home page (SPA routing issue).", stepsToRepro:"1. Open qire.co.ke/blog directly in browser. 2. 404 page shown.", adminReply:"Fixed — Cloudflare Pages config updated with /* redirect rule for SPA routing. Deployed Jan 15." },
      ],
      featureRequests:[
        { id:"fr304-1", title:"Pricing page with service tiers",          description:"Clients often ask about pricing upfront. A structured pricing page (Starter / Growth / Enterprise) would reduce unqualified leads.", votes:4, status:"Planned",      createdAt:"2025-01-20T00:00:00Z" },
        { id:"fr304-2", title:"Live chat widget — Crisp or Intercom",     description:"Add a chat widget to capture leads who don't want to fill a form. Low friction for quick questions.", votes:3, status:"Under Review", createdAt:"2025-02-01T00:00:00Z" },
      ],
      clientTasks:[],
      deliverables:[
        { id:"d304-1", name:"Brand Identity System — Figma",           fileName:"Qire_Brand_System.fig",    uploadedAt:"2024-10-15T00:00:00Z" },
        { id:"d304-2", name:"Website Designs — All Pages (Figma PDF)", fileName:"Qire_Website_Designs.pdf", uploadedAt:"2024-11-01T00:00:00Z" },
        { id:"d304-3", name:"SEO Audit & Keyword Map",                  fileName:"SEO_Strategy_v1.pdf",      uploadedAt:"2024-11-15T00:00:00Z" },
        { id:"d304-4", name:"Launch Checklist — Signed Off",            fileName:"Launch_Checklist.pdf",     uploadedAt:"2024-12-14T00:00:00Z" },
        { id:"d304-5", name:"Post-Launch Performance Report (Jan 2025)",fileName:"Perf_Report_Jan2025.pdf",  uploadedAt:"2025-01-20T00:00:00Z" },
      ],
    },
  ],
  invoices: [
    {id:401, number:"INV-2025-001", clientId:101, projectId:301, amount:30000, kesAmount:3900000, currency:"USD", status:"Paid",    issuedAt:"2025-01-10", dueAt:"2025-01-31", paidAt:"2025-01-28", items:[{desc:"Dashboard — Phase 1 (Auth + Setup)",      qty:1, rate:30000}], notes:"Thank you for your business!"},
    {id:402, number:"INV-2025-002", clientId:101, projectId:301, amount:25000, kesAmount:3250000, currency:"USD", status:"Paid",    issuedAt:"2025-02-05", dueAt:"2025-02-28", paidAt:"2025-02-25", items:[{desc:"Dashboard — Phase 2 (UI Components)",     qty:1, rate:25000}], notes:""},
    {id:403, number:"INV-2025-003", clientId:103, projectId:303, amount:20000, kesAmount:2600000, currency:"USD", status:"Pending", issuedAt:"2025-03-18", dueAt:"2025-04-15", paidAt:null,         items:[{desc:"Fleet App — Driver Module",               qty:1, rate:20000}], notes:"Payment due within 30 days."},
    {id:404, number:"INV-2025-004", clientId:102, projectId:302, amount:5200,  kesAmount:676000,  currency:"USD", status:"Draft",   issuedAt:"2025-04-01", dueAt:"2025-05-01", paidAt:null,         items:[{desc:"E-commerce — Discovery & Wireframes",     qty:1, rate:5200}],  notes:""},
    {id:405, number:"INV-2025-005", clientId:103, projectId:303, amount:15000, kesAmount:1950000, currency:"USD", status:"Overdue", issuedAt:"2025-02-01", dueAt:"2025-03-01", paidAt:null,         items:[{desc:"Fleet App — Dispatcher Module Deposit",   qty:1, rate:15000}], notes:"Please settle immediately."},
  ],
  expenses: [
    {id:501, description:"AWS Hosting (March)",        category:"Infrastructure", amount:41280,  currency:"KES", type:"expense", date:"2025-03-01", deductible:true,  vendor:"Amazon Web Services"},
    {id:502, description:"Figma Pro Team Plan",        category:"Software",       amount:5805,   currency:"KES", type:"expense", date:"2025-03-01", deductible:true,  vendor:"Figma Inc."},
    {id:503, description:"Nairobi Tech Summit Ticket", category:"Marketing",      amount:19350,  currency:"KES", type:"expense", date:"2025-02-20", deductible:true,  vendor:"NTS Events"},
    {id:504, description:"Office Rent (March)",        category:"Operations",     amount:154800, currency:"KES", type:"expense", date:"2025-03-01", deductible:true,  vendor:"Prime Properties"},
    {id:505, description:"Freelance UI Designer",      category:"Contractor",     amount:322500, currency:"KES", type:"expense", date:"2025-02-15", deductible:true,  vendor:"Jane Waceke"},
    {id:506, description:"Google Workspace",           category:"Software",       amount:9288,   currency:"KES", type:"expense", date:"2025-03-01", deductible:true,  vendor:"Google LLC"},
    {id:507, description:"INV-2025-001 — TechVault Ltd (Invoice Paid)", category:"Income", amount:3900000, currency:"KES", type:"income", date:"2025-01-28", deductible:false, vendor:""},
    {id:508, description:"INV-2025-002 — TechVault Ltd (Invoice Paid)", category:"Income", amount:3250000, currency:"KES", type:"income", date:"2025-02-25", deductible:false, vendor:""},
  ],
  taxRecords: [
    {id:601, period:"Q4 2024", type:"VAT",        turnover:185000, taxDue:29600, taxPaid:29600, status:"Filed", filedDate:"2025-01-20", notes:"Submitted via iTax"},
    {id:602, period:"Q1 2025", type:"VAT",        turnover:210000, taxDue:33600, taxPaid:0,     status:"Due",   filedDate:"",           notes:"Due April 20th"},
    {id:603, period:"FY 2024", type:"Income Tax", turnover:420000, taxDue:63000, taxPaid:63000, status:"Filed", filedDate:"2025-03-31", notes:"Annual return filed"},
  ],
  letters: [
    {id:701, subject:"Welcome to Qire Agency — TechVault Ltd",     to:"James Mwangi\nTechVault Ltd\nNairobi, Kenya",     date:"2025-01-10", type:"Client Letter", status:"Sent",  body:"Dear James,\n\nThank you for choosing Qire Agency as your development partner. We are thrilled to welcome TechVault Ltd and look forward to delivering exceptional results on your dashboard project.\n\nOur team is fully briefed and work commences immediately. You will receive weekly progress updates every Friday.\n\nWarm regards,\nAmon Cheruiyot\nFounder & Lead Developer · Qire Agency\nqireagency@gmail.com"},
    {id:702, subject:"Project Completion Notice — Qire Website v2", to:"Internal\nQire Agency",                           date:"2024-12-20", type:"Other",         status:"Saved", body:"Dear Team,\n\nThis letter confirms the successful completion of the Qire Agency website v2 project as of December 15, 2024. The new site is live at qireagency.com and includes the updated portfolio, case studies, and service pages.\n\nWell done to everyone involved.\n\nAmon Cheruiyot\nFounder & Lead Developer · Qire Agency"},
    {id:703, subject:"Outstanding Invoice Reminder — INV-2025-005", to:"Michael Kariuki\nSwiftLog Africa\nNairobi, Kenya", date:"2025-03-10", type:"Notice",        status:"Sent",  body:"Dear Michael,\n\nThis is a formal reminder that invoice INV-2025-005 for USD 15,000 was due on March 1, 2025 and remains unpaid. Kindly arrange payment at your earliest convenience to avoid interruption of services.\n\nWarm regards,\nAmon Cheruiyot\nFounder & Lead Developer · Qire Agency\nqireagency@gmail.com"},
  ],
  ads: [
    {id:801, title:"Q-Trader EA — LinkedIn Campaign", platform:"LinkedIn", budget:65000,  spent:40560,  status:"Active",    startDate:"2025-03-01", endDate:"2025-04-30", clicks:1840, impressions:48200, conversions:23, notes:"Targeting forex traders in East Africa"},
    {id:802, title:"Web Services — Google Ads",       platform:"Google",   budget:103600, spent:103600, status:"Completed", startDate:"2025-01-01", endDate:"2025-02-28", clicks:3200, impressions:91000, conversions:11, notes:"Q1 brand awareness campaign"},
    {id:803, title:"GreenLeaf Launch — Meta Ads",     platform:"Meta",     budget:38700,  spent:15480,  status:"Active",    startDate:"2025-03-15", endDate:"2025-05-15", clicks:910,  impressions:22400, conversions:8,  notes:"E-commerce store launch ads"},
  ],
  waitlist: [
    {id:901, name:"Tom Githinji",  email:"tom@mzigo.co.ke",     product:"Q-Trader EA",  source:"Website",  status:"Waiting",  joinedAt:"2025-03-01", notes:""},
    {id:902, name:"Amina Yusuf",   email:"amina@horizonbc.com",  product:"Q-Trader EA",  source:"Referral", status:"Notified", joinedAt:"2025-02-20", notes:"Sent access email"},
    {id:903, name:"Caleb Mutua",   email:"caleb@devhive.co.ke",  product:"Web Services", source:"LinkedIn", status:"Waiting",  joinedAt:"2025-03-05", notes:""},
    {id:904, name:"Rachael Moraa", email:"rachael@finspark.ke",  product:"Q-Trader EA",  source:"Event",    status:"Waiting",  joinedAt:"2025-03-08", notes:"Met at Nairobi Forex Expo"},
  ],
  payroll: [
    {id:1001, recipient:"Brian Ochieng",   recipientId:2, recipientRole:"developer", period:"March 2025",    gross:90000, deductions:5000, net:85000, currency:"KES", status:"Paid",    paidAt:"2025-03-31", method:"Bank Transfer", notes:"",                       projectBreakdown:[], deductionsList:[{label:"NHIF",amount:3000},{label:"NSSF",amount:2000}]},
    {id:1002, recipient:"Cynthia Wanjiru", recipientId:3, recipientRole:"sales",     period:"March 2025",    gross:75000, deductions:5000, net:70000, currency:"KES", status:"Paid",    paidAt:"2025-03-31", method:"Bank Transfer", notes:"+ KES 5k commission",    projectBreakdown:[], deductionsList:[{label:"NHIF",amount:3000},{label:"NSSF",amount:2000}]},
    {id:1003, recipient:"David Kamau",     recipientId:4, recipientRole:"support",   period:"March 2025",    gross:65000, deductions:5000, net:60000, currency:"KES", status:"Pending", paidAt:null,          method:"Bank Transfer", notes:"",                       projectBreakdown:[], deductionsList:[{label:"NHIF",amount:3000},{label:"NSSF",amount:2000}]},
    {id:1004, recipient:"Esther Njeri",    recipientId:5, recipientRole:"marketing", period:"March 2025",    gross:70000, deductions:5000, net:65000, currency:"KES", status:"Pending", paidAt:null,          method:"Bank Transfer", notes:"",                       projectBreakdown:[], deductionsList:[{label:"NHIF",amount:3000},{label:"NSSF",amount:2000}]},
    {id:1005, recipient:"Brian Ochieng",   recipientId:2, recipientRole:"developer", period:"February 2025", gross:90000, deductions:5000, net:85000, currency:"KES", status:"Paid",    paidAt:"2025-02-28", method:"Bank Transfer", notes:"",                       projectBreakdown:[], deductionsList:[{label:"NHIF",amount:3000},{label:"NSSF",amount:2000}]},
  ],
  contracts: [
    {id:1101, title:"TechVault Dashboard — Master Services Agreement", clientId:101, clientName:"TechVault Ltd",      value:120000, currency:"USD", status:"Active", signedDate:"2025-01-08", expiryDate:"2026-01-08", type:"Retainer",    body:"This Master Services Agreement is entered into between Qire Agency and TechVault Ltd for the provision of software development services..."},
    {id:1102, title:"SwiftLog Fleet App — Project Agreement",          clientId:103, clientName:"SwiftLog Africa",    value:85000,  currency:"USD", status:"Active", signedDate:"2024-11-28", expiryDate:"2025-11-28", type:"Fixed Price", body:"This Project Agreement covers the full development of the SwiftLog Fleet Management mobile application..."},
    {id:1103, title:"GreenLeaf E-commerce — Project Agreement",        clientId:102, clientName:"GreenLeaf Organics", value:45000,  currency:"USD", status:"Draft",  signedDate:"",           expiryDate:"",           type:"Fixed Price", body:"This Project Agreement covers the development of the GreenLeaf Organics e-commerce platform..."},
  ],
  timeEntries: [
    {id:1201, userId:2, userName:"Brian Ochieng",  projectId:301, projectName:"TechVault Dashboard Rebuild", task:"Auth module implementation",       hours:18, date:"2025-03-10", billable:true},
    {id:1202, userId:2, userName:"Brian Ochieng",  projectId:301, projectName:"TechVault Dashboard Rebuild", task:"Dashboard UI components",           hours:24, date:"2025-03-17", billable:true},
    {id:1203, userId:2, userName:"Brian Ochieng",  projectId:303, projectName:"SwiftLog Fleet Mobile App",   task:"Driver module — React Native",      hours:30, date:"2025-03-15", billable:true},
    {id:1204, userId:1, userName:"Amon Cheruiyot", projectId:301, projectName:"TechVault Dashboard Rebuild", task:"Client calls & project management", hours:6,  date:"2025-03-20", billable:true},
  ],
  proposals: [
    {id:1301, title:"TechVault — Phase 3 Analytics Module", clientId:101, clientName:"TechVault Ltd",    total:35000, currency:"USD", status:"Sent",     createdAt:"2025-03-15", validUntil:"2025-04-15", items:[{desc:"Advanced analytics dashboard",      qty:1, rate:20000},{desc:"Custom reporting engine",            qty:1, rate:10000},{desc:"Data export & API",                   qty:1, rate:5000}],  notes:"Includes 3 months post-launch support."},
    {id:1302, title:"AgriTech Kenya — Farm Management App",  clientId:null, clientName:"AgriTech Kenya", total:40000, currency:"USD", status:"Draft",    createdAt:"2025-02-25", validUntil:"2025-03-25", items:[{desc:"Mobile app (iOS + Android)",         qty:1, rate:30000},{desc:"Admin web portal",                    qty:1, rate:7000}, {desc:"SMS notification integration",          qty:1, rate:3000}],  notes:""},
    {id:1303, title:"Sunwave Energy — Corporate Website",    clientId:null, clientName:"Sunwave Energy",  total:22000, currency:"USD", status:"Accepted", createdAt:"2025-01-05", validUntil:"2025-02-05", items:[{desc:"5-page responsive website",          qty:1, rate:15000},{desc:"CMS integration",                     qty:1, rate:4000}, {desc:"SEO setup",                             qty:1, rate:3000}],  notes:"Contract being prepared."},
  ],
  messages: [
    {id:1401, projectId:301, fromId:"1",          senderName:"Amon Cheruiyot", type:"admin", body:"Hi James, quick update — the dashboard auth module is complete and on staging. Please review and share feedback.", read:true,  createdAt:"2025-03-18T09:00:00Z", attachments:[]},
    {id:1402, projectId:301, fromId:"client-101", senderName:"James Mwangi",   type:"admin", body:"Looks great! One thing — can we add 2FA support? Our IT team requires it.",                                        read:true,  createdAt:"2025-03-18T10:30:00Z", attachments:[]},
    {id:1403, projectId:301, fromId:"1",          senderName:"Amon Cheruiyot", type:"admin", body:"Absolutely, we will include TOTP-based 2FA in the next sprint. No extra charge.",                                  read:false, createdAt:"2025-03-19T08:00:00Z", attachments:[]},
    {id:1404, projectId:303, fromId:"1",          senderName:"Amon Cheruiyot", type:"admin", body:"Michael, the driver module is ready for testing. Test credentials sent to your email.",                            read:true,  createdAt:"2025-03-17T11:00:00Z", attachments:[]},
    {id:1405, projectId:301, fromId:"2",          senderName:"Brian Ochieng",  type:"dev",   body:"Reports module 40% done. ETA end of week. Need sign-off on Recharts vs Chart.js before I proceed.",               read:false, createdAt:"2025-03-20T14:00:00Z", attachments:[]},
  ],
  clientAccounts: [
    {id:101, username:"james",   password:"demo1234", name:"James Mwangi",   email:"james@techvault.co.ke",  active:true, clientId:101, company:"TechVault Ltd"},
    {id:102, username:"sara",    password:"demo1234", name:"Sara Omondi",    email:"sara@greenleaf.co.ke",   active:true, clientId:102, company:"GreenLeaf Organics"},
    {id:103, username:"michael", password:"demo1234", name:"Michael Kariuki", email:"michael@swiftlog.co.ke", active:true, clientId:103, company:"SwiftLog Africa"},
  ],
};

// ─── Q-TRADER MOCK DATA ───────────────────────────────────────────────────────
const QT_SEED = {
  products: [
    { id:"prod-1", name:"Q-Trader EA", slug:"qtrader-ea", description:"Intelligent zone-based MT5 Expert Advisor", price:299, currency:"USD", active:true },
    { id:"prod-2", name:"ArbitragePlus", slug:"arbitrage-plus", description:"Real-time P2P & OTC arbitrage scanner", price:99, currency:"USD", active:true },
  ],
  versions: [
    { id:"ver-1", version:"2.7.0", fileName:"QTrader_v2.7.0.ex5", releaseType:"MINOR", releaseNotes:"Improved zone detection algorithm, added session filter UI, fixed XAUUSD spread handling.", createdAt:"2025-03-15T08:00:00Z", isLatest:true,  changelog:{ features:["Session filter UI","XAUUSD spread fix"], fixes:["Zone overlap bug","Memory leak on close"], improvements:["40% faster zone calc"], security:[] } },
    { id:"ver-2", version:"2.6.2", fileName:"QTrader_v2.6.2.ex5", releaseType:"PATCH", releaseNotes:"Critical fix for MT5 build 4000+ compatibility.",                                          createdAt:"2025-02-10T08:00:00Z", isLatest:false, changelog:{ features:[], fixes:["MT5 b4000 crash","Account type detection"], improvements:[], security:["Token validation hardened"] } },
    { id:"ver-3", version:"2.6.0", fileName:"QTrader_v2.6.0.ex5", releaseType:"MINOR", releaseNotes:"Added multi-session mode and adaptive ATR multiplier.",                                    createdAt:"2025-01-05T08:00:00Z", isLatest:false, changelog:{ features:["Multi-session mode","Adaptive ATR"], fixes:["Lot size rounding"], improvements:["Dashboard refresh rate"], security:[] } },
  ],
  clients: [
    { id:"qtc-1", name:"James Mwangi",    email:"james@techvault.co.ke",  phone:"+254 712 345 678", company:"TechVault Ltd",       createdAt:"2024-03-01T00:00:00Z" },
    { id:"qtc-2", name:"Fatuma Hassan",   email:"fatuma@coastmedia.co",   phone:"+254 733 777 888", company:"Coast Media",          createdAt:"2024-06-15T00:00:00Z" },
    { id:"qtc-3", name:"Tom Githinji",    email:"tom@mzigo.co.ke",        phone:"+254 799 000 111", company:"Mzigo Capital",        createdAt:"2024-09-20T00:00:00Z" },
    { id:"qtc-4", name:"Amina Yusuf",     email:"amina@horizonbc.com",    phone:"+971 50 123 4567", company:"Horizon Business Corp", createdAt:"2025-01-10T00:00:00Z" },
    { id:"qtc-5", name:"George Otieno",   email:"george@agritech.ke",     phone:"+254 722 444 555", company:"AgriTech Kenya",       createdAt:"2025-02-01T00:00:00Z" },
  ],
  licenses: [
    { id:"lic-1", key:"QT-2024-JMSW-8812", clientId:"qtc-1", clientName:"James Mwangi",  email:"james@techvault.co.ke",  product:"Q-Trader EA", status:"ACTIVE",   expiresAt:"2026-03-01T00:00:00Z", activatedAt:"2024-03-15T00:00:00Z", maxAccounts:2, boundAccounts:[{accountNumber:"12345678",broker:"ICMarkets"},{accountNumber:"87654321",broker:"Pepperstone"}], notes:"VIP client — priority support" },
    { id:"lic-2", key:"QT-2024-FTHH-3341", clientId:"qtc-2", clientName:"Fatuma Hassan", email:"fatuma@coastmedia.co",   product:"Q-Trader EA", status:"ACTIVE",   expiresAt:"2026-06-15T00:00:00Z", activatedAt:"2024-06-20T00:00:00Z", maxAccounts:1, boundAccounts:[{accountNumber:"55512789",broker:"XM Group"}],   notes:"" },
    { id:"lic-3", key:"QT-2024-TGTJ-7750", clientId:"qtc-3", clientName:"Tom Githinji",  email:"tom@mzigo.co.ke",        product:"Q-Trader EA", status:"ACTIVE",   expiresAt:"2025-09-20T00:00:00Z", activatedAt:"2024-09-25T00:00:00Z", maxAccounts:1, boundAccounts:[{accountNumber:"99801234",broker:"HotForex"}],   notes:"Requested 2nd seat" },
    { id:"lic-4", key:"QT-2025-AMYU-1122", clientId:"qtc-4", clientName:"Amina Yusuf",   email:"amina@horizonbc.com",    product:"Q-Trader EA", status:"ACTIVE",   expiresAt:"2026-01-10T00:00:00Z", activatedAt:"2025-01-12T00:00:00Z", maxAccounts:1, boundAccounts:[],                                                 notes:"UAE-based trader" },
    { id:"lic-5", key:"QT-2025-GOAG-5599", clientId:"qtc-5", clientName:"George Otieno", email:"george@agritech.ke",     product:"Q-Trader EA", status:"INACTIVE", expiresAt:"2025-03-01T00:00:00Z", activatedAt:"2025-02-05T00:00:00Z", maxAccounts:1, boundAccounts:[{accountNumber:"44401122",broker:"Exness"}],    notes:"Expired — follow up for renewal" },
  ],
  bugs: [
    { id:"bug-1", title:"EA freezes on GBPJPY during London open", description:"When session filter is set to London-only, the EA hangs for 3-5s on GBPJPY pairs during the 08:00 UTC open spike.", severity:"HIGH",     status:"IN_PROGRESS", clientId:"qtc-1", clientName:"James Mwangi",  createdAt:"2025-03-10T09:00:00Z", adminReply:"Reproduced. Fix being tested in v2.7.1 — ETA 1 week.", version:"2.7.0" },
    { id:"bug-2", title:"Lot size rounding error on micro accounts", description:"On 0.01 base lot accounts, the adaptive sizing rounds up to 0.02 when it should stay at 0.01.", severity:"MEDIUM",   status:"RESOLVED",    clientId:"qtc-2", clientName:"Fatuma Hassan", createdAt:"2025-02-18T14:00:00Z", adminReply:"Fixed in v2.7.0 — please update.",                                   version:"2.6.2" },
    { id:"bug-3", title:"Dashboard not loading on MT5 mobile app",  description:"The 5-column live dashboard doesn't render on MetaTrader 5 mobile (Android). Desktop works fine.",                 severity:"LOW",      status:"OPEN",        clientId:"qtc-3", clientName:"Tom Githinji",  createdAt:"2025-03-20T11:00:00Z", adminReply:"",                                                                   version:"2.7.0" },
    { id:"bug-4", title:"XAUUSD zones not drawing correctly",        description:"Supply/demand zones on XAU/USD are offset by approximately 2 pips after the March broker feed update.",            severity:"CRITICAL",  status:"OPEN",        clientId:"qtc-4", clientName:"Amina Yusuf",   createdAt:"2025-03-22T07:30:00Z", adminReply:"",                                                                   version:"2.7.0" },
  ],
  tickets: [
    { id:"tk-1", subject:"How do I add a second MT5 account to my license?", body:"I have a second broker account and want to run Q-Trader on both. How do I bind it to my existing license?",    status:"RESOLVED", priority:"MEDIUM", clientId:"qtc-1", clientName:"James Mwangi",  createdAt:"2025-03-05T10:00:00Z", reply:"Log into your client portal → Q-Trader → License → Add Account. You have 2 seats on your plan.",                          resolvedAt:"2025-03-06T09:00:00Z" },
    { id:"tk-2", subject:"EA keeps stopping after 30 minutes",             body:"The EA runs fine then stops with error 'Trade context busy'. MT5 build 3980.",                                        status:"OPEN",     priority:"HIGH",   clientId:"qtc-3", clientName:"Tom Githinji",  createdAt:"2025-03-18T08:00:00Z", reply:"",                                                                                                                              resolvedAt:null },
    { id:"tk-3", subject:"Request upgrade to 2-seat license",              body:"I'd like to upgrade my current single-seat license to a 2-seat license. What's the cost?",                           status:"OPEN",     priority:"LOW",    clientId:"qtc-2", clientName:"Fatuma Hassan", createdAt:"2025-03-21T13:00:00Z", reply:"",                                                                                                                              resolvedAt:null },
    { id:"tk-4", subject:"Backtesting results differ from live",            body:"My backtest shows 68% winrate but live trading is showing 41%. Is this normal or a settings issue?",                status:"RESOLVED", priority:"MEDIUM", clientId:"qtc-4", clientName:"Amina Yusuf",   createdAt:"2025-02-28T16:00:00Z", reply:"Normal — backtest uses tick data while live uses real spreads/slippage. See our guide: docs.qire.co.ke/backtest-vs-live.", resolvedAt:"2025-03-01T11:00:00Z" },
    { id:"tk-5", subject:"License expired — renewal pricing?",             body:"My license expired yesterday. What are the renewal options?",                                                         status:"OPEN",     priority:"MEDIUM", clientId:"qtc-5", clientName:"George Otieno",  createdAt:"2025-03-22T09:00:00Z", reply:"",                                                                                                                              resolvedAt:null },
  ],
  subRequests: [
    { id:"sub-1", clientId:"qtc-3", clientName:"Tom Githinji",   email:"tom@mzigo.co.ke",       product:"Q-Trader EA", plan:"2-Seat License", status:"Pending",  requestedAt:"2025-03-20T10:00:00Z", notes:"Wants to run on 2 accounts simultaneously",              declineReason:"" },
    { id:"sub-2", clientId:"qtc-5", clientName:"George Otieno",  email:"george@agritech.ke",    product:"Q-Trader EA", plan:"1-Year Renewal", status:"Pending",  requestedAt:"2025-03-22T08:00:00Z", notes:"Renewal after expiry — wants discount",                   declineReason:"" },
    { id:"sub-3", clientId:"qtc-2", clientName:"Fatuma Hassan",  email:"fatuma@coastmedia.co",  product:"Q-Trader EA", plan:"1-Seat License", status:"Approved", requestedAt:"2025-01-10T09:00:00Z", notes:"",                                                        declineReason:"" },
    { id:"sub-4", clientId:"qtc-4", clientName:"Amina Yusuf",    email:"amina@horizonbc.com",   product:"Q-Trader EA", plan:"1-Seat License", status:"Declined", requestedAt:"2024-12-15T14:00:00Z", notes:"Was unsure about product fit",                            declineReason:"License already active under different email — please contact support." },
  ],
};
// ─── ICONS ────────────────────────────────────────────────────────────────────
const IC = {
  dashboard:  <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
  clients:    <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  projects:   <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></>,
  invoices:   <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
  finance:    <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
  tax:        <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><polyline points="9 10 12 13 15 10"/></>,
  letters:    <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  ads:        <><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></>,
  waitlist:   <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></>,
  team:       <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  sun:        <><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
  moon:       <><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></>,
  plus:       <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
  edit:       <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  trash:      <><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
  check:      <><polyline points="20 6 9 17 4 12"/></>,
  x:          <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  clock:      <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
  alert:      <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
  search:     <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  chevron:    <><polyline points="6 9 12 15 18 9"/></>,
  lock:       <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  logout:     <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
  eye:        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
  eyeOff:     <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>,
  shield:     <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  print:      <><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>,
  send:       <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
  trending:   <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>,
  download:   <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
  receipt:    <><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="15" y2="13"/></>,
  chart:      <><rect x="2" y="10" width="4" height="12"/><rect x="9" y="6" width="4" height="16"/><rect x="16" y="2" width="4" height="20"/></>,
  mail:       <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  payroll:    <><path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2z"/><path d="M2 20c0-4 4-7 10-7s10 3 10 7"/><line x1="16" y1="11" x2="22" y2="11"/><line x1="19" y1="8" x2="19" y2="14"/></>,
  contracts:  <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/><polyline points="9 9 10 9"/></>,
  reports: <><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></>,
  timer: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="6.34" y1="4.34" x2="7.75" y2="5.75"/></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
  portal:   <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/><line x1="12" y1="15" x2="12" y2="17"/></>,
  proposal: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></>,
  message:  <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
  funnel:   <><path d="M22 3H2l8 9.46V19l4 2V12.46L22 3z"/></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  phone:    <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.4a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></>,
  arrowR:   <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>,
  folder:   <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></>,
  upload:   <><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></>,
  pen:      <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>,
};
const I = ({ n, s=16, c="currentColor" }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{IC[n]}</svg>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtKES = n => `KES ${Number(n).toLocaleString()}`;
const fmtUSD = n => `$${Number(n).toLocaleString()}`;

const toDateInput = (v) => v ? new Date(v).toISOString().split('T')[0] : '';
const todayStr = () => new Date().toISOString().slice(0,10);
const hue = name => name.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % 360;
const sc  = (s, t) => ({
  Active:t.teal, Lead:t.amber, Proposal:t.blue, Inactive:t.textDimmer,
  "In Progress":t.teal, Planning:t.blue, Review:t.amber, Done:t.textDimmer,
  Paid:t.teal, Pending:t.amber, Overdue:t.red, Draft:t.textDimmer,
  Waiting:t.textDimmer, Notified:t.teal,
  High:t.red, Medium:t.amber, Low:t.teal,
  Filed:t.teal, Due:t.amber, Sent:t.teal, Saved:t.amber,
  Active_ad:t.teal, Paused:t.amber, Completed:t.textDimmer,
  income:t.teal, expense:t.red, 
}[s] || t.textDim);

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────
const Avatar = ({ name, size=34, t }) => {
  const h=hue(name), dk=t.id==="dark";
  return <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,background:`hsl(${h},${dk?"28%":"22%"},${dk?"22%":"68%"})`,border:`1.5px solid hsl(${h},${dk?"28%":"20%"},${dk?"30%":"52%"})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.33,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,color:`hsl(${h},${dk?"38%":"28%"},${dk?"78%":"22%"})`}}>{name.split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase()}</div>;
};

const Badge = ({ label, t }) => {
  const c=sc(label,t);
  const bg = t.id==="light" ? `${c}18` : `${c}1a`;
  const border = t.id==="light" ? `1px solid ${c}55` : `1px solid ${c}40`;
  return <span style={{display:"inline-flex",alignItems:"center",padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:500,fontFamily:"'Fira Code',monospace",letterSpacing:"0.1em",textTransform:"uppercase",background:bg,color:c,border}}>{label}</span>;
};

const Card = ({ children, t, style={}, onClick }) => {
  const [hov,setHov]=useState(false);
  return <div onClick={onClick} onMouseEnter={()=>onClick&&setHov(true)} onMouseLeave={()=>setHov(false)} style={{background:t.surface,borderRadius:8,padding:"1.4rem",border:`1px solid ${hov&&onClick?t.teal+"30":t.border}`,boxShadow:onClick&&hov?`inset 0 0 0 1px ${t.teal}22, ${t.shadow}`:"none",transition:"all 0.18s",cursor:onClick?"pointer":"default",...style}}>{children}</div>;
};

const Field = ({ t, label, value, onChange, type="text", placeholder="", options, textarea, required, icon, rows=3 }) => {
  const base={width:"100%",background:t.surface2,color:t.text,borderRadius:5,fontSize:14,fontFamily:"'Space Grotesk',sans-serif",outline:"none",resize:"vertical",border:`1px solid ${t.border}`,padding:icon?"8px 12px 8px 34px":"8px 12px",minHeight:36,transition:"border-color 0.15s"};
  return <div style={{display:"flex",flexDirection:"column",gap:5}}>
    {label&&<label style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.25em",color:t.textDimmer,textTransform:"uppercase"}}>{label}{required&&" *"}</label>}
    <div style={{position:"relative"}}>
      {icon&&<div style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><I n={icon} s={13} c={t.textDimmer}/></div>}
      {options
        ?<select value={value} onChange={e=>onChange(e.target.value)} style={{...base,cursor:"pointer"}}>{options.map(o=><option key={o.v||o} value={o.v||o}>{o.l||o}</option>)}</select>
        :textarea
          ?<textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{...base,minHeight:80}}/>
          :<input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={base}/>}
    </div>
  </div>;
};

const Modal = ({ open, onClose, title, children, t, wide=false }) => {
  if(!open) return null;
  return createPortal(
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.65)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"1rem",
    }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="fade-up" style={{
        background:t.surface, borderRadius:8, width:"100%",
        maxWidth:wide?700:560,
        maxHeight:"90vh",
        display:"flex", flexDirection:"column",
        border:`1px solid ${t.borderBright}`,
        borderTop:`2px solid ${t.teal}`,
        boxShadow:"0 24px 80px rgba(0,0,0,0.4)",
      }}>
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"1.1rem 1.5rem",
          borderBottom:`1px solid ${t.border}`,
          flexShrink:0,
        }}>
          <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:16,color:t.text,letterSpacing:"0.01em"}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4}}><I n="x" s={16}/></button>
        </div>
        <div style={{
          padding:"1.5rem",
          display:"flex", flexDirection:"column", gap:"0.85rem",
          overflowY:"auto",
          flex:1,
        }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Btn = ({ children, onClick, variant="primary", t, size="md", icon, disabled, fullWidth }) => {
  const [hov,setHov]=useState(false);
  const pad=size==="sm"?"5px 11px":"8px 18px", fz=size==="sm"?11:13;
  const vars={
    primary:{background:t.teal,color:t.id==="light"?"#fff":"#050507",border:"none"},
    secondary:{background:"transparent",color:t.textDim,border:`1px solid ${t.borderBright}`},
    danger:{background:`${t.red}18`,color:t.red,border:`1px solid ${t.red}44`},
    ghost:{background:hov?`${t.teal}12`:"transparent",color:hov?t.teal:t.textDim,border:"none"},
    amber:{background:`${t.amber}18`,color:t.amber,border:`1px solid ${t.amber}44`},
  };
  return <button onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,borderRadius:5,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,cursor:disabled?"not-allowed":"pointer",fontSize:fz,padding:pad,transition:"all 0.16s",opacity:disabled?0.45:1,width:fullWidth?"100%":"auto",letterSpacing:"0.04em",...vars[variant]}} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>{icon&&<I n={icon} s={13}/>}{children}</button>;
};

// ─── FIXED: SectionHeader — no duplicate letterSpacing ────────────────────────
const SectionHeader = ({ eyebrow, title, sub, t, action }) => (
  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:"1.5rem",flexWrap:"wrap",gap:"0.75rem"}}>
    <div>
      <div style={{fontFamily:"'Fira Code',monospace",fontSize:10,letterSpacing:"0.3em",textTransform:"uppercase",color:t.teal,marginBottom:6,display:"flex",alignItems:"center",gap:8}}>{eyebrow}<span style={{display:"inline-block",height:1,width:40,background:t.tealDim,opacity:0.5}}/></div>
      <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:400, letterSpacing:"0.025em", fontStyle:"italic", color:t.text, lineHeight:1.1}}>{title}</div>
      {sub&&<div style={{fontSize:15, color:t.textDim, fontWeight:400, marginTop:4}}>{sub}</div>}
    </div>
    {action}
  </div>
);

const ProgressBar = ({ pct, t, color }) => (
  <div style={{height:2,background:t.border,borderRadius:4,overflow:"hidden"}}>
    <div style={{height:"100%",width:`${pct}%`,background:color||(pct===100?t.teal:t.blue),borderRadius:4,transition:"width 0.6s"}}/>
  </div>
);

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
const EmptyState = ({ title, sub, t, action }) => (
  <div className="q-empty fade-up" style={{color:t.text}}>
    <div style={{width:40,height:40,borderRadius:"50%",border:`1px solid ${t.border}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"0.5rem",opacity:0.4}}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.6"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
    </div>
    <div className="q-empty-title" style={{color:t.text}}>{title || "Nothing here yet"}</div>
    {sub && <div className="q-empty-sub" style={{color:t.textDimmer}}>{sub}</div>}
    {action && <div style={{marginTop:"1rem"}}>{action}</div>}
  </div>
);

// ─── FIXED: StatCard — no duplicate letterSpacing ────────────────────────────
const StatCard = ({ label, value, sub, icon, color, t }) => {
  const iconBg = t.id==="light" ? `${color}15` : `${color}18`;
  const iconBorder = t.id==="light" ? `1px solid ${color}40` : `1px solid ${color}30`;
  return (
  <Card t={t}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
      <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.25em",color:t.textDimmer,textTransform:"uppercase"}}>{label}</span>
      <div style={{width:30,height:30,borderRadius:6,background:iconBg,border:iconBorder,display:"flex",alignItems:"center",justifyContent:"center"}}><I n={icon} s={13} c={color}/></div>
    </div>
    <div className="q-stat-value" style={{fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:400, letterSpacing:"0.01em", color:t.text, lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:t.textDimmer,marginTop:5,fontFamily:"'Fira Code',monospace"}}>{sub}</div>}
  </Card>
);};
 

// ─── PRINT HELPERS ────────────────────────────────────────────────────────────
// (PrintHeader and PrintFooter reserved for future PDF generation use)

// ─── PRINT ENGINE ─────────────────────────────────────────────────────────────
// ─── PRINT ENGINE v4 — inline header/footer, proper PDF print ────────────────
// Key insight: position:fixed breaks inside an iframe when printing because the
// browser clips the iframe at its content bounds. We use a full-page TABLE layout
// instead: <thead> = header (repeats on every page), <tfoot> = footer (repeats),
// <tbody> = content. @page margins are 0 so the bands touch the paper edges.

const SIG_PLACEHOLDER_ADMIN  = '__ADMIN_SIG__';
const SIG_PLACEHOLDER_CLIENT = '__CLIENT_SIG__';
const SIG_DATE_ADMIN         = '__ADMIN_SIG_DATE__';
const SIG_DATE_CLIENT        = '__CLIENT_SIG_DATE__';

const fmtSigDate = iso => iso
  ? new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })
  : '______________________';

const buildPrintHTML = (bodyHtml, title, opts = {}) => {
  const { adminSig = '', clientSig = '', adminSignedAt = '', clientSignedAt = '' } = opts;

  const adminImgHtml  = adminSig
    ? `<img src="${adminSig}" style="height:46px;max-width:180px;display:block;object-fit:contain" alt="Amon Cheruiyot signature"/>`
    : `<div style="height:46px"></div>`;
  const clientImgHtml = clientSig
    ? `<img src="${clientSig}" style="height:46px;max-width:180px;display:block;object-fit:contain" alt="client signature"/>`
    : `<div style="height:46px"></div>`;

  const finalBody = bodyHtml
    .replace(SIG_PLACEHOLDER_ADMIN,  adminImgHtml)
    .replace(SIG_PLACEHOLDER_CLIENT, clientImgHtml)
    .replace(SIG_DATE_ADMIN,  fmtSigDate(adminSignedAt))
    .replace(SIG_DATE_CLIENT, fmtSigDate(clientSignedAt));

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>${title}</title>
  <link href="https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Space+Grotesk:wght@300;400;500;600&family=Fira+Code:wght@400;500&display=swap" rel="stylesheet"/>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* Zero page margins — our table bands touch the paper edge */
    @page { size: A4; margin: 0; }

    html, body {
      width: 210mm;
      background: white;
      font-family: 'Space Grotesk', sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }

    /* ── Full-page table trick ── */
    /* The outer table fills the whole page. thead/tfoot repeat on every printed page.
       This is the only cross-browser reliable way to get repeating header/footer in print. */
    .page-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    /* Screen: tbody cell fills remaining height; print: natural page flow */
    .page-table > thead > tr,
    .page-table > tfoot > tr,
    .page-table > tbody > tr { vertical-align: top; }
    .page-table > thead > tr > td,
    .page-table > tfoot > tr > td,
    .page-table > tbody > tr > td { vertical-align: top; padding: 0; border: none; font-size: inherit; }
    @media screen {
      .page-table > tbody > tr > td { min-height: 297mm; }
    }
    @media print {
      .page-table { min-height: 297mm; }
    }

    /* ── HEADER band ── */
    .lh-header {
      background: #050506;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .lh-header-inner {
      padding: 1.1rem 2.5rem 0.9rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .lh-accent {
      height: 3px;
      background: linear-gradient(90deg, #00c9a7 0%, #00c9a730 60%, transparent 100%);
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .lh-logo { display: flex; align-items: center; gap: 0.7rem; }
    .lh-mark {
      width: 38px; height: 38px;
      background: #00c9a7; border-radius: 7px;
      display: flex; align-items: center; justify-content: center;
      font-family: 'Syne', sans-serif; font-weight: 800; font-size: 1.25rem;
      color: #050506; flex-shrink: 0;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .lh-wordmark { font-family: 'Syne', sans-serif; font-size: 1.2rem; font-weight: 700; color: #f0f0f5; line-height: 1.1; }
    .lh-wordmark span { color: #00c9a7; }
    .lh-tagline { font-family: 'Fira Code', monospace; font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase; color: #aaa; margin-top: 0.12rem; }
    .lh-contact { text-align: right; }
    .lh-contact p { font-family: 'Fira Code', monospace; font-size: 0.72rem; color: #aaa; letter-spacing: 0.03em; line-height: 1.75; }

    /* ── FOOTER band ── */
    .lh-footer {
      background: #050506;
      padding: 0.65rem 2.5rem;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .lh-footer-inner {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .lh-footer .fl { font-family: 'Fira Code', monospace; font-size: 0.68rem; letter-spacing: 0.14em; color: #aaa; text-transform: uppercase; }
    .lh-footer .fc { display: flex; align-items: center; gap: 0.35rem; }
    .lh-footer .fd { width: 5px; height: 5px; border-radius: 50%; background: #00c9a7; flex-shrink:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .lh-footer .fb { font-family: 'Syne', sans-serif; font-size: 0.78rem; font-weight: 700; color: #f0f0f5; }
    .lh-footer .fb span { color: #00c9a7; }
    .lh-footer .fr { font-family: 'Fira Code', monospace; font-size: 0.68rem; letter-spacing: 0.07em; color: #aaa; }

    /* ── Watermark ── */
    .watermark {
      position: fixed;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%) rotate(-28deg);
      font-family: 'Syne', sans-serif;
      font-weight: 800;
      font-size: 160px;
      color: rgba(0, 201, 167, 0.07);
      pointer-events: none;
      z-index: 0;
      user-select: none;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      line-height: 1;
    }

    /* ── Body content cell ── */
    .lh-body {
      position: relative;
      z-index: 1;
      padding: 1rem 2.5rem 1.5rem;
    }

    /* ── Print tweaks ── */
    @media print {
      .watermark { position: fixed; }
      .sig-row, .info-box, .no-break, .total-bar { page-break-inside: avoid; break-inside: avoid; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr    { page-break-inside: avoid; break-inside: avoid; }
      h1, h2, h3, .label { page-break-after: avoid; break-after: avoid; }
    }

    /* ── Shared document styles ── */
    .doc-divider { height: 1px; background: #e0e0e8; margin: 1.3rem 0; }
    .label { font-family: 'Fira Code', monospace; font-size: 0.68rem; letter-spacing: 0.22em; text-transform: uppercase; color: #888; margin-bottom: 0.3rem; display: block; }

    .sig-row { display: grid; grid-template-columns: 1fr 1fr; gap: 2.5rem; margin-top: 2rem; }
    .sig-cell .sig-line { border-bottom: 1px solid #ccc; min-height: 52px; margin-bottom: 5px; display: flex; align-items: flex-end; padding-bottom: 3px; }
    .sig-cell .sig-name { font-family: 'Space Grotesk', sans-serif; font-size: 0.9rem; color: #222; font-weight: 500; }
    .sig-cell .sig-role { font-family: 'Space Grotesk', sans-serif; font-size: 0.8rem; color: #888; margin-top: 1px; }
    .sig-cell .sig-date { font-family: 'Fira Code', monospace; font-size: 0.68rem; color: #aaa; margin-top: 4px; }

    /* Doc tables inside .lh-body only — never styles the page-table shell */
    .lh-body table { width: 100%; border-collapse: collapse; }
    .lh-body th { font-family: 'Fira Code', monospace; font-size: 0.68rem; letter-spacing: 0.14em; text-transform: uppercase; color: #888; padding: 0 10px 8px 0; text-align: left; border-bottom: 2px solid #050506; white-space: nowrap; }
    .lh-body th:last-child { text-align: right; padding-right: 0; }
    .lh-body td { font-family: 'Space Grotesk', sans-serif; font-size: 0.92rem; color: #222; padding: 10px 10px 10px 0; border-bottom: 1px solid #eee; vertical-align: top; }
    .lh-body td:last-child { text-align: right; padding-right: 0; font-family: 'Fira Code', monospace; }
    .total-bar { display: flex; justify-content: space-between; padding: 11px 14px; background: #050506; border-radius: 5px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .total-label { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 0.88rem; color: #f0f0f5; }
    .total-val   { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 0.88rem; color: #00c9a7; }
    .info-box { padding: 0.9rem 1.1rem; background: #f8f8fa; border-radius: 6px; border: 1px solid #e0e0e8; }
    .status-pill { display: inline-block; padding: 3px 10px; border-radius: 3px; font-family: 'Fira Code', monospace; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.12em; }
    .meta-grid { display: flex; justify-content: space-between; gap: 2rem; flex-wrap: wrap; margin-bottom: 1.6rem; align-items: flex-start; }
  </style>
</head>
<body>
  <div class="watermark" aria-hidden="true">DEMO</div>

  <!--
    Full-page table layout:
    thead = header band (repeats top of every page in print)
    tfoot = footer band (repeats bottom of every page in print)
    tbody > td = scrollable body content
  -->
  <table class="page-table" role="presentation">
    <thead>
      <tr>
        <td>
          <div class="lh-header">
            <div class="lh-header-inner">
              <div class="lh-logo">
                <div class="lh-mark">Q</div>
                <div>
                  <div class="lh-wordmark">Qire<span>Agency</span></div>
                  <div class="lh-tagline">Financial Technology · East Africa</div>
                </div>
              </div>
              <div class="lh-contact">
                <p>qireagency@gmail.com</p>
                <p>Eldoret, Kenya · Registered Business</p>
                <p>Reg. No: BN-12345678</p>
              </div>
            </div>
            <div class="lh-accent"></div>
          </div>
        </td>
      </tr>
    </thead>
    <tfoot>
      <tr>
        <td>
          <div style="height:0.4rem"></div>
          <div class="lh-footer">
            <div class="lh-footer-inner">
              <span class="fl">© 2026 Qire Agency</span>
              <div class="fc">
                <div class="fd"></div>
                <span class="fb">Qire<span>Agency</span></span>
              </div>
              <span class="fr">Reg. Business · Kenya · qireagency@gmail.com</span>
            </div>
          </div>
        </td>
      </tr>
    </tfoot>
    <tbody>
      <tr>
        <td>
          <div class="lh-body">
            ${finalBody}
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</body>
</html>`;
};

const sigBlock = (adminSig, clientLabel = 'Client', clientRole = '') => `
  <div class="doc-divider" style="margin-top:2.5rem"></div>
  <div class="sig-row no-break">
    <div class="sig-cell">
      <div class="label">Qire Agency — Authorized Signature</div>
      <div class="sig-line">${SIG_PLACEHOLDER_ADMIN}</div>
      <div class="sig-name">Amon Cheruiyot</div>
      <div class="sig-role">Founder · Qire Agency</div>
      <div class="sig-date">Date: ${SIG_DATE_ADMIN}</div>
    </div>
    <div class="sig-cell">
      <div class="label">${clientLabel} — Signature</div>
      <div class="sig-line">${SIG_PLACEHOLDER_CLIENT}</div>
      <div class="sig-name">${clientLabel}</div>
      ${clientRole ? `<div class="sig-role">${clientRole}</div>` : ''}
      <div class="sig-date">Date: ${SIG_DATE_CLIENT}</div>
    </div>
  </div>
`;

const buildPayslipHTML = (record) => {
  const monthName = record.period ? new Date(record.period + "-01").toLocaleDateString("en-GB",{month:"long",year:"numeric"}) : record.period;
  const role = record.recipientRole || record.role || '';
  const isOwner = role.toLowerCase().includes("owner") || role.toLowerCase().includes("founder");
  const ratePerHour = Number(record.ratePerHour || 0);

  // Build earnings rows — if we have projectBreakdown use it, else single gross line
  const breakdown = record.projectBreakdown || []; // [{projectName, hours, ratePerHour, amount}]
  const hasBreakdown = breakdown.length > 0;

  const earningsRows = hasBreakdown
    ? breakdown.map(b => `
        <tr style="border-bottom:1px solid #eee">
          <td style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;padding:11px 12px 11px 0">
            ${b.projectName}
            <div style="font-family:'Fira Code',monospace;font-size:9px;color:#888;margin-top:2px;letter-spacing:.04em">${b.hours} hrs @ KES ${Number(b.ratePerHour||ratePerHour).toLocaleString()}/hr</div>
          </td>
          <td style="font-family:'Fira Code',monospace;font-size:13px;color:#111;text-align:right;padding:11px 0">KES ${Number(b.amount||0).toLocaleString()}</td>
        </tr>`).join('')
    : `<tr style="border-bottom:1px solid #eee">
        <td style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;padding:11px 12px 11px 0">
          ${isOwner ? "Owner Draw" : role ? role + " — Monthly Compensation" : "Monthly Compensation"}
          ${ratePerHour > 0 ? `<div style="font-family:'Fira Code',monospace;font-size:9px;color:#888;margin-top:2px">Rate: KES ${Number(ratePerHour).toLocaleString()}/hr</div>` : ''}
        </td>
        <td style="font-family:'Fira Code',monospace;font-size:13px;color:#111;text-align:right;padding:11px 0">KES ${Number(record.gross||0).toLocaleString()}</td>
      </tr>`;

  // Build deductions rows — support array or single value
  const deductionsList = record.deductionsList || (Number(record.deductions||0) > 0 ? [{label:'Deductions', amount: Number(record.deductions||0)}] : []);
  const deductionsRows = deductionsList.length > 0
    ? deductionsList.map(d => `
        <tr style="border-bottom:1px solid #eee">
          <td style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;padding:11px 12px 11px 0">${d.label}</td>
          <td style="font-family:'Fira Code',monospace;font-size:13px;color:#c0394e;text-align:right;padding:11px 0">- KES ${Number(d.amount||0).toLocaleString()}</td>
        </tr>`).join('')
    : `<tr><td colspan="2" style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#aaa;padding:14px 0;text-align:center;font-style:italic">No deductions this period</td></tr>`;

  const paidDate = record.paidAt
    ? (record.paidAt.includes('T') ? new Date(record.paidAt).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : record.paidAt)
    : 'Pending';

  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:2rem;gap:2rem;align-items:flex-start">
      <div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:5px">Employee / Recipient</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:18px;color:#111;margin-bottom:3px">${record.recipient}</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#555;line-height:1.7">${role}</div>
        ${ratePerHour > 0 ? `<div style="margin-top:4px;font-family:'Fira Code',monospace;font-size:9px;color:#00c9a7;letter-spacing:.06em">KES ${Number(ratePerHour).toLocaleString()}/hr</div>` : ''}
      </div>
      <div style="text-align:right">
        <div style="font-family:Syne,sans-serif;font-weight:800;font-size:13px;color:#050506;text-transform:uppercase;letter-spacing:-.01em;margin-bottom:8px">Payslip</div>
        <div style="font-family:'Fira Code',monospace;font-size:10px;color:#888;line-height:2.2">
          Period: ${monthName}<br/>
          Paid: ${paidDate}<br/>
          Method: ${record.method || '—'}<br/>
          Status: ${record.status || '—'}
        </div>
      </div>
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:2rem"></div>

    <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:10px">Earnings</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:1.5rem">
      <thead>
        <tr style="border-bottom:2px solid #050506">
          <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:left">Description</th>
          <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 0 9px;text-align:right">Amount (KES)</th>
        </tr>
      </thead>
      <tbody>${earningsRows}</tbody>
    </table>

    <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:10px">Deductions</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:2rem">
      <thead>
        <tr style="border-bottom:2px solid #050506">
          <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:left">Description</th>
          <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 0 9px;text-align:right">Amount (KES)</th>
        </tr>
      </thead>
      <tbody>${deductionsRows}</tbody>
    </table>

    <div style="height:1px;background:#e0e0e8;margin-bottom:1rem"></div>
    <div style="display:flex;justify-content:flex-end;margin-bottom:2.5rem">
      <div style="width:340px">
        <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee">
          <span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666">Gross Pay</span>
          <span style="font-family:'Fira Code',monospace;font-size:14px;color:#333">KES ${Number(record.gross||0).toLocaleString()}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee">
          <span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666">Total Deductions</span>
          <span style="font-family:'Fira Code',monospace;font-size:14px;color:#c0394e">- KES ${Number(record.deductions||0).toLocaleString()}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:13px 12px;background:#050506;margin-top:8px;border-radius:5px">
          <span style="font-family:Syne,sans-serif;font-weight:700;font-size:17px;color:#f0f0f5">Net Pay</span>
          <span style="font-family:Syne,sans-serif;font-weight:700;font-size:17px;color:#00c9a7">KES ${Number(record.net||0).toLocaleString()}</span>
        </div>
      </div>
    </div>
    <div style="padding:1.2rem;background:#f8f8fa;border-radius:7px;border:1px solid #e0e0e8;margin-top:1rem">
      <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:10px">Payment Details</div>
      <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#333;line-height:2">
        Method: <strong>${record.method || '—'}</strong><br/>
        Issued by: <strong>Qire Agency</strong><br/>
        Reg No: <strong>BN-12345678</strong>
      </div>
      ${record.notes ? `<div style="margin-top:8px;font-family:'Space Grotesk',sans-serif;font-size:13px;color:#888;font-style:italic">${record.notes}</div>` : ""}
    </div>
    <div class="doc-divider" style="margin-top:2rem"></div>
    <div class="sig-row no-break" style="display:grid;grid-template-columns:1fr 1fr;gap:2.5rem;margin-top:1.5rem">
      <div class="sig-cell">
        <div class="label">Authorized By — Qire Agency</div>
        <div class="sig-line">__ADMIN_SIG__</div>
        <div class="sig-name">Amon Cheruiyot</div>
        <div class="sig-role">Founder · Qire Agency</div>
        <div class="sig-date">Date: ${SIG_DATE_ADMIN}</div>
      </div>
      <div></div>
    </div>
  `;
};

// ─── SIGNATURE CANVAS MODAL ───────────────────────────────────────────────────
const SignaturePad = ({ label, onSave, onClose, t }) => {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);

  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    // Scale for canvas internal vs display size
    return {
      x: (src.clientX - r.left) * (canvas.width / r.width),
      y: (src.clientY - r.top)  * (canvas.height / r.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };
  const endDraw = () => { drawing.current = false; lastPos.current = null; };

  // Attach touch listeners imperatively with { passive: false } so
  // e.preventDefault() works — React JSX touch handlers are passive by default.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('touchstart', startDraw, { passive: false });
    canvas.addEventListener('touchmove',  draw,      { passive: false });
    canvas.addEventListener('touchend',   endDraw,   { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', startDraw);
      canvas.removeEventListener('touchmove',  draw);
      canvas.removeEventListener('touchend',   endDraw);
    };
  }, []);

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  const save = () => {
    const c = canvasRef.current;
    const dataUrl = c.toDataURL('image/png');
    const blank = document.createElement('canvas');
    blank.width = c.width; blank.height = c.height;
    if (dataUrl === blank.toDataURL('image/png')) return;
    onSave(dataUrl);
  };

  return createPortal(
    <div style={{position:'fixed',inset:0,zIndex:20000,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
      <div style={{background:t.surface,borderRadius:10,padding:'1.5rem',width:'100%',maxWidth:480,boxShadow:'0 24px 80px rgba(0,0,0,0.5)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
          <div>
            <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16,color:t.text}}>Sign Document</div>
            <div style={{fontFamily:"'Fira Code',monospace",fontSize:10,color:t.textDim,textTransform:'uppercase',letterSpacing:'0.15em',marginTop:2}}>{label}</div>
          </div>
          <button onClick={onClose} style={{background:'transparent',border:'none',color:t.textDim,cursor:'pointer',padding:4}}><I n="x" s={16}/></button>
        </div>

        <div style={{border:`2px solid ${t.border}`,borderRadius:8,background:'#fff',position:'relative',marginBottom:'1rem',touchAction:'none',userSelect:'none',overflow:'hidden'}}>
          <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',fontFamily:"'Fira Code',monospace",fontSize:11,color:'#ccc',pointerEvents:'none',whiteSpace:'nowrap'}}>Draw your signature here</div>
          {/* Touch events attached via useEffect above; mouse events via JSX */}
          <canvas
            ref={canvasRef}
            width={440}
            height={160}
            style={{display:'block',width:'100%',height:160,cursor:'crosshair',borderRadius:6}}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
          />
        </div>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={clear} style={{padding:'7px 16px',borderRadius:6,background:'transparent',border:`1px solid ${t.border}`,color:t.textDim,cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontSize:12}}>
            Clear
          </button>
          <button onClick={onClose} style={{padding:'7px 16px',borderRadius:6,background:'transparent',border:`1px solid ${t.border}`,color:t.textDim,cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontSize:12}}>
            Cancel
          </button>
          <button onClick={save} style={{padding:'7px 20px',borderRadius:6,background:'#00c9a7',border:'none',color:'#050507',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:12}}>
            Apply Signature
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── PDF PREVIEW OVERLAY ──────────────────────────────────────────────────────
const PDFPreview = ({ html, title, onClose, t,
  user = null,
  // doc type flags
  isContract = false, contractId = null, setContracts = null,
  isProposal = false, proposalId = null, setProposals = null,
  isInvoice  = false, invoiceId  = null, setInvoices  = null,
  isLetter   = false, letterId   = null, setLetters   = null,
  isPayslip  = false, payslipId  = null, setPayroll   = null,
  // pre-existing sigs and dates loaded from record
  initialAdminSig = '', initialClientSig = '',
  initialAdminSignedAt = '', initialClientSignedAt = '',
}) => {
  const [adminSig,        setAdminSig]        = useState(initialAdminSig);
  const [clientSig,       setClientSig]       = useState(initialClientSig);
  const [adminSignedAt,   setAdminSignedAt]   = useState(initialAdminSignedAt);
  const [clientSignedAt,  setClientSignedAt]  = useState(initialClientSignedAt);
  const [sigPad,          setSigPad]          = useState(null);
  const iframeRef = useRef(null);

  // Reset sigs whenever a different document is opened.
  const docKey = String(contractId||proposalId||invoiceId||letterId||payslipId||'');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAdminSig(initialAdminSig || '');
    setClientSig(initialClientSig || '');
    setAdminSignedAt(initialAdminSignedAt || '');
    setClientSignedAt(initialClientSignedAt || '');
    setSigPad(null);
  }, [docKey]);

  if (!html) return null;

  // ── Role detection ──────────────────────────────────────────────────────────
  const role      = (user?.role || '').toLowerCase();
  const isAdmin   = role === 'admin';
  const isClientU = role === 'client';

  // Authorize button: admin only, only on signable docs, only if not yet signed
  const canAdminSign  = isAdmin && (isContract||isProposal||isInvoice||isLetter||isPayslip) && !adminSig;
  // Client sign button: client only, only on contract/proposal/invoice, only if not yet signed
  const canClientSign = isClientU && (isContract||isProposal||isInvoice) && !clientSig;

  const builtHtml = buildPrintHTML(html, title, { adminSig, clientSig, adminSignedAt, clientSignedAt });

  const doPrint = () => {
    const iframe = iframeRef.current;
    const orig = document.title;
    document.title = title;
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => { document.title = orig; }, 1000);
    }, 300);
  };

  const saveSignature = async (type, dataUrl) => {
    const now = new Date().toISOString();
    if (type === 'admin')  { setAdminSig(dataUrl);  setAdminSignedAt(now); }
    if (type === 'client') { setClientSig(dataUrl); setClientSignedAt(now); }
    setSigPad(null);

    const field = type === 'admin' ? 'adminSignature' : 'clientSignature';
    const isClientSig = type === 'client';

    // Persist to each doc type
    try {
      if (isContract && contractId && setContracts) {
        let res;
        if (isClientSig) {
          res = await apiFetch(`/api/v1/contracts/${contractId}/sign`, { method:'POST', body: JSON.stringify({ clientSignature: dataUrl, clientSignedAt: now }) });
        } else {
          res = await apiFetch(`/api/v1/contracts/${contractId}`, { method:'PATCH', body: JSON.stringify({ adminSignature: dataUrl, adminSignedAt: now }) });
        }
        if (res.contract) setContracts(cs => cs.map(c => c.id === contractId ? { ...c, ...res.contract } : c));
        else setContracts(cs => cs.map(c => c.id === contractId ? { ...c, [field]: dataUrl, ...(isClientSig ? { status:'SIGNED', signedAt: now, clientSignedAt: now } : { adminSignedAt: now }) } : c));
      }
      if (isProposal && proposalId && setProposals) {
        let res;
        if (isClientSig) {
          res = await apiFetch(`/api/v1/proposals/${proposalId}/sign`, { method:'POST', body: JSON.stringify({ clientSignature: dataUrl, clientSignedAt: now }) });
        } else {
          res = await apiFetch(`/api/v1/proposals/${proposalId}`, { method:'PATCH', body: JSON.stringify({ adminSignature: dataUrl, adminSignedAt: now }) });
        }
        if (res.proposal) setProposals(ps => ps.map(p => p.id === proposalId ? { ...p, ...res.proposal } : p));
        else setProposals(ps => ps.map(p => p.id === proposalId ? { ...p, [field]: dataUrl, ...(isClientSig ? { status:'Accepted', signedAt: now, clientSignedAt: now } : { adminSignedAt: now }) } : p));
      }
      if (isInvoice && invoiceId && setInvoices) {
        let res;
        if (isClientSig) {
          res = await apiFetch(`/api/v1/finance/invoices/${invoiceId}/sign`, { method:'POST', body: JSON.stringify({ clientSignature: dataUrl, clientSignedAt: now }) });
        } else {
          res = await apiFetch(`/api/v1/finance/invoices/${invoiceId}`, { method:'PATCH', body: JSON.stringify({ adminSignature: dataUrl, adminSignedAt: now }) });
        }
        if (res.invoice) setInvoices(iv => iv.map(i => i.id === invoiceId ? { ...i, ...res.invoice } : i));
        else setInvoices(iv => iv.map(i => i.id === invoiceId ? { ...i, [field]: dataUrl, ...(isClientSig ? { clientSignedAt: now } : { adminSignedAt: now }) } : i));
      }
      if (isLetter && letterId && setLetters) {
        const res = await apiFetch(`/api/v1/letters/${letterId}`, { method:'PATCH', body: JSON.stringify({ adminSignature: dataUrl, adminSignedAt: now }) });
        if (res.letter) setLetters(ls => ls.map(l => l.id === letterId ? { ...l, ...res.letter } : l));
        else setLetters(ls => ls.map(l => l.id === letterId ? { ...l, adminSignature: dataUrl, adminSignedAt: now } : l));
      }
      if (isPayslip && payslipId && setPayroll) {
        const res = await apiFetch(`/api/v1/finance/payroll/${payslipId}`, { method:'PATCH', body: JSON.stringify({ adminSignature: dataUrl, adminSignedAt: now }) });
        if (res.payroll) setPayroll(pr => pr.map(p => p.id === payslipId ? { ...p, ...res.payroll } : p));
        else setPayroll(pr => pr.map(p => p.id === payslipId ? { ...p, adminSignature: dataUrl, adminSignedAt: now } : p));
      }
    } catch (e) {
      console.warn('sig persist failed', e);
    }
  };

  return createPortal(
    <div style={{position:'fixed',inset:0,zIndex:10000,background:'#181818',display:'flex',flexDirection:'column'}}>

      {/* ── Top bar ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0.6rem 1rem',background:'#111',borderBottom:'1px solid #222',flexShrink:0,gap:8,overflowX:'auto'}}>
        <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:13,color:'#f0f0f5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flexShrink:1,minWidth:0}}>{title}</span>
        <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>
          {canAdminSign && (
            <button onClick={() => setSigPad('admin')}
              style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',borderRadius:6,background:'#1a1a1a',color:'#c9a84c',border:'1px solid #c9a84c40',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>
              <I n="pen" s={11} c="#c9a84c"/> Authorize
            </button>
          )}
          {adminSig && (isContract||isProposal||isInvoice||isLetter||isPayslip) && (
            <span style={{display:'flex',alignItems:'center',gap:4,padding:'6px 10px',borderRadius:6,background:'#00c9a715',color:'#00c9a7',border:'1px solid #00c9a730',fontSize:11,fontFamily:"'Fira Code',monospace",letterSpacing:'0.05em',whiteSpace:'nowrap'}}>
              <I n="check" s={10} c="#00c9a7"/> Signed
            </span>
          )}
          {canClientSign && (
            <button onClick={() => setSigPad('client')}
              style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',borderRadius:6,background:'#1a1a1a',color:'#a78bfa',border:'1px solid #a78bfa40',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>
              <I n="pen" s={11} c="#a78bfa"/> Client Sign
            </button>
          )}
          {clientSig && (isContract||isProposal||isInvoice) && (
            <span style={{display:'flex',alignItems:'center',gap:4,padding:'6px 10px',borderRadius:6,background:'#a78bfa15',color:'#a78bfa',border:'1px solid #a78bfa30',fontSize:11,fontFamily:"'Fira Code',monospace",letterSpacing:'0.05em',whiteSpace:'nowrap'}}>
              <I n="check" s={10} c="#a78bfa"/> Signed
            </span>
          )}
          <button onClick={doPrint}
            style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:6,background:'#00c9a7',color:'#050507',border:'none',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:12,whiteSpace:'nowrap'}}>
            <I n="print" s={12} c="#050507"/> Save PDF
          </button>
          <button onClick={onClose}
            style={{display:'flex',alignItems:'center',justifyContent:'center',width:32,height:32,borderRadius:6,background:'transparent',color:'#666',border:'1px solid #333',cursor:'pointer',flexShrink:0}}>
            <I n="x" s={13}/>
          </button>
        </div>
      </div>

      {/* ── Preview area — mobile-responsive ── */}
      <div style={{flex:1, overflow:'auto', background:'#181818', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:'0.75rem 0'}}>
        {(() => {
          const availW = Math.min(window.innerWidth - 16, 794);
          const scale  = availW / 794;
          const scaledH = Math.round(1123 * scale);
          return (
            <div style={{width: availW, height: scaledH, position:'relative', flexShrink:0, borderRadius:4, overflow:'hidden', boxShadow:'0 8px 60px rgba(0,0,0,0.7)'}}>
              <iframe
                ref={iframeRef}
                key={adminSig + clientSig}
                srcDoc={builtHtml}
                style={{
                  width: 794,
                  height: 1123,
                  border:'none',
                  background:'white',
                  display:'block',
                  transformOrigin:'top left',
                  transform:`scale(${scale})`,
                  position:'absolute',
                  top:0, left:0,
                }}
                title={title}
              />
            </div>
          );
        })()}
      </div>

      {/* Signature pad modal */}
      {sigPad && (
        <SignaturePad
          label={sigPad === 'admin' ? 'Qire Agency — Amon Cheruiyot' : 'Client Signature'}
          onSave={(url) => saveSignature(sigPad, url)}
          onClose={() => setSigPad(null)}
          t={t}
        />
      )}
    </div>,
    document.body
  );
};

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
const LoginPage = ({ t, onLogin, team }) => {
  const [username,setUsername]=useState(""); const [password,setPassword]=useState(""); 
  const [showPass,setShowPass]=useState(false); const [error,setError]=useState(""); const [loading,setLoading]=useState(false);
  const handle = () => {
    setLoading(true); setError("");
    // Demo: check team accounts first, then client accounts
    const teamUser = (team||INITIAL_TEAM).filter(u=>u.active!==false)
      .find(u=>u.username===username && u.password===password);
    if (teamUser) { setLoading(false); onLogin(teamUser); return; }
    // Check client accounts
    const clientAcc = (SEED.clientAccounts||[]).find(c=>c.active!==false && c.username===username && c.password===password);
    if (clientAcc) {
      setLoading(false);
      onLogin({ ...clientAcc, role: 'client', id: clientAcc.clientId });
      return;
    }
    setLoading(false);
    setError("Invalid credentials. Try: amon / demo1234");
  };
  return <div style={{minHeight:"100vh",background:t.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem"}}>
    <div className="fade-up" style={{width:"100%",maxWidth:380}}>
      <div style={{textAlign:"center",marginBottom:"2.5rem"}}>
        <div style={{display:"inline-flex",marginBottom:"1rem"}}><LogoMark size={52}/></div>
        <div style={{fontFamily:"'Syne',sans-serif",fontSize:26,fontWeight:800,color:t.text,letterSpacing:"-0.03em",lineHeight:1.1}}>Qire<span style={{color:t.teal}}>OS</span></div>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:"0.2em",textTransform:"uppercase",marginTop:5}}>Agency Management System</div>
      </div>
      {/* Demo banner */}
      <div style={{background:`${t.teal}18`,border:`1px solid ${t.teal}44`,borderRadius:7,padding:"10px 14px",marginBottom:"0.75rem",fontSize:11,fontFamily:"'Fira Code',monospace",color:t.teal,lineHeight:1.6}}>
        <div style={{fontWeight:700,marginBottom:4}}> INTERACTIVE DEMO</div>
        <div style={{color:t.textDim,fontSize:10}}>Try any role below — all data is mock. Nothing is saved.</div>
        <div style={{marginTop:6,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2px 12px"}}>
          {[["amon","Admin"],["brian","Developer"],["cynthia","Sales"],["david","Support"],["esther","Marketing"],["james","Client"]].map(([u,r])=>(
            <button key={u} onClick={()=>{setUsername(u);setPassword("demo1234");}} style={{background:"none",border:"none",padding:"1px 0",cursor:"pointer",textAlign:"left",color:t.teal,fontSize:10,fontFamily:"'Fira Code',monospace"}}>→ {u} <span style={{color:t.textDimmer}}>({r})</span></button>
          ))}
        </div>
        <div style={{marginTop:4,fontSize:9,color:t.textDimmer}}>Password for all: demo1234</div>
      </div>
      <div style={{background:t.surface,borderRadius:8,padding:"2rem",border:`1px solid ${t.border}`}}>
        <div style={{marginBottom:"1.5rem"}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:18,fontWeight:700,color:t.text,letterSpacing:"-0.02em"}}>Sign In</div>
          <div style={{width:32,height:1,background:t.teal,margin:"7px 0"}}/>
          <div style={{fontSize:14,color:t.textDimmer,fontWeight:400}}>Access your agency workspace</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.85rem"}}>
          <Field t={t} label="Username" value={username} onChange={setUsername} placeholder="Enter username" icon="lock"/>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.25em",color:t.textDimmer,textTransform:"uppercase"}}>Password</label>
            <div style={{position:"relative"}}>
              <input type={showPass?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} placeholder="Enter password" style={{width:"100%",background:t.surface2,color:t.text,padding:"8px 36px 8px 12px",borderRadius:5,fontSize:13,outline:"none",fontFamily:"'Space Grotesk',sans-serif",minHeight:36,border:`1px solid ${t.border}`}}/>
              <button onClick={()=>setShowPass(!showPass)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:t.textDimmer,padding:0}}><I n={showPass?"eyeOff":"eye"} s={14}/></button>
            </div>
          </div>
          {error&&<div style={{background:`${t.red}15`,border:`1px solid ${t.red}44`,borderRadius:5,padding:"8px 12px",fontSize:12,color:t.red,display:"flex",alignItems:"center",gap:6}}><I n="alert" s={13} c={t.red}/>{error}</div>}
          <Btn t={t} onClick={handle} disabled={loading||!username||!password} fullWidth>{loading?"Signing in…":"Sign In →"}</Btn>
        </div>

      </div>
      <div style={{textAlign:"center",marginTop:"1.5rem",fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:"0.12em"}}>© 2026 QIRE AGENCY · ELDORET, KENYA</div>
    </div>
  </div>;
};

// ─── MODULE: SALES PIPELINE ───────────────────────────────────────────────────
const LEAD_STAGES = ["NEW","CONTACTED","DISCOVERY","PROPOSED","WON","LOST","JUNK"];

// ─── LeadDrawer — proper component (useState must be at top level) ─────────────
const LeadDrawer = ({ detail, t, move, addNote, convertToClient, onClose, onScheduleCall }) => {
  const [noteInput, setNoteInput] = useState("");
  const submitNote = () => { addNote(detail.id, noteInput); setNoteInput(""); };
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  return createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9200,display:"flex",justifyContent:"flex-end"}} onClick={onClose}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)"}}/>
      <div
        className="fade-in"
        style={{
          position:"relative",
          width: isMobile ? "100vw" : "min(500px,100vw)",
          height: isMobile ? "92dvh" : "100%",
          marginTop: isMobile ? "8dvh" : 0,
          borderRadius: isMobile ? "16px 16px 0 0" : 0,
          background:t.surface,
          borderLeft: isMobile ? "none" : `1px solid ${t.border}`,
          borderTop: isMobile ? `1px solid ${t.border}` : "none",
          overflowY:"auto",
          display:"flex",
          flexDirection:"column",
        }}
        onClick={e=>e.stopPropagation()}
      >
        {/* Drag handle on mobile */}
        {isMobile && <div style={{width:36,height:4,borderRadius:2,background:t.border,margin:"10px auto 0",flexShrink:0}}/>}
        <div style={{padding:"1.25rem 1.5rem",borderBottom:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexShrink:0}}>
          <div style={{minWidth:0,flex:1,paddingRight:8}}>
            <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:800,fontSize:18,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{detail.name}</div>
            <div style={{fontSize:13,color:t.textDim,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{detail.company||detail.email}</div>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
            <span style={{fontSize:9,padding:"3px 8px",borderRadius:3,background:`${STAGE_COLOR(detail.status)}18`,color:STAGE_COLOR(detail.status),fontFamily:"'Fira Code',monospace",textTransform:"uppercase",border:`1px solid ${STAGE_COLOR(detail.status)}40`}}>{detail.status}</span>
            <Btn variant="ghost" size="sm" t={t} onClick={onClose}><I n="x" s={14}/></Btn>
          </div>
        </div>
        <div style={{padding:"1.25rem 1.5rem",display:"flex",flexDirection:"column",gap:"1.25rem",flex:1,overflowY:"auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[["Email",detail.email],["Phone",detail.phone||"—"],["Source",detail.source],["Product",detail.product||"—"],["Budget",detail.budget?`$${Number(detail.budget).toLocaleString()}`:"—"],["Added",detail.createdAt?toDateInput(detail.createdAt):"—"]].map(([label,val])=>(
              <div key={label} style={{padding:"0.6rem 0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`}}>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:3}}>{label}</div>
                <div style={{fontSize:14,color:t.text,fontFamily:"'Space Grotesk',sans-serif",fontWeight:500,wordBreak:"break-all"}}>{val}</div>
              </div>
            ))}
          </div>
          {detail.message&&<div style={{fontSize:13,color:t.textDim,padding:"0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`,fontStyle:"italic"}}>{detail.message}</div>}
          <div>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.2em",marginBottom:8}}>Move to Stage</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {LEAD_STAGES.filter(s=>s!==detail.status).map(s=>(
                <button key={s} onClick={()=>move(detail.id,s)} style={{padding:"6px 12px",borderRadius:4,fontSize:10,fontFamily:"'Fira Code',monospace",cursor:"pointer",background:`${STAGE_COLOR(s)}15`,color:STAGE_COLOR(s),border:`1px solid ${STAGE_COLOR(s)}40`,textTransform:"uppercase",letterSpacing:"0.08em"}}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <Btn variant="secondary" t={t} icon="calendar" onClick={()=>onScheduleCall(detail)}>Schedule Call</Btn>
            {!["Won","Lost","Junk"].includes(detail.status)&&(
              <Btn t={t} icon="arrowR" onClick={()=>convertToClient(detail)}>Convert to Client</Btn>
            )}
          </div>
          <div>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.2em",marginBottom:8}}>Activity</div>
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              <input value={noteInput} onChange={e=>setNoteInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")submitNote();}} placeholder="Add a note… (Enter to save)" style={{flex:1,padding:"8px 10px",background:t.surface2,color:t.text,border:`1px solid ${t.border}`,borderRadius:5,fontSize:13,fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
              <Btn variant="secondary" size="sm" t={t} onClick={submitNote}>Add</Btn>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:220,overflowY:"auto"}}>
              {(detail.activities||[]).slice().reverse().map(a=>(
                <div key={a.id} style={{fontSize:12,padding:"8px 10px",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`}}>
                  <div style={{color:t.text}}>{a.body}</div>
                  <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:3}}>{a.createdAt?toDateInput(a.createdAt):"—"}</div>
                </div>
              ))}
              {!(detail.activities||[]).length&&<div style={{fontSize:14,color:t.textDimmer,fontStyle:"italic"}}>No activity yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── ScheduleCallModal — proper component (useState must be at top level) ──────
const ScheduleCallModal = ({ lead, t, onClose, onConfirm }) => {
  const [dt,    setDt]    = useState("");
  const [notes, setNotes] = useState("");
  return (
    <Modal open={true} onClose={onClose} title={`Schedule Call — ${lead.name}`} t={t}>
      <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
        <Field t={t} label="Date & Time" type="datetime-local" value={dt} onChange={setDt}/>
        <Field t={t} label="Notes / Brief" textarea value={notes} onChange={setNotes}/>
        <div style={{padding:"0.75rem",background:`${t.teal}08`,border:`1px solid ${t.teal}20`,borderRadius:5,fontSize:14,color:t.textDim}}>
          Share your Google Meet or Zoom link with {lead.name} separately.
        </div>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
          <Btn variant="secondary" t={t} onClick={onClose}>Cancel</Btn>
          <Btn t={t} onClick={()=>onConfirm(lead,dt,notes)} disabled={!dt}>Confirm Call</Btn>
        </div>
      </div>
    </Modal>
  );
};
const STAGE_COLOR = s => ({NEW:"#4a9eff",CONTACTED:"#c9a84c",DISCOVERY:"#a78bfa",PROPOSED:"#00c9a7",WON:"#22c55e",LOST:"#c0394e",JUNK:"#555"}[s]||"#888");

const SalesPipeline = ({ leads, setLeads, clients, setClients, proposals, setProposals, t, setPage }) => {
  const [view, setView]     = useState("kanban"); // "kanban" | "table" | "calls"
  const [modal, setModal]   = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [callModal, setCallModal] = useState(null); // lead object
  const [form, setForm]     = useState({name:"",email:"",phone:"",company:"",product:"Q-Trader EA",message:"",budget:"",currency:"USD",source:"Direct",status:"NEW"});
  const F = k => v => setForm(f=>({...f,[k]:v}));

  const detail = leads.find(l=>l.id===detailId);

  const save = async () => {
    const e = {...form, budget:Number(form.budget)||0, id:Date.now(), createdAt:todayStr(), activities:[]};
    setLeads(ls=>[...ls,e]);
    setForm({name:"",email:"",phone:"",company:"",product:"Q-Trader EA",message:"",budget:"",currency:"USD",source:"Direct",status:"NEW"});
    setModal(false);
    try {
      const res = await apiFetch('/api/v1/leads', { method:'POST', body:JSON.stringify(e) });
      if(res.lead?.id) setLeads(ls=>ls.map(l=>l.id===e.id?{...l,...res.lead}:l));
    } catch{}
  };
  const move = async (id, status) => {
    setLeads(ls=>ls.map(l=>l.id===id?{...l,status}:l));
    try { await apiFetch(`/api/v1/leads/${id}`, { method:'PATCH', body:JSON.stringify({ status }) }); } catch{}
  };

  const addNote = async (id, note) => {
    if(!note.trim()) return;
    const entry={id:Date.now(),type:"NOTE",body:note,createdAt:new Date().toLocaleString()};
    setLeads(ls=>ls.map(l=>l.id===id?{...l,activities:[...(l.activities||[]),entry]}:l));
    try { await apiFetch(`/api/v1/leads/${id}/activities`, { method:'POST', body:JSON.stringify({ type:'NOTE', body:note }) }); } catch{}
  };

  const convertToClient = async (lead) => {
    const newClient = {id:Date.now(),name:lead.name,email:lead.email,phone:lead.phone||"",company:lead.company||"",source:lead.source,status:"Active",deal:lead.budget||0,currency:lead.currency||"USD",notes:lead.message||"",tags:[lead.product].filter(Boolean),createdAt:todayStr()};
    setClients(cs=>[...cs,newClient]);
    move(lead.id,"Won");
    setDetailId(null);
    setPage("clients");
    try {
      const res = await apiFetch(`/api/v1/leads/${lead.id}/convert`, { method:'POST', body:JSON.stringify({ createClient:true }) });
      if(res.client?.id) setClients(cs=>cs.map(c=>c.id===newClient.id?{...c,...res.client}:c));
    } catch{}
  };

  const scheduleCall = (lead, dateTime, notes) => {
    const call = {id:Date.now(),leadId:lead.id,leadName:lead.name,leadEmail:lead.email,scheduledAt:dateTime,notes,status:"Scheduled",createdAt:todayStr()};
    setLeads(ls=>ls.map(l=>l.id===lead.id?{...l,calls:[...(l.calls||[]),call],status:l.status==="NEW"?"CONTACTED":l.status}:l));
    setCallModal(null);
  };

  // Stats
  const won     = leads.filter(l=>l.status==="Won");
  const active  = leads.filter(l=>!["Won","Lost","Junk"].includes(l.status));
  const revenue = won.reduce((a,l)=>a+(l.budget||0),0);

  const stats = [
    {label:"Total Leads",    val:leads.length,              color:t.teal},
    {label:"Active",         val:active.length,             color:"#4a9eff"},
    {label:"Won",            val:won.length,                color:"#22c55e"},
    {label:"Pipeline Value", val:`$${revenue.toLocaleString()}`, color:t.amber},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow="Sales" title="Pipeline"
        sub={`${leads.length} leads · ${active.length} active · $${revenue.toLocaleString()} potential`}
        t={t}
        action={<div style={{display:"flex",gap:6}}>
          <Btn variant="secondary" t={t} onClick={()=>setView(v=>v==="kanban"?"table":v==="table"?"calls":"kanban")} icon={view==="kanban"?"chart":"funnel"}>
            {view==="kanban"?"Table":view==="table"?"Calls":"Kanban"}
          </Btn>
          <Btn icon="plus" t={t} onClick={()=>setModal(true)}>Add Lead</Btn>
        </div>}
      />

      {/* ── Stats row ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:"0.6rem"}}>
        {stats.map(s=>(
          <Card key={s.label} t={t} style={{padding:"0.85rem",textAlign:"center"}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:32,fontStyle:"italic",color:s.color}}>{s.val}</div>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,letterSpacing:"0.2em",textTransform:"uppercase",marginTop:3}}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* ── KANBAN ── */}
      {view==="kanban" && (
        <div style={{display:"flex",gap:"0.75rem",overflowX:"auto",paddingBottom:8}}>
          {LEAD_STAGES.map(stage=>{
            const col = leads.filter(l=>(l.status||'').toUpperCase()===stage);
            const colVal = col.reduce((a,l)=>a+(l.budget||0),0);
            return (
              <div key={stage} style={{minWidth:190,flex:"0 0 190px",display:"flex",flexDirection:"column",gap:6}}>
                {/* Column header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 4px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:STAGE_COLOR(stage)}}/>
                    <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",fontWeight:600,color:t.text,textTransform:"uppercase",letterSpacing:"0.12em"}}>{stage}</span>
                    <span style={{fontSize:9,padding:"1px 5px",borderRadius:10,background:t.surface3,color:t.textDim,fontFamily:"'Fira Code',monospace"}}>{col.length}</span>
                  </div>
                  {colVal>0&&<span style={{fontSize:9,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>${colVal.toLocaleString()}</span>}
                </div>
                {/* Cards */}
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {col.map(lead=>(
                    <div key={lead.id} onClick={()=>setDetailId(lead.id)} style={{background:t.surface,border:`1px solid ${t.border}`,borderLeft:`3px solid ${STAGE_COLOR(stage)}`,borderRadius:6,padding:"0.7rem",cursor:"pointer",transition:"border-color 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=STAGE_COLOR(stage)}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=t.border}>
                      <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:13,color:t.text,marginBottom:3}}>{lead.name}</div>
                      {lead.company&&<div style={{fontSize:11,color:t.textDim,marginBottom:4}}>{lead.company}</div>}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:4}}>
                        {lead.product&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:`${t.teal}18`,color:t.teal,fontFamily:"'Fira Code',monospace",textTransform:"uppercase"}}>{lead.product}</span>}
                        {lead.budget>0&&<span style={{fontSize:10,color:t.amber,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700}}>${Number(lead.budget).toLocaleString()}</span>}
                      </div>
                    </div>
                  ))}
                  {col.length===0&&<div style={{padding:"1rem 0.5rem",textAlign:"center",fontSize:11,color:t.textDimmer,fontStyle:"italic",border:`1px dashed ${t.border}`,borderRadius:6}}>Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── TABLE ── */}
      {view==="table" && (
        <Card t={t} style={{padding:0,overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${t.border}`}}>
                  {["Name","Company","Product","Stage","Budget","Source","Date",""].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.15em",fontWeight:500,whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map(l=>(
                  <tr key={l.id} style={{borderBottom:`1px solid ${t.border}`,cursor:"pointer"}} onClick={()=>setDetailId(l.id)}
                    onMouseEnter={e=>e.currentTarget.style.background=t.surface2}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{padding:"10px 14px"}}>
                      <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>{l.name}</div>
                      <div style={{fontSize:15,color:t.textDimmer}}>{l.email}</div>
                    </td>
                    <td style={{padding:"10px 14px",fontSize:14,color:t.textDim}}>{l.company||"—"}</td>
                    <td style={{padding:"10px 14px"}}>
                      {l.product&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.teal}18`,color:t.teal,fontFamily:"'Fira Code',monospace",textTransform:"uppercase"}}>{l.product}</span>}
                    </td>
                    <td style={{padding:"10px 14px"}}>
                      <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${STAGE_COLOR(l.status)}18`,color:STAGE_COLOR(l.status),fontFamily:"'Fira Code',monospace",textTransform:"uppercase",border:`1px solid ${STAGE_COLOR(l.status)}40`}}>{l.status}</span>
                    </td>
                    <td style={{padding:"10px 14px",fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,color:t.amber}}>{l.budget?`$${Number(l.budget).toLocaleString()}`:"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:15,color:t.textDim}}>{l.source}</td>
                    <td style={{padding:"10px 14px",fontSize:11,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{l.createdAt?toDateInput(l.createdAt):"—"}</td>
                    <td style={{padding:"10px 14px"}}>
                      <div style={{display:"flex",gap:4}} onClick={e=>e.stopPropagation()}>
                        <Btn variant="ghost" size="sm" t={t} icon="phone" onClick={()=>setCallModal(l)}>{""}</Btn>
                        <Btn variant="ghost" size="sm" t={t} icon="trash" onClick={()=>setLeads(ls=>ls.filter(x=>x.id!==l.id))}>{""}</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
                {leads.length===0&&<tr><td colSpan={8}><EmptyState title="No leads yet" sub="Add your first lead to start tracking the pipeline" t={t}/></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── CALLS LIST ── */}
      {view==="calls" && (
        <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.2em",marginBottom:4}}>Scheduled Discovery Calls</div>
          {leads.flatMap(l=>(l.calls||[]).map(c=>({...c,leadName:l.name,leadEmail:l.email,leadProduct:l.product}))).sort((a,b)=>new Date(a.scheduledAt)-new Date(b.scheduledAt)).map(call=>(
            <Card key={call.id} t={t}>
              <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
                <div style={{width:40,height:40,borderRadius:8,background:`${t.teal}18`,border:`1px solid ${t.teal}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <I n="calendar" s={18} c={t.teal}/>
                </div>
                <div style={{flex:1,minWidth:120}}>
                  <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:14,color:t.text}}>{call.leadName}</div>
                  <div style={{fontSize:14,color:t.textDim}}>{call.leadEmail}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Fira Code',monospace",fontSize:12,color:t.teal}}>{new Date(call.scheduledAt).toLocaleString()}</div>
                  {call.leadProduct&&<div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",textTransform:"uppercase"}}>{call.leadProduct}</div>}
                </div>
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:call.status==="Completed"?`${t.teal}18`:`${"#4a9eff"}18`,color:call.status==="Completed"?t.teal:"#4a9eff",fontFamily:"'Fira Code',monospace",border:`1px solid ${call.status==="Completed"?t.teal+"40":"#4a9eff40"}`}}>{call.status}</span>
              </div>
              {call.notes&&<div style={{fontSize:12,color:t.textDimmer,marginTop:8,paddingTop:8,borderTop:`1px solid ${t.border}`,fontStyle:"italic"}}>{call.notes}</div>}
            </Card>
          ))}
          {leads.every(l=>!(l.calls||[]).length)&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No discovery calls scheduled yet.</div>}
        </div>
      )}

      {/* ── ADD LEAD MODAL ── */}
      <Modal open={modal} onClose={()=>setModal(false)} title="Add Lead" t={t} wide>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
          <Field t={t} label="Name" required value={form.name} onChange={F("name")}/>
          <Field t={t} label="Email" type="email" required value={form.email} onChange={F("email")}/>
          <Field t={t} label="Company" value={form.company} onChange={F("company")}/>
          <Field t={t} label="Phone" value={form.phone} onChange={F("phone")}/>
          <Field t={t} label="Product Interest" options={["Q-Trader EA","TradesPlus","ArbitragePlus","SheetsInsider","Custom Dev","Other"]} value={form.product} onChange={F("product")}/>
          <Field t={t} label="Source" options={["Direct","Upwork","Fiverr","Referral","Landing Page","Waitlist","LinkedIn","Twitter/X","Other"]} value={form.source} onChange={F("source")}/>
          <Field t={t} label="Budget (USD)" type="number" value={form.budget} onChange={F("budget")}/>
          <Field t={t} label="Stage" options={LEAD_STAGES} value={form.status} onChange={F("status")}/>
        </div>
        <Field t={t} label="Message / Notes" textarea value={form.message} onChange={F("message")}/>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end",marginTop:4}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={save} disabled={!form.name||!form.email}>Add Lead</Btn>
        </div>
      </Modal>

      {/* ── LEAD DETAIL DRAWER ── */}
      {detail && (
        <LeadDrawer
          detail={detail}
          t={t}
          move={move}
          addNote={addNote}
          convertToClient={convertToClient}
          onClose={()=>setDetailId(null)}
          onScheduleCall={(lead)=>{setCallModal(lead);setDetailId(null);}}
        />
      )}

      {/* ── SCHEDULE CALL MODAL ── */}
      {callModal && (
        <ScheduleCallModal
          lead={callModal}
          t={t}
          onClose={()=>setCallModal(null)}
          onConfirm={scheduleCall}
        />
      )}
    </div>
  );
};

// ─── MODULE: DASHBOARD ────────────────────────────────────────────────────────
const Dashboard = ({ data, t, user, setPage }) => {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const isAdmin=user.role==="admin";
  const paid=data.invoices.filter(i=>i.status==="Paid").reduce((a,i)=>a+i.amount,0);
  const outstanding=data.invoices.filter(i=>i.status!=="Paid").reduce((a,i)=>a+i.amount,0);
  const income=data.expenses.filter(e=>e.type==="income").reduce((a,e)=>a+e.amount,0);
  const expTotal=data.expenses.filter(e=>e.type==="expense").reduce((a,e)=>a+e.amount,0);
  const myProjects=isAdmin?data.projects:data.projects.filter(p=>p.assignee === user.id);

  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <div>
      {/* FIXED: dashboard greeting — Cormorant Garamond, no duplicate letterSpacing, fontWeight:300 for large display */}
      <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:300, fontStyle:"italic", color:t.text, letterSpacing:"0.01em", lineHeight:1.1}}>Good day, {user.name.split(" ")[0]}</div>
      <div style={{width:32,height:1,background:t.teal,margin:"8px 0"}}/>
      <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{isAdmin?"Qire Agency OS — full operational overview.":myProjects.filter(p=>p.status==="In Progress").length+" active project(s) assigned."}</div>
    </div>

    {isAdmin&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.85rem"}}>
      <StatCard label="USD Revenue" value={fmtUSD(paid)} sub="all paid invoices" icon="finance" color={t.teal} t={t}/>
      <StatCard label="Outstanding" value={fmtUSD(outstanding)} sub="pending + overdue" icon="clock" color={t.amber} t={t}/>
      <StatCard label="KES Income" value={fmtKES(income)} sub="this period" icon="trending" color={t.teal} t={t}/>
      <StatCard label="Expenses" value={fmtKES(expTotal)} sub="incl. deductibles" icon="receipt" color={t.red} t={t}/>
      <StatCard label="Net Profit" value={fmtKES(income-expTotal)} sub="income - expenses" icon="chart" color={income>expTotal?t.teal:t.red} t={t}/>
      <StatCard label="Active Clients" value={data.clients.filter(c=>c.status==="Active").length} sub={`${data.clients.length} total`} icon="clients" color={t.blue} t={t}/>
    </div>}

    {isAdmin&&<div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.65rem"}}>
      {[{label:"New Invoice",icon:"invoices",page:"invoices",color:t.teal},{label:"New Letter",icon:"letters",page:"letters",color:t.blue},{label:"Log Expense",icon:"finance",page:"finance",color:t.amber},{label:"File Tax",icon:"tax",page:"tax",color:t.red}].map(q=>(
        <Card key={q.label} t={t} onClick={()=>setPage(q.page)} style={{padding:"1rem",textAlign:"center"}}>
          <div style={{marginBottom:6}}><I n={q.icon} s={18} c={q.color}/></div>
          <div style={{fontSize:11,fontWeight:500,color:t.text,fontFamily:"'Space Grotesk',sans-serif"}}>{q.label}</div>
        </Card>
      ))}
    </div>}

    {/* Dev stats — FIXED: Space Grotesk 700 instead of Syne */}
    {!isAdmin&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:"0.75rem"}}>
      {[
        {l:"My Projects",  v:String(myProjects.filter(p=>p.status==="In Progress").length)+" active", c:t.teal,  i:"projects"},
        {l:"Tasks Done",   v:String(myProjects.reduce((a,p)=>a+p.tasks.filter(tk=>tk.done).length,0)), c:t.blue,  i:"check"},
        {l:"Tasks Total",  v:String(myProjects.reduce((a,p)=>a+p.tasks.length,0)),                   c:t.textDim,i:"clock"},
      ].map(s=>(
        <Card key={s.l} t={t} style={{padding:"1rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",color:t.textDim,textTransform:"uppercase"}}>{s.l}</span>
            <I n={s.i} s={12} c={s.c}/>
          </div>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:400,fontStyle:"italic",color:s.c}}>{s.v}</div>
        </Card>
      ))}
    </div>}

    <div style={{display:"grid",gridTemplateColumns:isAdmin&&!isMobile?"1fr 1fr":"1fr",gap:"0.85rem"}}>
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>{isAdmin?"Project Progress":"My Projects"}</div>
        {myProjects.length===0&&<div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No projects yet.</div>}
        {myProjects.map(p=>{
          const done=p.tasks.filter(tk=>tk.done).length;
          const pct=p.tasks.length?Math.round((done/p.tasks.length)*100):0;
          return <div key={p.id} style={{marginBottom:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6,gap:8}}>
              <span style={{fontSize:15,color:t.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{p.name}</span>
              <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",flexShrink:0}}>{pct}%</span>
            </div>
            <ProgressBar pct={pct} t={t}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6,gap:6}}>
              <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",flexShrink:0}}>{done}/{p.tasks.length} tasks</span>
              <Badge label={p.status} t={t}/>
            </div>
          </div>;
        })}
      </Card>
      {isAdmin&&<Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>KRA Tax Records</div>
        {data.taxRecords.map(r=>(
          <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${t.border}`}}>
            <div>
              <span style={{fontSize:12,fontFamily:"'Fira Code',monospace",color:t.text,fontWeight:500}}>{r.period}</span>
              {r.turnover>0&&<span style={{fontSize:10,color:t.textDim,marginLeft:8}}>{fmtKES(r.turnover)}</span>}
            </div>
            <Badge label={r.status} t={t}/>
          </div>
        ))}
        <div style={{marginTop:"0.75rem",fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>Business: QIRE AGENCY · Sole Proprietor</div>
      </Card>}
    </div>
  </div>;
};

// ─── MODULE: CLIENT DASHBOARD ─────────────────────────────────────────────────
const ClientDashboard = ({ user, clients, projects, invoices, setInvoices, contracts, setContracts, messages, proposals, setProposals, qtLicenses, qtVersions, qtTickets, t, setPage, onPreview }) => {
  // Match logged-in client to their record by username (portalUsername in DB)
  const acc = { clientId: user.id, username: user.portalUsername || user.username, name: user.name, email: user.email, active: user.isActive !== false };
  const cId       = acc?.clientId;

  // ── Filtered data (this client only) ──────────────────────────────────────
  const myProjects  = projects.filter(p => p.clientId === cId);
  const myInvoices  = invoices.filter(i => i.clientId === cId && i.status !== "Draft");
  const myContracts = contracts.filter(c => c.clientId === cId);
  const myLicenses  = qtLicenses.filter(l => l.clientName === user.name || l.email === user.email);
  const latestVer   = qtVersions.find(v => v.isLatest);
  const myTickets   = qtTickets.filter(tk => tk.clientName === user.name)
  const myProposals = proposals.filter(p => String(p.clientId) === String(cId));

  // ── Message unread count ──────────────────────────────────────────────────
  const myMsgThreads = myProjects.flatMap(p => {
    const adminMsgs = messages.filter(m => m.projectId === p.id && m.type === "admin");
    return adminMsgs;
  });
  const unread = myMsgThreads.filter(m => !m.read && m.fromId !== `client-${cId}`).length;

  // ── Derived stats ─────────────────────────────────────────────────────────
  const activeProjects  = myProjects.filter(p => p.status === "In Progress" || p.status === "Planning").length;
  const invoicesDue     = myInvoices.filter(i => i.status === "Pending" || i.status === "Overdue").reduce((a, i) => a + i.amount, 0);
  const openTickets     = myTickets.filter(tk => tk.status === "OPEN" || tk.status === "IN_PROGRESS").length;

  // ── Greeting ──────────────────────────────────────────────────────────────
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", marginBottom: "0.85rem" }}>{children}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} className="fade-up">

      {/* ── Header ── */}
      <div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, fontWeight: 300, fontStyle: "italic", color: t.text, letterSpacing: "0.01em", lineHeight: 1.1 }}>
          {greeting}, {user.name.split(" ")[0]}
        </div>
        <div style={{ width: 32, height: 1, background: t.teal, margin: "8px 0" }} />
        <div style={{ fontSize: 13, color: t.textDim }}>{acc?.email || ""}</div>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "0.75rem" }}>
        {[
          { l: "Active Projects", v: activeProjects,                         c: t.teal,     i: "projects",  page: "myproject" },
          { l: "Amount Due",      v: invoicesDue > 0 ? `$${invoicesDue}` : "—", c: invoicesDue > 0 ? t.amber : t.textDimmer, i: "invoices", page: "invoices" },
          { l: "Unread Messages", v: unread,                                 c: unread > 0 ? t.blue : t.textDimmer, i: "message", page: "messages" },
          { l: "Open Tickets",    v: openTickets,                            c: openTickets > 0 ? t.red : t.textDimmer, i: "support", page: "qtrader" },
        ].map(s => (
          <Card key={s.l} t={t} onClick={() => setPage(s.page)} style={{ padding: "1rem", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase" }}>{s.l}</div>
              <I n={s.i} s={12} c={s.c} />
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, fontWeight: 400, fontStyle: "italic", color: s.c, lineHeight: 1 }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* ── Active Projects ── */}
      {myProjects.length > 0 && (
        <Card t={t}>
          <SectionLabel>Active Projects</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            {myProjects.map(p => {
              const done = p.tasks.filter(tk => tk.done).length;
              const pct  = p.tasks.length ? Math.round((done / p.tasks.length) * 100) : 0;
              const threadMsgs = messages.filter(m => m.projectId === p.id && m.type === "admin");
              const threadUnread = threadMsgs.filter(m => !m.read && m.fromId !== `client-${cId}`).length;
              return (
                <div key={p.id} onClick={() => setPage("myproject")} style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                      {threadUnread > 0 && (
                        <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: t.blue, color: "#050507", fontFamily: "'Fira Code',monospace", fontWeight: 700, flexShrink: 0 }}>{threadUnread}</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, color: t.textDimmer, fontFamily: "'Fira Code',monospace" }}>{pct}%</span>
                      <Badge label={p.status} t={t} />
                    </div>
                  </div>
                  <ProgressBar pct={pct} t={t} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                    <span style={{ fontSize: 10, color: t.textDimmer, fontFamily: "'Fira Code',monospace" }}>{done}/{p.tasks.length} tasks</span>
                    {p.deadline && <span style={{ fontSize: 10, color: t.textDimmer, fontFamily: "'Fira Code',monospace" }}>Due {toDateInput(p.deadline)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Active Products / Licenses ── */}
      {myLicenses.length > 0 && (
        <Card t={t}>
          <SectionLabel>Your Products</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {myLicenses.map(lic => {
              const meta = TIER_META[lic.tier] || { color: t.teal, label: lic.tier };
              const activeAccounts = (lic.boundAccounts || []).filter(a => a.isActive).length;
              const daysLeft = getDaysRemaining(lic.expiresAt);
              const isExpiringSoon = daysLeft !== null && daysLeft <= 30;
              return (
                <div key={lic.id} onClick={() => setPage("qtrader")} style={{
                  padding: "1rem", borderRadius: 8, cursor: "pointer",
                  background: `${meta.color}08`, border: `1px solid ${meta.color}25`,
                  transition: "border-color 0.15s",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 7, background: `${meta.color}15`, border: `1px solid ${meta.color}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 13, color: t.text }}>Q-Trader EA</div>
                        <div style={{ fontSize: 10, color: meta.color, fontFamily: "'Fira Code',monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>{meta.label} · {lic.status}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 11, color: isExpiringSoon ? t.amber : t.textDim }}>
                        {daysLeft === null ? "Lifetime" : `${daysLeft}d left`}
                      </div>
                      <div style={{ fontSize: 10, color: t.textDimmer }}>{activeAccounts}/{lic.maxAccounts} accounts</div>
                    </div>
                  </div>
                  {/* License key */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 10, color: t.textDimmer, letterSpacing: "0.08em", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {maskKey(lic.licenseKey)}
                    </span>
                    <CopyBtn text={lic.licenseKey} label="Copy" t={t} />
                  </div>
                  {/* Account usage bar */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 9, color: t.textDimmer, fontFamily: "'Fira Code',monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>Account Slots</span>
                      <span style={{ fontSize: 9, color: t.textDimmer, fontFamily: "'Fira Code',monospace" }}>{activeAccounts}/{lic.maxAccounts}</span>
                    </div>
                    <ProgressBar pct={lic.maxAccounts > 0 ? Math.round((activeAccounts / lic.maxAccounts) * 100) : 0} t={t} color={meta.color} />
                  </div>
                  {/* Latest version notice */}
                  {latestVer && (
                    <div style={{ marginTop: 10, padding: "6px 10px", borderRadius: 5, background: `${t.teal}10`, border: `1px solid ${t.teal}25`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 10, color: t.textDim, fontFamily: "'Fira Code',monospace" }}>Latest: v{latestVer.version}</span>
                      <span style={{ fontSize: 9, color: t.teal, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>Downloads →</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Bottom: Invoices + Messages side by side ── */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth > 480 ? "1fr 1fr" : "1fr", gap: "0.85rem" }}>

        {/* Recent Invoices */}
        <Card t={t}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.85rem" }}>
            <SectionLabel>Recent Invoices</SectionLabel>
            {myInvoices.length > 2 && (
              <span onClick={() => setPage("invoices")} style={{ fontSize: 9, color: t.teal, fontFamily: "'Fira Code',monospace", cursor: "pointer", letterSpacing: "0.1em" }}>VIEW ALL →</span>
            )}
          </div>
          {myInvoices.length === 0 && <div style={{ fontSize: 12, color: t.textDimmer, fontStyle: "italic" }}>No invoices yet.</div>}
          {myInvoices.slice(0, 3).map(inv => (
            <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.text, fontFamily: "'Fira Code',monospace" }}>{inv.number}</div>
                <div style={{ fontSize: 10, color: t.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.notes || "—"}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text, fontFamily: "'Space Grotesk',sans-serif" }}>${inv.amount}</div>
                <Badge label={inv.status} t={t} />
              </div>
            </div>
          ))}
          {myProposals.filter(p => p.status === "Accepted").length > 0 && (
            <div style={{ marginTop: "0.75rem", padding: "6px 10px", borderRadius: 5, background: `${t.teal}10`, border: `1px solid ${t.teal}25`, fontSize: 11, color: t.teal }}>
              ✓ {myProposals.filter(p => p.status === "Accepted").length} accepted proposal{myProposals.filter(p => p.status === "Accepted").length > 1 ? "s" : ""}
            </div>
          )}
        </Card>

        {/* Recent Messages */}
        <Card t={t}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.85rem" }}>
            <SectionLabel>Messages</SectionLabel>
            <span onClick={() => setPage("messages")} style={{ fontSize: 9, color: t.teal, fontFamily: "'Fira Code',monospace", cursor: "pointer", letterSpacing: "0.1em" }}>OPEN →</span>
          </div>
          {myProjects.length === 0 && <div style={{ fontSize: 12, color: t.textDimmer, fontStyle: "italic" }}>No threads yet.</div>}
          {myProjects.slice(0, 3).map(p => {
            const threadMsgs = messages.filter(m => m.projectId === p.id && m.type === "admin");
            const lastMsg = threadMsgs[threadMsgs.length - 1];
            const threadUnread = threadMsgs.filter(m => !m.read && m.fromId !== `client-${cId}`).length;
            return (
              <div key={p.id} onClick={() => setPage("messages")} style={{ display: "flex", gap: 8, padding: "8px 0", borderBottom: `1px solid ${t.border}`, cursor: "pointer" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: threadUnread > 0 ? t.blue : t.border, flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: threadUnread > 0 ? 700 : 500, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                    {threadUnread > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 8, background: t.blue, color: "#050507", fontWeight: 700, flexShrink: 0 }}>{threadUnread}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: t.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {lastMsg ? lastMsg.text : "No messages yet"}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      </div>

      {/* ── Contract status ── */}
      {myContracts.length > 0 && (
        <Card t={t} style={{ padding: "0.85rem 1rem" }}>
          {myContracts.map(c => {
            const clientObj = clients.find(x => x.id === c.clientId);
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: myContracts.length > 1 ? "6px 0" : 0, borderBottom: myContracts.length > 1 ? `1px solid ${t.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                  <I n="contracts" s={16} c={c.status === "Signed" ? t.teal : t.amber} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title}</div>
                    <div style={{ fontSize: 10, color: t.textDim, fontFamily: "'Fira Code',monospace" }}>
                      {c.type} · {c.startDate ? new Date(c.startDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'} → {c.endDate ? new Date(c.endDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : "ongoing"}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <Badge label={c.status} t={t} />
                  <Btn variant="secondary" size="sm" t={t} icon="print"
                    onClick={() => onPreview({ html: buildContractHTML(c, clientObj), title: `${c.title} · Qire Agency`, isContract: true, contractId: c.id, initialAdminSig: c.adminSignature||'', initialClientSig: c.clientSignature||'', initialAdminSignedAt: c.adminSignedAt||'', initialClientSignedAt: c.clientSignedAt||c.signedAt||'' })}>
                    Preview
                  </Btn>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {/* ── Quick actions ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "0.65rem" }}>
        {[
          { label: "My Project",   icon: "projects",  page: "myproject",  color: t.teal },
          { label: "Messages",     icon: "message",   page: "messages",   color: t.blue },
          { label: "Invoices",     icon: "invoices",  page: "invoices",   color: t.amber },
          { label: "Q-Trader EA",  icon: "shield",    page: "qtrader",    color: t.textDim },
        ].map(q => (
          <Card key={q.label} t={t} onClick={() => setPage(q.page)} style={{ padding: "0.85rem", textAlign: "center", cursor: "pointer" }}>
            <div style={{ marginBottom: 5 }}><I n={q.icon} s={16} c={q.color} /></div>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.text, fontFamily: "'Space Grotesk',sans-serif" }}>{q.label}</div>
          </Card>
        ))}
      </div>

    </div>
  );
};

// ─── MODULE: CRM ──────────────────────────────────────────────────────────────
const CRM = ({ clients, setClients, qtToken, t }) => {
  const [search,setSearch]=useState(""); const [filter,setFilter]=useState("All");
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",email:"",phone:"",company:"",source:"Direct",status:"Lead",deal:"",currency:"USD",notes:"",tags:""});
  const [credModal, setCredModal] = useState(null); // { name, username, password, email }
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const filtered=clients.filter(c=>(filter==="All"||c.status===filter)&&(c.name.toLowerCase().includes(search.toLowerCase())||c.company.toLowerCase().includes(search.toLowerCase())));
  const openAdd=()=>{setEditing(null);setForm({name:"",email:"",phone:"",company:"",source:"Direct",status:"Lead",deal:"",currency:"USD",notes:"",tags:""});setModal(true);};
  const openEdit=c=>{setEditing(c.id);setForm({...c,deal:String(c.deal),tags:c.tags.join(", ")});setModal(true);};
  const grantPortalAccess = async (client) => {
    // Generate username = first name, lowercased, alphanumeric only
    const base = client.name.toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g,"");
    const pass  = base + Math.floor(1000 + Math.random() * 9000);
    try {
      await apiFetch(`/api/v1/admin/clients/${client.id}/portal-access`, {
        method: 'POST',
        body: JSON.stringify({ username: base, password: pass }),
      });
      // Update the client object in state directly — source of truth is client.portalActive
      setClients(cs => cs.map(c => c.id === client.id
        ? { ...c, portalActive: true, portalUsername: base }
        : c
      ));
      setCredModal({ name: client.name, username: base, password: pass, email: client.email });
    } catch(err) {
      // Username taken? try with random suffix
      const base2 = base + Math.floor(10 + Math.random() * 90);
      try {
        await apiFetch(`/api/v1/admin/clients/${client.id}/portal-access`, {
          method: 'POST',
          body: JSON.stringify({ username: base2, password: pass }),
        });
        setClients(cs => cs.map(c => c.id === client.id
          ? { ...c, portalActive: true, portalUsername: base2 }
          : c
        ));
        setCredModal({ name: client.name, username: base2, password: pass, email: client.email });
      } catch(err2) {
        alert('Could not grant access: ' + (err2.message || 'API error'));
      }
    }
  };
  const save = async () => {
    const e={...form,deal:Number(form.deal)||0,tags:form.tags.split(",").map(x=>x.trim()).filter(Boolean)};
    if(editing){
      // Optimistic update
      setClients(cs=>cs.map(c=>c.id===editing?{...c,...e}:c));
      // Portal access is granted explicitly via Grant Access button — not auto on status change
      // Sync to API
      try { await apiFetch(`/api/v1/admin/clients/${editing}`, { method:'PATCH', body:JSON.stringify(e) }); } catch{}
    } else {
      const newClient={...e,id:Date.now(),createdAt:todayStr()};
      setClients(cs=>[...cs,newClient]);
      // Portal access is granted explicitly via Grant Access button
      // Sync to API — create client record via admin endpoint
      try {
        const res = await apiFetch('/api/v1/admin/clients', {
          method:'POST',
          body:JSON.stringify({
            name: e.name, email: e.email, phone: e.phone||undefined,
            company: e.company||undefined, source: e.source,
            status: e.status, deal: e.deal, currency: e.currency,
            notes: e.notes||undefined, tags: e.tags,
          }),
        });
        if(res.client?.id) {
          setClients(cs=>cs.map(c=>c.id===newClient.id?{...c,...res.client}:c));
        }
      } catch{}
    }
    setModal(false);
  };
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Client CRM" title="Contacts" sub={`${clients.length} total · ${clients.filter(c=>c.status==="Active").length} active`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Add Client</Btn>}/>
    <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap",alignItems:"center"}}>
      <div style={{position:"relative",flex:1,minWidth:180}}>
        <div style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><I n="search" s={13} c={t.textDimmer}/></div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients…" style={{width:"100%",paddingLeft:32,padding:"7px 12px 7px 32px",background:t.surface,color:t.text,borderRadius:5,fontSize:14,fontFamily:"'Space Grotesk',sans-serif",outline:"none",border:`1px solid ${t.border}`}}/>
      </div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {["All","Lead","Proposal","Active","Inactive"].map(s=><button key={s} onClick={()=>setFilter(s)} style={{padding:"5px 11px",borderRadius:5,fontSize:11,fontWeight:500,cursor:"pointer",fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",background:filter===s?t.teal:"transparent",color:filter===s?(t.id==="light"?"#fff":"#050507"):t.textDim,border:filter===s?"none":`1px solid ${t.border}`}}>{s}</button>)}
      </div>
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {filtered.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No clients found.</div>}
      {filtered.map(c=><Card key={c.id} t={t}>
        <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
          <Avatar name={c.name} t={t}/>
          <div style={{flex:1,minWidth:120}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}><span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>{c.name}</span><Badge label={c.status} t={t}/></div>
            <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{c.company} · {c.email}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:22,fontStyle:"italic",color:t.teal}}>{fmtUSD(c.deal)}</div>
            <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>via {c.source}</div>
          </div>
          <div style={{display:"flex",gap:3,alignItems:"center",flexWrap:"wrap"}}>
            {(()=>{
              if(c.portalActive && c.portalUsername) return (
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.teal}18`,color:t.teal,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.teal}40`}}>
                  ● Portal @{c.portalUsername}
                </span>
              );
              if(c.status==="Active"||c.status==="Proposal") return (
                <Btn variant="secondary" size="sm" t={t} onClick={()=>grantPortalAccess(c)} icon="portal">Grant Access</Btn>
              );
              return null;
            })()}
            <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(c)} icon="edit">{""}</Btn>
            <Btn variant="ghost" size="sm" t={t} onClick={()=>setClients(cs=>cs.filter(x=>x.id!==c.id))} icon="trash">{""}</Btn>
          </div>
        </div>
        {c.tags.length>0&&<div style={{display:"flex",gap:5,marginTop:"0.6rem",flexWrap:"wrap"}}>{c.tags.map(tag=><span key={tag} style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:t.surface3,color:t.textDimmer,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.08em"}}>{tag}</span>)}</div>}
        {c.notes&&<div style={{fontSize:12,color:t.textDimmer,marginTop:8,paddingTop:8,borderTop:`1px solid ${t.border}`,fontWeight:400,fontStyle:"italic"}}>{c.notes}</div>}
      </Card>)}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Client":"Add Client"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Name" required value={form.name} onChange={F("name")}/>
        <Field t={t} label="Company" value={form.company} onChange={F("company")}/>
        <Field t={t} label="Email" type="email" value={form.email} onChange={F("email")}/>
        <Field t={t} label="Phone" value={form.phone} onChange={F("phone")}/>
        <Field t={t} label="Status" options={["Lead","Proposal","Active","Inactive"]} value={form.status} onChange={F("status")}/>
        <Field t={t} label="Source" options={["Direct","Upwork","Fiverr","Freelancer","Strill","Referral","LinkedIn","Other"]} value={form.source} onChange={F("source")}/>
        <Field t={t} label="Deal Value" type="number" value={form.deal} onChange={F("deal")}/>
        <Field t={t} label="Currency" options={["USD","KES","EUR","GBP"]} value={form.currency} onChange={F("currency")}/>
      </div>
      <Field t={t} label="Tags (comma-separated)" placeholder="MT5, React, Crypto" value={form.tags} onChange={F("tags")}/>
      <Field t={t} label="Notes" textarea value={form.notes} onChange={F("notes")}/>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save} disabled={!form.name}>{editing?"Save Changes":"Add Client"}</Btn>
      </div>
    </Modal>
    {/* ── Credentials reveal modal ── */}
    {credModal && createPortal(
      <div style={{position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
        <div style={{background:t.surface,borderRadius:10,width:"100%",maxWidth:420,border:`1px solid ${t.teal}40`,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
          <div style={{padding:"1.1rem 1.5rem",borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",gap:10}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.teal} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>Portal Access Created</span>
          </div>
          <div style={{padding:"1.5rem",display:"flex",flexDirection:"column",gap:"1rem"}}>
            <div style={{padding:"0.75rem 1rem",background:`${t.amber}10`,border:`1px solid ${t.amber}30`,borderRadius:6,fontSize:12,color:t.amber,fontFamily:"'Space Grotesk',sans-serif"}}>
              ⚠ Save these credentials now — the password will not be shown again.
            </div>
            {[["Client", credModal.name],["Email", credModal.email],["Username", credModal.username],["Password", credModal.password]].map(([label, value]) => (
              <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.6rem 0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`}}>
                <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,textTransform:"uppercase",letterSpacing:"0.15em"}}>{label}</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,color:t.text,fontFamily:"'Fira Code',monospace",fontWeight:600}}>{value}</span>
                  <CopyBtn text={value} label="copy" t={t} />
                </div>
              </div>
            ))}
            <CopyBtn
              text={`Email: ${credModal.email}\nUsername: ${credModal.username}\nPassword: ${credModal.password}`}
              label="Copy All Credentials"
              t={t}
              style={{padding:"8px",width:"100%",justifyContent:"center",borderRadius:5,fontSize:12,fontWeight:600}}
            />
            <button onClick={()=>setCredModal(null)} style={{padding:"8px",borderRadius:5,background:t.teal,border:"none",color:"#050507",cursor:"pointer",fontSize:13,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700}}>
              Done — I've saved these
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
  </div>;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECTS PAGE — Drop-in replacement for the `Projects` component in Portal.js
//
// USAGE: Replace the existing `const Projects = ...` block (lines ~2031–2230)
//
// WHAT'S NEW vs old component:
//   - Per-project detail drawer with full tab view
//   - Releases tab: admin/dev can publish releases with versioning + grouped changelog
//   - Bugs queue: see all client-reported bugs per project with triage controls
//   - Feature requests: review, prioritise, accept/decline
//   - Client tasks: update status on client task requests
//   - Deliverables management (existing, improved)
//   - Time entries inline
//   - All existing project CRUD preserved
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// PROJECTS PAGE — Drop-in replacement for the `Projects` component in Portal.js
//
// USAGE: Replace the existing `const Projects = ...` block (lines ~2031–2230)
//
// WHAT'S NEW vs old component:
//   - Per-project detail drawer with full tab view
//   - Releases tab: admin/dev can publish releases with versioning + grouped changelog
//   - Bugs queue: see all client-reported bugs per project with triage controls
//   - Feature requests: review, prioritise, accept/decline
//   - Client tasks: update status on client task requests
//   - Deliverables management (existing, improved)
//   - Time entries inline
//   - All existing project CRUD preserved
// ═══════════════════════════════════════════════════════════════════════════════

// ProjectBugRow — admin/dev bugs triage row with inline reply state
const ProjectBugRow = ({ bug, t, onStatusChange, onReply }) => {
  const [showReply, setShowReply] = React.useState(false);
  const [replyDraft, setReplyDraft] = React.useState(bug.adminReply||'');
  const sevColor = ({CRITICAL:t.red,HIGH:t.red,MEDIUM:t.amber,LOW:t.teal,Critical:t.red,High:t.red,Medium:t.amber,Low:t.teal}[bug.severity]||t.textDim);
  const fmt = iso => iso ? new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—';
  return (
    <div style={{padding:'0.75rem',background:t.surface2,borderRadius:6,borderLeft:`3px solid ${sevColor}`}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:6}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:14,color:t.text}}>{bug.title}</span>
            <span style={{fontSize:9,padding:'1px 6px',borderRadius:3,background:`${sevColor}18`,color:sevColor,fontFamily:"'Fira Code',monospace",textTransform:'uppercase'}}>{bug.severity}</span>
          </div>
          {bug.description&&<div style={{fontSize:15,color:t.textDim,lineHeight:1.5}}>{bug.description}</div>}
          {bug.stepsToRepro&&<div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:4,padding:'4px 8px',background:t.surface3,borderRadius:3}}>{bug.stepsToRepro}</div>}
          {bug.adminReply&&<div style={{fontSize:11,color:t.teal,marginTop:6,padding:'6px 8px',background:`${t.teal}08`,borderRadius:4}}><span style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.08em',marginRight:6}}>Reply:</span>{bug.adminReply}</div>}
        </div>
        <div style={{display:'flex',gap:5,alignItems:'center',flexShrink:0}}>
          <select value={bug.status||'Open'} onChange={e=>onStatusChange(bug.id,e.target.value)} style={{fontSize:10,background:t.surface3,color:t.text,border:`1px solid ${t.border}`,borderRadius:4,padding:'3px 6px',fontFamily:"'Fira Code',monospace",cursor:'pointer',outline:'none'}}>
            {['Open','In Progress','Investigating','Resolved','WONT_FIX'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={()=>setShowReply(!showReply)} style={{fontSize:10,background:`${t.blue}15`,border:`1px solid ${t.blue}30`,borderRadius:4,color:t.blue,padding:'3px 8px',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif"}}>Reply</button>
        </div>
      </div>
      {showReply&&(
        <div style={{marginTop:6,display:'flex',gap:6}}>
          <input value={replyDraft} onChange={e=>setReplyDraft(e.target.value)} placeholder="Write a reply to the client…" style={{flex:1,fontSize:11,background:t.surface3,border:`1px solid ${t.border}`,borderRadius:4,padding:'5px 8px',color:t.text,fontFamily:"'Space Grotesk',sans-serif",outline:'none'}}/>
          <button onClick={()=>{onReply(bug.id,replyDraft);setShowReply(false);}} style={{fontSize:10,background:t.teal,border:'none',borderRadius:4,color:t.id==='dark'?'#050507':'#fff',padding:'5px 10px',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontWeight:600}}>Send</button>
        </div>
      )}
      <div style={{fontSize:9,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:5}}>{fmt(bug.createdAt)}</div>
    </div>
  );
};

const Projects = ({ projects, setProjects, clients, team, t, user }) => {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const isAdmin  = ['admin','ADMIN'].includes((user?.role||'').toLowerCase());
  const visible  = isAdmin ? projects : projects.filter(p => p.assignee === user?.id || p.assigneeId === user?.id);

  // ── List view state ──────────────────────────────────────────────────────────
  const [search,      setSearch]      = React.useState('');
  const [filterStatus,setFilterStatus]= React.useState('');
  const [listModal,   setListModal]   = React.useState(false);  // new project modal
  const [editing,     setEditing]     = React.useState(null);
  const [form,        setForm]        = React.useState({});

  // ── Detail drawer ────────────────────────────────────────────────────────────
  const [detailId,    setDetailId]    = React.useState(null);
  const [detailTab,   setDetailTab]   = React.useState('overview');

  // ── Release form ─────────────────────────────────────────────────────────────
  const [relModal,    setRelModal]    = React.useState(false);
  const [relForm,     setRelForm]     = React.useState({
    version:'', releaseType:'PATCH', releaseNotes:'',
    changelog: { features:[], fixes:[], improvements:[], security:[] },
    file:null, fileName:'', _featDraft:'', _fixDraft:'', _impDraft:'', _secDraft:'',
  });

  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = React.useState({});

  const proj = projects.find(p => p.id === detailId);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const setSave = (key,v) => setSaving(s => ({...s,[key]:v}));
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB',{ day:'numeric',month:'short',year:'numeric' }) : '—';

  const priorityColor = s => ({High:t.red,URGENT:t.red,Medium:t.amber,MEDIUM:t.amber,Low:t.teal,LOW:t.teal}[s]||t.textDim);
  const statusColor   = s => ({
    'In Progress':t.teal,IN_PROGRESS:t.teal,Planning:t.blue,PLANNING:t.blue,
    Review:t.amber,REVIEW:t.amber,Completed:t.textDimmer,COMPLETED:t.textDimmer,
    'On Hold':t.amber,ON_HOLD:t.amber,Cancelled:t.red,CANCELLED:t.red,
    Open:t.red,OPEN:t.red,Resolved:t.teal,RESOLVED:t.teal,
    Requested:t.amber,
  }[s]||t.textDim);
  const relTypeColor = rt => ({MAJOR:t.red,Major:t.red,MINOR:t.blue,Minor:t.blue,PATCH:t.teal,Patch:t.teal,HOTFIX:t.amber,Hotfix:t.amber}[rt]||t.textDim);

  const updateProj = (id, updater) =>
    setProjects(ps => ps.map(p => p.id===id ? updater(p) : p));

  // ── Task status cycle (available for future client task UI) ──────────────────
  // eslint-disable-next-line no-unused-vars
  const cycleTaskStatus = async (projId, taskId) => {
    const p = projects.find(x=>x.id===projId);
    const task = (p?.clientTasks||[]).find(t=>t.id===taskId);
    if (!task) return;
    const cycle = { Requested:'In Progress', 'In Progress':'Completed', Completed:'Requested' };
    const next = cycle[task.status] || 'In Progress';
    updateProj(projId, p => ({...p, clientTasks:(p.clientTasks||[]).map(t=>t.id===taskId?{...t,status:next}:t)}));
  };

  // ── Bug status update ────────────────────────────────────────────────────────
  const updateBugStatus = async (projId, bugId, status) => {
    updateProj(projId, p => ({...p, bugs:(p.bugs||[]).map(b=>b.id===bugId?{...b,status}:b)}));
    setSave(`bug-${bugId}`, true);
    try {
      await apiFetch(`/api/v1/projects/${projId}/bugs/${bugId}`, { method:'PATCH', body:JSON.stringify({ status }) });
    } catch {}
    setSave(`bug-${bugId}`, false);
  };

  const replyToBug = async (projId, bugId, adminReply) => {
    updateProj(projId, p => ({...p, bugs:(p.bugs||[]).map(b=>b.id===bugId?{...b,adminReply,repliedAt:new Date().toISOString()}:b)}));
    try { await apiFetch(`/api/v1/projects/${projId}/bugs/${bugId}`, { method:'PATCH', body:JSON.stringify({ adminReply }) }); } catch {}
  };

  // ── Feature request status ───────────────────────────────────────────────────
  const updateFRStatus = (projId, reqId, status) => {
    updateProj(projId, p => ({...p, featureRequests:(p.featureRequests||[]).map(r=>r.id===reqId?{...r,status}:r)}));
    apiFetch(`/api/v1/projects/${projId}/feature-requests/${reqId}`, {
      method:'PATCH', body:JSON.stringify({ status }),
    }).catch(()=>{});
  };

  // eslint-disable-next-line no-unused-vars
  const updateClientTaskStatus = (projId, taskId, status) => {
    updateProj(projId, p => ({...p, clientTasks:(p.clientTasks||[]).map(t=>t.id===taskId?{...t,status}:t)}));
    apiFetch(`/api/v1/projects/${projId}/client-tasks/${taskId}`, {
      method:'PATCH', body:JSON.stringify({ status }),
    }).catch(()=>{});
  };

  // ── Publish release ──────────────────────────────────────────────────────────
  const publishRelease = async () => {
    if (!relForm.version.trim()) return;
    const release = {
      id: `rel-${Date.now()}`,
      version: relForm.version,
      releaseType: relForm.releaseType,
      releaseNotes: relForm.releaseNotes,
      changelog: {
        features:     relForm.changelog.features,
        fixes:        relForm.changelog.fixes,
        improvements: relForm.changelog.improvements,
        security:     relForm.changelog.security,
      },
      downloadUrl: relForm.downloadUrl || null,
      media: [],
      releasedAt: new Date().toISOString(),
      publishedBy: user?.name || 'Admin',
      acknowledgedAt: null,
    };
    updateProj(detailId, p => ({ ...p, releases: [release, ...(p.releases||[])] }));
    // Persist via dedicated releases endpoint (multipart so file is optional)
    try {
      const fd = new FormData();
      fd.append('version',      relForm.version);
      fd.append('releaseType',  relForm.releaseType);
      fd.append('releaseNotes', relForm.releaseNotes);
      fd.append('changelog',    JSON.stringify(relForm.changelog));
      if (relForm.file) fd.append('file', relForm.file);

      // Demo: add release to local state directly
      const tmpRel = { ...relForm, id: Date.now(), createdAt: new Date().toISOString(), fileUrl: null };
      updateProj(detailId, p => ({ ...p, releases: [...(p.releases||[]), tmpRel] }));
    } catch {}
    setRelForm({ version:'', releaseType:'PATCH', releaseNotes:'', changelog:{features:[],fixes:[],improvements:[],security:[]}, file:null, fileName:'', _featDraft:'', _fixDraft:'', _impDraft:'', _secDraft:'' });
    setRelModal(false);
  };

  // ── Changelog tag helper ─────────────────────────────────────────────────────
  const addChangelogItem = (type, draftKey) => {
    const val = (relForm[draftKey]||'').trim();
    if (!val) return;
    setRelForm(f => ({ ...f, changelog:{ ...f.changelog, [type]:[...f.changelog[type],val] }, [draftKey]:'' }));
  };
  const removeChangelogItem = (type, i) => {
    setRelForm(f => ({ ...f, changelog:{ ...f.changelog, [type]:f.changelog[type].filter((_,idx)=>idx!==i) } }));
  };

  // ── Project CRUD ─────────────────────────────────────────────────────────────
  const saveProject = async (e) => {
    if (!e.name?.trim() || !e.clientId) return;
    if (editing) {
      updateProj(editing, p => ({...p,...e}));
      setListModal(false);
      try { await apiFetch(`/api/v1/projects/${editing}`, { method:'PATCH', body:JSON.stringify({...e, status:(e.status||'PLANNING').toUpperCase().replace(' ','_'), priority:(e.priority||'MEDIUM').toUpperCase()}) }); } catch {}
    } else {
      const tmp = { id:`tmp-${Date.now()}`, ...e, tasks:[], clientTasks:[], clientIssues:[], bugs:[], featureRequests:[], releases:[], deliverables:[] };
      setProjects(ps => [...ps, tmp]);
      setListModal(false);
      try {
        const res = await apiFetch('/api/v1/projects', { method:'POST', body:JSON.stringify({...e, status:(e.status||'PLANNING').toUpperCase().replace(' ','_'), priority:(e.priority||'MEDIUM').toUpperCase()}) });
        if (res.project?.id) setProjects(ps => ps.map(p => p.id===tmp.id ? {...p,...res.project,tasks:res.project.tasks||[]} : p));
      } catch {}
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    setProjects(ps => ps.filter(p => p.id!==id));
    if (detailId===id) setDetailId(null);
    try { await apiFetch(`/api/v1/projects/${id}`, { method:'DELETE' }); } catch {}
  };

  // ── Toggle task done ──────────────────────────────────────────────────────────
  const toggleTask = async (projId, taskId) => {
    const p = projects.find(x=>x.id===projId);
    const task = (p?.tasks||[]).find(t=>t.id===taskId);
    if (!task) return;
    updateProj(projId, p => ({...p, tasks:(p.tasks||[]).map(t=>t.id===taskId?{...t,done:!t.done}:t)}));
    try { await apiFetch(`/api/v1/projects/${projId}/tasks/${taskId}`, { method:'PATCH', body:JSON.stringify({ done:!task.done }) }); } catch {}
  };

  // ── Filtered projects ─────────────────────────────────────────────────────────
  const filtered = visible.filter(p => {
    const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase()) || clients.find(c=>c.id===p.clientId)?.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || p.status===filterStatus;
    return matchSearch && matchStatus;
  });

  // ── Tab styles ────────────────────────────────────────────────────────────────
  const tabBtn = (id) => ({
    display:'flex', alignItems:'center', gap:5,
    padding:'6px 12px', borderRadius:5, cursor:'pointer',
    background: detailTab===id ? t.surface3 : 'transparent',
    color: detailTab===id ? t.text : t.textDim,
    border: detailTab===id ? `1px solid ${t.borderBright}` : `1px solid transparent`,
    fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight: detailTab===id ? 600 : 400,
    transition:'all 0.15s', whiteSpace:'nowrap',
  });

  const inputStyle = {
    background: t.surface2, border:`1px solid ${t.border}`, borderRadius:5,
    color:t.text, fontSize:12, padding:'6px 10px',
    fontFamily:"'Space Grotesk',sans-serif", outline:'none', width:'100%',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER — split view: list on left, detail on right (or full list if no selection)
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>

      {/* ── Header ── */}
      <SectionHeader
        eyebrow="Work" title="Projects"
        sub={`${visible.length} project${visible.length!==1?'s':''}`} t={t}
        action={isAdmin && (
          <Btn icon="plus" t={t} onClick={()=>{
            setEditing(null);
            setForm({ clientId:clients[0]?.id||'', name:'', description:'', status:'Planning', priority:'Medium', deadline:'', assigneeId:'' });
            setListModal(true);
          }}>New Project</Btn>
        )}
      />

      {/* ── Search + filter bar ── */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <input
            placeholder="Search projects or clients…"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft:28 }}
          />
          <I n="search" s={12} c={t.textDimmer} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)' }}/>
        </div>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...inputStyle, width:'auto', cursor:'pointer' }}>
          <option value="">All statuses</option>
          {['Planning','In Progress','Review','On Hold','Completed','Cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ── Split view ── */}
      <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:'1rem', alignItems:'flex-start' }}>

        {/* Project list — hidden on mobile when detail is open */}
        <div style={{
          flex: (!isMobile && detailId) ? '0 0 300px' : '1',
          minWidth:0,
          display: (isMobile && detailId) ? 'none' : 'flex',
          flexDirection:'column',
          gap:'0.5rem',
        }}>
          {filtered.length === 0 && <EmptyState title="No projects yet" sub="Create your first project to get started" t={t}/>}
          {filtered.map(p => {
            const client = clients.find(c => c.id===p.clientId);
            const done   = (p.tasks||[]).filter(tk=>tk.done).length;
            const total  = (p.tasks||[]).length;
            const pct    = total ? Math.round((done/total)*100) : 0;
            const openBugs = (p.bugs||[]).filter(b=>b.status!=='Resolved'&&b.status!=='RESOLVED').length;
            const unackRels = (p.releases||[]).filter(r=>!r.acknowledgedAt).length;
            const isSelected = detailId===p.id;

            return (
              <div
                key={p.id}
                onClick={()=>{ setDetailId(p.id); setDetailTab('overview'); }}
                style={{
                  padding:'0.85rem 1rem', borderRadius:8, cursor:'pointer',
                  background: isSelected ? t.surface3 : t.surface,
                  border:`1px solid ${isSelected?t.borderBright:t.border}`,
                  borderLeft:`3px solid ${statusColor(p.status)}`,
                  transition:'all 0.15s',
                }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:13, color:t.text, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize:10, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginBottom:6 }}>{client?.name || 'No client'}</div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                      <Badge label={p.status} t={t}/>
                      <span style={{ fontSize:9, color:priorityColor(p.priority), fontFamily:"'Fira Code',monospace", textTransform:'uppercase' }}>{p.priority}</span>
                      {p.deadline && <span style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>due {fmt(p.deadline)}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                    {openBugs > 0 && <span style={{ fontSize:9, background:`${t.red}18`, color:t.red, borderRadius:8, padding:'1px 6px', fontFamily:"'Fira Code',monospace" }}>{openBugs} bug{openBugs>1?'s':''}</span>}
                    {unackRels > 0 && <span style={{ fontSize:9, background:`${t.teal}18`, color:t.teal, borderRadius:8, padding:'1px 6px', fontFamily:"'Fira Code',monospace" }}>{unackRels} pending ack</span>}
                  </div>
                </div>
                {total > 0 && (
                  <div style={{ marginTop:8 }}>
                    <div style={{ height:3, background:t.border, borderRadius:2, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:t.teal, borderRadius:2, transition:'width 0.4s' }}/>
                    </div>
                    <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:3 }}>{done}/{total} tasks · {pct}%</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Project Detail Panel ── */}
        {proj && (
          <div style={{ flex:1, minWidth:0, background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, overflow:'hidden', width: isMobile ? '100%' : undefined }} className="fade-in">

            {/* Detail header */}
            <div style={{ padding:'1rem 1.25rem', borderBottom:`1px solid ${t.border}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
              <div style={{display:'flex',alignItems:'flex-start',gap:8,flex:1,minWidth:0}}>
                {isMobile && (
                  <button onClick={()=>setDetailId(null)} style={{background:'none',border:'none',cursor:'pointer',color:t.textDim,padding:'2px 4px',display:'flex',alignItems:'center',flexShrink:0,marginTop:2,transform:'rotate(90deg)'}}>
                    <I n="chevron" s={16} c={t.textDim}/>
                  </button>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:16, color:t.text, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{proj.name}</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                    <Badge label={proj.status} t={t}/>
                    <span style={{ fontSize:9, color:priorityColor(proj.priority), fontFamily:"'Fira Code',monospace", textTransform:'uppercase' }}>{proj.priority}</span>
                    <span style={{ fontSize:10, color:t.textDimmer }}>{clients.find(c=>c.id===proj.clientId)?.name}</span>
                    {proj.deadline && <span style={{ fontSize:10, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>due {fmt(proj.deadline)}</span>}
                    {proj.assigneeName && <span style={{ fontSize:10, color:t.textDim }}>→ {proj.assigneeName}</span>}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                {isAdmin && <Btn variant="ghost" size="sm" t={t} icon="edit" onClick={()=>{ setEditing(proj.id); setForm({clientId:proj.clientId,name:proj.name,description:proj.description||'',status:proj.status,priority:proj.priority,deadline:toDateInput(proj.deadline),assigneeId:proj.assignee||proj.assigneeId||''}); setListModal(true); }}>{''}</Btn>}
                {isAdmin && <Btn variant="ghost" size="sm" t={t} icon="trash" onClick={()=>deleteProject(proj.id)}>{''}</Btn>}
                {!isMobile && <Btn variant="ghost" size="sm" t={t} icon="x" onClick={()=>setDetailId(null)}>{''}</Btn>}
              </div>
            </div>

            {/* Detail tabs */}
            <div style={{ padding:'0.6rem 1.25rem', borderBottom:`1px solid ${t.border}`, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
              <div style={{ display:'flex', gap:5, minWidth:'max-content' }}>
              {[
                { id:'overview',   label:'Overview',   icon:'dashboard' },
                { id:'releases',   label:'Releases',   icon:'trending',  badge:(proj.releases||[]).filter(r=>!r.acknowledgedAt).length },
                { id:'bugs',       label:'Bugs',       icon:'alert',     badge:(proj.bugs||[]).filter(b=>b.status!=='Resolved'&&b.status!=='RESOLVED').length },
                { id:'requests',   label:'Requests',   icon:'proposal',  badge:(proj.featureRequests||[]).filter(r=>r.status==='Under Review').length },
                { id:'ctasks',     label:'Client Tasks',icon:'check',    badge:(proj.clientTasks||[]).filter(t=>t.status==='Requested').length },
                { id:'tasks',      label:'Dev Tasks',  icon:'clock' },
                { id:'files', label:'Project Files', icon:'folder' },
              ].map(tb => (
                <button key={tb.id} onClick={()=>setDetailTab(tb.id)} style={tabBtn(tb.id)}>
                  <I n={tb.icon} s={10} c="currentColor"/>
                  {tb.label}
                  {tb.badge > 0 && <span style={{ fontSize:9, background:`${t.red}20`, color:t.red, borderRadius:8, padding:'0px 5px', fontFamily:"'Fira Code',monospace" }}>{tb.badge}</span>}
                </button>
              ))}
              </div>
            </div>

            {/* Detail content */}
            <div style={{ padding:'1rem 1.25rem', maxHeight: isMobile ? 'none' : '60vh', overflowY: isMobile ? 'visible' : 'auto' }}>

              {/* OVERVIEW */}
              {detailTab==='overview' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                  {proj.description && <div style={{ fontSize:13, color:t.textDim, lineHeight:1.7 }}>{proj.description}</div>}
                  {/* Task progress */}
                  {(proj.tasks||[]).length > 0 && (
                    <div>
                      <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.15em', color:t.textDim, marginBottom:8 }}>Dev Tasks — {proj.tasks.filter(tk=>tk.done).length}/{proj.tasks.length}</div>
                      {(proj.tasks||[]).map(task => (
                        <div key={task.id} onClick={()=>toggleTask(proj.id,task.id)} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', cursor:'pointer' }}>
                          <div style={{ width:14, height:14, borderRadius:3, border:`1.5px solid ${task.done?t.teal:t.borderBright}`, background:task.done?t.teal:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            {task.done && <I n="check" s={9} c={t.id==='dark'?'#050507':'#fff'}/>}
                          </div>
                          <span style={{ fontSize:12, color:task.done?t.textDimmer:t.text, textDecoration:task.done?'line-through':'none' }}>{task.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Quick stats */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:'0.5rem' }}>
                    {[
                      { l:'Releases',     v:(proj.releases||[]).length,         c:t.teal },
                      { l:'Open Bugs',    v:(proj.bugs||[]).filter(b=>b.status!=='Resolved').length, c:t.red },
                      { l:'Requests',     v:(proj.featureRequests||[]).length,   c:t.blue },
                      { l:'Client Tasks', v:(proj.clientTasks||[]).length,       c:t.amber },
                    ].map(s => (
                      <div key={s.l} style={{ padding:'0.7rem', background:t.surface2, borderRadius:6, textAlign:'center' }}>
                        <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.textDim, textTransform:'uppercase', marginBottom:3 }}>{s.l}</div>
                        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:24, fontStyle:"italic", color:s.c }}>{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RELEASES */}
              {detailTab==='releases' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ fontSize:11, color:t.textDim }}>{(proj.releases||[]).length} release{(proj.releases||[]).length!==1?'s':''} published</div>
                    <Btn icon="plus" t={t} size="sm" onClick={()=>setRelModal(true)}>Publish Release</Btn>
                  </div>

                  {(proj.releases||[]).length===0 && (
                    <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No releases yet. Publish the first one above.</div>
                  )}

                  {(proj.releases||[]).map(rel => (
                    <div key={rel.id} style={{ padding:'0.85rem', background:t.surface2, borderRadius:7, borderLeft:`3px solid ${relTypeColor(rel.releaseType)}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                        <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:15, color:t.text }}>v{rel.version}</span>
                        <span style={{ fontSize:9, padding:'2px 7px', borderRadius:3, background:`${relTypeColor(rel.releaseType)}18`, color:relTypeColor(rel.releaseType), fontFamily:"'Fira Code',monospace", textTransform:'uppercase' }}>{rel.releaseType}</span>
                        <span style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.textDimmer }}>{fmt(rel.releasedAt)}</span>
                        {rel.acknowledgedAt
                          ? <span style={{ fontSize:9, color:t.teal, fontFamily:"'Fira Code',monospace" }}>✓ Client acknowledged {fmt(rel.acknowledgedAt)}</span>
                          : <span style={{ fontSize:9, color:t.amber, fontFamily:"'Fira Code',monospace" }}>⏳ Awaiting client sign-off</span>}
                      </div>
                      <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6 }}>{rel.releaseNotes?.substring(0,200)}{rel.releaseNotes?.length>200?'…':''}</div>
                      {/* Changelog summary */}
                      {Object.entries(rel.changelog||{}).filter(([,v])=>v?.length).map(([k,v]) => (
                        <div key={k} style={{ fontSize:10, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:4 }}>
                          {k==='features'?'✨':k==='fixes'?'🐛':k==='improvements'?'⚡':'🔒'} {v.length} {k}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* BUGS */}
              {detailTab==='bugs' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {(proj.bugs||[]).length===0 && <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No bugs reported.</div>}
                  {(proj.bugs||[]).map(bug => (
                    <ProjectBugRow key={bug.id} bug={bug} t={t}
                      onStatusChange={(bugId, status) => updateBugStatus(proj.id, bugId, status)}
                      onReply={(bugId, reply) => replyToBug(proj.id, bugId, reply)}
                    />
                  ))}
                </div>
              )}

              {/* FEATURE REQUESTS */}
              {detailTab==='requests' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {(proj.featureRequests||[]).length===0 && <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No feature requests yet.</div>}
                  {(proj.featureRequests||[]).sort((a,b)=>(b.votes||0)-(a.votes||0)).map(req => (
                    <div key={req.id} style={{ padding:'0.75rem', background:t.surface2, borderRadius:6, display:'flex', alignItems:'flex-start', gap:12 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:t.teal, fontFamily:"'Space Grotesk',sans-serif", minWidth:28, textAlign:'center' }}>{req.votes||0}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:12, color:t.text, marginBottom:3 }}>{req.title}</div>
                        {req.description && <div style={{ fontSize:11, color:t.textDim, lineHeight:1.5 }}>{req.description}</div>}
                        <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:4 }}>{fmt(req.createdAt)}</div>
                      </div>
                      <select
                        value={req.status||'Under Review'}
                        onChange={e=>updateFRStatus(proj.id,req.id,e.target.value)}
                        style={{ fontSize:10, background:t.surface3, color:t.text, border:`1px solid ${t.border}`, borderRadius:4, padding:'3px 6px', fontFamily:"'Fira Code',monospace", cursor:'pointer', outline:'none', flexShrink:0 }}>
                        {['Under Review','Planned','In Progress','Completed','Declined'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {/* CLIENT TASKS */}
              {detailTab==='ctasks' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {(proj.clientTasks||[]).length===0 && <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No client task requests yet.</div>}
                  {(proj.clientTasks||[]).map(task => (
                    <div key={task.id} style={{ padding:'0.75rem', background:t.surface2, borderRadius:6, display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
                      <div>
                        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:12, color:t.text, marginBottom:3 }}>{task.title}</div>
                        {task.description && <div style={{ fontSize:11, color:t.textDim }}>{task.description}</div>}
                        <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:4 }}>{fmt(task.createdAt)}</div>
                      </div>
                      <select
                        value={task.status||'Requested'}
                        onChange={e=>{ const val=e.target.value; updateProj(proj.id,p=>({...p,clientTasks:(p.clientTasks||[]).map(t=>t.id===task.id?{...t,status:val}:t)})); }}
                        style={{ fontSize:10, background:t.surface3, color:t.text, border:`1px solid ${t.border}`, borderRadius:4, padding:'3px 6px', fontFamily:"'Fira Code',monospace", cursor:'pointer', outline:'none', flexShrink:0 }}>
                        {['Requested','In Progress','Completed'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {/* DEV TASKS */}
              {detailTab==='tasks' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                  {(proj.tasks||[]).map(task => (
                    <div key={task.id} onClick={()=>toggleTask(proj.id,task.id)} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', cursor:'pointer', borderBottom:`1px solid ${t.border}` }}>
                      <div style={{ width:14, height:14, borderRadius:3, border:`1.5px solid ${task.done?t.teal:t.borderBright}`, background:task.done?t.teal:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {task.done && <I n="check" s={9} c={t.id==='dark'?'#050507':'#fff'}/>}
                      </div>
                      <span style={{ fontSize:12, color:task.done?t.textDimmer:t.text, textDecoration:task.done?'line-through':'none', flex:1 }}>{task.title}</span>
                    </div>
                  ))}
                  {(proj.tasks||[]).length===0 && <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No dev tasks yet.</div>}
                </div>
              )}

              {/* PROJECT FILES */}
              {detailTab==='files' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {(proj.deliverables||[]).length===0 && <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No project files yet.</div>}
                  {(proj.deliverables||[]).map(d => (
                    <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem', background:t.surface2, borderRadius:6, gap:10 }}>
                      <div>
                        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:12, color:t.text }}>{d.name}</div>
                        <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:2 }}>{fmt(d.uploadedAt)}</div>
                      </div>
                      {d.fileName && (
                      <button onClick={async () => {
                        try {
                          alert('Demo mode — file downloads disabled.');
} catch {}
                      }} style={{ fontSize:11, color:t.teal, fontFamily:"'Space Grotesk',sans-serif", background:'none', border:'none', cursor:'pointer', padding:0 }}>
                        {d.fileName} →
                      </button>
                    )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          PUBLISH RELEASE MODAL
      ══════════════════════════════════════════════════════════════ */}
      <Modal open={relModal} onClose={()=>setRelModal(false)} title={`Publish Release · ${proj?.name||''}`} t={t} wide={true}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 1rem' }}>
          <Field t={t} label="Version Number" required value={relForm.version} onChange={v=>setRelForm(f=>({...f,version:v}))} placeholder="e.g. 1.2.3"/>
          <Field t={t} label="Release Type" options={['PATCH','MINOR','MAJOR','HOTFIX']} value={relForm.releaseType} onChange={v=>setRelForm(f=>({...f,releaseType:v}))}/>
        </div>
        <Field t={t} label="Release Notes (summary)" textarea value={relForm.releaseNotes} onChange={v=>setRelForm(f=>({...f,releaseNotes:v}))} placeholder="Describe what changed in this release for the client…" rows={3}/>

        {/* Grouped changelog builder */}
        <div style={{ marginBottom:'0.75rem' }}>
          <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.15em', color:t.textDim, marginBottom:'0.75rem' }}>Changelog (optional — grouped by type)</div>
          {[
            { key:'features',     label:'✨ New Features',  draftKey:'_featDraft', color:t.teal  },
            { key:'improvements', label:'⚡ Improvements',  draftKey:'_impDraft',  color:t.blue  },
            { key:'fixes',        label:'🐛 Bug Fixes',      draftKey:'_fixDraft',  color:t.amber },
            { key:'security',     label:'🔒 Security',       draftKey:'_secDraft',  color:t.red   },
          ].map(g => (
            <div key={g.key} style={{ marginBottom:'0.65rem' }}>
              <div style={{ fontSize:10, color:g.color, fontFamily:"'Fira Code',monospace", marginBottom:4 }}>{g.label}</div>
              <div style={{ display:'flex', gap:6, marginBottom:4 }}>
                <input
                  value={relForm[g.draftKey]||''}
                  onChange={e=>setRelForm(f=>({...f,[g.draftKey]:e.target.value}))}
                  onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addChangelogItem(g.key,g.draftKey); }}}
                  placeholder={`Add ${g.key.replace(/s$/,'')}… (Enter to add)`}
                  style={{ flex:1, fontSize:11, background:t.surface2, border:`1px solid ${t.border}`, borderRadius:4, padding:'5px 8px', color:t.text, fontFamily:"'Space Grotesk',sans-serif", outline:'none' }}
                />
                <button onClick={()=>addChangelogItem(g.key,g.draftKey)} style={{ fontSize:11, background:`${g.color}15`, border:`1px solid ${g.color}40`, borderRadius:4, color:g.color, padding:'5px 10px', cursor:'pointer' }}>+</button>
              </div>
              {(relForm.changelog[g.key]||[]).map((item,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0' }}>
                  <div style={{ width:4, height:4, borderRadius:'50%', background:g.color, flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:t.textDim, flex:1 }}>{item}</span>
                  <button onClick={()=>removeChangelogItem(g.key,i)} style={{ background:'none', border:'none', cursor:'pointer', color:t.textDimmer, fontSize:12, padding:0 }}>×</button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* File upload — optional, stores in Postgres */}
        <div style={{ marginBottom:'0.75rem' }}>
          <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.15em', color:t.textDim, marginBottom:6 }}>Attach File (optional — .zip, .ex5, .pdf, etc.)</div>
          <label style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:t.surface2, border:`1px dashed ${relForm.file ? t.teal : t.border}`, borderRadius:6, cursor:'pointer', fontSize:12, color:relForm.file ? t.teal : t.textDim, fontFamily:"'Space Grotesk',sans-serif" }}>
            <I n="upload" s={14} c={relForm.file ? t.teal : t.textDimmer}/>
            {relForm.fileName || 'Click to attach a file…'}
            <input type="file" style={{ display:'none' }} onChange={e => {
              const f = e.target.files?.[0];
              if (f) setRelForm(prev => ({ ...prev, file: f, fileName: f.name }));
            }}/>
          </label>
          {relForm.file && (
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
              <span style={{ fontSize:10, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>{(relForm.file.size/1024/1024).toFixed(2)} MB</span>
              <button onClick={()=>setRelForm(f=>({...f,file:null,fileName:''}))} style={{ fontSize:10, color:t.red, background:'none', border:'none', cursor:'pointer', padding:'0 4px' }}>✕ Remove</button>
            </div>
          )}
        </div>
        <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end' }}>
          <Btn variant="secondary" t={t} onClick={()=>setRelModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={publishRelease} disabled={!relForm.version.trim()} icon="send">Publish Release</Btn>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════
          NEW / EDIT PROJECT MODAL
      ══════════════════════════════════════════════════════════════ */}
      <Modal open={listModal} onClose={()=>setListModal(false)} title={editing?'Edit Project':'New Project'} t={t}>
        <Field t={t} label="Client" required options={clients.map(c=>({v:c.id,l:c.name}))} value={form.clientId} onChange={v=>setForm(f=>({...f,clientId:v}))}/>
        <Field t={t} label="Project Name" required value={form.name||''} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="e.g. POS System v2, Restaurant Management App…"/>
        <Field t={t} label="Description" textarea value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} rows={3} placeholder="What is this project?"/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 1rem' }}>
          <Field t={t} label="Status" options={['Planning','In Progress','Review','On Hold','Completed','Cancelled']} value={form.status||'Planning'} onChange={v=>setForm(f=>({...f,status:v}))}/>
          <Field t={t} label="Priority" options={['Low','Medium','High','Urgent']} value={form.priority||'Medium'} onChange={v=>setForm(f=>({...f,priority:v}))}/>
        </div>
        <Field t={t} label="Deadline" type="date" value={form.deadline||''} onChange={v=>setForm(f=>({...f,deadline:v}))}/>
        <Field t={t} label="Assign To" options={[{v:'',l:'Unassigned'},...(team||[]).filter(u=>u.active!==false).map(u=>({v:u.id,l:u.name}))]} value={form.assigneeId||''} onChange={v=>setForm(f=>({...f,assigneeId:v}))}/>
        <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end' }}>
          <Btn variant="secondary" t={t} onClick={()=>setListModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={()=>saveProject(form)} disabled={!form.name?.trim()||!form.clientId} icon="check">{editing?'Save Changes':'Create Project'}</Btn>
        </div>
      </Modal>

    </div>
  );
};

// ─── INVOICE PRINT FUNCTION ───────────────────────────────────────────────────
const buildInvoiceHTML = (invoice, client, project) => {
  const kes  = invoice?.kesAmount || Math.round((invoice?.amount||0)*129);
  const amt  = (invoice?.amount||0).toLocaleString();
  const cur  = invoice?.currency||"USD";
  const sCol = invoice?.status==="Paid"?"#00c9a7":invoice?.status==="Overdue"?"#c0394e":"#f5a623";
  const sBg  = invoice?.status==="Paid"?"#00c9a720":invoice?.status==="Overdue"?"#c0394e20":"#f5a62320";
  const sBdr = invoice?.status==="Paid"?"#00c9a740":invoice?.status==="Overdue"?"#c0394e40":"#f5a62340";
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:2rem;gap:2rem;align-items:flex-start">
      <div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:5px">Bill To</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:15px;color:#111;margin-bottom:3px">${client?.name||""}</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#555;line-height:1.7">${client?.company||""}<br/>${client?.email||""}<br/>${client?.phone||""}</div>
      </div>
      <div style="text-align:right">
        <div style="font-family:Syne,sans-serif;font-weight:800;font-size:44px;color:#050506;letter-spacing:-1.5px;line-height:1">${invoice?.number||""}</div>
        <div style="font-family:'Fira Code',monospace;font-size:10px;color:#888;margin-top:10px;line-height:2">
          Issued: ${invoice?.issuedAt?toDateInput(invoice.issuedAt):""}<br/>Due: ${invoice?.dueAt?toDateInput(invoice.dueAt):""}${invoice?.taxPeriod?`<br/>KRA Period: ${invoice.taxPeriod}`:""}
        </div>
        <div style="margin-top:8px;display:inline-block;padding:4px 12px;border-radius:3px;background:${sBg};color:${sCol};font-family:'Fira Code',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.12em;border:1px solid ${sBdr}">${invoice?.status||""}</div>
      </div>
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.5rem"></div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:1.5rem">
      <thead><tr style="border-bottom:2px solid #050506">
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:left">Description</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:left">Project</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:center">Qty</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:right">Rate</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 0 9px;text-align:right">Amount</th>
      </tr></thead>
      <tbody><tr>
        <td style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;padding:15px 12px 15px 0">${invoice?.notes||"Professional services"}</td>
        <td style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666;padding:15px 12px 15px 0">${project?.name||"—"}</td>
        <td style="font-family:'Fira Code',monospace;font-size:12px;color:#444;padding:15px 12px 15px 0;text-align:center">1</td>
        <td style="font-family:'Fira Code',monospace;font-size:12px;color:#444;padding:15px 12px 15px 0;text-align:right">${cur} ${amt}</td>
        <td style="font-family:'Fira Code',monospace;font-size:13px;color:#111;text-align:right;font-weight:600;padding:15px 0">${cur} ${amt}</td>
      </tr></tbody>
    </table>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.5rem"></div>
    <div style="display:flex;justify-content:flex-end;margin-bottom:2.5rem">
      <div style="width:330px">
        <div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #eee"><span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666">Subtotal</span><span style="font-family:'Fira Code',monospace;font-size:14px;color:#333">${cur} ${amt}</span></div>
        <div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #eee"><span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666">VAT / Tax (Presumptive — filed separately)</span><span style="font-family:'Fira Code',monospace;font-size:14px;color:#333">0</span></div>
        <div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #eee"><span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#666">KES Equivalent (est. @ 129)</span><span style="font-family:'Fira Code',monospace;font-size:14px;color:#333">KES ${kes.toLocaleString()}</span></div>
        <div style="display:flex;justify-content:space-between;padding:13px 12px;background:#050506;margin-top:8px;border-radius:5px"><span style="font-family:Syne,sans-serif;font-weight:700;font-size:17px;color:#f0f0f5">Total Due</span><span style="font-family:Syne,sans-serif;font-weight:700;font-size:17px;color:#00c9a7">${cur} ${amt}</span></div>
      </div>
    </div>
    <div style="padding:1.2rem 1.4rem;background:#f8f8fa;border-radius:7px;border:1px solid #e0e0e8">
      ${invoice?.paymentInstructions
        ? `<div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:10px">Payment Instructions</div>
           <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#333;line-height:1.9;white-space:pre-line">${invoice.paymentInstructions}</div>`
        : `<div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:10px">Payment Details · KCB Bank Kenya</div>
           <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 16px">
             ${[["Account Name","QIRE AGENCY"],["Business Reg.","BN-12345678"],["KES Account","Qire Agency Biashara"],["USD Account","Qire Agency USD"],["M-Pesa","Amon Cheruiyot"],["Email","qireagency@gmail.com"]].map(([l,v])=>`<div><span style="font-family:'Fira Code',monospace;font-size:9px;color:#bbb;text-transform:uppercase;letter-spacing:.08em">${l}: </span><span style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#333;font-weight:500">${v}</span></div>`).join("")}
           </div>`
      }
    </div>
    ${sigBlock('', client?.name||'Client', client?.company||'')}
  `;
};

// ─── MODULE: INVOICES ─────────────────────────────────────────────────────────
const Invoices = ({ invoices, setInvoices, clients, projects, t, onPreview, setExpenses }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [filter,setFilter]=useState("All");
  const [form,setForm]=useState({clientId:"",projectId:"",number:"",amount:"",currency:"USD",kesAmount:"",status:"Draft",issuedAt:"",dueAt:"",notes:"",taxPeriod:"2026-H1"});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const filtered=invoices.filter(i=>filter==="All"||i.status===filter);
  const paidT=invoices.filter(i=>i.status==="Paid").reduce((a,i)=>a+i.amount,0);
  const pendT=invoices.filter(i=>i.status==="Pending").reduce((a,i)=>a+i.amount,0);
  const overdT=invoices.filter(i=>i.status==="Overdue").reduce((a,i)=>a+i.amount,0);
  const openAdd=()=>{setEditing(null);setForm({clientId:clients[0]?.id||"",projectId:projects[0]?.id||"",number:`INV-${String(invoices.length+1).padStart(3,"0")}`,amount:"",currency:"USD",kesAmount:"",status:"Draft",issuedAt:todayStr(),dueAt:"",notes:"",taxPeriod:"2026-H1"});setModal(true);};
  const save = async () => {
    const e={...form,clientId:form.clientId,projectId:form.projectId||null,amount:Number(form.amount)||0,kesAmount:Number(form.kesAmount)||0};
    if(editing){
      setInvoices(is=>is.map(i=>i.id===editing?{...i,...e}:i));
      try { await apiFetch(`/api/v1/finance/invoices/${editing}`, { method:'PATCH', body:JSON.stringify({...e, status:(e.status||e.status).toUpperCase()}) }); } catch{}
    } else {
      const tmp={...e,id:Date.now()};
      setInvoices(is=>[...is,tmp]);
      try {
        const res = await apiFetch('/api/v1/finance/invoices', { method:'POST', body:JSON.stringify({...e, status:(e.status||'DRAFT').toUpperCase()}) });
        if(res.invoice?.id) setInvoices(is=>is.map(i=>i.id===tmp.id?{...i,...res.invoice}:i));
      } catch{}
    }
    setModal(false);
  };
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Billing" title="Invoices" sub={`${invoices.length} total · printable · KRA linked`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>New Invoice</Btn>}/>
    {/* FIXED: Invoices stat cards — Space Grotesk 700 */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
      {[{l:"Paid",v:paidT,c:t.teal},{l:"Pending",v:pendT,c:t.amber},{l:"Overdue",v:overdT,c:t.red}].map(s=><Card key={s.l} t={t} style={{padding:"1rem",textAlign:"center"}}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:400,fontStyle:"italic",color:s.c}}>{fmtUSD(s.v)}</div>
      </Card>)}
    </div>
    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
      {["All","Draft","Sent","Pending","Paid","Overdue"].map(s=><button key={s} onClick={()=>setFilter(s)} style={{padding:"5px 11px",borderRadius:5,fontSize:11,fontWeight:500,cursor:"pointer",fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",background:filter===s?t.teal:"transparent",color:filter===s?(t.id==="light"?"#fff":"#050507"):t.textDim,border:filter===s?"none":`1px solid ${t.border}`}}>{s}</button>)}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {filtered.map(inv=>{
        const client=clients.find(c=>c.id===inv.clientId); const proj=projects.find(p=>p.id===inv.projectId);
        return <Card key={inv.id} t={t}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                <span style={{fontFamily:"'Fira Code',monospace",fontSize:11,fontWeight:500,color:t.textDim}}>{inv.number}</span>
                <Badge label={inv.status} t={t}/>
                {inv.taxPeriod&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.teal}15`,color:t.teal,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em"}}>KRA {inv.taxPeriod}</span>}
              </div>
              <div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:14,fontWeight:700,color:t.text}}>{client?.name}{proj&&` · ${proj.name}`}</div>
              <div style={{fontSize:11,color:t.textDimmer,fontWeight:400,marginTop:2}}>Issued {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'} · Due {inv.dueAt ? new Date(inv.dueAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}</div>
              {inv.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:2,fontStyle:"italic",fontWeight:400}}>{inv.notes}</div>}
              {inv.kesAmount>0&&<div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>≈ {fmtKES(inv.kesAmount)}</div>}
            </div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:26,fontStyle:"italic",color:inv.status==="Paid"?t.teal:inv.status==="Overdue"?t.red:t.text,flexShrink:0}}>{inv.currency} {inv.amount?.toLocaleString()}</div>
            <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
              <Btn variant="ghost" size="sm" t={t} onClick={() => onPreview({html:buildInvoiceHTML(inv,clients.find(c=>c.id===inv.clientId),projects.find(p=>p.id===inv.projectId)),title:`${inv.number} · Qire Agency`, isInvoice:true, invoiceId:inv.id, initialAdminSig:inv.adminSignature||'', initialClientSig:inv.clientSignature||'', initialAdminSignedAt:inv.adminSignedAt||'', initialClientSignedAt:inv.clientSignedAt||''})} icon="print">{""}</Btn> 
              {inv.status==="Draft"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>setInvoices(is=>is.map(i=>i.id===inv.id?{...i,status:"Sent"}:i))} icon="send">Send</Btn>}
              {(inv.status==="Sent"||inv.status==="Pending")&&
                <Btn variant="secondary" size="sm" t={t} onClick={()=>{setInvoices(is=>is.map(i=>i.id===inv.id?{...i,status:"Paid"}:i));const kesAmt=inv.kesAmount||Math.round((inv.amount||0)*129);setExpenses(es=>[...es,{id:Date.now(),category:"Income",description:`${inv.number} — ${clients.find(c=>c.id===inv.clientId)?.name||"Client"} (Invoice Paid)`,amount:kesAmt,currency:"KES",date:todayStr(),type:"income",deductible:false}]);}} icon="check">Mark Paid</Btn>}
              <Btn variant="ghost" size="sm" t={t} onClick={()=>{setEditing(inv.id);setForm({...inv,clientId:inv.clientId,projectId:inv.projectId,amount:String(inv.amount),kesAmount:String(inv.kesAmount||""),issuedAt:toDateInput(inv.issuedAt),dueAt:toDateInput(inv.dueAt),paidAt:toDateInput(inv.paidAt)});setModal(true);}} icon="edit">{""}</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>setInvoices(is=>is.filter(i=>i.id!==inv.id))} icon="trash">{""}</Btn>
            </div>
          </div>
        </Card>;
      })}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Invoice":"New Invoice"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Invoice #" value={form.number} onChange={F("number")}/>
        <Field t={t} label="Status" options={["Draft","Sent","Pending","Paid","Overdue"]} value={form.status} onChange={F("status")}/>
        <Field t={t} label="Client" options={clients.map(c=>({v:c.id,l:c.name}))} value={form.clientId} onChange={F("clientId")}/>
        <Field t={t} label="Project" options={projects.map(p=>({v:p.id,l:p.name}))} value={form.projectId} onChange={F("projectId")}/>
        <Field t={t} label="Amount (USD)" type="number" value={form.amount} onChange={v=>{F("amount")(v);F("kesAmount")(String(Math.round(Number(v)*129)));}}/>
        <Field t={t} label="KES Equivalent" type="number" value={form.kesAmount} onChange={F("kesAmount")} placeholder="Auto-calculated"/>
        <Field t={t} label="Issue Date" type="date" value={form.issuedAt} onChange={F("issuedAt")}/>
        <Field t={t} label="Due Date" type="date" value={form.dueAt} onChange={F("dueAt")}/>
        <Field t={t} label="Currency" options={["USD","KES","EUR","GBP"]} value={form.currency} onChange={F("currency")}/>
        <Field t={t} label="KRA Tax Period" options={["2026-H1","2026-H2","2027-H1","2027-H2"]} value={form.taxPeriod} onChange={F("taxPeriod")}/>
      </div>
      <Field t={t} label="Description / Notes" textarea value={form.notes} onChange={F("notes")}/>
      <Field t={t} label="Payment Instructions (optional — overrides default bank details on invoice)" textarea value={form.paymentInstructions||""} onChange={F("paymentInstructions")} placeholder={"Local: M-Pesa — 0700 000 000 (Amon Cheruiyot)\n\nInternational: PayPal — qireagency@gmail.com\n   or Wire: [Bank Name], SWIFT: XXXXXXXX, Account: XXXXXXXXXX"}/>
      <div style={{fontSize:11,color:t.textDimmer,marginTop:-4,paddingLeft:2,fontFamily:"'Fira Code',monospace"}}>
        💡 Leave blank to use default KCB bank details. Fill this for international clients (wire, PayPal, Skrill, Wise).
      </div>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save}>{editing?"Save":"Create Invoice"}</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── MODULE: FINANCE ─────────────────────────────────────────────────────────
const Finance = ({ expenses, setExpenses, t }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [filter,setFilter]=useState("All");
  const [form,setForm]=useState({category:"Software",description:"",amount:"",currency:"KES",date:todayStr(),type:"expense",deductible:"true"});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const cats = ["Software","Internet","Equipment","Marketing","Travel","Professional Fees","Salary","Owner Draw","Utilities","Other"];
  const income=expenses.filter(e=>e.type==="income").reduce((a,e)=>a+e.amount,0);
  const expTotal=expenses.filter(e=>e.type==="expense").reduce((a,e)=>a+e.amount,0);
  const deductible=expenses.filter(e=>e.type==="expense"&&(e.deductible===true||e.deductible==="true")).reduce((a,e)=>a+e.amount,0);
  const sorted=[...expenses].sort((a,b)=>new Date(b.date)-new Date(a.date));
  const filtered=filter==="All"?sorted:filter==="income"?sorted.filter(e=>e.type==="income"):sorted.filter(e=>e.type==="expense"&&e.category===filter);
  const openAdd=()=>{setEditing(null);setForm({category:"Software",description:"",amount:"",currency:"KES",date:todayStr(),type:"expense",deductible:"true"});setModal(true);};
  const openEdit=e=>{setEditing(e.id);setForm({...e,amount:String(e.amount),deductible:String(e.deductible),date:toDateInput(e.date)});setModal(true);};
  const save = async () => {
    const e={...form,amount:Number(form.amount)||0,deductible:form.deductible==="true"||form.deductible===true};
    if(editing){
      setExpenses(es=>es.map(x=>x.id===editing?{...x,...e}:x));
      try { await apiFetch(`/api/v1/finance/expenses/${editing}`, { method:'PATCH', body:JSON.stringify({...e, type:(e.type||e.type).toUpperCase()}) }); } catch{}
    } else {
      const tmp={...e,id:Date.now()};
      setExpenses(es=>[...es,tmp]);
      try {
        const res = await apiFetch('/api/v1/finance/expenses', { method:'POST', body:JSON.stringify({...e, type:(e.type||'EXPENSE').toUpperCase()}) });
        if(res.expense?.id) setExpenses(es=>es.map(x=>x.id===tmp.id?{...x,...normalizeExpenses([res.expense])[0]}:x));
      } catch{}
    }
    setModal(false);
  };  
  
  // API returns type as 'EXPENSE'/'INCOME' — UI checks lowercase 'expense'/'income'
  const normalizeExpenses = (arr) => arr.map(e => ({
    ...e,
    type:       (e.type || 'EXPENSE').toLowerCase(),
    deductible: e.deductible === true || e.deductible === 'true',
    date:       e.date ? (typeof e.date === 'string' ? e.date.split('T')[0] : e.date) : '',
  }));
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Finance" title="Income & Expenses" sub="Full ledger · deductibles flagged for KRA filing" t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Add Entry</Btn>}/>
    {/* FIXED: Finance KPI cards — Cormorant Garamond italic, no duplicate letterSpacing */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:"0.75rem"}}>
      {[{l:"Total Income",v:fmtKES(income),c:t.teal,i:"trending"},{l:"Total Expenses",v:fmtKES(expTotal),c:t.red,i:"receipt"},{l:"Net Profit",v:fmtKES(income-expTotal),c:income>=expTotal?t.teal:t.red,i:"chart"},{l:"Tax Deductible",v:fmtKES(deductible),c:t.amber,i:"tag"}].map(s=>(
        <Card key={s.l} t={t} style={{padding:"1rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}><span style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",color:t.textDim,textTransform:"uppercase"}}>{s.l}</span><I n={s.i} s={12} c={s.c}/></div>
          <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:s.c}}>{s.v}</div>
        </Card>
      ))}
    </div>
    {/* KRA estimate card — FIXED: Space Grotesk 700 */}
    <Card t={t} style={{border:`1px solid ${t.teal}35`,background:`${t.teal}07`}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <I n="tax" s={18} c={t.teal}/>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text}}>KRA Presumptive Tax Estimate — 2026-H1</div>
          <div style={{fontSize:11,color:t.textDim,marginTop:2}}>Qire Agency declared turnover × 15% · File on iTax by <strong>July 15, 2026</strong></div>
          <div style={{fontSize:10,color:t.textDimmer,marginTop:2,fontFamily:"'Fira Code',monospace"}}>Business: QIRE AGENCY · Sole Proprietor · Presumptive Tax</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:30,fontStyle:"italic",color:t.teal}}>{fmtKES(Math.round(income*0.15))}</div>
          <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>15% of {fmtKES(income)}</div>
        </div>
      </div>
    </Card>
    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
      {["All","income",...cats.slice(0,6)].map(f=><button key={f} onClick={()=>setFilter(f)} style={{padding:"4px 10px",borderRadius:5,fontSize:10,cursor:"pointer",fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase",background:filter===f?t.teal:"transparent",color:filter===f?(t.id==="light"?"#fff":"#050507"):t.textDim,border:filter===f?"none":`1px solid ${t.border}`}}>{f}</button>)}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
      {filtered.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No entries found.</div>}
      {filtered.map(e=>{
        const isInc=e.type==="income"; const c=isInc?t.teal:t.red;
        return <Card key={e.id} t={t} style={{padding:"1rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:c,flexShrink:0,marginTop:2}}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:500,fontSize:15,color:t.text}}>{e.description}</span>
                <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.surface3}`,color:t.textDimmer,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.08em"}}>{e.category}</span>
                {!isInc&&(e.deductible===true||e.deductible==="true")&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.amber}18`,color:t.amber,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",border:`1px solid ${t.amber}40`}}>Deductible</span>}
              </div>
              <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>{e.date?toDateInput(e.date):"—"}</div>
            </div>
            {/* FIXED: ledger amounts — Space Grotesk 600 */}
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:20,fontStyle:"italic",color:c,flexShrink:0}}>{isInc?"+":"-"}{fmtKES(e.amount)}</div>
            <div style={{display:"flex",gap:3}}>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(e)} icon="edit">{""}</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>setExpenses(es=>es.filter(x=>x.id!==e.id))} icon="trash">{""}</Btn>
            </div>
          </div>
        </Card>;
      })}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Entry":"Add Entry"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Type" options={[{v:"expense",l:"Expense"},{v:"income",l:"Income"}]} value={form.type} onChange={F("type")}/>
        <Field t={t} label="Category" options={form.type==="income"?["Income","Freelance","Trading","Other"]:cats} value={form.category} onChange={F("category")}/>
        <Field t={t} label="Amount" type="number" value={form.amount} onChange={F("amount")}/>
        <Field t={t} label="Currency" options={["KES","USD","EUR","GBP"]} value={form.currency} onChange={F("currency")}/>
        <Field t={t} label="Date" type="date" value={form.date} onChange={F("date")}/>
        {form.type==="expense"&&<Field t={t} label="Tax Deductible?" options={[{v:"true",l:"Yes — Deductible"},{v:"false",l:"No"}]} value={form.deductible} onChange={F("deductible")}/>}
      </div>
      <Field t={t} label="Description" value={form.description} onChange={F("description")} placeholder="Safaricom Fibre, Adobe CC, Client payment…"/>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save} disabled={!form.description||!form.amount}>{editing?"Save":"Add Entry"}</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── MODULE: TAX TRACKER ─────────────────────────────────────────────────────
const TaxTracker = ({ taxRecords, setTaxRecords, expenses, invoices, t }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({period:"",turnover:0,taxDue:0,taxPaid:0,status:"Due",filedAt:"",notes:""});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const income=expenses.filter(e=>e.type==="income").reduce((a,e)=>a+e.amount,0);
  const usdInvoicesPaid=invoices.filter(i=>i.status==="Paid").reduce((a,i)=>a+(i.kesAmount||i.amount*129),0);
  const totalTurnover=income+usdInvoicesPaid;
  const openEdit=r=>{setEditing(r.id);setForm({...r,turnover:String(r.turnover),taxDue:String(r.taxDue),taxPaid:String(r.taxPaid),filedAt:toDateInput(r.filedAt)});setModal(true);};
  const openAdd=()=>{setEditing(null);const next=`202${Math.floor(taxRecords.length/2)+6}-H${(taxRecords.length%2)+1}`;setForm({period:next,turnover:"0",taxDue:"0",taxPaid:"0",status:"Due",filedAt:"",notes:""});setModal(true);};
  const save = async () => {
    const e={...form,turnover:Number(form.turnover)||0,taxDue:Number(form.taxDue)||0,taxPaid:Number(form.taxPaid)||0};
    if(editing){
      setTaxRecords(rs=>rs.map(r=>r.id===editing?{...r,...e}:r));
      try { await apiFetch(`/api/v1/finance/tax/${editing}`, { method:'PATCH', body:JSON.stringify({...e, status:(e.status||e.status).toUpperCase()}) }); } catch{}
    } else {
      const tmp={...e,id:Date.now()};
      setTaxRecords(rs=>[...rs,tmp]);
      try {
        const res = await apiFetch('/api/v1/finance/tax', { method:'POST', body:JSON.stringify({...e, status:(e.status||'DUE').toUpperCase()}) });
        if(res.record?.id) setTaxRecords(rs=>rs.map(r=>r.id===tmp.id?{...r,...res.record}:r));
      } catch{}
    }
    setModal(false);
  };  const totalPaid=taxRecords.reduce((a,r)=>a+r.taxPaid,0);
  const totalDue=taxRecords.filter(r=>r.status==="Due"||r.status==="Overdue").reduce((a,r)=>a+r.taxDue,0);
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Compliance" title="KRA Tax Records" sub="Qire Agency · Presumptive Tax (15% of turnover)" t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Add Period</Btn>}/>
    {/* iTax guide — FIXED: Space Grotesk 700 */}
    <Card t={t} style={{border:`1px solid ${t.blue}35`,background:`${t.blue}07`}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
        <I n="shield" s={18} c={t.blue}/>
        <div>
          <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text,marginBottom:4}}>How to File on iTax (Step-by-Step)</div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {["1. Login at itax.kra.go.ke with your personal KRA PIN","2. Go to Returns → Income Tax – Resident Individual","3. Select: Business Income → Presumptive Tax","4. Enter Business Name: QIRE AGENCY · Reg: BN-12345678","5. Enter Gross Turnover for the period (from your Finance ledger)","6. System calculates 15% tax automatically","7. Submit → get PRN → Pay via M-Pesa Paybill 572572","8. Save the receipt here in Notes"].map(s=>(
              <div key={s} style={{fontSize:11,color:t.textDim,fontFamily:"'Space Grotesk',sans-serif"}}>{s}</div>
            ))}
          </div>
          <div style={{marginTop:8,fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>FILING DEADLINES: Jan–Jun period → July 15 · Jul–Dec period → January 15</div>
        </div>
      </div>
    </Card>
    {/* Current period estimate — FIXED: Space Grotesk 700 */}
    <Card t={t} style={{border:`1px solid ${t.amber}35`,background:`${t.amber}07`}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <I n="tax" s={18} c={t.amber}/>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text}}>2026-H1 Estimate (Jan–Jun 2026)</div>
          <div style={{fontSize:11,color:t.textDim,marginTop:2}}>Based on income + paid USD invoices converted to KES</div>
        </div>
        <div style={{textAlign:"right"}}>
          {/* FIXED: Cormorant Garamond, no duplicate letterSpacing */}
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:26,fontStyle:"italic",color:t.amber,letterSpacing:"0.02em"}}>{fmtKES(Math.round(totalTurnover*0.15))}</div>
          <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>15% of {fmtKES(totalTurnover)}</div>
        </div>
      </div>
    </Card>
    {/* FIXED: Tax stat cards — Cormorant Garamond, no duplicate letterSpacing */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
      {[{l:"Total Tax Paid",v:fmtKES(totalPaid),c:t.teal},{l:"Currently Due",v:fmtKES(totalDue),c:t.amber},{l:"Filing Periods",v:taxRecords.filter(r=>r.status==="Filed").length+"/"+taxRecords.length,c:t.blue}].map(s=>(
        <Card key={s.l} t={t} style={{padding:"1rem",textAlign:"center"}}>
          <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
          <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:s.c}}>{s.v}</div>
        </Card>
      ))}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
      {taxRecords.map(r=>(
        <Card key={r.id} t={t}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                <span style={{fontFamily:"'Fira Code',monospace",fontWeight:500,fontSize:15,color:t.text}}>{r.period}</span>
                <Badge label={r.status} t={t}/>
              </div>
              <div style={{fontSize:11,color:t.textDim,fontWeight:400}}>{r.turnover>0?`Turnover: ${fmtKES(r.turnover)} · Tax due: ${fmtKES(r.taxDue)} · Paid: ${fmtKES(r.taxPaid)}`:"Nil return — zero income"}</div>
              {r.filedAt&&<div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>Filed: {r.filedAt?toDateInput(r.filedAt):"—"}</div>}
              {r.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:2,fontStyle:"italic",fontWeight:400}}>{r.notes}</div>}
            </div>
            <div style={{display:"flex",gap:3}}>
              {r.status==="Due"&&<Btn variant="amber" size="sm" t={t} onClick={()=>openEdit(r)} icon="tax">File Now</Btn>}
              <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(r)} icon="edit">{""}</Btn>
            </div>
          </div>
        </Card>
      ))}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Update Tax Record":"Add Tax Period"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Period (e.g. 2026-H1)" value={form.period} onChange={F("period")}/>
        <Field t={t} label="Status" options={["Due","Filed","Overdue"]} value={form.status} onChange={F("status")}/>
        <Field t={t} label="Gross Turnover (KES)" type="number" value={form.turnover} onChange={v=>{F("turnover")(v);F("taxDue")(String(Math.round(Number(v)*0.15)));}}/>
        <Field t={t} label="Tax Due (auto 15%)" type="number" value={form.taxDue} onChange={F("taxDue")}/>
        <Field t={t} label="Tax Paid (KES)" type="number" value={form.taxPaid} onChange={F("taxPaid")}/>
        <Field t={t} label="Filed At (date)" type="date" value={form.filedAt} onChange={F("filedAt")}/>
      </div>
      <Field t={t} label="Notes (receipts, PRN, etc.)" textarea value={form.notes} onChange={F("notes")}/>
      <div style={{padding:"0.85rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`}}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:4}}>Pay via M-Pesa</div>
        <div style={{fontSize:14,color:t.textDim}}>Paybill: <strong style={{color:t.teal}}>572572</strong> · Account: Your PRN from iTax</div>
      </div>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save}>{editing?"Update Record":"Add Period"}</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── LETTER PRINT FUNCTION ────────────────────────────────────────────────────
const buildLetterHTML = (letter) => {
  const dateStr = letter.date ? new Date(letter.date).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}) : "";
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:2rem;gap:2rem;align-items:flex-start">
      <div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:5px">To</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;line-height:1.8;white-space:pre-line">${letter.to||""}</div>
      </div>
      <div style="text-align:right">
        <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:5px">Date</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;margin-bottom:4px">${dateStr}</div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;color:#aaa;text-transform:uppercase;letter-spacing:.1em">${letter.type||""}</div>
      </div>
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.8rem"></div>
    <div style="margin-bottom:1.8rem">
      <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:6px">Re</div>
      <div style="font-family:Syne,sans-serif;font-weight:700;font-size:22px;color:#111;letter-spacing:-.01em;line-height:1.2">${letter.subject||""}</div>
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.8rem"></div>
    <div style="font-family:'Space Grotesk',sans-serif;font-size:16px;color:#333;line-height:2.1;white-space:pre-wrap">${letter.body||""}</div>
    <div class="doc-divider" style="margin-top:2.5rem"></div>
    <div class="sig-row no-break" style="display:grid;grid-template-columns:1fr 1fr;gap:2.5rem;margin-top:1.5rem">
      <div class="sig-cell">
        <div class="label">Qire Agency — Authorized Signature</div>
        <div class="sig-line">__ADMIN_SIG__</div>
        <div class="sig-name">Amon Cheruiyot</div>
        <div class="sig-role">Founder · Qire Agency</div>
        <div class="sig-date">Date: ${SIG_DATE_ADMIN}</div>
      </div>
      <div></div>
    </div>
  `;
};

// ─── MODULE: LETTERS ──────────────────────────────────────────────────────────
const Letters = ({ letters, setLetters, t, onPreview }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const defaultBody="Dear [Name],\n\nThank you for your continued trust in Qire Agency.\n\n[Write your letter here.]\n\nWarm regards,\nAmon Cheruiyot\nFounder & Lead Developer · Qire Agency\nqireagency@gmail.com";
  const [form,setForm]=useState({to:"",subject:"",date:todayStr(),body:defaultBody,status:"Saved",type:"Client Letter"});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const openAdd=()=>{setEditing(null);setForm({to:"",subject:"",date:todayStr(),body:defaultBody,status:"Saved",type:"Client Letter"});setModal(true);};
  const openEdit=l=>{setEditing(l.id);setForm({...l,date:toDateInput(l.date)});setModal(true);};
  const save = async () => {
    if(editing){
      setLetters(ls=>ls.map(l=>l.id===editing?{...l,...form}:l));
      try { await apiFetch(`/api/v1/letters/${editing}`, { method:'PATCH', body:JSON.stringify(form) }); } catch{}
    } else {
      const tmp={...form,id:Date.now()};
      setLetters(ls=>[...ls,tmp]);
      try {
        const res = await apiFetch('/api/v1/letters', { method:'POST', body:JSON.stringify(form) });
        if(res.letter?.id) setLetters(ls=>ls.map(l=>l.id===tmp.id?{...l,...res.letter}:l));
      } catch{}
    }
    setModal(false);
  };
  const typeColors={"Client Letter":t.teal,"Contract":t.amber,"Proposal":t.blue,"Invoice Cover":t.teal,"Notice":t.red,"Other":t.textDim};
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Communication" title="Letters & Letterhead" sub={`${letters.length} saved · branded printable documents`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Compose Letter</Btn>}/>
    <Card t={t} style={{border:`1px solid ${t.border}`,background:t.surface2,padding:"1rem"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <I n="mail" s={14} c={t.teal}/>
        <div style={{flex:1}}>
          <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>All letters use your Qire Agency letterhead with official branding, contact details & registration number. Click <strong style={{color:t.text}}>Preview & Print</strong> to open the printable version — save as PDF from your browser.</div>
        </div>
      </div>
    </Card>
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {letters.length===0&&<EmptyState title="No letters yet" sub="Compose your first letter" t={t}/>}
      {letters.map(l=>{
        const c=typeColors[l.type]||t.textDim;
        return <Card key={l.id} t={t}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{width:36,height:36,borderRadius:6,background:`${c}18`,border:`1px solid ${c}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><I n="mail" s={15} c={c}/></div>
            <div style={{flex:1,minWidth:120}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{l.subject}</span>
                <Badge label={l.status} t={t}/>
                <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${c}15`,color:c,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase"}}>{l.type}</span>
              </div>
              <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>To: {(l.to||"").split("\n")[0]} · {l.date}</div>
              <div style={{fontSize:11,color:t.textDimmer,marginTop:3,fontStyle:"italic",fontWeight:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:340}}>{(l.body||"").split("\n").find(line=>line.trim()&&!line.startsWith("Dear"))}</div>
            </div>
            <div style={{display:"flex",gap:3,flexShrink:0}}>
              <Btn variant="secondary" size="sm" t={t} onClick={()=>onPreview({html:buildLetterHTML(l),title:`${l.subject} · Qire Agency`, isLetter:true, letterId:l.id, initialAdminSig:l.adminSignature||'', initialAdminSignedAt:l.adminSignedAt||''})} icon="print">Preview</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(l)} icon="edit">{""}</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>setLetters(ls=>ls.filter(x=>x.id!==l.id))} icon="trash">{""}</Btn>
            </div>
          </div>
        </Card>;
      })}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Letter":"Compose Letter"} t={t} wide>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Type" options={["Client Letter","Contract","Proposal","Invoice Cover","Notice","Other"]} value={form.type} onChange={F("type")}/>
        <Field t={t} label="Date" type="date" value={form.date} onChange={F("date")}/>
      </div>
      <Field t={t} label="To (Recipient name, company, city)" value={form.to} onChange={F("to")} placeholder={"James Mwangi\nKapital KE\nNairobi, Kenya"} textarea rows={2}/>
      <Field t={t} label="Subject" value={form.subject} onChange={F("subject")} placeholder="RE: Project Name / Agreement"/>
      <Field t={t} label="Body" textarea value={form.body} onChange={F("body")} rows={11}/>
      <Field t={t} label="Status" options={["Saved","Sent","Draft"]} value={form.status} onChange={F("status")}/>
      <div style={{padding:"0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`,fontSize:15,color:t.textDim}}>
        <I n="mail" s={11} c={t.teal}/>{" "}Letterhead (header + footer with Qire Agency branding) is added automatically when you preview & print.
      </div>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end",flexWrap:"wrap"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn variant="secondary" t={t} onClick={()=>onPreview({html:buildLetterHTML({...form,id:Date.now()}),title:`${form.subject||"Letter"} · Qire Agency`})} icon="print" disabled={!form.to||!form.subject}>Preview & Print</Btn>
        <Btn t={t} onClick={save} disabled={!form.to||!form.subject} icon="send">Save Letter</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── MODULE: ADS TRACKER ─────────────────────────────────────────────────────
const Ads = ({ ads, setAds, t }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({platform:"Twitter/X",campaign:"",budget:"",spent:"",currency:"KES",status:"Active",startDate:"",endDate:"",leads:0,notes:""});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const openAdd=()=>{setEditing(null);setForm({platform:"Twitter/X",campaign:"",budget:"",spent:"0",currency:"KES",status:"Active",startDate:todayStr(),endDate:"",leads:0,notes:""});setModal(true);};
  const openEdit=a=>{setEditing(a.id);setForm({...a,budget:String(a.budget),spent:String(a.spent),leads:String(a.leads),startDate:toDateInput(a.startDate),endDate:toDateInput(a.endDate)});setModal(true);};
  const save = async () => {
    const e={...form,budget:Number(form.budget)||0,spent:Number(form.spent)||0,leads:Number(form.leads)||0};
    if(editing){
      setAds(as=>as.map(a=>a.id===editing?{...a,...e}:a));
      try { await apiFetch(`/api/v1/ads/${editing}`, { method:'PATCH', body:JSON.stringify(e) }); } catch{}
    } else {
      const tmp={...e,id:Date.now()};
      setAds(as=>[...as,tmp]);
      try {
        const res = await apiFetch('/api/v1/ads', { method:'POST', body:JSON.stringify(e) });
        if(res.ad?.id) setAds(as=>as.map(a=>a.id===tmp.id?{...a,...res.ad}:a));
      } catch{}
    }
    setModal(false);
  };
  const totalBudget=ads.reduce((a,x)=>a+x.budget,0);
  const totalSpent=ads.reduce((a,x)=>a+x.spent,0);
  const totalLeads=ads.reduce((a,x)=>a+x.leads,0);
  const platformColors={"Twitter/X":t.blue,"Facebook":t.blue,"Instagram":t.amber,"LinkedIn":t.blue,"Google":t.teal,"TikTok":t.red,"Other":t.textDim};
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Marketing" title="Ad Campaigns" sub={`${ads.length} campaigns · ${ads.filter(a=>a.status==="Active").length} active`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>New Campaign</Btn>}/>
    {/* FIXED: Ads stat cards — Cormorant Garamond, no duplicate letterSpacing */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.75rem"}}>
      {[{l:"Total Budget",v:fmtKES(totalBudget),c:t.textDim},{l:"Total Spent",v:fmtKES(totalSpent),c:t.amber},{l:"Remaining",v:fmtKES(totalBudget-totalSpent),c:t.teal},{l:"Total Leads",v:String(totalLeads),c:t.blue}].map(s=>(
        <Card key={s.l} t={t} style={{padding:"1rem"}}>
          <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontWeight:400,letterSpacing:"0.01em",fontStyle:"italic",color:s.c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.v}</div>
        </Card>
      ))}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {ads.length===0&&<EmptyState title="No campaigns yet" sub="Create your first ad campaign" t={t}/>}
      {ads.map(a=>{
        const pct=a.budget>0?Math.min(100,Math.round((a.spent/a.budget)*100)):0;
        const cpl=a.leads>0?Math.round(a.spent/a.leads):0;
        const pc=platformColors[a.platform]||t.textDim;
        return <Card key={a.id} t={t}>
          <div style={{display:"flex",alignItems:"flex-start",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${pc}18`,color:pc,fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",border:`1px solid ${pc}40`}}>{a.platform}</span>
                {/* FIXED: campaign name — Space Grotesk 700 */}
                <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{a.campaign}</span>
                <Badge label={a.status} t={t}/>
              </div>
              <div style={{fontSize:11,color:t.textDim,fontWeight:400,marginBottom:8}}>{a.startDate ? new Date(a.startDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'} → {a.endDate ? new Date(a.endDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : "ongoing"}</div>
              <div style={{marginBottom:6,maxWidth:340}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>Spend: {fmtKES(a.spent)} / {fmtKES(a.budget)}</span>
                  <span style={{fontSize:10,color:pct>=90?t.red:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{pct}%</span>
                </div>
                <ProgressBar pct={pct} t={t} color={pct>=90?t.red:t.amber}/>
              </div>
              <div style={{display:"flex",gap:"1.2rem",flexWrap:"wrap"}}>
                <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}><span style={{color:t.teal,fontWeight:500}}>{a.leads}</span> leads</div>
                {cpl>0&&<div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}><span style={{color:t.blue,fontWeight:500}}>{fmtKES(cpl)}</span> / lead</div>}
              </div>
              {a.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:5,fontStyle:"italic",fontWeight:400}}>{a.notes}</div>}
            </div>
            <div style={{display:"flex",gap:3,flexShrink:0}}>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(a)} icon="edit">{""}</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>setAds(as=>as.filter(x=>x.id!==a.id))} icon="trash">{""}</Btn>
            </div>
          </div>
        </Card>;
      })}
    </div>
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Campaign":"New Campaign"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Platform" options={["Twitter/X","Facebook","Instagram","LinkedIn","Google","TikTok","Other"]} value={form.platform} onChange={F("platform")}/>
        <Field t={t} label="Status" options={["Active","Paused","Completed"]} value={form.status} onChange={F("status")}/>
        <Field t={t} label="Budget (KES)" type="number" value={form.budget} onChange={F("budget")}/>
        <Field t={t} label="Spent So Far (KES)" type="number" value={form.spent} onChange={F("spent")}/>
        <Field t={t} label="Start Date" type="date" value={form.startDate} onChange={F("startDate")}/>
        <Field t={t} label="End Date" type="date" value={form.endDate} onChange={F("endDate")}/>
        <Field t={t} label="Leads Generated" type="number" value={form.leads} onChange={F("leads")}/>
        <Field t={t} label="Currency" options={["KES","USD"]} value={form.currency} onChange={F("currency")}/>
      </div>
      <Field t={t} label="Campaign Name" required value={form.campaign} onChange={F("campaign")} placeholder="TradesPlus Beta Launch"/>
      <Field t={t} label="Notes / Target Audience" textarea value={form.notes} onChange={F("notes")}/>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save} disabled={!form.campaign}>{editing?"Save":"Create Campaign"}</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── MODULE: WAITLIST ─────────────────────────────────────────────────────────
const Waitlist = ({ waitlist, setWaitlist, t }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({product:"TradesPlus",name:"",email:"",joinedAt:todayStr(),status:"Waiting",notes:""});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const products = ["TradesPlus", "ArbitragePlus", "Q-Trader EA", "SheetsInsider", "Custom Trading", "Partnership", "Other"];
  const grouped=products.reduce((acc,p)=>{acc[p]=waitlist.filter(w=>w.product===p);return acc;},{});
  const openAdd=()=>{setEditing(null);setForm({product:"TradesPlus",name:"",email:"",joinedAt:todayStr(),status:"Waiting",notes:""});setModal(true);};
  const openEdit=w=>{setEditing(w.id);setForm({...w,joinedAt:toDateInput(w.joinedAt)});setModal(true);};
  const save = async () => {
    if(editing){
      setWaitlist(ws=>ws.map(w=>w.id===editing?{...w,...form}:w));
      try { await apiFetch(`/api/v1/waitlist/${editing}`, { method:'PATCH', body:JSON.stringify(form) }); } catch{}
    } else {
      const tmp={...form,id:Date.now(),status:'Waiting',createdAt:todayStr()};
      setWaitlist(ws=>[...ws,tmp]);
      try { await apiFetch('/api/v1/waitlist', { method:'POST', body:JSON.stringify(form) }); } catch{}
    }
    setModal(false);
  };
  const notify=id=>setWaitlist(ws=>ws.map(w=>w.id===id?{...w,status:"Notified"}:w));
  const pColors = {TradesPlus:t.teal,ArbitragePlus:t.amber,"Q-Trader EA":t.blue,SheetsInsider:t.red,"Custom Trading":t.teal,Partnership:t.amber,Other:t.textDimmer};
  return <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Products" title="Waitlist" sub={`${waitlist.length} signups · ${waitlist.filter(w=>w.status==="Waiting").length} pending`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Add Signup</Btn>}/>
    {products.map(p=>{const entries=grouped[p]||[];if(!entries.length&&p==="Other")return null;return <div key={p}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"0.75rem"}}>
        <div style={{width:8,height:8,borderRadius:"50%",background:entries.some(e=>e.status==="Waiting")?pColors[p]:t.textDimmer,animation:entries.some(e=>e.status==="Waiting")?"portalPulse 2s infinite":"none"}}/>
        <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>{p}</span>
        <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>{entries.length} signup{entries.length!==1?"s":""} · {entries.filter(e=>e.status==="Waiting").length} waiting</span>
      </div>
      {entries.length===0&&<Card t={t} style={{padding:"0.75rem"}}><span style={{fontSize:14,color:t.textDimmer,fontStyle:"italic"}}>No signups yet.</span></Card>}
      <div style={{display:"flex",flexDirection:"column",gap:"0.45rem",marginBottom:"1rem"}}>
        {entries.map(w=><Card key={w.id} t={t} style={{padding:"0.9rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.85rem",flexWrap:"wrap"}}>
            <Avatar name={w.name} size={30} t={t}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:2}}>
                <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>{w.name}</span>
                <Badge label={w.status} t={t}/>
              </div>
              <div style={{fontSize:11,color:t.textDim,fontWeight:400}}>{w.email} · Joined {w.joinedAt ? new Date(w.joinedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}</div>
              {w.notes&&<div style={{fontSize:10,color:t.textDimmer,marginTop:2,fontStyle:"italic"}}>{w.notes}</div>}
            </div>
            <div style={{display:"flex",gap:3,flexShrink:0}}>
              {w.status==="Waiting"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>notify(w.id)} icon="send">Notify</Btn>}
              <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(w)} icon="edit">{""}</Btn>
              <Btn variant="ghost" size="sm" t={t} onClick={()=>setWaitlist(ws=>ws.filter(x=>x.id!==w.id))} icon="trash">{""}</Btn>
            </div>
          </div>
        </Card>)}
      </div>
    </div>;})}
    <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Signup":"Add Signup"} t={t}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
        <Field t={t} label="Product" options={products} value={form.product} onChange={F("product")}/>
        <Field t={t} label="Status" options={["Waiting","Notified"]} value={form.status} onChange={F("status")}/>
        <Field t={t} label="Name" required value={form.name} onChange={F("name")}/>
        <Field t={t} label="Email" type="email" value={form.email} onChange={F("email")}/>
        <Field t={t} label="Joined At" type="date" value={form.joinedAt} onChange={F("joinedAt")}/>
      </div>
      <Field t={t} label="Notes" value={form.notes} onChange={F("notes")} placeholder="Trading interests, referral source…"/>
      <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
        <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
        <Btn t={t} onClick={save} disabled={!form.name}>{editing?"Save":"Add Signup"}</Btn>
      </div>
    </Modal>
  </div>;
};

// ─── STAFF PROFILE VIEW ───────────────────────────────────────────────────────
const StaffProfile = ({ user: u, projects, timeEntries, payroll, team, t, onEdit, onClose }) => {
  const [tab, setTab] = useState("overview");

  // ── computed data ──
  const myEntries  = timeEntries.filter(e => String(e.userId) === String(u.id));
  const myProjects = projects.filter(p => String(p.assignee) === String(u.id));
  const myPayroll  = payroll.filter(p => String(p.recipientId) === String(u.id) || p.recipient === u.name);

  const totalHours    = myEntries.reduce((a,e) => a + Number(e.hours), 0);
  const billableHours = myEntries.filter(e => e.billable===true||e.billable==="true").reduce((a,e) => a + Number(e.hours), 0);
  const unbilledHours = myEntries.filter(e => (e.billable===true||e.billable==="true") && (e.billed===false||e.billed==="false")).reduce((a,e) => a + Number(e.hours), 0);
  const billedHours   = billableHours - unbilledHours;

  const totalPaid    = myPayroll.filter(p => p.status==="Paid"||p.status==="PAID").reduce((a,p) => a + Number(p.net||0), 0);
  const pendingPay   = myPayroll.filter(p => p.status==="Pending"||p.status==="PENDING").reduce((a,p) => a + Number(p.net||0), 0);

  const totalTasks   = myProjects.reduce((a,p) => a + p.tasks.length, 0);
  const doneTasks    = myProjects.reduce((a,p) => a + p.tasks.filter(tk=>tk.done).length, 0);
  const pendingTasks = totalTasks - doneTasks;
  const taskPct      = totalTasks ? Math.round((doneTasks/totalTasks)*100) : 0;

  // Hours by project this month
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
  const monthEntries = myEntries.filter(e => (e.date||"").slice(0,7) === thisMonth);
  const monthHours   = monthEntries.reduce((a,e) => a + Number(e.hours), 0);

  // Per-project breakdown
  const projectBreakdown = myProjects.map(p => {
    const entries  = myEntries.filter(e => String(e.projectId) === String(p.id));
    const hrs      = entries.reduce((a,e) => a + Number(e.hours), 0);
    const billable = entries.filter(e => e.billable===true||e.billable==="true").reduce((a,e) => a + Number(e.hours), 0);
    const unbilled = entries.filter(e => (e.billable===true||e.billable==="true")&&(e.billed===false||e.billed==="false")).reduce((a,e) => a + Number(e.hours), 0);
    const done     = p.tasks.filter(tk=>tk.done).length;
    const total    = p.tasks.length;
    const earned   = Number(u.ratePerHour||0) * billable;
    return { ...p, hrs, billable, unbilled, done, total, earned };
  }).filter(p => p.hrs > 0 || p.tasks.length > 0).sort((a,b) => b.hrs - a.hrs);

  const ratePerHour    = Number(u.ratePerHour || 0);
  const monthlySalary  = Number(u.monthlySalary || 0);
  const totalEarned    = ratePerHour > 0 ? billableHours * ratePerHour : 0;
  const unbilledValue  = ratePerHour > 0 ? unbilledHours * ratePerHour : 0;

  // Contract status
  const contractExpired = u.contractEnd && new Date(u.contractEnd) < new Date();
  const contractSoon    = u.contractEnd && !contractExpired && new Date(u.contractEnd) < new Date(Date.now() + 30*24*60*60*1000);

  const TABS = [
    { id:"overview",  label:"Overview"  },
    { id:"hours",     label:"Hours"     },
    { id:"projects",  label:"Projects"  },
    { id:"payroll",   label:"Payroll"   },
    { id:"profile",   label:"Profile"   },
  ];

  const Section = ({label, children, style={}}) => (
    <div style={{marginBottom:"1.25rem",...style}}>
      <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.22em",textTransform:"uppercase",color:t.textDimmer,marginBottom:"0.6rem"}}>{label}</div>
      {children}
    </div>
  );

  const KV = ({label, value, mono=false, accent=false}) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${t.border}`}}>
      <span style={{fontSize:12,color:t.textDim,fontFamily:"'Space Grotesk',sans-serif"}}>{label}</span>
      <span style={{fontSize:12,fontFamily:mono?"'Fira Code',monospace":"'Space Grotesk',sans-serif",fontWeight:500,color:accent?t.teal:t.text}}>{value||"—"}</span>
    </div>
  );

  const StatPill = ({label, value, sub, color}) => (
    <div style={{padding:"0.75rem 0.9rem",background:t.surface2,borderRadius:7,border:`1px solid ${t.border}`,flex:1,minWidth:100}}>
      <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:18,color:color||t.text,letterSpacing:"-0.02em"}}>{value}</div>
      <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,textTransform:"uppercase",letterSpacing:"0.1em",marginTop:2}}>{label}</div>
      {sub && <div style={{fontSize:10,color:t.textDimmer,marginTop:2}}>{sub}</div>}
    </div>
  );

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  return createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9400,background:"rgba(0,0,0,0.55)",display:"flex",alignItems: isMobile ? "flex-end" : "flex-start",justifyContent: isMobile ? "center" : "flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{
        width: isMobile ? "100vw" : "min(680px,100vw)",
        height: isMobile ? "92dvh" : "100vh",
        borderRadius: isMobile ? "16px 16px 0 0" : 0,
        overflowY:"auto",
        background:t.surface,
        borderLeft: isMobile ? "none" : `1px solid ${t.border}`,
        borderTop: isMobile ? `1px solid ${t.border}` : "none",
        display:"flex", flexDirection:"column",
      }}>
        {/* Mobile drag handle */}
        {isMobile && <div style={{width:36,height:4,borderRadius:2,background:t.border,margin:"10px auto 0",flexShrink:0}}/>}
        {/* ── Header ── */}
        <div style={{padding:"1.25rem 1.5rem 0",background:t.surface,position:"sticky",top:0,zIndex:10,borderBottom:`1px solid ${t.border}`,paddingBottom:"0.75rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"0.85rem"}}>
            <Avatar name={u.name} size={52} t={t}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:19,color:t.text}}>{u.name}</span>
                {u.active===false && <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.red}18`,color:t.red,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.red}30`}}>Inactive</span>}
                {contractExpired && <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.red}18`,color:t.red,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.red}30`}}>Contract Expired</span>}
                {contractSoon && <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.amber}18`,color:t.amber,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.amber}30`}}>Expiring Soon</span>}
              </div>
              <div style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.teal,textTransform:"uppercase",letterSpacing:"0.1em",marginTop:2}}>{u.title||u.role}</div>
              <div style={{fontSize:11,color:t.textDimmer,marginTop:2,display:"flex",gap:10,flexWrap:"wrap"}}>
                {u.contractType && <span>{u.contractType==="contractor"?"Contractor":"Employee"}</span>}
                {u.payType==="hourly" && ratePerHour>0 && <span style={{color:t.amber}}>KES {ratePerHour.toLocaleString()}/hr</span>}
                {u.payType==="fixed" && monthlySalary>0 && <span style={{color:t.teal}}>KES {monthlySalary.toLocaleString()}/mo</span>}
                {u.phone && <span>{u.phone}</span>}
              </div>
            </div>
            <div style={{display:"flex",gap:5,flexShrink:0}}>
              <Btn variant="secondary" size="sm" t={t} onClick={onEdit} icon="edit">Edit</Btn>
              <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4,display:"flex",alignItems:"center"}}><I n="x" s={18} c={t.textDim}/></button>
            </div>
          </div>
          {/* Tab bar */}
          <div style={{display:"flex",gap:3,overflowX:"auto"}}>
            {TABS.map(tb => (
              <button key={tb.id} onClick={()=>setTab(tb.id)} style={{
                padding:"5px 14px",fontSize:11,cursor:"pointer",whiteSpace:"nowrap",
                fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase",
                background:"transparent",
                color: tb.id===tab ? t.teal : t.textDim,
                border:"none", borderBottom: tb.id===tab ? `2px solid ${t.teal}` : "2px solid transparent",
              }}>{tb.label}</button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{padding:"1.25rem 1.5rem",flex:1}}>

          {/* ══ OVERVIEW ══ */}
          {tab==="overview" && <>
            {/* Alert banners */}
            {pendingPay > 0 && (
              <div style={{padding:"0.6rem 0.85rem",background:`${t.amber}10`,border:`1px solid ${t.amber}30`,borderRadius:6,marginBottom:"0.85rem",fontSize:12,color:t.amber,display:"flex",justifyContent:"space-between"}}>
                <span>💰 Pending payment</span>
                <strong>KES {pendingPay.toLocaleString()}</strong>
              </div>
            )}
            {pendingTasks > 0 && (
              <div style={{padding:"0.6rem 0.85rem",background:`${t.blue}08`,border:`1px solid ${t.blue}25`,borderRadius:6,marginBottom:"0.85rem",fontSize:12,color:t.blue,display:"flex",justifyContent:"space-between"}}>
                <span>📋 Pending tasks</span>
                <strong>{pendingTasks} task{pendingTasks!==1?"s":""}</strong>
              </div>
            )}
            {unbilledValue > 0 && (
              <div style={{padding:"0.6rem 0.85rem",background:`${t.teal}08`,border:`1px solid ${t.teal}25`,borderRadius:6,marginBottom:"0.85rem",fontSize:12,color:t.teal,display:"flex",justifyContent:"space-between"}}>
                <span>⏱ Unbilled hours value</span>
                <strong>KES {unbilledValue.toLocaleString()}</strong>
              </div>
            )}

            {/* Stat pills */}
            <div style={{display:"flex",gap:"0.6rem",flexWrap:"wrap",marginBottom:"1.25rem"}}>
              <StatPill label="Total Hours" value={`${totalHours.toFixed(1)}h`} sub={`${monthHours.toFixed(1)}h this month`} color={t.blue}/>
              <StatPill label="Billable" value={`${billableHours.toFixed(1)}h`} sub={`${unbilledHours.toFixed(1)}h unbilled`} color={t.teal}/>
              <StatPill label="Tasks Done" value={`${doneTasks}/${totalTasks}`} sub={`${taskPct}% complete`} color={t.amber}/>
              <StatPill label="Total Paid" value={`KES ${totalPaid.toLocaleString()}`} sub={pendingPay>0?`KES ${pendingPay.toLocaleString()} pending`:""} color={t.teal}/>
            </div>

            {/* Project breakdown */}
            {projectBreakdown.length > 0 && (
              <Section label="Project Breakdown">
                {projectBreakdown.map(p => (
                  <div key={p.id} style={{padding:"0.75rem",background:t.surface2,borderRadius:6,border:`1px solid ${t.border}`,marginBottom:"0.5rem"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                      <div>
                        <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>{p.name}</div>
                        <div style={{fontSize:10,color:t.textDimmer,marginTop:1,fontFamily:"'Fira Code',monospace"}}>
                          {p.hrs.toFixed(1)}h total · {p.billable.toFixed(1)}h billable · {p.unbilled.toFixed(1)}h unbilled
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        {ratePerHour > 0 && <div style={{fontFamily:"'Fira Code',monospace",fontSize:12,color:t.teal,fontWeight:600}}>KES {p.earned.toLocaleString()}</div>}
                        <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{p.done}/{p.total} tasks</div>
                      </div>
                    </div>
                    <div style={{height:4,borderRadius:2,background:t.surface3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${p.total?Math.round((p.done/p.total)*100):0}%`,background:t.teal,borderRadius:2,transition:"width 0.3s"}}/>
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {/* Recent time entries */}
            {myEntries.length > 0 && (
              <Section label="Recent Time Entries">
                {[...myEntries].sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,5).map(e => {
                  const proj = projects.find(p=>String(p.id)===String(e.projectId));
                  return (
                    <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${t.border}`}}>
                      <div>
                        <div style={{fontSize:14,color:t.text}}>{e.description||"—"}</div>
                        <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>{proj?.name||"Unknown"} · {toDateInput(e.date)}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                        <div style={{fontFamily:"'Fira Code',monospace",fontSize:12,color:e.billed===true||e.billed==="true"?t.textDim:t.teal}}>{Number(e.hours).toFixed(1)}h</div>
                        <div style={{fontSize:9,color:e.billed===true||e.billed==="true"?t.textDimmer:t.amber,fontFamily:"'Fira Code',monospace"}}>
                          {e.billed===true||e.billed==="true"?"billed":e.billable===true||e.billable==="true"?"unbilled":"non-billable"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}
          </>}

          {/* ══ HOURS ══ */}
          {tab==="hours" && <>
            <div style={{display:"flex",gap:"0.6rem",flexWrap:"wrap",marginBottom:"1.25rem"}}>
              <StatPill label="Total Hours" value={`${totalHours.toFixed(1)}h`} color={t.blue}/>
              <StatPill label="Billable" value={`${billableHours.toFixed(1)}h`} color={t.teal}/>
              <StatPill label="Billed" value={`${billedHours.toFixed(1)}h`} color={t.textDim}/>
              <StatPill label="Unbilled" value={`${unbilledHours.toFixed(1)}h`} sub={ratePerHour>0?`KES ${unbilledValue.toLocaleString()} value`:"set hourly rate to see value"} color={t.amber}/>
            </div>
            {ratePerHour > 0 && (
              <div style={{padding:"0.75rem 1rem",background:`${t.teal}08`,border:`1px solid ${t.teal}25`,borderRadius:6,marginBottom:"1.25rem",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:14,color:t.textDim}}>Total earnings (billable hrs × KES {ratePerHour.toLocaleString()}/hr)</span>
                <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16,color:t.teal}}>KES {totalEarned.toLocaleString()}</span>
              </div>
            )}
            <Section label="All Time Entries">
              {myEntries.length === 0 && <div style={{fontSize:14,color:t.textDimmer,fontStyle:"italic"}}>No time entries yet.</div>}
              {[...myEntries].sort((a,b)=>new Date(b.date)-new Date(a.date)).map(e => {
                const proj = projects.find(p=>String(p.id)===String(e.projectId));
                const isBilled   = e.billed===true||e.billed==="true";
                const isBillable = e.billable===true||e.billable==="true";
                return (
                  <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"9px 0",borderBottom:`1px solid ${t.border}`}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,color:t.text,marginBottom:2}}>{e.description||"—"}</div>
                      <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{proj?.name||"?"} · {toDateInput(e.date)}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                      <div style={{fontFamily:"'Fira Code',monospace",fontSize:13,fontWeight:600,color:t.text}}>{Number(e.hours).toFixed(1)}h</div>
                      {ratePerHour > 0 && isBillable && <div style={{fontSize:10,color:t.teal,fontFamily:"'Fira Code',monospace"}}>KES {(Number(e.hours)*ratePerHour).toLocaleString()}</div>}
                      <div style={{marginTop:2,display:"flex",gap:3,justifyContent:"flex-end"}}>
                        <span style={{fontSize:8,padding:"1px 5px",borderRadius:3,background:isBillable?`${t.teal}15`:`${t.textDimmer}15`,color:isBillable?t.teal:t.textDimmer,fontFamily:"'Fira Code',monospace",textTransform:"uppercase"}}>
                          {isBillable?"billable":"non-bill"}
                        </span>
                        {isBillable && <span style={{fontSize:8,padding:"1px 5px",borderRadius:3,background:isBilled?`${t.textDimmer}10`:`${t.amber}15`,color:isBilled?t.textDimmer:t.amber,fontFamily:"'Fira Code',monospace",textTransform:"uppercase"}}>
                          {isBilled?"billed":"unbilled"}
                        </span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </Section>
          </>}

          {/* ══ PROJECTS ══ */}
          {tab==="projects" && <>
            {myProjects.length === 0 && <div style={{fontSize:14,color:t.textDimmer,fontStyle:"italic"}}>No projects assigned.</div>}
            {myProjects.map(p => {
              const entries  = myEntries.filter(e=>String(e.projectId)===String(p.id));
              const hrs      = entries.reduce((a,e)=>a+Number(e.hours),0);
              const unbilled = entries.filter(e=>(e.billable===true||e.billable==="true")&&(e.billed===false||e.billed==="false")).reduce((a,e)=>a+Number(e.hours),0);
              const done     = p.tasks.filter(tk=>tk.done).length;
              const total    = p.tasks.length;
              const pct      = total ? Math.round((done/total)*100) : 0;
              const pendingTsk = p.tasks.filter(tk=>!tk.done);
              return (
                <div key={p.id} style={{padding:"1rem",background:t.surface2,borderRadius:8,border:`1px solid ${t.border}`,marginBottom:"0.75rem"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text,marginBottom:2}}>{p.name}</div>
                      <Badge label={p.status} t={t}/>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'Fira Code',monospace",fontSize:15,color:t.textDim}}>{hrs.toFixed(1)}h logged</div>
                      {unbilled>0 && <div style={{fontSize:10,color:t.amber,fontFamily:"'Fira Code',monospace"}}>{unbilled.toFixed(1)}h unbilled</div>}
                    </div>
                  </div>
                  <div style={{marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDim}}>Task progress</span>
                      <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>{done}/{total} · {pct}%</span>
                    </div>
                    <ProgressBar pct={pct} t={t}/>
                  </div>
                  {pendingTsk.length > 0 && (
                    <div>
                      <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:4}}>Pending</div>
                      {pendingTsk.slice(0,4).map(tk => (
                        <div key={tk.id} style={{fontSize:11,color:t.textDim,padding:"3px 0",borderBottom:`1px solid ${t.border}`,display:"flex",gap:6,alignItems:"center"}}>
                          <I n="circle" s={8} c={t.textDimmer}/> {tk.title}
                        </div>
                      ))}
                      {pendingTsk.length > 4 && <div style={{fontSize:10,color:t.textDimmer,marginTop:4,fontFamily:"'Fira Code',monospace"}}>+{pendingTsk.length-4} more</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </>}

          {/* ══ PAYROLL ══ */}
          {tab==="payroll" && <>
            <div style={{display:"flex",gap:"0.6rem",flexWrap:"wrap",marginBottom:"1.25rem"}}>
              <StatPill label="Total Paid" value={`KES ${totalPaid.toLocaleString()}`} color={t.teal}/>
              <StatPill label="Pending" value={`KES ${pendingPay.toLocaleString()}`} color={pendingPay>0?t.amber:t.textDim}/>
              <StatPill label="Payslips" value={myPayroll.length} color={t.blue}/>
            </div>
            {ratePerHour > 0 && unbilledValue > 0 && (
              <div style={{padding:"0.75rem 1rem",background:`${t.amber}08`,border:`1px solid ${t.amber}25`,borderRadius:6,marginBottom:"1rem",fontSize:12,color:t.amber,display:"flex",justifyContent:"space-between"}}>
                <span>Unbilled hours not yet on any payslip</span>
                <strong>KES {unbilledValue.toLocaleString()} ({unbilledHours.toFixed(1)}h)</strong>
              </div>
            )}
            {myPayroll.length === 0 && <div style={{fontSize:14,color:t.textDimmer,fontStyle:"italic",marginBottom:"1rem"}}>No payroll records yet.</div>}
            {[...myPayroll].sort((a,b)=>new Date(b.period||0)-new Date(a.period||0)).map(p => {
              const isPaid = p.status==="Paid"||p.status==="PAID";
              return (
                <div key={p.id} style={{padding:"0.9rem 1rem",background:t.surface2,borderRadius:7,border:`1px solid ${t.border}`,marginBottom:"0.5rem",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>{p.period||"—"}</div>
                    <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>
                      {p.method||"—"} · gross KES {Number(p.gross||0).toLocaleString()} · deductions KES {Number(p.deductions||0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:15,color:isPaid?t.teal:t.amber}}>KES {Number(p.net||0).toLocaleString()}</div>
                    <Badge label={p.status} t={t}/>
                  </div>
                </div>
              );
            })}
          </>}

          {/* ══ PROFILE ══ */}
          {tab==="profile" && <>
            <Section label="Identity">
              <KV label="Full Name"    value={u.name}/>
              <KV label="Username"     value={u.username} mono/>
              <KV label="Phone"        value={u.phone}/>
              <KV label="National ID"  value={u.nationalId} mono/>
              <KV label="KRA PIN"      value={u.kraPin} mono/>
            </Section>
            <Section label="Payment Info">
              <KV label="M-Pesa"         value={u.mpesa} mono accent/>
              <KV label="Bank Name"      value={u.bankName}/>
              <KV label="Bank Account"   value={u.bankAccount} mono/>
            </Section>
            <Section label="Employment">
              <KV label="Contract Type"   value={u.contractType==="contractor"?"Contractor / Freelancer":"Employee (Formal)"}/>
              <KV label="Pay Type"        value={u.payType==="hourly"?"Per Hour":u.payType==="fixed"?"Fixed Monthly":"Per Project"}/>
              {u.payType==="hourly"   && <KV label="Hourly Rate"     value={ratePerHour>0?`KES ${ratePerHour.toLocaleString()}/hr`:"Not set"} accent={ratePerHour>0}/>}
              {u.payType==="fixed"    && <KV label="Monthly Salary"  value={monthlySalary>0?`KES ${monthlySalary.toLocaleString()}/mo`:"Not set"} accent={monthlySalary>0}/>}
              <KV label="Withholding Tax" value={u.withholdingTax?"5% WHT applies (contractor)":"Not applicable"}/>
            </Section>
            <Section label="Contract">
              <KV label="Start Date"  value={u.joinedAt    ? toDateInput(u.joinedAt)    : "—"}/>
              <KV label="End Date"    value={u.contractEnd ? toDateInput(u.contractEnd) : "Ongoing"}/>
              {contractExpired && <div style={{padding:"0.5rem 0.75rem",background:`${t.red}10`,border:`1px solid ${t.red}25`,borderRadius:5,fontSize:11,color:t.red,marginTop:8}}>⚠ Contract expired {toDateInput(u.contractEnd)}</div>}
              {contractSoon   && <div style={{padding:"0.5rem 0.75rem",background:`${t.amber}10`,border:`1px solid ${t.amber}25`,borderRadius:5,fontSize:11,color:t.amber,marginTop:8}}>⚠ Contract expires {toDateInput(u.contractEnd)}</div>}
              {u.contractNotes||u.notes ? <div style={{marginTop:8,padding:"0.65rem 0.8rem",background:t.surface2,borderRadius:6,border:`1px solid ${t.border}`,fontSize:14,color:t.textDim,lineHeight:1.7}}>{u.contractNotes||u.notes}</div> : null}
            </Section>
          </>}

        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── MODULE: TEAM ─────────────────────────────────────────────────────────────
const Team = ({ projects, team, setTeam, timeEntries=[], payroll=[], t }) => {
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter,  setFilter]  = useState("all");   // all | active | inactive
  const [confirmFire, setConfirmFire] = useState(null); // user id
  const [selectedProfile, setSelectedProfile] = useState(null); // user id for profile view
  const TEAM_EMPTY = {
    name:"", username:"", password:"", role:"developer", title:"", active:true,
    phone:"", mpesa:"", bankName:"", bankAccount:"", kraPin:"", nationalId:"",
    payType:"hourly", ratePerHour:"", monthlySalary:"", withholdingTax:true,
    joinedAt:"", contractEnd:"", contractType:"contractor", notes:"",
  };
  const [form, setForm] = useState(TEAM_EMPTY);
  const [profileTab, setProfileTab] = useState("identity");
  const F = k => v => setForm(f=>({...f,[k]:v}));

  // Role metadata
  const ROLES = [
    { v:"admin",     l:"Admin",     color:t.teal,      default_title:"Founder & Lead Developer" },
    { v:"developer", l:"Developer", color:t.amber,     default_title:"Full Stack Developer"     },
    { v:"sales",     l:"Sales",     color:"#4a9eff",   default_title:"Sales Executive"          },
    { v:"support",   l:"Support",   color:"#a78bfa",   default_title:"Support Specialist"       },
    { v:"marketing", l:"Marketing", color:"#c9a84c",   default_title:"Marketing Manager"        },
  ];
  const roleColor = r => ROLES.find(x=>x.v===r)?.color || t.textDim;
  const roleLabel = r => ROLES.find(x=>x.v===r)?.l || r;

  // Group team
  const admins    = team.filter(u=>u.role==="admin");
  const staff     = team.filter(u=>u.role!=="admin");
  const displayed = filter==="active"   ? staff.filter(u=>u.active!==false)
                  : filter==="inactive" ? staff.filter(u=>u.active===false)
                  : staff;

  const openAdd  = () => {
    setEditing(null);
    setForm({...TEAM_EMPTY, title:"Full Stack Developer", joinedAt:todayStr()});
    setProfileTab("identity");
    setModal(true);
  };
  const openEdit = u => {
    setEditing(u.id);
    setForm({...TEAM_EMPTY, ...u});
    setProfileTab("identity");
    setModal(true);
  };

  const save = async () => {
    const entry = {...form, active: form.active!==false};
    // All profile fields that the User model supports
    const profilePayload = {
      name:            entry.name,
      phone:           entry.phone        || null,
      title:           entry.title        || null,
      username:        entry.username     || null,
      timezone:        entry.timezone     || 'Africa/Nairobi',
      avatar:          entry.avatar       || null,
      // Staff profile
      nationalId:      entry.nationalId   || null,
      kraPin:          entry.kraPin       || null,
      mpesa:           entry.mpesa        || null,
      bankName:        entry.bankName     || null,
      bankAccount:     entry.bankAccount  || null,
      // Employment & pay
      contractType:    entry.contractType || 'contractor',
      payType:         entry.payType      || 'hourly',
      ratePerHour:     entry.ratePerHour  ? parseFloat(entry.ratePerHour)  : null,
      monthlySalary:   entry.monthlySalary? parseFloat(entry.monthlySalary): null,
      withholdingTax:  entry.withholdingTax !== false,
      // Contract dates
      contractStart:   entry.joinedAt     ? entry.joinedAt   : null,
      contractEnd:     entry.contractEnd  ? entry.contractEnd: null,
      contractNotes:   entry.notes        || null,
      // Password — only send if explicitly changed
      ...(entry.password ? { password: entry.password } : {}),
    };
    if(editing) {
      setTeam(ts=>ts.map(u=>u.id===editing ? {...u,...entry} : u));
      try { await apiFetch(`/api/v1/admin/users/${editing}`, { method:'PATCH', body:JSON.stringify({
        ...profilePayload,
        ...(entry.email ? { email: entry.email } : {}),
        role:     entry.role?.toUpperCase(),
        isActive: entry.active !== false,
      }) }); } catch{}
    } else {
      const tmp = {...entry, id:Date.now(), joinedAt:entry.joinedAt||todayStr()};
      setTeam(ts=>[...ts, tmp]);
      try {
        const res = await apiFetch('/api/v1/admin/users', {
          method:'POST',
          body:JSON.stringify({
            ...profilePayload,
            email:    entry.email || `${(entry.name||'user').toLowerCase().replace(/\s+/g,'.')}@qire.internal`,
            password: entry.password || (entry.name||'user').toLowerCase().replace(/\s+/g,'') + '2026',
            role:     entry.role?.toUpperCase() || 'DEVELOPER',
          }),
        });
        if(res.user?.id) setTeam(ts=>ts.map(u=>u.id===tmp.id ? {...u,...res.user, role:(res.user.role||entry.role).toLowerCase()} : u));
      } catch{}
    }
    setModal(false);
  };

  const fire = async (id) => {
    setTeam(ts=>ts.map(u=>u.id===id ? {...u,active:false} : u));
    setConfirmFire(null);
    try { await apiFetch(`/api/v1/admin/users/${id}`, { method:'PATCH', body:JSON.stringify({ isActive:false }) }); } catch{}
  };

  const restore = async (id) => {
    setTeam(ts=>ts.map(u=>u.id===id ? {...u,active:true} : u));
    try { await apiFetch(`/api/v1/admin/users/${id}`, { method:'PATCH', body:JSON.stringify({ isActive:true }) }); } catch{}
  };

  const activeCount = team.filter(u=>u.active!==false).length;
  const inactiveCount = team.filter(u=>u.active===false).length;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow="People"
        title="Team Management"
        sub={`${activeCount} active · ${inactiveCount} inactive · ${team.length} total`}
        t={t}
        action={<Btn icon="plus" onClick={openAdd} t={t}>Hire Member</Btn>}
      />

      {/* ── Stats row ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:"0.65rem"}}>
        {ROLES.map(r => {
          const count = team.filter(u=>u.role===r.v&&u.active!==false).length;
          return (
            <Card key={r.v} t={t} style={{padding:"0.85rem",textAlign:"center"}}>
              <div style={{fontSize:28,fontWeight:400,fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",color:r.color}}>{count}</div>
              <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.15em",marginTop:3}}>{r.l}</div>
            </Card>
          );
        })}
      </div>

      {/* ── Leadership (admins — shown separately, no fire button) ── */}
      <div>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"0.75rem"}}>Leadership</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:"0.75rem"}}>
          {admins.map(u => {
            const myProjects = projects.filter(p=>p.assignee === u.id);
            const done  = myProjects.reduce((a,p)=>a+p.tasks.filter(tk=>tk.done).length,0);
            const total = myProjects.reduce((a,p)=>a+p.tasks.length,0);
            return (
              <Card key={u.id} t={t}>
                <div style={{display:"flex",alignItems:"center",gap:"0.85rem",marginBottom:"0.85rem"}}>
                  <Avatar name={u.name} size={44} t={t}/>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>{u.name}</div>
                    <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.teal,marginTop:2,textTransform:"uppercase",letterSpacing:"0.1em"}}>{u.title||"Founder"}</div>
                    <div style={{fontSize:10,color:t.textDimmer,marginTop:1}}>@{u.username} · joined {u.joinedAt?toDateInput(u.joinedAt):"—"}</div>
                  </div>
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.teal}18`,color:t.teal,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",border:`1px solid ${t.teal}40`}}>Admin</span>
                </div>
                <div style={{paddingTop:"0.65rem",borderTop:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{myProjects.length} project{myProjects.length!==1?"s":""} · {done}/{total} tasks</span>
                  <div style={{display:"flex",gap:5}}>
                    <Btn variant="secondary" size="sm" t={t} onClick={()=>setSelectedProfile(u.id)} icon="eye">Profile</Btn>
                    <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(u)} icon="edit">Edit</Btn>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Staff: filter bar + cards ── */}
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.75rem",gap:8,flexWrap:"wrap"}}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase"}}>Staff</div>
          <div style={{display:"flex",gap:5}}>
            {["all","active","inactive"].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{
                padding:"4px 12px",borderRadius:4,fontSize:10,fontFamily:"'Fira Code',monospace",cursor:"pointer",
                textTransform:"uppercase",letterSpacing:"0.08em",
                background:filter===f?t.teal:"transparent",
                color:filter===f?(t.id==="dark"?"#050507":"#fff"):t.textDim,
                border:filter===f?"none":`1px solid ${t.border}`,
              }}>{f}</button>
            ))}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(260px,100%),1fr))",gap:"0.75rem"}}>
          {displayed.length===0 && (
            <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic",padding:"1rem 0"}}>
              {filter==="inactive" ? "No inactive staff." : "No staff yet — hire someone above."}
            </div>
          )}
          {displayed.map(u => {
            const myProjects = projects.filter(p=>p.assignee === u.id);
            const done  = myProjects.reduce((a,p)=>a+p.tasks.filter(tk=>tk.done).length,0);
            const total = myProjects.reduce((a,p)=>a+p.tasks.length,0);
            const pct   = total ? Math.round((done/total)*100) : 0;
            const rColor = roleColor(u.role);
            const isActive = u.active !== false;
            return (
              <Card key={u.id} t={t} style={{opacity:isActive?1:0.5}}>
                <div style={{display:"flex",alignItems:"center",gap:"0.85rem",marginBottom:"0.85rem"}}>
                  <Avatar name={u.name} size={44} t={t}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</span>
                      {!isActive && <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.red}18`,color:t.red,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.red}40`,flexShrink:0}}>Inactive</span>}
                    </div>
                    <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:rColor,textTransform:"uppercase",letterSpacing:"0.1em"}}>{u.title||roleLabel(u.role)}</div>
                    <div style={{fontSize:10,color:t.textDimmer,marginTop:1}}>@{u.username} · {u.joinedAt ? `joined ${u.joinedAt}` : "—"}</div>
                    {/* Pay info */}
                    <div style={{marginTop:3,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                      {u.payType==="fixed" && u.monthlySalary > 0 && (
                        <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.teal,background:`${t.teal}10`,padding:"1px 6px",borderRadius:3,border:`1px solid ${t.teal}25`}}>
                          KES {Number(u.monthlySalary).toLocaleString()}/mo
                        </span>
                      )}
                      {(u.payType==="hourly"||u.payType==="project"||(u.ratePerHour>0&&!u.payType)) && u.ratePerHour > 0 && (
                        <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.amber,background:`${t.amber}10`,padding:"1px 6px",borderRadius:3,border:`1px solid ${t.amber}25`}}>
                          KES {Number(u.ratePerHour).toLocaleString()}/hr
                        </span>
                      )}
                      {u.contractType && (
                        <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,background:t.surface3,padding:"1px 6px",borderRadius:3}}>
                          {u.contractType==="contractor"?"contractor":"employee"}
                        </span>
                      )}
                      {u.contractEnd && new Date(u.contractEnd) < new Date(Date.now()+30*24*60*60*1000) && (
                        <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:new Date(u.contractEnd)<new Date()?t.red:t.amber,padding:"1px 0"}}>
                          {new Date(u.contractEnd)<new Date()?"contract expired":"expiring soon"}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${rColor}18`,color:rColor,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",border:`1px solid ${rColor}40`,flexShrink:0}}>{roleLabel(u.role)}</span>
                </div>

                {/* Project progress — only for developers */}
                {u.role==="developer" && (
                  <div style={{marginBottom:"0.75rem"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.1em"}}>Tasks</span>
                      <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{pct}%</span>
                    </div>
                    <ProgressBar pct={pct} t={t}/>
                    <div style={{fontSize:10,color:t.textDimmer,marginTop:3,fontFamily:"'Fira Code',monospace"}}>{done}/{total} tasks · {myProjects.length} project{myProjects.length!==1?"s":""}</div>
                  </div>
                )}

                <div style={{paddingTop:"0.65rem",borderTop:`1px solid ${t.border}`,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <Btn variant="secondary" size="sm" t={t} onClick={()=>setSelectedProfile(u.id)} icon="eye">Profile</Btn>
                  <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(u)} icon="edit">Edit</Btn>
                  {isActive
                    ? <Btn variant="danger" size="sm" t={t} onClick={()=>setConfirmFire(u.id)}>Fire</Btn>
                    : <Btn variant="secondary" size="sm" t={t} onClick={()=>restore(u.id)}>Restore</Btn>
                  }
                  <div style={{marginLeft:"auto",fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",alignSelf:"center",userSelect:"all"}}>pw: {u.password}</div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Staff Profile Drawer ── */}
      {selectedProfile && (() => {
        const u = team.find(x => x.id === selectedProfile);
        if (!u) return null;
        return (
          <StaffProfile
            user={u}
            projects={projects}
            timeEntries={timeEntries}
            payroll={payroll}
            team={team}
            t={t}
            onEdit={() => { setSelectedProfile(null); openEdit(u); }}
            onClose={() => setSelectedProfile(null)}
          />
        );
      })()}

      {/* ── Confirm fire dialog ── */}
      {confirmFire && (() => {
        const u = team.find(x=>x.id===confirmFire);
        return (
          <div style={{position:"fixed",inset:0,zIndex:9500,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}} onClick={()=>setConfirmFire(null)}>
            <div style={{background:t.surface,borderRadius:8,padding:"1.5rem",maxWidth:380,width:"100%",border:`1px solid ${t.red}40`}} onClick={e=>e.stopPropagation()}>
              <div style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:700,color:t.red,marginBottom:8}}>Confirm: Fire {u?.name}?</div>
              <div style={{fontSize:13,color:t.textDim,marginBottom:"1.25rem",lineHeight:1.6}}>
                Their account will be marked <strong style={{color:t.text}}>inactive</strong> and they will lose portal access immediately. This can be reversed via Restore.
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn variant="secondary" t={t} onClick={()=>setConfirmFire(null)}>Cancel</Btn>
                <Btn variant="danger" t={t} onClick={()=>fire(confirmFire)}>Yes, Fire</Btn>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Add / Edit modal ── */}
      <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Team Member":"Hire Team Member"} t={t}>
        {/* Tab bar */}
        <div style={{display:"flex",gap:3,marginBottom:"1rem",borderBottom:`1px solid ${t.border}`,paddingBottom:"0.65rem"}}>
          {[
            {id:"identity", label:"Identity"},
            {id:"pay",      label:"Pay & Rates"},
            {id:"contract", label:"Contract"},
          ].map(tab => (
            <button key={tab.id} onClick={()=>setProfileTab(tab.id)} style={{
              padding:"5px 12px", borderRadius:5, fontSize:11, cursor:"pointer",
              fontFamily:"'Fira Code',monospace", letterSpacing:"0.06em", textTransform:"uppercase",
              background: profileTab===tab.id ? t.teal : "transparent",
              color: profileTab===tab.id ? (t.id==="dark"?"#050507":"#fff") : t.textDim,
              border: profileTab===tab.id ? "none" : `1px solid ${t.border}`,
            }}>{tab.label}</button>
          ))}
        </div>

        {/* ── IDENTITY TAB ── */}
        {profileTab==="identity" && <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
            <Field t={t} label="Full Name"    value={form.name}     onChange={F("name")}     placeholder="Jane Doe"/>
            <Field t={t} label="Phone / WhatsApp" value={form.phone||""} onChange={F("phone")} placeholder="+254 7XX XXX XXX"/>
            <Field t={t} label="Username"     value={form.username||""} onChange={F("username")} placeholder="jane.dev"/>
            <Field t={t} label="Email"        type="email" value={form.email||""} onChange={F("email")} placeholder="jane@qire.co.ke"/>
            <Field t={t} label="Password"     value={form.password||""} onChange={F("password")} placeholder={editing ? "Leave blank to keep current" : "jane2026"}/>
            <Field t={t} label="Role"
              options={ROLES.map(r=>({v:r.v,l:r.l}))}
              value={form.role}
              onChange={v=>{ F("role")(v); if(!editing) F("title")(ROLES.find(r=>r.v===v)?.default_title||""); }}
            />
            <Field t={t} label="Title / Specialization" value={form.title||""} onChange={F("title")} placeholder="Full Stack Developer…"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
            <Field t={t} label="National ID No." value={form.nationalId||""} onChange={F("nationalId")} placeholder="12345678"/>
            <Field t={t} label="KRA PIN" value={form.kraPin||""} onChange={F("kraPin")} placeholder="A000000000X"/>
          </div>
          {!editing && (
            <div style={{padding:"0.65rem 0.85rem",background:`${t.teal}08`,border:`1px solid ${t.teal}20`,borderRadius:5,fontSize:14,color:t.textDim}}>
              💡 Default password: <span style={{fontFamily:"'Fira Code',monospace",color:t.text}}>{form.username||"username"}2026</span>
            </div>
          )}
        </>}

        {/* ── PAY TAB ── */}
        {profileTab==="pay" && <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
            <Field t={t} label="Pay Type"
              options={[{v:"hourly",l:"Per Hour (logged hours)"},{v:"fixed",l:"Fixed Monthly Salary"},{v:"project",l:"Per Project (negotiated)"}]}
              value={form.payType||"hourly"} onChange={F("payType")}
            />
            <Field t={t} label="Contract Type"
              options={[{v:"contractor",l:"Contractor / Freelancer"},{v:"employee",l:"Employee (formal)"}]}
              value={form.contractType||"contractor"} onChange={F("contractType")}
            />
            {(form.payType==="hourly"||form.payType==="project") && (
              <Field t={t} label="Rate per Hour (KES)" type="number" value={form.ratePerHour||""} onChange={F("ratePerHour")} placeholder="e.g. 2500"/>
            )}
            {form.payType==="fixed" && (
              <Field t={t} label="Monthly Salary (KES)" type="number" value={form.monthlySalary||""} onChange={F("monthlySalary")} placeholder="e.g. 50000"/>
            )}
          </div>
          <div style={{padding:"0.75rem 0.9rem",background:t.surface2,borderRadius:6,border:`1px solid ${t.border}`,fontSize:15,color:t.textDim,lineHeight:1.7}}>
            {form.contractType==="contractor"
              ? <><strong style={{color:t.text}}>Contractor / Freelancer:</strong> You pay gross. They handle their own taxes. If you pay them <strong style={{color:t.amber}}>more than KES 24,000</strong> in a single payment, you should withhold <strong style={{color:t.amber}}>5% WHT</strong> (Withholding Tax) and remit to KRA.</>
              : <><strong style={{color:t.text}}>Employee:</strong> You are responsible for PAYE, NSSF (KES 420/mo), and SHA/SHIF (2.75% of gross). These are deducted from gross and remitted to KRA/NSSF/SHA.</>
            }
          </div>
          {form.contractType==="contractor" && (
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:14,color:t.textDim}}>
              <div onClick={()=>F("withholdingTax")(!form.withholdingTax)} style={{width:16,height:16,borderRadius:3,border:`1.5px solid ${form.withholdingTax?t.amber:t.borderBright}`,background:form.withholdingTax?t.amber:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                {form.withholdingTax && <I n="check" s={9} c={t.id==="dark"?"#050507":"#fff"}/>}
              </div>
              Auto-apply 5% Withholding Tax on payslips over KES 24,000
            </label>
          )}
          {form.contractType==="employee" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0.65rem"}}>
              <div style={{padding:"0.6rem 0.8rem",background:`${t.amber}10`,border:`1px solid ${t.amber}30`,borderRadius:5}}>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.amber,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3}}>NSSF</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>KES 420/mo</div>
                <div style={{fontSize:10,color:t.textDimmer,marginTop:2}}>employee + employer</div>
              </div>
              <div style={{padding:"0.6rem 0.8rem",background:`${t.blue}10`,border:`1px solid ${t.blue}30`,borderRadius:5}}>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.blue,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3}}>SHA/SHIF</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>2.75% gross</div>
                <div style={{fontSize:10,color:t.textDimmer,marginTop:2}}>replaces NHIF</div>
              </div>
              <div style={{padding:"0.6rem 0.8rem",background:`${t.red}10`,border:`1px solid ${t.red}30`,borderRadius:5}}>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.red,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3}}>PAYE</div>
                <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>&gt; KES 24k</div>
                <div style={{fontSize:10,color:t.textDimmer,marginTop:2}}>progressive bands</div>
              </div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem",marginTop:"0.25rem"}}>
            <Field t={t} label="M-Pesa Number" value={form.mpesa||""} onChange={F("mpesa")} placeholder="07XX XXX XXX"/>
            <Field t={t} label="Bank Name" value={form.bankName||""} onChange={F("bankName")} placeholder="KCB, Equity, NCBA…"/>
            <Field t={t} label="Bank Account No." value={form.bankAccount||""} onChange={F("bankAccount")} placeholder="Account number"/>
          </div>
        </>}

        {/* ── CONTRACT TAB ── */}
        {profileTab==="contract" && <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
            <Field t={t} label="Contract Start" type="date" value={form.joinedAt||""} onChange={F("joinedAt")}/>
            <Field t={t} label="Contract End (blank = ongoing)" type="date" value={form.contractEnd||""} onChange={F("contractEnd")}/>
          </div>
          {form.contractEnd && new Date(form.contractEnd) < new Date() && (
            <div style={{padding:"0.5rem 0.8rem",background:`${t.red}10`,border:`1px solid ${t.red}30`,borderRadius:5,fontSize:13,color:t.red}}>
              ⚠ This contract has already expired ({form.contractEnd}). Consider firing or renewing.
            </div>
          )}
          {form.contractEnd && new Date(form.contractEnd) > new Date() && new Date(form.contractEnd) < new Date(Date.now() + 30*24*60*60*1000) && (
            <div style={{padding:"0.5rem 0.8rem",background:`${t.amber}10`,border:`1px solid ${t.amber}30`,borderRadius:5,fontSize:13,color:t.amber}}>
              ⚠ Contract expires in less than 30 days. Renew or discuss extension.
            </div>
          )}
          <Field t={t} label="Notes / Scope of Work" textarea value={form.notes||""} onChange={F("notes")} placeholder="Scope, project details, special terms, link to contract file…" rows={3}/>
          <div style={{padding:"0.65rem 0.85rem",background:t.surface2,border:`1px solid ${t.border}`,borderRadius:5,fontSize:15,color:t.textDim,lineHeight:1.7}}>
            💡 Keep signed contracts in <strong style={{color:t.text}}>Project Files</strong> under the relevant project for easy reference.
          </div>
        </>}

        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end",marginTop:"0.5rem",borderTop:`1px solid ${t.border}`,paddingTop:"0.75rem"}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={save} disabled={!form.name||!form.username}>{editing?"Save Changes":"Hire"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

// ─── MODULE: PAYROLL ─────────────────────────────────────────────────────────
const Payroll = ({ payroll, setPayroll, setExpenses, team, timeEntries=[], projects=[], t, onPreview }) => {
  const [modal,setModal]=useState(false); const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({recipient:"Amon Cheruiyot",role:"Owner / Lead Developer",period:"",gross:"",deductions:"0",net:"",method:"KCB Transfer",status:"Pending",paidAt:"",notes:""});
  const F=k=>v=>setForm(f=>({...f,[k]:v}));
  const totalPaid=payroll.filter(p=>p.status==="Paid").reduce((a,p)=>a+p.net,0);
  const totalPending=payroll.filter(p=>p.status==="Pending").reduce((a,p)=>a+p.net,0);
  const members=[...new Set(payroll.map(p=>p.recipient))];

  // Enrich a payslip record with actual hours breakdown from timeEntries
  const enrichPayslip = (p) => {
    const member = team.find(u => String(u.id)===String(p.recipientId) || u.name===p.recipient);
    const ratePerHour = Number(member?.ratePerHour || p.ratePerHour || 0);

    // Always compute time entry stats
    const userId = member?.id;
    const periodEntries = p.period ? timeEntries.filter(e =>
      (String(e.userId)===String(userId) || e.userName===p.recipient) &&
      (e.date||"").slice(0,7) === p.period
    ) : [];
    const totalHrs    = periodEntries.reduce((a,e) => a + Number(e.hours), 0);
    const billableHrs = periodEntries.filter(e=>e.billable===true||e.billable==="true").reduce((a,e) => a + Number(e.hours), 0);
    const unbilledHrs = periodEntries.filter(e=>(e.billable===true||e.billable==="true")&&(e.billed===false||e.billed==="false")).reduce((a,e) => a + Number(e.hours), 0);

    // Build auto breakdown from time entries
    let autoBreakdown = [];
    if (ratePerHour && periodEntries.length > 0) {
      const byProject = {};
      periodEntries.forEach(e => {
        const proj = projects.find(pr => String(pr.id)===String(e.projectId));
        const key  = String(e.projectId);
        if (!byProject[key]) byProject[key] = { projectName: proj?.name || "Unknown Project", hours: 0, ratePerHour, amount: 0, _auto: true };
        byProject[key].hours  = Math.round((byProject[key].hours + Number(e.hours)) * 100) / 100;
        byProject[key].amount = Math.round(byProject[key].hours * ratePerHour);
      });
      autoBreakdown = Object.values(byProject);
    }

    // Manually added rows = stored breakdown rows that are NOT auto (no matching project in time logs)
    // They are identified by _auto:false or simply rows that were manually typed in
    const storedBreakdown = Array.isArray(p.projectBreakdown) ? p.projectBreakdown : [];
    const manualRows = storedBreakdown.filter(r => !r._auto);

    // Merge: auto rows first, then manual additions on top
    const projectBreakdown = [...autoBreakdown, ...manualRows];

    const computedGross = projectBreakdown.reduce((a,b) => a + Number(b.amount||0), 0);
    const gross = computedGross || Number(p.gross) || 0;
    const rawDeductions = Number(p.deductions||0);

    // Deductions: stored manual deductions take priority, else auto-calculate
    const storedDeductions = Array.isArray(p.deductionsList) && p.deductionsList.length > 0
      ? p.deductionsList
      : null;

    let deductionsList = storedDeductions || [];
    if (!storedDeductions) {
      if (member?.contractType==="employee") {
        const nssf = 420;
        const shif = Math.round(gross * 0.0275);
        const paye = gross > 24000 ? Math.round((gross - 24000) * 0.1) : 0;
        if (nssf) deductionsList.push({ label:"NSSF (Pension)",             amount: nssf });
        if (shif) deductionsList.push({ label:"SHA/SHIF (Health Insurance)", amount: shif });
        if (paye) deductionsList.push({ label:"PAYE (Income Tax)",           amount: paye });
      } else if (member?.withholdingTax && gross > 24000) {
        deductionsList.push({ label:"Withholding Tax (5% — WHT, remit to KRA)", amount: Math.round(gross * 0.05) });
      } else if (rawDeductions > 0) {
        deductionsList.push({ label:"Deductions", amount: rawDeductions });
      }
    }

    const totalDeductions = deductionsList.reduce((a,d) => a + d.amount, 0);

    return {
      ...p,
      ratePerHour,
      projectBreakdown,
      gross: computedGross || p.gross,
      deductions: totalDeductions || rawDeductions,
      deductionsList,
      net: gross - totalDeductions,
      totalHrs, billableHrs, unbilledHrs,
    };
  };
  //const totalPaid=payroll.filter(p=>p.status==="Paid").reduce((a,p)=>a+p.net,0);
  //const totalPending=payroll.filter(p=>p.status==="Pending").reduce((a,p)=>a+p.net,0);
  //const members=[...new Set(payroll.map(p=>p.recipient))];
  const normalizePayroll = (arr) => arr.map(p => ({
    ...p,
    // recipient from API is a User object {id,name,role} — flatten
    recipient:     (p.recipient && typeof p.recipient === 'object') ? p.recipient.name : (p.recipient || ''),
    recipientId:   (p.recipient && typeof p.recipient === 'object') ? p.recipient.id   : (p.recipientId || ''),
    recipientRole: (p.recipient && typeof p.recipient === 'object') ? (p.recipient.role||'').toLowerCase() : (p.recipientRole || p.role || ''),
    // API returns UPPERCASE status — normalize for UI comparisons
    status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Pending',
  }));
  // ── Enhanced modal state ──────────────────────────────────────────────────────
  const [modalTab, setModalTab] = useState("basic"); // "basic" | "earnings" | "deductions" | "payment"
  // Earnings rows (manual project breakdown)
  const [earningRows, setEarningRows] = useState([]); // [{id, description, hours, rate, amount}]
  const [earningDraft, setEarningDraft] = useState({ description:"", hours:"", rate:"", amount:"" });
  // Deduction rows
  const [deductionRows, setDeductionRows] = useState([]); // [{id, label, amount}]
  const [deductionDraft, setDeductionDraft] = useState({ label:"", amount:"" });

  const recalcTotals = (eRows, dRows) => {
    const gross = eRows.reduce((a,r)=>a+Number(r.amount||0),0);
    const deductions = dRows.reduce((a,r)=>a+Number(r.amount||0),0);
    const net = gross - deductions;
    setForm(f=>({...f, gross:String(gross), deductions:String(deductions), net:String(net)}));
  };

  const addEarningRow = () => {
    if (!earningDraft.description && !earningDraft.amount) return;
    const hrs = Number(earningDraft.hours||0);
    const rate = Number(earningDraft.rate||0);
    const amount = Number(earningDraft.amount||0) || (hrs * rate) || 0;
    const row = { id: Date.now(), description: earningDraft.description||"Compensation", hours: hrs||null, rate: rate||null, amount, _auto: false };
    const next = [...earningRows, row];
    setEarningRows(next);
    setEarningDraft({ description:"", hours:"", rate:"", amount:"" });
    recalcTotals(next, deductionRows);
  };
  const removeEarningRow = (id) => {
    const next = earningRows.filter(r=>r.id!==id);
    setEarningRows(next);
    recalcTotals(next, deductionRows);
  };

  const addDeductionRow = () => {
    if (!deductionDraft.label || !deductionDraft.amount) return;
    const row = { id: Date.now(), label: deductionDraft.label, amount: Number(deductionDraft.amount)||0 };
    const next = [...deductionRows, row];
    setDeductionRows(next);
    setDeductionDraft({ label:"", amount:"" });
    recalcTotals(earningRows, next);
  };
  const removeDeductionRow = (id) => {
    const next = deductionRows.filter(r=>r.id!==id);
    setDeductionRows(next);
    recalcTotals(earningRows, next);
  };

  // Auto-suggest deductions based on selected team member
  const autoFillDeductions = (member) => {
    const gross = earningRows.reduce((a,r)=>a+Number(r.amount||0), Number(form.gross||0));
    const rows = [];
    if (!member) return;
    if (member.contractType==="employee") {
      rows.push({ id: Date.now()+1, label:"NSSF (Pension)", amount:420 });
      const shif = Math.round(gross*0.0275);
      if (shif) rows.push({ id: Date.now()+2, label:"SHA/SHIF (Health Insurance)", amount:shif });
      const paye = gross>24000 ? Math.round((gross-24000)*0.1) : 0;
      if (paye) rows.push({ id: Date.now()+3, label:"PAYE (Income Tax)", amount:paye });
    } else if (member.withholdingTax && gross>24000) {
      rows.push({ id: Date.now()+1, label:"Withholding Tax (5% WHT — remit to KRA)", amount:Math.round(gross*0.05) });
    }
    setDeductionRows(rows);
    recalcTotals(earningRows, rows);
  };

  // Pre-fill earnings from logged time entries when period changes
  const autoFillFromHours = (recipientId, period) => {
    const member = (team||[]).find(u=>String(u.id)===String(recipientId));
    if (!member || !period) return;
    const rate = Number(member.ratePerHour||0);
    if (!rate) return;
    const entries = timeEntries.filter(e=>(String(e.userId)===String(member.id)||e.userName===member.name)&&(e.date||"").slice(0,7)===period);
    if (entries.length===0) return;
    const byProject = {};
    entries.forEach(e=>{
      const proj = projects.find(pr=>String(pr.id)===String(e.projectId));
      const key = String(e.projectId);
      if (!byProject[key]) byProject[key]={ description: proj?.name||"Project", hours:0, rate, amount:0 };
      byProject[key].hours = Math.round((byProject[key].hours+Number(e.hours))*100)/100;
      byProject[key].amount = Math.round(byProject[key].hours*rate);
    });
    const rows = Object.values(byProject).map((r,i)=>({id:Date.now()+i, ...r}));
    setEarningRows(rows);
    recalcTotals(rows, deductionRows);
  };

  const openAdd=()=>{
    setEditing(null);
    const now=new Date();
    const period=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    setForm({recipient:"",recipientId:"",role:"",period,gross:"",deductions:"0",net:"",method:"KCB Transfer",status:"Pending",paidAt:"",notes:"",ratePerHour:""});
    setEarningRows([]);
    setDeductionRows([]);
    setEarningDraft({ description:"", hours:"", rate:"", amount:"" });
    setDeductionDraft({ label:"", amount:"" });
    setModalTab("basic");
    setModal(true);
  };
  const openEdit=p=>{
    setEditing(p.id);
    setForm({...p,gross:String(p.gross),deductions:String(p.deductions),net:String(p.net),paidAt:toDateInput(p.paidAt)});
    const enriched = enrichPayslip(p);
    // Auto rows (from time logs) always first, manual additions on top
    const allRows = enriched.projectBreakdown || [];
    setEarningRows(allRows.map((r,i)=>({
      id: i+1,
      description: r.projectName||r.description||"",
      hours: r.hours||null,
      rate: r.ratePerHour||null,
      amount: r.amount||0,
      _auto: r._auto !== false, // preserve flag; manual rows have _auto:false
    })));
    setDeductionRows((enriched.deductionsList||[]).map((r,i)=>({id:i+1, label:r.label, amount:r.amount})));
    setEarningDraft({ description:"", hours:"", rate:"", amount:"" });
    setDeductionDraft({ label:"", amount:"" });
    setModalTab("basic");
    setModal(true);
  };
  const save = async () => {
    const gross=Number(form.gross)||0;
    const deductions=Number(form.deductions)||0;
    const net=gross-deductions;
    // Only save manual rows to DB — auto rows are always recalculated from time entries
    // Tag manual rows with _auto:false so enrichPayslip knows to keep them
    const manualBreakdown = earningRows
      .filter(r => r._auto === false)
      .map(r=>({ projectName:r.description, hours:r.hours||0, ratePerHour:r.rate||0, amount:r.amount, _auto:false }));
    const deductionsList  = deductionRows.map(r=>({ label:r.label, amount:r.amount }));
    const entry={...form, gross, deductions, net, id:editing||Date.now(), projectBreakdown: manualBreakdown, deductionsList };
    if(editing){
      setPayroll(ps=>ps.map(p=>p.id===editing?entry:p));
      try { await apiFetch(`/api/v1/finance/payroll/${editing}`, { method:'PATCH', body:JSON.stringify({
        status: entry.status==='Paid'?'PAID':'PENDING',
        paidAt: entry.paidAt||null, notes: entry.notes||null, method: entry.method,
        gross: Number(entry.gross)||0,
        deductions: Number(entry.deductions)||0,
        net: Number(entry.net)||0,
        projectBreakdown: manualBreakdown, deductionsList,
      }) }); } catch{}
    } else {
      setPayroll(ps=>[...ps,entry]);
      setExpenses(es=>[...es,{id:Date.now()+1,type:'expense',category:'Payroll',description:`Payroll — ${entry.recipient} ${entry.period}`,amount:entry.net,currency:'KES',date:entry.paidAt||todayStr()}]);
      try {
        const res = await apiFetch('/api/v1/finance/payroll', { method:'POST', body:JSON.stringify({
          recipientId: entry.recipientId||undefined,
          period: entry.period, gross, deductions, net,
          method: entry.method,
          status: entry.status==='Paid'?'PAID':'PENDING',
          paidAt: entry.paidAt||null, notes: entry.notes||null,
          projectBreakdown: manualBreakdown, deductionsList,
        }) });
        if(res.entry?.id) setPayroll(ps=>ps.map(p=>p.id===entry.id?{...p,...normalizePayroll([res.entry])[0]}:p));
      } catch{}
    }
    setModal(false);
  };  const markPaid=id=>{
    const p=payroll.find(x=>x.id===id);
    setPayroll(ps=>ps.map(x=>x.id===id?{...x,status:"Paid",paidAt:todayStr()}:x));
    setExpenses(es=>[...es,{id:Date.now(),category:(p.recipientRole||p.role||'').toLowerCase().includes("owner")||(p.recipientRole||p.role||'').toLowerCase().includes("founder")?"Owner Draw":"Salary",description:`${p.recipient} — ${p.period} Payroll`,amount:p.net,currency:"KES",date:todayStr(),type:"expense",deductible:true}]);
  };
  const periods=[...new Set(payroll.map(p=>p.period))].sort().reverse();
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader eyebrow="People & Pay" title="Payroll" sub={`${payroll.length} records · ${members.length} recipients`} t={t} action={<Btn icon="plus" onClick={openAdd} t={t}>Add Payroll</Btn>}/>
      {/* FIXED: Payroll stat cards — Cormorant Garamond, no duplicate letterSpacing */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:"0.75rem"}}>
        {[
          {l:"Total Paid Out",v:fmtKES(totalPaid),c:t.teal,i:"finance"},
          {l:"Pending",v:fmtKES(totalPending),c:t.amber,i:"clock"},
          {l:"Recipients",v:String(members.length),c:t.blue,i:"clients"},
          {l:"This Month",v:fmtKES(payroll.filter(p=>{const now=new Date();return p.period===`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;}).reduce((a,p)=>a+p.net,0)),c:t.textDim,i:"calendar"},
        ].map(s=>(
          <Card key={s.l} t={t} style={{padding:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",color:t.textDim,textTransform:"uppercase"}}>{s.l}</span>
              <I n={s.i} s={12} c={s.c}/>
            </div>
            <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:s.c}}>{s.v}</div>
          </Card>
        ))}
      </div>
      <Card t={t} style={{border:`1px solid ${t.amber}35`,background:`${t.amber}07`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <I n="alert" s={16} c={t.amber}/>
          <div style={{flex:1,fontSize:15,color:t.textDim,fontWeight:400}}>
            As a sole proprietor, your pay is an <strong style={{color:t.text}}>owner's draw</strong> — not a formal salary. All payroll entries are automatically logged to Finance as deductible expenses. Developer pay counts as a business expense for KRA purposes.
          </div>
        </div>
      </Card>
      {periods.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No payroll records yet.</div>}
      {periods.map(period=>{
        const entries=payroll.filter(p=>p.period===period);
        const periodTotal=entries.reduce((a,p)=>a+p.net,0);
        return (<div key={period}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.65rem"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontFamily:"'Fira Code',monospace",fontWeight:600,fontSize:15,color:t.text}}>{period}</span>
              <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{entries.length} recipient{entries.length!==1?"s":""}</span>
            </div>
            {/* FIXED: period total — Space Grotesk 700 */}
            <span style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:20,fontStyle:"italic",color:t.teal}}>{fmtKES(periodTotal)}</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"0.5rem",marginBottom:"1rem"}}>
            {entries.map(p=>(
              <Card key={p.id} t={t}>
                <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
                  <Avatar name={p.recipient} size={36} t={t}/>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                      <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{p.recipient}</span>
                      <Badge label={p.status} t={t}/>
                      <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:t.surface3,color:t.textDimmer,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",display:"inline-block"}}>
                        {(p.recipientRole||p.role||'').replace("Owner / Lead Developer","Owner · Dev").replace("Founder & Lead Developer","Founder · Dev")}
                      </span>
                    </div>
                    <div style={{fontSize:11,color:t.textDim,fontWeight:400}}>Gross: {fmtKES(p.gross)} · Deductions: {fmtKES(p.deductions)} · Net: <strong style={{color:t.text}}>{fmtKES(p.net)}</strong></div>
                    <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>via {p.method}{p.paidAt?` · Paid ${p.paidAt ? new Date(p.paidAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—"}`:""}</div>
                    {p.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:2,fontStyle:"italic",fontWeight:400}}>{p.notes}</div>}
                  </div>
                  <div style={{display:"flex",gap:3,flexShrink:0,flexWrap:"wrap"}}>
                    <Btn variant="secondary" size="sm" t={t} onClick={()=>{ const enriched=enrichPayslip(p); onPreview({html:buildPayslipHTML(enriched),title:`Payslip · ${p.recipient} · ${p.period}`, isPayslip:true, payslipId:p.id, initialAdminSig:p.adminSignature||'', initialAdminSignedAt:p.adminSignedAt||''}); }} icon="print">Payslip</Btn>
                    {p.status==="Pending"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>markPaid(p.id)} icon="check">Mark Paid</Btn>}
                    <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(p)} icon="edit">{""}</Btn>
                    <Btn variant="ghost" size="sm" t={t} onClick={()=>setPayroll(ps=>ps.filter(x=>x.id!==p.id))} icon="trash">{""}</Btn>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>);
      })}
      <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Payroll Record":"New Payroll Record"} t={t} wide>
        {/* ── Tab bar ── */}
        <div style={{display:"flex",gap:4,borderBottom:`1px solid ${t.border}`,paddingBottom:"0.75rem",marginBottom:"0.25rem",overflowX:"auto"}}>
          {[
            {id:"basic",      label:"Basic Info",   icon:"clients"},
            {id:"earnings",   label:"Earnings",     icon:"finance"},
            {id:"deductions", label:"Deductions",   icon:"receipt"},
            {id:"payment",    label:"Payment",      icon:"check"},
          ].map(tab=>(
            <button key={tab.id} onClick={()=>setModalTab(tab.id)} style={{
              display:"flex",alignItems:"center",gap:5,
              padding:"6px 14px",borderRadius:5,cursor:"pointer",whiteSpace:"nowrap",
              fontFamily:"'Space Grotesk',sans-serif",fontWeight:modalTab===tab.id?600:400,fontSize:12,
              background:modalTab===tab.id?t.teal:"transparent",
              color:modalTab===tab.id?(t.id==="dark"?"#050507":"#fff"):t.textDim,
              border:modalTab===tab.id?"none":`1px solid ${t.border}`,
              transition:"all 0.15s",
            }}>
              <I n={tab.icon} s={11} c="currentColor"/>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══ BASIC INFO TAB ══ */}
        {modalTab==="basic" && (
          <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
              <div style={{gridColumn:"1/-1"}}>
                <Field t={t} label="Recipient"
                  options={[{v:"",l:"— Select team member —"},...(team||[]).map(u=>({v:String(u.id),l:u.name+(u.title?` · ${u.title}`:"")})),{v:"__external__",l:"Other (External Contractor)"}]}
                  value={form.recipientId||"__external__"}
                  onChange={v=>{
                    const u=(team||[]).find(m=>String(m.id)===v);
                    if(u){
                      F("recipientId")(v);
                      F("recipient")(u.name);
                      F("role")(u.title||u.role||"");
                      F("ratePerHour")(String(u.ratePerHour||""));
                      // Auto-fill hours if period is set
                      if(form.period) autoFillFromHours(v, form.period);
                      autoFillDeductions(u);
                    } else {
                      F("recipientId")(""); F("recipient")(""); F("ratePerHour")("");
                    }
                  }}
                />
              </div>
              {!form.recipientId&&<Field t={t} label="Recipient Name (External)" value={form.recipient||""} onChange={F("recipient")} placeholder="Jane Doe — Contractor"/>}
              <Field t={t} label="Role / Title" value={form.role||""} onChange={F("role")} placeholder="Developer, Designer…"/>
              <Field t={t} label="Period (YYYY-MM)" value={form.period||""} onChange={v=>{
                F("period")(v);
                if(form.recipientId && v.length===7) autoFillFromHours(form.recipientId, v);
              }} placeholder="2026-01"/>
            </div>
            <Field t={t} label="Internal Notes" value={form.notes||""} onChange={F("notes")} placeholder="Owner draw, bonus, retainer, special circumstances…" textarea/>
            {/* Summary preview */}
            <div style={{padding:"0.85rem",background:t.surface2,borderRadius:6,border:`1px solid ${t.border}`}}>
              <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.18em",color:t.textDim,marginBottom:8}}>Live Summary</div>
              <div style={{display:"flex",gap:"1.5rem",flexWrap:"wrap"}}>
                {[
                  {l:"Earnings",   v:Number(form.gross||0).toLocaleString(),   c:t.teal},
                  {l:"Deductions", v:Number(form.deductions||0).toLocaleString(), c:t.red},
                  {l:"Net Pay",    v:Number(form.net||0).toLocaleString(),      c:t.text},
                ].map(s=>(
                  <div key={s.l}>
                    <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",marginBottom:2}}>{s.l}</div>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:24,fontStyle:"italic",color:s.c}}>KES {s.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
              <Btn variant="secondary" t={t} onClick={()=>setModalTab("earnings")} icon="finance">Set Up Earnings →</Btn>
            </div>
          </div>
        )}

        {/* ══ EARNINGS TAB ══ */}
        {modalTab==="earnings" && (
          <div style={{display:"flex",flexDirection:"column",gap:"0.75rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,color:t.textDim}}>Add earnings line items. These appear as the <strong style={{color:t.text}}>Earnings table</strong> on the payslip.</div>
              {form.recipientId && form.period && (
                <Btn variant="secondary" size="sm" t={t} icon="clock" onClick={()=>autoFillFromHours(form.recipientId,form.period)}>Auto-fill from hours</Btn>
              )}
            </div>

            {/* Existing rows */}
            {earningRows.length>0 && (
              <div style={{border:`1px solid ${t.border}`,borderRadius:7,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 90px 32px",gap:0,padding:"7px 10px",background:t.surface2,borderBottom:`1px solid ${t.border}`}}>
                  {["Description","Hours","Rate","Amount",""].map(h=>(
                    <span key={h} style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.14em",color:t.textDim}}>{h}</span>
                  ))}
                </div>
                {earningRows.map((row,i)=>(
                  <div key={row.id} style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 90px 32px",gap:0,padding:"8px 10px",borderBottom:i<earningRows.length-1?`1px solid ${t.border}`:"none",alignItems:"center"}}>
                    <span style={{fontSize:14,color:t.text,fontWeight:500}}>{row.description}</span>
                    <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDim}}>{row.hours!=null?`${row.hours}h`:"—"}</span>
                    <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDim}}>{row.rate?`${Number(row.rate).toLocaleString()}/h`:"—"}</span>
                    <span style={{fontSize:12,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,color:t.teal}}>KES {Number(row.amount).toLocaleString()}</span>
                    <button onClick={()=>removeEarningRow(row.id)} style={{background:"none",border:"none",cursor:"pointer",color:t.red,padding:2,display:"flex",alignItems:"center"}}><I n="x" s={12} c={t.red}/></button>
                  </div>
                ))}
                <div style={{padding:"8px 10px",borderTop:`1px solid ${t.border}`,background:t.surface2,display:"flex",justifyContent:"flex-end",gap:16}}>
                  <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDim}}>Gross Total</span>
                  <span style={{fontSize:13,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,color:t.teal}}>KES {earningRows.reduce((a,r)=>a+Number(r.amount),0).toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Add new row */}
            <div style={{padding:"0.85rem",background:t.surface2,borderRadius:7,border:`1px dashed ${t.border}`}}>
              <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.15em",color:t.textDimmer,marginBottom:"0.6rem"}}>Add Line Item</div>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:"0.5rem",marginBottom:"0.5rem"}}>
                <input value={earningDraft.description} onChange={e=>setEarningDraft(d=>({...d,description:e.target.value}))}
                  placeholder="e.g. Q-Trader Project, Bonus…"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
                <input value={earningDraft.hours} onChange={e=>{
                  const h=e.target.value;
                  const amt = h&&earningDraft.rate ? String(Math.round(Number(h)*Number(earningDraft.rate))) : earningDraft.amount;
                  setEarningDraft(d=>({...d,hours:h,amount:amt}));
                }} placeholder="Hours" type="number"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
                <input value={earningDraft.rate} onChange={e=>{
                  const r=e.target.value;
                  const amt = r&&earningDraft.hours ? String(Math.round(Number(earningDraft.hours)*Number(r))) : earningDraft.amount;
                  setEarningDraft(d=>({...d,rate:r,amount:amt}));
                }} placeholder="Rate/hr" type="number"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
                <input value={earningDraft.amount} onChange={e=>setEarningDraft(d=>({...d,amount:e.target.value}))}
                  placeholder="Amount (KES)" type="number"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
              </div>
              <Btn t={t} size="sm" icon="plus" onClick={addEarningRow} disabled={!earningDraft.description&&!earningDraft.amount}>Add Row</Btn>
            </div>

            {earningRows.length===0&&<div style={{textAlign:"center",fontSize:14,color:t.textDimmer,fontStyle:"italic",padding:"0.5rem"}}>No earnings yet. Add rows above or use auto-fill from logged hours.</div>}

            <div style={{display:"flex",gap:6,justifyContent:"space-between",alignItems:"center",marginTop:4}}>
              <button onClick={()=>setModalTab("basic")} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:t.textDim,fontFamily:"'Space Grotesk',sans-serif"}}>← Back</button>
              <Btn variant="secondary" t={t} onClick={()=>setModalTab("deductions")}>Set Up Deductions →</Btn>
            </div>
          </div>
        )}

        {/* ══ DEDUCTIONS TAB ══ */}
        {modalTab==="deductions" && (
          <div style={{display:"flex",flexDirection:"column",gap:"0.75rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:14,color:t.textDim}}>Add deductions — taxes, NSSF, penalties, advances, etc.</div>
              {form.recipientId && (
                <Btn variant="secondary" size="sm" t={t} icon="alert" onClick={()=>autoFillDeductions((team||[]).find(u=>String(u.id)===String(form.recipientId)))}>
                  Auto-fill KRA
                </Btn>
              )}
            </div>

            {deductionRows.length>0 && (
              <div style={{border:`1px solid ${t.border}`,borderRadius:7,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 100px 32px",gap:0,padding:"7px 10px",background:t.surface2,borderBottom:`1px solid ${t.border}`}}>
                  {["Description","Amount",""].map(h=>(
                    <span key={h} style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.14em",color:t.textDim}}>{h}</span>
                  ))}
                </div>
                {deductionRows.map((row,i)=>(
                  <div key={row.id} style={{display:"grid",gridTemplateColumns:"1fr 100px 32px",gap:0,padding:"8px 10px",borderBottom:i<deductionRows.length-1?`1px solid ${t.border}`:"none",alignItems:"center"}}>
                    <span style={{fontSize:14,color:t.text,fontWeight:500}}>{row.label}</span>
                    <span style={{fontSize:12,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,color:t.red}}>- KES {Number(row.amount).toLocaleString()}</span>
                    <button onClick={()=>removeDeductionRow(row.id)} style={{background:"none",border:"none",cursor:"pointer",color:t.red,padding:2,display:"flex",alignItems:"center"}}><I n="x" s={12} c={t.red}/></button>
                  </div>
                ))}
                <div style={{padding:"8px 10px",borderTop:`1px solid ${t.border}`,background:t.surface2,display:"flex",justifyContent:"flex-end",gap:16}}>
                  <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDim}}>Total Deductions</span>
                  <span style={{fontSize:13,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,color:t.red}}>KES {deductionRows.reduce((a,r)=>a+Number(r.amount),0).toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Preset suggestions */}
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {[
                {label:"NSSF (Pension)", amount:420},
                {label:"SHA/SHIF (2.75%)", amount:Math.round(Number(form.gross||0)*0.0275)},
                {label:"WHT 5%", amount:Math.round(Number(form.gross||0)*0.05)},
                {label:"PAYE", amount:Number(form.gross||0)>24000?Math.round((Number(form.gross||0)-24000)*0.1):0},
                {label:"Advance Recovery", amount:null},
                {label:"Late Penalty", amount:null},
              ].filter(s=>s.amount!==0).map(s=>(
                <button key={s.label} onClick={()=>setDeductionDraft(d=>({...d,label:s.label,amount:s.amount!=null?String(s.amount):""}))}
                  style={{padding:"3px 9px",borderRadius:4,fontSize:10,fontFamily:"'Fira Code',monospace",cursor:"pointer",background:`${t.amber}10`,color:t.amber,border:`1px solid ${t.amber}30`,whiteSpace:"nowrap"}}>
                  + {s.label}{s.amount?` (${s.amount.toLocaleString()})`:""}</button>
              ))}
            </div>

            <div style={{padding:"0.85rem",background:t.surface2,borderRadius:7,border:`1px dashed ${t.border}`}}>
              <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.15em",color:t.textDimmer,marginBottom:"0.6rem"}}>Add Deduction</div>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:"0.5rem",marginBottom:"0.5rem"}}>
                <input value={deductionDraft.label} onChange={e=>setDeductionDraft(d=>({...d,label:e.target.value}))}
                  placeholder="e.g. NSSF, PAYE, WHT, Advance Recovery…"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
                <input value={deductionDraft.amount} onChange={e=>setDeductionDraft(d=>({...d,amount:e.target.value}))}
                  placeholder="Amount (KES)" type="number"
                  style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:5,color:t.text,fontSize:12,padding:"7px 10px",fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
              </div>
              <Btn t={t} size="sm" icon="plus" onClick={addDeductionRow} disabled={!deductionDraft.label||!deductionDraft.amount}>Add Deduction</Btn>
            </div>

            {deductionRows.length===0&&<div style={{textAlign:"center",fontSize:14,color:t.textDimmer,fontStyle:"italic",padding:"0.5rem"}}>No deductions. Use presets above or click "Auto-fill KRA".</div>}

            {/* Net pay preview */}
            <div style={{padding:"0.85rem",background:`${t.teal}08`,border:`1px solid ${t.teal}25`,borderRadius:7,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div style={{display:"flex",gap:"1.5rem"}}>
                <div><div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",marginBottom:2}}>Gross</div><div style={{fontSize:14,fontWeight:700,fontFamily:"'Space Grotesk',sans-serif",color:t.teal}}>KES {Number(form.gross||0).toLocaleString()}</div></div>
                <div style={{fontSize:18,color:t.textDimmer,alignSelf:"flex-end",paddingBottom:2}}>−</div>
                <div><div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",marginBottom:2}}>Deductions</div><div style={{fontSize:14,fontWeight:700,fontFamily:"'Space Grotesk',sans-serif",color:t.red}}>KES {Number(form.deductions||0).toLocaleString()}</div></div>
                <div style={{fontSize:18,color:t.textDimmer,alignSelf:"flex-end",paddingBottom:2}}>=</div>
                <div><div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",marginBottom:2}}>Net Pay</div><div style={{fontSize:16,fontWeight:700,fontFamily:"'Space Grotesk',sans-serif",color:t.text}}>KES {Number(form.net||0).toLocaleString()}</div></div>
              </div>
            </div>

            <div style={{display:"flex",gap:6,justifyContent:"space-between",alignItems:"center",marginTop:4}}>
              <button onClick={()=>setModalTab("earnings")} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:t.textDim,fontFamily:"'Space Grotesk',sans-serif"}}>← Back</button>
              <Btn variant="secondary" t={t} onClick={()=>setModalTab("payment")}>Payment Details →</Btn>
            </div>
          </div>
        )}

        {/* ══ PAYMENT TAB ══ */}
        {modalTab==="payment" && (
          <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
              <Field t={t} label="Payment Method" options={["KCB Transfer","M-Pesa","Cash","Equity Bank","Payoneer","Wise","Other"]} value={form.method||"KCB Transfer"} onChange={F("method")}/>
              <Field t={t} label="Status" options={["Pending","Paid"]} value={form.status||"Pending"} onChange={F("status")}/>
              {form.status==="Paid"&&<Field t={t} label="Payment Date" type="date" value={form.paidAt||""} onChange={F("paidAt")}/>}
              <Field t={t} label="Rate Per Hour (KES)" type="number" value={form.ratePerHour||""} onChange={F("ratePerHour")} placeholder="Override hourly rate on payslip"/>
            </div>

            {/* Final summary card */}
            <div style={{padding:"1rem",background:t.surface2,borderRadius:8,border:`1px solid ${t.border}`}}>
              <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.18em",color:t.textDim,marginBottom:"0.75rem"}}>Payslip Summary</div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{fontSize:14,color:t.textDim}}>Recipient</span>
                <span style={{fontSize:14,color:t.text,fontWeight:600}}>{form.recipient||"—"}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{fontSize:14,color:t.textDim}}>Period</span>
                <span style={{fontSize:12,fontFamily:"'Fira Code',monospace",color:t.text}}>{form.period||"—"}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{fontSize:14,color:t.textDim}}>Earnings ({earningRows.length} line{earningRows.length!==1?"s":""})</span>
                <span style={{fontSize:12,fontFamily:"'Fira Code',monospace",color:t.teal}}>KES {Number(form.gross||0).toLocaleString()}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{fontSize:14,color:t.textDim}}>Deductions ({deductionRows.length} line{deductionRows.length!==1?"s":""})</span>
                <span style={{fontSize:12,fontFamily:"'Fira Code',monospace",color:t.red}}>- KES {Number(form.deductions||0).toLocaleString()}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"10px 12px",background:t.surface3,borderRadius:5,marginTop:8}}>
                <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>Net Pay</span>
                <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:14,color:t.teal}}>KES {Number(form.net||0).toLocaleString()}</span>
              </div>
            </div>

            <div style={{padding:"0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`,fontSize:15,color:t.textDim}}>
              <I n="alert" s={11} c={t.teal}/>{" "}Saving a <strong style={{color:t.text}}>Paid</strong> record automatically logs it as a deductible expense in Finance.
            </div>

            <div style={{display:"flex",gap:6,justifyContent:"space-between",alignItems:"center",marginTop:4}}>
              <button onClick={()=>setModalTab("deductions")} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:t.textDim,fontFamily:"'Space Grotesk',sans-serif"}}>← Back</button>
              <div style={{display:"flex",gap:6}}>
                <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
                <Btn t={t} onClick={save} disabled={!form.recipient||(!form.gross&&earningRows.length===0)} icon="check">{editing?"Save Changes":"Create Payroll"}</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── CONTRACT PRINT FUNCTION ──────────────────────────────────────────────────
const buildContractHTML = (contract, client) => {
  const startStr = contract.startDate ? new Date(contract.startDate).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}) : "";
  const endStr   = contract.endDate   ? new Date(contract.endDate).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})   : "Ongoing";
  const sCol = contract.status==="Signed"||contract.status==="SIGNED"?  "#00c9a7" : contract.status==="Expired"? "#c0394e" : "#f5a623";
  const sBg  = contract.status==="Signed"||contract.status==="SIGNED"?  "#00c9a720": contract.status==="Expired"? "#c0394e20": "#f5a62320";
  const sBdr = contract.status==="Signed"||contract.status==="SIGNED"?  "#00c9a740": contract.status==="Expired"? "#c0394e40": "#f5a62340";
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:1.8rem;gap:2rem;align-items:flex-start;flex-wrap:wrap">
      <div>
        <div class="label">Between</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:15px;color:#111;margin-bottom:3px">Qire Agency</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#555;line-height:1.7"> Eldoret, Kenya<br/>qireagency@gmail.com</div>
        <div style="margin-top:10px;font-family:'Fira Code',monospace;font-size:9px;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px">And</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:15px;color:#111;margin-bottom:3px">${client?.name||"Client"}</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#555;line-height:1.7">${client?.company||""}<br/>${client?.email||""}</div>
      </div>
      <div style="text-align:right">
        <div style="font-family:Syne,sans-serif;font-weight:800;font-size:14px;color:#050506;letter-spacing:-.01em;margin-bottom:8px;text-transform:uppercase">${contract.type||"Service Agreement"}</div>
        <div style="font-family:'Fira Code',monospace;font-size:10px;color:#888;line-height:2.2">
          Value: ${contract.currency||"USD"} ${Number(contract.value||0).toLocaleString()}<br/>
          Start: ${startStr}<br/>
          End: ${endStr}<br/>
          Terms: ${contract.paymentTerms||""}
        </div>
        <div style="margin-top:10px;display:inline-block;padding:4px 12px;border-radius:3px;background:${sBg};color:${sCol};font-family:'Fira Code',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.12em;border:1px solid ${sBdr}">${contract.status||"Draft"}</div>
      </div>
    </div>
    <div class="doc-divider"></div>
    <div style="margin-bottom:1.6rem">
      <div class="label">Agreement Title</div>
      <div style="font-family:Syne,sans-serif;font-weight:700;font-size:20px;color:#111;letter-spacing:-.01em;line-height:1.2">${contract.title||""}</div>
    </div>
    <div class="doc-divider"></div>
    <div style="font-family:'Space Grotesk',sans-serif;font-size:16px;color:#333;line-height:2;white-space:pre-wrap;margin-bottom:2rem">${contract.body||""}</div>
    <div class="doc-divider"></div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:2.5rem;margin-top:2rem">
      <div>
        <div class="label">Qire Agency — Authorized Signature</div>
        <div class="sig-box">__ADMIN_SIG__</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#333">Amon Cheruiyot<br/><span style="color:#888;font-size:13px">Founder · Qire Agency</span></div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;color:#aaa;margin-top:4px">Date: ${SIG_DATE_ADMIN}</div>
      </div>
      <div>
        <div class="label">${client?.name||"Client"} — Signature</div>
        <div class="sig-box">__CLIENT_SIG__</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#333">${client?.name||""}<br/><span style="color:#888;font-size:13px">${client?.company||""}</span></div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;color:#aaa;margin-top:4px">Date: ${SIG_DATE_CLIENT}</div>
      </div>
    </div>
  `;
};

// ─── MODULE: CONTRACTS ────────────────────────────────────────────────────────
const Contracts = ({ contracts, setContracts, clients, projects, t, onPreview }) => {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const defaultBody = "This agreement is entered into between Qire Agency and the Client named above.\n\nScope of Work:\n[Describe deliverables clearly]\n\nPayment Terms:\n[e.g. 50% upfront, 50% on delivery]\n\nTimeline:\n[Estimated delivery date]\n\nIntellectual Property:\nUpon full payment, all deliverables become the property of the Client.\n\nConfidentiality:\nBoth parties agree to keep project details confidential.\n\nWarm regards,\nAmon Cheruiyot\nFounder · Qire Agency";
  const [form, setForm] = useState({
    clientId:"", projectId:"", title:"", type:"Service Agreement",
    value:"", currency:"USD", startDate:todayStr(), endDate:"",
    status:"Draft", paymentTerms:"50% upfront, 50% on delivery",
    body:defaultBody, notes:""
  });
  const F = k => v => setForm(f=>({...f,[k]:v}));

  const openAdd = () => { setEditing(null); setForm({clientId:clients[0]?.id||"",projectId:projects[0]?.id||"",title:"",type:"Service Agreement",value:"",currency:"USD",startDate:todayStr(),endDate:"",status:"Draft",paymentTerms:"50% upfront, 50% on delivery",body:defaultBody,notes:""}); setModal(true); };
  const openEdit = c => { setEditing(c.id); setForm({...c,value:String(c.value),startDate:toDateInput(c.startDate),endDate:toDateInput(c.endDate),signedAt:toDateInput(c.signedAt)}); setModal(true); };
  const save = async () => {
    const e={...form,clientId:form.clientId,projectId:form.projectId||null,value:Number(form.value)||0};
    if(editing) {
      setContracts(cs=>cs.map(c=>c.id===editing?{...c,...e}:c));
      try { await apiFetch(`/api/v1/contracts/${editing}`, { method:'PATCH', body:JSON.stringify({...e, status:(e.status||e.status).toUpperCase()}) }); } catch{}
    } else {
      const tmp={...e,id:Date.now()};
      setContracts(cs=>[...cs,tmp]);
      try {
        const res = await apiFetch('/api/v1/contracts', { method:'POST', body:JSON.stringify({...e, status:(e.status||'DRAFT').toUpperCase()}) });
        if(res.contract?.id) setContracts(cs=>cs.map(c=>c.id===tmp.id?{...c,...res.contract}:c));
      } catch{}
    }
    setModal(false);
  };
  const typeColors = {"Service Agreement":t.teal,"NDA":t.amber,"Retainer":t.blue,"Freelance":t.teal,"Other":t.textDim};
  const statusColors = {Draft:t.textDimmer,Sent:t.amber,Signed:t.teal,Expired:t.red};

  const active = contracts.filter(c=>c.status==="Signed").length;
  const totalValue = contracts.filter(c=>c.status!=="Expired").reduce((a,c)=>a+c.value,0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader eyebrow="Legal" title="Contracts" sub={`${contracts.length} contracts · ${active} signed · printable`} t={t}
        action={<Btn icon="plus" onClick={openAdd} t={t}>New Contract</Btn>}/>

      {/* FIXED: Contracts stat cards — Cormorant Garamond italic, single letterSpacing */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
        {[
          {l:"Total Value",  v:fmtUSD(totalValue), c:t.teal},
          {l:"Signed",       v:String(contracts.filter(c=>c.status==="Signed").length),  c:t.teal},
          {l:"Pending",      v:String(contracts.filter(c=>c.status==="Sent"||c.status==="Draft").length), c:t.amber},
          {l:"Expired",      v:String(contracts.filter(c=>c.status==="Expired").length), c:t.red},
        ].map(s=>(
          <Card key={s.l} t={t} style={{padding:"1rem",textAlign:"center"}}>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:s.c}}>{s.v}</div>
          </Card>
        ))}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
        {contracts.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No contracts yet.</div>}
        {contracts.map(c=>{
          const client = clients.find(x=>x.id===c.clientId);
          const project = projects.find(x=>x.id===c.projectId);
          const tc = typeColors[c.type]||t.textDim;
          const sc2 = statusColors[c.status]||t.textDim;
          return (
            <Card key={c.id} t={t}>
              <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
                <div style={{width:38,height:38,borderRadius:6,background:`${tc}18`,border:`1px solid ${tc}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <I n="contracts" s={16} c={tc}/>
                </div>
                <div style={{flex:1,minWidth:120}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                    <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{c.title}</span>
                    <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${sc2}18`,color:sc2,fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",border:`1px solid ${sc2}40`}}>{c.status}</span>
                    <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${tc}15`,color:tc,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase"}}>{c.type}</span>
                  </div>
                  <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>
                    {client?.name||"No client"}{project?` · ${project.name}`:""} · {c.startDate ? new Date(c.startDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'} → {c.endDate ? new Date(c.endDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : "Ongoing"}
                  </div>
                  {c.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:2,fontStyle:"italic",fontWeight:400}}>{c.notes}</div>}
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  {/* FIXED: contract value — Space Grotesk 700 instead of Syne */}
                  <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:24,fontStyle:"italic",color:t.teal}}>{c.currency} {Number(c.value).toLocaleString()}</div>
                  <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{c.paymentTerms}</div>
                </div>
                <div style={{display:"flex",gap:3,flexShrink:0,flexWrap:"wrap"}}>
                  <Btn variant="secondary" size="sm" t={t} onClick={()=>onPreview({
                    html: buildContractHTML(c, client),
                    title: `${c.title} · Qire Agency`,
                    isContract: true,
                    contractId: c.id,
                    initialAdminSig: c.adminSignature||'',
                    initialClientSig: c.clientSignature||'',
                    initialAdminSignedAt: c.adminSignedAt||'',
                    initialClientSignedAt: c.clientSignedAt||c.signedAt||'',
                  })} icon="print">Preview</Btn>
                  {c.status==="Draft"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>setContracts(cs=>cs.map(x=>x.id===c.id?{...x,status:"Sent"}:x))}>Mark Sent</Btn>}
                  {c.status==="Sent"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>setContracts(cs=>cs.map(x=>x.id===c.id?{...x,status:"Signed"}:x))} icon="check">Sign</Btn>}
                  <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(c)} icon="edit">{""}</Btn>
                  <Btn variant="ghost" size="sm" t={t} onClick={()=>setContracts(cs=>cs.filter(x=>x.id!==c.id))} icon="trash">{""}</Btn>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Contract":"New Contract"} t={t} wide>
        <Field t={t} label="Contract Title" required value={form.title} onChange={F("title")} placeholder="Q-Trader EA v2 — Service Agreement"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
          <Field t={t} label="Type" options={["Service Agreement","NDA","Retainer","Freelance","Other"]} value={form.type} onChange={F("type")}/>
          <Field t={t} label="Status" options={["Draft","Sent","Signed","Expired"]} value={form.status} onChange={F("status")}/>
          <Field t={t} label="Client" options={clients.map(c=>({v:c.id,l:c.name}))} value={form.clientId} onChange={F("clientId")}/>
          <Field t={t} label="Project" options={[{v:"",l:"None"},...projects.map(p=>({v:p.id,l:p.name}))]} value={form.projectId} onChange={F("projectId")}/>
          <Field t={t} label="Value" type="number" value={form.value} onChange={F("value")}/>
          <Field t={t} label="Currency" options={["USD","KES","EUR","GBP"]} value={form.currency} onChange={F("currency")}/>
          <Field t={t} label="Start Date" type="date" value={form.startDate} onChange={F("startDate")}/>
          <Field t={t} label="End Date" type="date" value={form.endDate} onChange={F("endDate")}/>
        </div>
        <Field t={t} label="Payment Terms" value={form.paymentTerms} onChange={F("paymentTerms")} placeholder="50% upfront, 50% on delivery"/>
        <Field t={t} label="Contract Body" textarea value={form.body} onChange={F("body")} rows={10}/>
        <Field t={t} label="Internal Notes" value={form.notes} onChange={F("notes")} placeholder="Signed via email, Docusign link, etc."/>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end",flexWrap:"wrap"}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn variant="secondary" t={t} onClick={()=>onPreview({html:buildContractHTML({...form,id:Date.now()},clients.find(c=>c.id===Number(form.clientId))),title:`${form.title||"Contract"} · Qire Agency`})} icon="print" disabled={!form.title}>Preview</Btn>
          <Btn t={t} onClick={save} disabled={!form.title} icon="send">{editing?"Save Changes":"Create Contract"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

// ─── MODULE: REPORTS ─────────────────────────────────────────────────────────
const Reports = ({ invoices, expenses, clients, projects, payroll, contracts, t }) => {

  const paidInvoices = invoices.filter(i => i.status === "Paid");
  const income       = expenses.filter(e => e.type === "income");
  const expList      = expenses.filter(e => e.type === "expense");

  const totalRevenue    = paidInvoices.reduce((a,i) => a + i.amount, 0);
  const totalKESIncome  = income.reduce((a,e) => a + e.amount, 0);
  const totalExpenses   = expList.reduce((a,e) => a + e.amount, 0);
  const totalDeductible = expList.filter(e=>e.deductible===true||e.deductible==="true").reduce((a,e)=>a+e.amount,0);
  const netProfit       = totalKESIncome - totalExpenses;
  const taxEstimate     = Math.round(totalKESIncome * 0.15);
  const totalPayroll    = payroll.filter(p=>p.status==="Paid").reduce((a,p)=>a+p.net,0);

  const monthlyData = (() => {
    const months = [];
    for(let i=5; i>=0; i--){
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      const label = d.toLocaleDateString("en-GB",{month:"short"});
      const revenue = paidInvoices
        .filter(inv => inv.issuedAt?.startsWith(key))
        .reduce((a,inv) => a + (inv.kesAmount || inv.amount*129), 0);
      const exp = expList
        .filter(e => e.date?.startsWith(key))
        .reduce((a,e) => a + e.amount, 0);
      months.push({label, key, revenue, exp});
    }
    return months;
  })();

  const maxMonthly = Math.max(...monthlyData.map(m => Math.max(m.revenue, m.exp)), 1);

  const clientStats = clients.map(c => {
    const cInvoices = invoices.filter(i => i.clientId===c.id);
    const paid      = cInvoices.filter(i=>i.status==="Paid").reduce((a,i)=>a+i.amount,0);
    const pending   = cInvoices.filter(i=>i.status!=="Paid").reduce((a,i)=>a+i.amount,0);
    const cProjects = projects.filter(p => p.clientId===c.id);
    return {...c, paid, pending, projectCount:cProjects.length, invoiceCount:cInvoices.length};
  }).sort((a,b) => b.paid - a.paid);

  const expByCategory = expList.reduce((acc,e) => {
    acc[e.category] = (acc[e.category]||0) + e.amount;
    return acc;
  }, {});
  const expCategories = Object.entries(expByCategory).sort((a,b)=>b[1]-a[1]);
  const maxExp = Math.max(...expCategories.map(([,v])=>v), 1);

  const projectStats = {
    done:       projects.filter(p=>p.status==="Completed"||p.status==="COMPLETED").length,
    inProgress: projects.filter(p=>p.status==="In Progress").length,
    planning:   projects.filter(p=>p.status==="Planning").length,
    total:      projects.length,
  };

  const BAR_H = 120;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow="Analytics"
        title="Reports"
        sub="Financial overview · client profitability · project health"
        t={t}
      />

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.75rem"}}>
        {[
          {l:"USD Revenue",    v:fmtUSD(totalRevenue),   c:t.teal,  i:"finance"},
          {l:"KES Income",     v:fmtKES(totalKESIncome), c:t.teal,  i:"trending"},
          {l:"Total Expenses", v:fmtKES(totalExpenses),  c:t.red,   i:"receipt"},
          {l:"Net Profit",     v:fmtKES(netProfit),      c:netProfit>=0?t.teal:t.red, i:"chart"},
          {l:"Tax Estimate",   v:fmtKES(taxEstimate),    c:t.amber, i:"tax"},
          {l:"Payroll Paid",   v:fmtKES(totalPayroll),   c:t.blue,  i:"payroll"},
        ].map(s=>(
          <StatCard key={s.l} label={s.l} value={s.v} icon={s.i} color={s.c} t={t}/>
        ))}
      </div>

      {/* Monthly chart */}
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
          Monthly — Revenue vs Expenses (KES)
        </div>
        <div style={{display:"flex",alignItems:"flex-end",gap:"0.75rem",height:BAR_H+40}}>
          {monthlyData.map((m,i) => (
            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{display:"flex",alignItems:"flex-end",gap:3,height:BAR_H}}>
                <div style={{position:"relative",display:"flex",flexDirection:"column",alignItems:"center"}}>
                  <div style={{width:14,height:m.revenue?Math.max(4,Math.round((m.revenue/maxMonthly)*BAR_H)):4,background:m.revenue?`${t.teal}d4`:t.border,borderRadius:"3px 3px 0 0",transition:"height 0.6s"}}/>
                </div>
                <div style={{position:"relative",display:"flex",flexDirection:"column",alignItems:"center"}}>
                  <div style={{width:14,height:m.exp?Math.max(4,Math.round((m.exp/maxMonthly)*BAR_H)):4,background:m.exp?`${t.red}99`:t.border,borderRadius:"3px 3px 0 0",transition:"height 0.6s"}}/>
                </div>
              </div>
              <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:"0.05em"}}>{m.label}</span>
              {(m.revenue>0||m.exp>0)&&<div style={{textAlign:"center"}}>
                {m.revenue>0&&<div style={{fontSize:8,color:t.teal,fontFamily:"'Fira Code',monospace"}}>{fmtKES(m.revenue).replace("KES ","")}</div>}
                {m.exp>0&&<div style={{fontSize:8,color:t.red,fontFamily:"'Fira Code',monospace"}}>{fmtKES(m.exp).replace("KES ","")}</div>}
              </div>}
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:"1.5rem",marginTop:"0.5rem",borderTop:`1px solid ${t.border}`,paddingTop:"0.75rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:10,height:10,borderRadius:2,background:`${t.teal}d4`}}/>
            <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>Revenue (KES)</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:10,height:10,borderRadius:2,background:`${t.red}99`}}/>
            <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>Expenses (KES)</span>
          </div>
        </div>
      </Card>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,340px),1fr))",gap:"0.85rem"}}>
        {/* Expense Breakdown */}
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
            Expense Breakdown
          </div>
          {expCategories.length===0&&<div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No expenses logged.</div>}
          {expCategories.map(([cat,val])=>(
            <div key={cat} style={{marginBottom:"0.85rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:14,color:t.text,fontWeight:500}}>{cat}</span>
                <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>{fmtKES(val)}</span>
              </div>
              <div style={{height:4,background:t.border,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.round((val/maxExp)*100)}%`,background:t.red,borderRadius:4,transition:"width 0.6s"}}/>
              </div>
              <div style={{fontSize:9,color:t.textDimmer,marginTop:3,fontFamily:"'Fira Code',monospace"}}>
                {Math.round((val/totalExpenses)*100)}% of total
              </div>
            </div>
          ))}
          <div style={{marginTop:"0.75rem",paddingTop:"0.75rem",borderTop:`1px solid ${t.border}`,display:"flex",justifyContent:"space-between"}}>
            <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>Deductible</span>
            <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.amber}}>{fmtKES(totalDeductible)}</span>
          </div>
        </Card>

        {/* Project Health */}
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
            Project Health
          </div>
          {[
            {l:"In Progress", v:projectStats.inProgress, c:t.teal},
            {l:"Planning",    v:projectStats.planning,   c:t.blue},
            {l:"Completed",   v:projectStats.done,       c:t.textDimmer},
          ].map(s=>(
            <div key={s.l} style={{marginBottom:"0.85rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:14,color:t.text,fontWeight:500}}>{s.l}</span>
                {/* FIXED: project counts — Space Grotesk 700 */}
                <span style={{fontSize:11,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,color:s.c}}>{s.v}</span>
              </div>
              <div style={{height:4,background:t.border,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${projectStats.total?Math.round((s.v/projectStats.total)*100):0}%`,background:s.c,borderRadius:4,transition:"width 0.6s"}}/>
              </div>
            </div>
          ))}
          <div style={{marginTop:"1rem",paddingTop:"0.75rem",borderTop:`1px solid ${t.border}`}}>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginBottom:"0.75rem",letterSpacing:"0.15em",textTransform:"uppercase"}}>Task Completion by Project</div>
            {projects.map(p=>{
              const done = p.tasks.filter(tk=>tk.done).length;
              const pct  = p.tasks.length ? Math.round((done/p.tasks.length)*100) : 0;
              return (
                <div key={p.id} style={{marginBottom:"0.65rem"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"70%"}}>{p.name}</span>
                    <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>{pct}%</span>
                  </div>
                  <ProgressBar pct={pct} t={t}/>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Client Profitability */}
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
          Client Profitability
        </div>
        {clientStats.length===0&&<div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No clients yet.</div>}
        <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
          {clientStats.map((c,i)=>(
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:"1rem",padding:"0.75rem",background:t.surface2,borderRadius:6,flexWrap:"wrap"}}>
              <div style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDimmer,width:18,textAlign:"center"}}>#{i+1}</div>
              <Avatar name={c.name} size={32} t={t}/>
              <div style={{flex:1,minWidth:100}}>
                {/* FIXED: client name — Space Grotesk 700 instead of Syne */}
                <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text}}>{c.name}</div>
                <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{c.company} · {c.projectCount} project{c.projectCount!==1?"s":""} · {c.invoiceCount} invoice{c.invoiceCount!==1?"s":""}</div>
              </div>
              <div style={{display:"flex",gap:"1.5rem",flexWrap:"wrap"}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.1em"}}>Paid</div>
                  {/* FIXED: Paid/Pending values — Space Grotesk 700 instead of Syne */}
                  <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.teal}}>{fmtUSD(c.paid)}</div>
                </div>
                {c.pending>0&&<div style={{textAlign:"right"}}>
                  <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDim,textTransform:"uppercase",letterSpacing:"0.1em"}}>Pending</div>
                  <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.amber}}>{fmtUSD(c.pending)}</div>
                </div>}
                <Badge label={c.status} t={t}/>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Invoice Pipeline */}
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
          Invoice Pipeline
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:"0.75rem"}}>
          {["Paid","Pending","Overdue","Draft"].map(status=>{
            const count = invoices.filter(i=>i.status===status).length;
            const value = invoices.filter(i=>i.status===status).reduce((a,i)=>a+i.amount,0);
            const c = sc(status,t);
            return (
              <div key={status} style={{padding:"1rem",background:t.surface2,borderRadius:6,textAlign:"center"}}>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",color:t.textDim,textTransform:"uppercase",marginBottom:6}}>{status}</div>
                {/* FIXED: invoice counts — Cormorant Garamond italic, single letterSpacing */}
                <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:c}}>{count}</div>
                <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginTop:3}}>{fmtUSD(value)}</div>
              </div>
            );
          })}
        </div>
      </Card>

    </div>
  );
};
// ─── MODULE: TIME TRACKER ─────────────────────────────────────────────────────
const TimeTracker = ({ timeEntries, setTimeEntries, projects, team, t }) => {
  const [modal, setModal]   = useState(false); 
  const [editing, setEditing] = useState(null);
  const [filter, setFilter]   = useState("all");
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [timerStart, setTimerStart] = useState(null);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    projectId:"", userId:"1", description:"",
    hours:"", date:todayStr(), billable:"true", billed:"false"
  });
  const F = k => v => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    if(running && timerStart){
      timerRef.current = setInterval(()=>{
        setElapsed(Math.floor((Date.now()-timerStart)/1000));
      },1000);
    }
    return ()=>clearInterval(timerRef.current);
  },[running, timerStart]);

  const stopTimer = () => {
    clearInterval(timerRef.current);
    setRunning(false);
  };

  const logTimer = () => {
    stopTimer();
    const hours = Math.round((elapsed/3600)*100)/100;
    setForm(f=>({...f, hours:String(hours), date:todayStr()}));
    setElapsed(0);
    setModal(true);
  };

  const resetTimer = () => {
    stopTimer();
    setElapsed(0);
  };

  const fmtElapsed = s => {
    const h = Math.floor(s/3600);
    const m = Math.floor((s%3600)/60);
    const sec = s%60;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  };

  const totalHours    = timeEntries.reduce((a,e)=>a+e.hours,0);
  const billableHours = timeEntries.filter(e=>e.billable===true||e.billable==="true").reduce((a,e)=>a+e.hours,0);
  const unbilledHours = timeEntries.filter(e=>(e.billable===true||e.billable==="true")&&(e.billed===false||e.billed==="false")).reduce((a,e)=>a+e.hours,0);

  const byProject = projects.map(p=>{
    const entries = timeEntries.filter(e=>e.projectId===p.id||e.projectId===String(p.id));
    const hours   = entries.reduce((a,e)=>a+e.hours,0);
    const billable= entries.filter(e=>e.billable===true||e.billable==="true").reduce((a,e)=>a+e.hours,0);
    return {...p, loggedHours:hours, billableHours:billable, entryCount:entries.length};
  }).filter(p=>p.loggedHours>0).sort((a,b)=>b.loggedHours-a.loggedHours);

  const maxHours = Math.max(...byProject.map(p=>p.loggedHours),1);

  const sorted = [...timeEntries].sort((a,b)=>new Date(b.date)-new Date(a.date));
  const filtered = filter==="all" ? sorted
    : filter==="billable"   ? sorted.filter(e=>e.billable===true||e.billable==="true")
    : filter==="unbilled"   ? sorted.filter(e=>(e.billable===true||e.billable==="true")&&(e.billed===false||e.billed==="false"))
    : sorted.filter(e=>String(e.projectId)===filter);

  const openAdd = () => {
    setEditing(null);
    setForm({projectId:String(projects[0]?.id||""), userId:"1", description:"", hours:"", date:todayStr(), billable:"true", billed:"false"});
    setModal(true);
  };
  const openEdit = e => {
    setEditing(e.id);
    setForm({...e, projectId:String(e.projectId), hours:String(e.hours), billable:String(e.billable), billed:String(e.billed), date:toDateInput(e.date)});
    setModal(true);
  };
  const save = async () => {
    const entry = {
      ...form,
      projectId: Number(form.projectId),
      hours: Number(form.hours)||0,
      billable: form.billable==="true",
      billed: form.billed==="true",
      id: editing || Date.now(),
    };
    if(editing) {
      setTimeEntries(es=>es.map(e=>e.id===editing?entry:e));
      try { await apiFetch(`/api/v1/finance/time/${editing}`, { method:'PATCH', body:JSON.stringify(entry) }); } catch{}
    } else {
      setTimeEntries(es=>[...es,entry]);
      try {
        const res = await apiFetch('/api/v1/finance/time', { method:'POST', body:JSON.stringify(entry) });
        if(res.entry?.id) setTimeEntries(es=>es.map(e=>e.id===entry.id?{...e,...res.entry}:e));
      } catch{}
    }
    setModal(false);
  };
  const markBilled = id => setTimeEntries(es=>es.map(e=>e.id===id?{...e,billed:true}:e));

  const devOpts = (team||[]).map(u=>({v:String(u.id),l:u.name}));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow="Productivity"
        title="Time Tracking"
        sub={`${timeEntries.length} entries · ${totalHours.toFixed(1)}h logged`}
        t={t}
        action={<Btn icon="plus" onClick={openAdd} t={t}>Log Time</Btn>}
      />

      {/* Live Timer */}
      <Card t={t} style={{border:`1px solid ${running?t.teal:t.border}`,background:running?`${t.teal}07`:t.surface,transition:"all 0.3s"}}>
        <div style={{display:"flex",alignItems:"center",gap:"1.5rem",flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:6}}>Live Timer</div>
            <div style={{fontFamily:"'Fira Code',monospace",fontSize:36,fontWeight:500,color:running?t.teal:t.text,letterSpacing:"0.1em",lineHeight:1,transition:"color 0.3s"}}>
              {fmtElapsed(elapsed)}
            </div>
          </div>
          <div style={{flex:1,fontSize:15,color:t.textDim,fontWeight:400}}>
            {running
              ? "Timer is running — click Stop when done, then Log to save the entry."
              : elapsed>0
                ? "Timer paused — Resume or Log this session."
                : "Start the timer when you begin working on a task."}
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {!running
              ? <Btn t={t} onClick={()=>{setTimerStart(Date.now()-elapsed*1000);setRunning(true);}} icon="timer">
                  {elapsed>0?"Resume":"Start"}
                </Btn>
              : <Btn t={t} variant="amber" onClick={stopTimer} icon="x">Stop</Btn>
            }
            {elapsed>0&&!running&&<Btn t={t} variant="secondary" onClick={logTimer} icon="check">Log Entry</Btn>}
            {elapsed>0&&<Btn t={t} variant="ghost" onClick={resetTimer} icon="trash">Reset</Btn>}
          </div>
        </div>
      </Card>

      {/* FIXED: Time stats — Cormorant Garamond italic, single letterSpacing */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
        {[
          {l:"Total Hours",    v:`${totalHours.toFixed(1)}h`,    c:t.teal},
          {l:"Billable Hours", v:`${billableHours.toFixed(1)}h`, c:t.blue},
          {l:"Unbilled",       v:`${unbilledHours.toFixed(1)}h`, c:t.amber},
          {l:"Projects",       v:String(byProject.length),       c:t.textDim},
        ].map(s=>(
          <Card key={s.l} t={t} style={{padding:"1rem",textAlign:"center"}}>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, letterSpacing:"0.02em", fontStyle:"italic", color:s.c}}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Hours by Project */}
      {byProject.length>0&&<Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1.25rem"}}>
          Hours by Project
        </div>
        {byProject.map(p=>(
          <div key={p.id} style={{marginBottom:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <span style={{fontSize:15,color:t.text,fontWeight:500}}>{p.name}</span>
              <div style={{display:"flex",gap:"1rem",alignItems:"center"}}>
                <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{p.entryCount} entries</span>
                <span style={{fontSize:12,fontFamily:"'Fira Code',monospace",color:t.teal,fontWeight:500}}>{p.loggedHours.toFixed(1)}h</span>
              </div>
            </div>
            <div style={{height:6,background:t.border,borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${Math.round((p.loggedHours/maxHours)*100)}%`,background:`linear-gradient(to right,${t.teal},${t.blue})`,borderRadius:4,transition:"width 0.6s"}}/>
            </div>
            <div style={{fontSize:10,color:t.textDimmer,marginTop:3,fontFamily:"'Fira Code',monospace"}}>
              {p.billableHours.toFixed(1)}h billable
            </div>
          </div>
        ))}
      </Card>}

      {/* Filter tabs */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {[
          {v:"all",      l:"All"},
          {v:"billable", l:"Billable"},
          {v:"unbilled", l:"Unbilled"},
          ...projects.map(p=>({v:String(p.id),l:p.name})),
        ].map(f=>(
          <button key={f.v} onClick={()=>setFilter(f.v)} style={{
            padding:"5px 11px",borderRadius:5,fontSize:10,cursor:"pointer",
            fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",
            background:filter===f.v?t.teal:"transparent",
            color:filter===f.v?(t.id==="light"?"#fff":"#050507"):t.textDim,
            border:filter===f.v?"none":`1px solid ${t.border}`,
          }}>{f.l}</button>
        ))}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        {filtered.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No entries found.</div>}
        {filtered.map(e=>{
          const proj  = projects.find(p=>p.id===e.projectId||p.id===Number(e.projectId));
          const member= team.find(u=>u.id===e.userId||u.id===Number(e.userId));
          const isBillable = e.billable===true||e.billable==="true";
          const isBilled   = e.billed===true||e.billed==="true";
          return (
            <Card key={e.id} t={t} style={{padding:"0.85rem 1rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                {/* Hours badge */}
                <div style={{width:42,height:42,borderRadius:6,flexShrink:0,background:`${t.teal}15`,border:`1px solid ${t.teal}30`,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>
                  <span style={{fontFamily:"'Fira Code',monospace",fontSize:13,fontWeight:600,color:t.teal,lineHeight:1}}>{e.hours}</span>
                  <span style={{fontFamily:"'Fira Code',monospace",fontSize:8,color:t.textDimmer}}>hrs</span>
                </div>
                {/* Info + actions */}
                <div style={{flex:1,minWidth:0}}>
                  {/* Row 1: title + status badge */}
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:600,color:t.text,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description}</span>
                    {isBillable&&!isBilled&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.amber}18`,color:t.amber,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.amber}40`,flexShrink:0}}>Unbilled</span>}
                    {isBillable&&isBilled&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.teal}18`,color:t.teal,fontFamily:"'Fira Code',monospace",border:`1px solid ${t.teal}40`,flexShrink:0}}>Billed</span>}
                    {!isBillable&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:t.surface3,color:t.textDimmer,fontFamily:"'Fira Code',monospace",flexShrink:0}}>Non-billable</span>}
                  </div>
                  {/* Row 2: meta + actions */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                    <span style={{fontSize:11,color:t.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
                      {proj?.name||"No project"} · {member?.name||"Unknown"} · {e.date?toDateInput(e.date):"—"}
                    </span>
                    <div style={{display:"flex",gap:3,flexShrink:0}}>
                      {isBillable&&!isBilled&&<Btn variant="secondary" size="sm" t={t} onClick={()=>markBilled(e.id)} icon="check">Mark Billed</Btn>}
                      <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(e)} icon="edit">{""}</Btn>
                      <Btn variant="ghost" size="sm" t={t} onClick={()=>setTimeEntries(es=>es.filter(x=>x.id!==e.id))} icon="trash">{""}</Btn>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Entry":"Log Time"} t={t}>
        <Field t={t} label="Description" required value={form.description} onChange={F("description")} placeholder="What did you work on?"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
          <Field t={t} label="Project" options={projects.map(p=>({v:String(p.id),l:p.name}))} value={form.projectId} onChange={F("projectId")}/>
          <Field t={t} label="Team Member" options={devOpts} value={form.userId} onChange={F("userId")}/>
          <Field t={t} label="Hours" type="number" value={form.hours} onChange={F("hours")} placeholder="1.5"/>
          <Field t={t} label="Date" type="date" value={form.date} onChange={F("date")}/>
          <Field t={t} label="Billable?" options={[{v:"true",l:"Yes — Billable"},{v:"false",l:"No — Internal"}]} value={form.billable} onChange={F("billable")}/>
          <Field t={t} label="Billed to Client?" options={[{v:"false",l:"Not yet"},{v:"true",l:"Already billed"}]} value={form.billed} onChange={F("billed")}/>
        </div>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={save} disabled={!form.description||!form.hours} icon="check">{editing?"Save":"Log Entry"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

const useNotifications = ({ invoices, taxRecords, contracts, payroll, projects, waitlist, user }) => {
  const today = new Date();
  const notes = [];
  const isAdmin  = user?.role==="admin";
  const isClient = user?.role==="client";
  const isDev    = user?.role==="developer";

  // ── ADMIN ONLY notifications ──
  if(isAdmin){
    invoices.filter(i=>i.status==="Overdue").forEach(i=>{
      notes.push({id:`inv-overdue-${i.id}`,type:"danger",icon:"invoices",title:`Invoice ${i.number} Overdue`,body:`${i.currency} ${i.amount?.toLocaleString()} — due ${i.dueAt?toDateInput(i.dueAt):"—"}`,page:"invoices"});
    });
    invoices.filter(i=>i.status==="Pending"&&i.dueAt).forEach(i=>{
      const diff=Math.ceil((new Date(i.dueAt)-today)/(1000*60*60*24));
      if(diff>=0&&diff<=5) notes.push({id:`inv-due-${i.id}`,type:"warning",icon:"invoices",title:`Invoice ${i.number} Due Soon`,body:`Due in ${diff} day${diff!==1?"s":""} · ${i.currency} ${i.amount?.toLocaleString()}`,page:"invoices"});
    });
    taxRecords.filter(r=>r.status==="Due").forEach(r=>{
      notes.push({id:`tax-due-${r.id}`,type:"warning",icon:"tax",title:`KRA Tax Due — ${r.period}`,body:`File on iTax · Pay via M-Pesa Paybill 572572`,page:"tax"});
    });
    taxRecords.filter(r=>r.status==="Overdue").forEach(r=>{
      notes.push({id:`tax-overdue-${r.id}`,type:"danger",icon:"tax",title:`KRA Filing Overdue — ${r.period}`,body:`This period is past its filing deadline`,page:"tax"});
    });
    contracts?.filter(c=>c.status==="Signed"&&c.endDate).forEach(c=>{
      const diff=Math.ceil((new Date(c.endDate)-today)/(1000*60*60*24));
      if(diff>=0&&diff<=14) notes.push({id:`contract-exp-${c.id}`,type:"warning",icon:"contracts",title:`Contract Expiring — ${c.title}`,body:`Expires in ${diff} day${diff!==1?"s":""}`,page:"contracts"});
      if(diff<0) notes.push({id:`contract-expired-${c.id}`,type:"danger",icon:"contracts",title:`Contract Expired — ${c.title}`,body:`Expired ${c.endDate} · update status`,page:"contracts"});
    });
    payroll?.filter(p=>p.status==="Pending").forEach(p=>{
      notes.push({id:`payroll-pending-${p.id}`,type:"info",icon:"payroll",title:`Payroll Pending — ${p.recipient}`,body:`${p.period} · KES ${p.net?.toLocaleString()} via ${p.method}`,page:"payroll"});
    });
    const waiting=waitlist.filter(w=>w.status==="Waiting");
    if(waiting.length>0) notes.push({id:"waitlist-waiting",type:"info",icon:"waitlist",title:`${waiting.length} Waitlist Signup${waiting.length!==1?"s":""} Pending`,body:waiting.map(w=>w.name).slice(0,3).join(", ")+(waiting.length>3?` +${waiting.length-3} more`:""),page:"waitlist"});
  }

  // ── ADMIN + DEV — project deadlines ──
  if(isAdmin||isDev){
    const myProjects = isDev
      ? projects.filter(p=>p.assignee === user.id)
      : projects;
    myProjects.filter(p=>p.deadline&&p.status!=="Completed"&&p.status!=="COMPLETED").forEach(p=>{
      const diff=Math.ceil((new Date(p.deadline)-today)/(1000*60*60*24));
      if(diff<0) notes.push({id:`project-overdue-${p.id}`,type:"danger",icon:"projects",title:`Project Overdue — ${p.name}`,body:`Deadline was ${p.deadline} · still ${p.status}`,page:"projects"});
      else if(diff<=7) notes.push({id:`project-due-${p.id}`,type:"warning",icon:"projects",title:`Deadline Soon — ${p.name}`,body:`Due in ${diff} day${diff!==1?"s":""}`,page:"projects"});
    });
  }

  // ── CLIENT ONLY notifications ──
  if(isClient){
    const acc = user ? { clientId: user.id, username: user.portalUsername || user.username } : null;
    invoices.filter(i=>i.clientId===acc?.clientId&&i.status==="Overdue").forEach(i=>{
      notes.push({id:`inv-overdue-${i.id}`,type:"danger",icon:"invoices",title:`Invoice Overdue`,body:`${i.currency} ${i.amount?.toLocaleString()} — due ${i.dueAt?toDateInput(i.dueAt):"—"}`,page:"invoices"});
    });
    invoices.filter(i=>i.clientId===acc?.clientId&&i.status==="Pending"&&i.dueAt).forEach(i=>{
      const diff=Math.ceil((new Date(i.dueAt)-today)/(1000*60*60*24));
      if(diff>=0&&diff<=5) notes.push({id:`inv-due-${i.id}`,type:"warning",icon:"invoices",title:`Invoice Due Soon`,body:`Due in ${diff} day${diff!==1?"s":""}`,page:"invoices"});
    });
    // New proposals sent to them
    const myProps = (window.__proposals||[]).filter(p=>p.clientId===acc?.clientId&&p.status==="Sent");
    myProps.forEach(p=>{
      notes.push({id:`proposal-sent-${p.id}`,type:"info",icon:"proposal",title:`Proposal Awaiting Review`,body:p.title,page:"proposals"});
    });
  }

  const order={danger:0,warning:1,info:2};
  return notes.sort((a,b)=>order[a.type]-order[b.type]);
};

// ─── FIXED: NotificationPanel — Space Grotesk 700 (was Syne) ─────────────────
const NotificationPanel = ({ notes, onClose, onNavigate, t }) => {
  if(!notes) return null;

  const typeStyles = {
    danger:  { color:t.red,   bg:`${t.red}12`,   border:`${t.red}35`   },
    warning: { color:t.amber, bg:`${t.amber}12`,  border:`${t.amber}35` },
    info:    { color:t.blue,  bg:`${t.blue}12`,   border:`${t.blue}35`  },
  };

  return createPortal(
    <div style={{position:"fixed",inset:0,zIndex:8000}} onClick={onClose}>
      <div
        onClick={e=>e.stopPropagation()}
        className="fade-up"
        style={{
          position:"fixed", top:0, right:0,
          width:340, height:"100vh",
          background:t.surface,
          borderLeft:`1px solid ${t.border}`,
          display:"flex", flexDirection:"column",
          boxShadow:"-8px 0 40px rgba(0,0,0,0.3)",
          zIndex:8001,
        }}
      >
        {/* FIXED: header — Space Grotesk 700 instead of Syne */}
        <div style={{
          padding:"1.2rem 1.25rem",
          borderBottom:`1px solid ${t.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          flexShrink:0,
        }}>
          <div>
            <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>Notifications</div>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginTop:2}}>
              {notes.length} alert{notes.length!==1?"s":""} · auto-generated
            </div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4}}>
            <I n="x" s={16}/>
          </button>
        </div>

        {/* List */}
        <div style={{flex:1,overflowY:"auto",padding:"0.75rem"}}>
          {notes.length===0&&(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60%",gap:"0.75rem"}}>
              <I n="check" s={28} c={t.teal}/>
              {/* FIXED: Space Grotesk 700 */}
              <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>All clear!</div>
              <div style={{fontSize:12,color:t.textDimmer,textAlign:"center",fontWeight:400}}>No pending alerts or actions needed.</div>
            </div>
          )}
          {notes.map(n=>{
            const s = typeStyles[n.type];
            return (
              <div
                key={n.id}
                onClick={()=>{onNavigate(n.page);onClose();}}
                style={{
                  padding:"0.85rem",
                  borderRadius:6,
                  border:`1px solid ${s.border}`,
                  background:s.bg,
                  marginBottom:"0.5rem",
                  cursor:"pointer",
                  transition:"opacity 0.15s",
                }}
                onMouseEnter={e=>e.currentTarget.style.opacity="0.8"}
                onMouseLeave={e=>e.currentTarget.style.opacity="1"}
              >
                <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                  <I n={n.icon} s={14} c={s.color}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:t.text,marginBottom:3,lineHeight:1.3}}>{n.title}</div>
                    <div style={{fontSize:11,color:t.textDim,fontWeight:400,lineHeight:1.5}}>{n.body}</div>
                    <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:s.color,marginTop:5,textTransform:"uppercase",letterSpacing:"0.08em"}}>
                      → Go to {n.page}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{padding:"0.75rem",borderTop:`1px solid ${t.border}`,flexShrink:0}}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,textAlign:"center",letterSpacing:"0.1em"}}>
            Alerts update automatically from your data
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
// ─── PROPOSAL PRINT FUNCTION ──────────────────────────────────────────────────
const buildProposalHTML = (proposal, client) => {
  const total = proposal.items?.reduce((a,i)=>a+i.total,0)||0;
  const sCol = proposal.status==="Accepted"?"#00c9a7":proposal.status==="Declined"?"#c0394e":"#f5a623";
  const sBg  = proposal.status==="Accepted"?"#00c9a720":proposal.status==="Declined"?"#c0394e20":"#f5a62320";
  const sBdr = proposal.status==="Accepted"?"#00c9a740":proposal.status==="Declined"?"#c0394e40":"#f5a62340";
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:2rem;gap:2rem;align-items:flex-start">
      <div>
        <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:5px">Prepared For</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:15px;color:#111;margin-bottom:3px">${client?.name||""}</div>
        <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#555;line-height:1.7">${client?.company||""}<br/>${client?.email||""}</div>
      </div>
      <div style="text-align:right">
        <div style="font-family:Syne,sans-serif;font-weight:800;font-size:13px;color:#050506;text-transform:uppercase;letter-spacing:-.01em;margin-bottom:8px">Proposal</div>
        <div style="font-family:'Fira Code',monospace;font-size:10px;color:#888;line-height:2.2">
          Created: ${proposal.createdAt?toDateInput(proposal.createdAt):""}<br/>
          Valid Until: ${proposal.validUntil?toDateInput(proposal.validUntil):""}<br/>
          Currency: ${proposal.currency||"USD"}
        </div>
        <div style="margin-top:8px;display:inline-block;padding:4px 12px;border-radius:3px;background:${sBg};color:${sCol};font-family:'Fira Code',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.12em;border:1px solid ${sBdr}">${proposal.status}</div>
      </div>
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.8rem"></div>
    <div style="margin-bottom:2rem">
      <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:6px">Project</div>
      <div style="font-family:Syne,sans-serif;font-weight:700;font-size:20px;color:#111;letter-spacing:-.01em">${proposal.title||""}</div>
      ${proposal.summary?`<div style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#555;line-height:1.8;margin-top:8px">${proposal.summary}</div>`:""}
    </div>
    <div style="height:1px;background:#e0e0e8;margin-bottom:1.5rem"></div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:1.5rem">
      <thead><tr style="border-bottom:2px solid #050506">
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px 0;text-align:left">Description</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px;text-align:center">Qty</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 12px 9px;text-align:right">Rate</th>
        <th style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#888;padding:0 0 9px;text-align:right">Total</th>
      </tr></thead>
      <tbody>
        ${(proposal.items||[]).map(item=>`
          <tr style="border-bottom:1px solid #eee">
            <td style="font-family:'Space Grotesk',sans-serif;font-size:15px;color:#222;padding:12px 12px 12px 0">${item.description}</td>
            <td style="font-family:'Fira Code',monospace;font-size:12px;color:#444;padding:12px;text-align:center">${item.qty}</td>
            <td style="font-family:'Fira Code',monospace;font-size:12px;color:#444;padding:12px;text-align:right">${proposal.currency} ${item.rate?.toLocaleString()}</td>
            <td style="font-family:'Fira Code',monospace;font-size:12px;color:#111;padding:12px 0;text-align:right;font-weight:600">${proposal.currency} ${item.total?.toLocaleString()}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin-bottom:2rem">
      <div style="width:300px">
        <div style="display:flex;justify-content:space-between;padding:12px;background:#050506;border-radius:5px">
          <span style="font-family:Syne,sans-serif;font-weight:700;font-size:15px;color:#f0f0f5">Total</span>
          <span style="font-family:Syne,sans-serif;font-weight:700;font-size:15px;color:#00c9a7">${proposal.currency} ${total.toLocaleString()}</span>
        </div>
      </div>
    </div>
    ${proposal.terms?`
    <div style="padding:1.2rem 1.4rem;background:#f8f8fa;border-radius:7px;border:1px solid #e0e0e8;margin-bottom:1.5rem">
      <div style="font-family:'Fira Code',monospace;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#888;margin-bottom:8px">Terms & Payment</div>
      <div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#444;line-height:1.7">${proposal.terms}</div>
    </div>`:""}
    ${proposal.notes?`<div style="font-family:'Space Grotesk',sans-serif;font-size:14px;color:#888;font-style:italic;line-height:1.7">${proposal.notes}</div>`:""}
    ${sigBlock('', client?.name||'Client', client?.company||'')}
  `;
};

// ─── MODULE: PROPOSALS ────────────────────────────────────────────────────────
// NOTE: Proposals stat cards use Syne in original — FIXED to Space Grotesk 700
const Proposals = ({ proposals, setProposals, clients, user, t, onPreview }) => {
  const isAdmin = user.role==="admin";
  const isClient = user.role==="client";
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    clientId:"", title:"", status:"Draft", createdAt:todayStr(), validUntil:"",
    summary:"", terms:"50% upfront, 50% on delivery. Revisions included within 14 days.",
    notes:"", currency:"USD", items:[{description:"",qty:1,rate:"",total:0}]
  });
  const F = k => v => setForm(f=>({...f,[k]:v}));

  const visible = isClient
    ? proposals.filter(p => String(p.clientId) === String(user.id) || String(p.clientId) === String(user.clientId))
    : proposals;

  const updateItem = (i,k,v) => {
    setForm(f=>{
      const items=[...f.items];
      items[i]={...items[i],[k]:v};
      if(k==="qty"||k==="rate") items[i].total=(Number(k==="qty"?v:items[i].qty)||1)*(Number(k==="rate"?v:items[i].rate)||0);
      return {...f,items};
    });
  };
  const addItem = () => setForm(f=>({...f,items:[...f.items,{description:"",qty:1,rate:"",total:0}]}));
  const removeItem = i => setForm(f=>({...f,items:f.items.filter((_,idx)=>idx!==i)}));

  const total = form.items.reduce((a,i)=>a+(i.total||0),0);

  const openAdd = () => {
    setEditing(null);
    setForm({clientId:String(clients[0]?.id||""),title:"",status:"Draft",createdAt:todayStr(),validUntil:"",summary:"",terms:"50% upfront, 50% on delivery. Revisions included within 14 days.",notes:"",currency:"USD",items:[{description:"",qty:1,rate:"",total:0}]});
    setModal(true);
  };
  const openEdit = p => {
    setEditing(p.id);
    setForm({...p,clientId:String(p.clientId),items:(Array.isArray(p.items)?p.items:[]).map(i=>({...i,rate:String(i.rate)}))});
    setModal(true);
  };
  const save = async () => {
    const e={...form,clientId:form.clientId,items:form.items.map(i=>({...i,qty:Number(i.qty)||1,rate:Number(i.rate)||0,total:(Number(i.qty)||1)*(Number(i.rate)||0)}))};
    if(editing) {
      setProposals(ps=>ps.map(p=>p.id===editing?{...p,...e}:p));
      try { await apiFetch(`/api/v1/proposals/${editing}`, { method:'PATCH', body:JSON.stringify({...e, status:(e.status||e.status).toUpperCase()}) }); } catch{}
    } else {
      const tmp={...e,id:Date.now(),status:'Draft',createdAt:todayStr()};
      setProposals(ps=>[...ps,tmp]);
      try {
        const res = await apiFetch('/api/v1/proposals', { method:'POST', body:JSON.stringify({...e, status:(e.status||'DRAFT').toUpperCase()}) });
        if(res.proposal?.id) setProposals(ps=>ps.map(p=>p.id===tmp.id?{...p,...res.proposal}:p));
      } catch{}
    }
    setModal(false);
  };
  const statusColors={Draft:t.textDimmer,Sent:t.blue,Viewed:t.amber,Accepted:t.teal,Declined:t.red};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow={isClient?"Your Proposals":"Business"}
        title="Proposals"
        sub={isClient?"Review and accept your project proposals":`${proposals.length} proposals · printable · client-facing`}
        t={t}
        action={isAdmin&&<Btn icon="plus" onClick={openAdd} t={t}>New Proposal</Btn>}
      />

      {/* FIXED: Proposal summary stats — Space Grotesk 700 instead of Syne */}
      {isAdmin&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:"0.75rem"}}>
        {["Draft","Sent","Accepted","Declined"].map(s=>{
          const count=proposals.filter(p=>p.status===s).length;
          const val=proposals.filter(p=>p.status===s).reduce((a,p)=>a+(p.items?.reduce((b,i)=>b+i.total,0)||0),0);
          return <Card key={s} t={t} style={{padding:"1rem",textAlign:"center"}}>
            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:4}}>{s}</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:400,fontStyle:"italic",color:statusColors[s]}}>{count}</div>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginTop:2}}>{fmtUSD(val)}</div>
          </Card>;
        })}
      </div>}

      <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
        {visible.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No proposals yet.</div>}
        {visible.map(p=>{
          const client=clients.find(c=>c.id===p.clientId||c.id===Number(p.clientId));
          const total=p.items?.reduce((a,i)=>a+i.total,0)||0;
          const sc2=statusColors[p.status]||t.textDim;
          return <Card key={p.id} t={t}>
            <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
              <div style={{width:38,height:38,borderRadius:6,background:`${sc2}18`,border:`1px solid ${sc2}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <I n="proposal" s={16} c={sc2}/>
              </div>
              <div style={{flex:1,minWidth:120}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                  <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{p.title}</span>
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${sc2}18`,color:sc2,fontFamily:"'Fira Code',monospace",letterSpacing:"0.08em",textTransform:"uppercase",border:`1px solid ${sc2}40`}}>{p.status}</span>
                </div>
                <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>
                  {client?.name||"Unknown client"} · Created {p.createdAt?toDateInput(p.createdAt):"—"}{p.validUntil?` · Valid until ${toDateInput(p.validUntil)}`:""}
                </div>
                {p.summary&&<div style={{fontSize:11,color:t.textDimmer,marginTop:3,fontStyle:"italic",fontWeight:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:400}}>{p.summary}</div>}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                {/* FIXED: proposal total — Space Grotesk 700 instead of Syne */}
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:24,fontStyle:"italic",color:t.teal}}>{p.currency} {total.toLocaleString()}</div>
                <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{p.items?.length||0} line items</div>
              </div>
              <div style={{display:"flex",gap:3,flexShrink:0,flexWrap:"wrap"}}>
                {isClient&&p.status==="Sent"&&<>
                  <Btn variant="secondary" size="sm" t={t} onClick={()=>setProposals(ps=>ps.map(x=>x.id===p.id?{...x,status:"Accepted"}:x))} icon="check">Accept</Btn>
                  <Btn variant="danger" size="sm" t={t} onClick={()=>setProposals(ps=>ps.map(x=>x.id===p.id?{...x,status:"Declined"}:x))}>Decline</Btn>
                </>}
                {isAdmin&&p.status==="Draft"&&<Btn variant="secondary" size="sm" t={t} onClick={()=>setProposals(ps=>ps.map(x=>x.id===p.id?{...x,status:"Sent"}:x))} icon="send">Send</Btn>}
                <Btn variant="secondary" size="sm" t={t} onClick={()=>onPreview({
                  html:buildProposalHTML(p,client),
                  title:`Proposal · ${p.title} · Qire Agency`,
                  isProposal: true, proposalId: p.id,
                  initialAdminSig: p.adminSignature||'', initialClientSig: p.clientSignature||'',
                  initialAdminSignedAt: p.adminSignedAt||'', initialClientSignedAt: p.clientSignedAt||p.signedAt||'',
                })} icon="print">Preview</Btn>
                {isAdmin&&<>
                  <Btn variant="ghost" size="sm" t={t} onClick={()=>openEdit(p)} icon="edit">{""}</Btn>
                  <Btn variant="ghost" size="sm" t={t} onClick={()=>setProposals(ps=>ps.filter(x=>x.id!==p.id))} icon="trash">{""}</Btn>
                </>}
              </div>
            </div>
          </Card>;
        })}
      </div>

      {isAdmin&&<Modal open={modal} onClose={()=>setModal(false)} title={editing?"Edit Proposal":"New Proposal"} t={t} wide>
        <Field t={t} label="Proposal Title" required value={form.title} onChange={F("title")} placeholder="Q-Trader EA v2 — Full Development"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
          <Field t={t} label="Client" options={clients.map(c=>({v:String(c.id),l:c.name}))} value={form.clientId} onChange={F("clientId")}/>
          <Field t={t} label="Status" options={["Draft","Sent","Viewed","Accepted","Declined"]} value={form.status} onChange={F("status")}/>
          <Field t={t} label="Created" type="date" value={form.createdAt} onChange={F("createdAt")}/>
          <Field t={t} label="Valid Until" type="date" value={form.validUntil} onChange={F("validUntil")}/>
          <Field t={t} label="Currency" options={["USD","KES","EUR","GBP"]} value={form.currency} onChange={F("currency")}/>
        </div>
        <Field t={t} label="Project Summary" textarea value={form.summary} onChange={F("summary")} rows={3} placeholder="Brief description of the scope…"/>

        <div>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:8}}>Line Items</div>
          {form.items.map((item,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 60px 80px 80px 30px",gap:"0.5rem",marginBottom:"0.5rem",alignItems:"center"}}>
              <input value={item.description} onChange={e=>updateItem(i,"description",e.target.value)} placeholder="Description" style={{background:t.surface2,color:t.text,border:`1px solid ${t.border}`,borderRadius:5,padding:"7px 10px",fontSize:12,fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
              <input type="number" value={item.qty} onChange={e=>updateItem(i,"qty",e.target.value)} placeholder="Qty" style={{background:t.surface2,color:t.text,border:`1px solid ${t.border}`,borderRadius:5,padding:"7px 8px",fontSize:12,fontFamily:"'Space Grotesk',sans-serif",outline:"none",textAlign:"center"}}/>
              <input type="number" value={item.rate} onChange={e=>updateItem(i,"rate",e.target.value)} placeholder="Rate" style={{background:t.surface2,color:t.text,border:`1px solid ${t.border}`,borderRadius:5,padding:"7px 8px",fontSize:12,fontFamily:"'Space Grotesk',sans-serif",outline:"none"}}/>
              <div style={{fontFamily:"'Fira Code',monospace",fontSize:11,color:t.teal,textAlign:"right"}}>{form.currency} {item.total?.toLocaleString()}</div>
              <button onClick={()=>removeItem(i)} style={{background:"none",border:"none",cursor:"pointer",color:t.textDimmer,padding:4}}><I n="x" s={13}/></button>
            </div>
          ))}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8}}>
            <Btn variant="ghost" size="sm" t={t} onClick={addItem} icon="plus">Add Item</Btn>
            {/* FIXED: proposal total in modal — Space Grotesk 700 instead of Syne */}
            <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:16,color:t.teal}}>Total: {form.currency} {total.toLocaleString()}</div>
          </div>
        </div>

        <Field t={t} label="Terms & Payment" textarea value={form.terms} onChange={F("terms")} rows={2}/>
        <Field t={t} label="Internal Notes" value={form.notes} onChange={F("notes")} placeholder="Source code included, support period, etc."/>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end",flexWrap:"wrap"}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn variant="secondary" t={t} onClick={()=>onPreview({html:buildProposalHTML({...form,id:Date.now()},clients.find(c=>c.id===Number(form.clientId))),title:`Proposal · ${form.title||"Draft"} · Qire Agency`})} icon="print" disabled={!form.title}>Preview</Btn>
          <Btn t={t} onClick={save} disabled={!form.title} icon="send">{editing?"Save":"Create Proposal"}</Btn>
        </div>
      </Modal>}
    </div>
  );
};


// ─── FIXED: ClientPortalManager — Space Grotesk 700 (was Syne in info card) ──
const ClientPortalManager = ({ clients, setClients, t }) => {
  // Source of truth: c.portalActive + c.portalUsername on each client object (from API)
  const [modal,    setModal]    = useState(false);
  const [working,  setWorking]  = useState(null);   // clientId being processed
  const [credModal,setCredModal]= useState(null);   // { name, username, password, email }
  const [form,     setForm]     = useState({ clientId:"", username:"", password:"" });
  const F = k => v => setForm(f=>({...f,[k]:v}));

  const activeClients = clients.filter(c => c.status==="Active" || c.status==="Proposal");

  // Grant access — calls POST /admin/clients/:id/portal-access
  const grant = async (client) => {
    const base = client.name.toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g,"");
    const pass = base + Math.floor(1000 + Math.random() * 9000);
    setForm({ clientId: String(client.id), username: base, password: pass });
    setModal(true);
  };

  const saveAccess = async () => {
    const client = clients.find(c => String(c.id) === String(form.clientId));
    if (!client) return;
    setWorking(client.id);
    try {
      await apiFetch(`/api/v1/admin/clients/${client.id}/portal-access`, {
        method: 'POST',
        body: JSON.stringify({ username: form.username, password: form.password }),
      });
      // Update client in state — portalActive + portalUsername are now real
      setClients(cs => cs.map(c => String(c.id) === String(form.clientId)
        ? { ...c, portalActive: true, portalUsername: form.username }
        : c
      ));
      setModal(false);
      setCredModal({ name: client.name, username: form.username, password: form.password, email: client.email });
    } catch(err) {
      alert('Failed: ' + (err.message || 'API error'));
    } finally {
      setWorking(null);
    }
  };

  // Suspend / restore — calls PATCH /admin/clients/:id/portal-access (sets portalActive)
  const toggleAccess = async (client) => {
    const next = !client.portalActive;
    setClients(cs => cs.map(c => c.id === client.id ? { ...c, portalActive: next } : c));
    try {
      await apiFetch(`/api/v1/admin/clients/${client.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ portalActive: next }),
      });
    } catch {
      // revert on failure
      setClients(cs => cs.map(c => c.id === client.id ? { ...c, portalActive: !next } : c));
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader
        eyebrow="Access Control"
        title="Client Portal"
        sub="Manage client login credentials · invite-only access"
        t={t}
      />

      <Card t={t} style={{border:`1px solid ${t.blue}35`,background:`${t.blue}07`}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
          <I n="portal" s={18} c={t.blue}/>
          <div>
            <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text,marginBottom:4}}>How Client Portal Works</div>
            <div style={{fontSize:14,color:t.textDim,lineHeight:1.8}}>
              1. Add client in CRM and set status to Active or Proposal<br/>
              2. Click <strong style={{color:t.text}}>Grant Access</strong> — credentials are auto-generated<br/>
              3. Send the credentials to the client — they log in at your OS URL<br/>
              4. Client sees their project, invoices, proposals, and can message you<br/>
              5. Suspend access any time without deleting the client record
            </div>
          </div>
        </div>
      </Card>

      {/* Active clients — portal status */}
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>
          Active Clients — Portal Status
        </div>
        {activeClients.length === 0 && (
          <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No active clients yet. Add clients in CRM and set status to Active.</div>
        )}
        {activeClients.map(c => (
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:"1rem",padding:"0.75rem 0",borderBottom:`1px solid ${t.border}`,flexWrap:"wrap"}}>
            <Avatar name={c.name} size={32} t={t}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:t.text}}>{c.name}</div>
              <div style={{fontSize:11,color:t.textDim,fontWeight:400}}>{c.company} · {c.email}</div>
            </div>
            {c.portalActive && c.portalUsername ? (
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.teal}}>● Portal Active</div>
                  <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>@{c.portalUsername}</div>
                </div>
                <Btn variant="secondary" size="sm" t={t} onClick={()=>{setForm({clientId:String(c.id),username:c.portalUsername,password:""});setModal(true);}}>Manage</Btn>
                <Btn variant="ghost" size="sm" t={t} onClick={()=>toggleAccess(c)}>Suspend</Btn>
              </div>
            ) : c.portalActive === false && c.portalUsername ? (
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.red}}>○ Suspended</div>
                  <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>@{c.portalUsername}</div>
                </div>
                <Btn variant="secondary" size="sm" t={t} onClick={()=>toggleAccess(c)}>Restore</Btn>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>No access yet</span>
                <Btn size="sm" t={t} onClick={()=>grant(c)} icon="plus" disabled={working===c.id}>Grant Access</Btn>
              </div>
            )}
          </div>
        ))}
      </Card>

      {/* All clients with portal access */}
      {clients.filter(c=>c.portalUsername).length > 0 && (
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>
            All Portal Accounts ({clients.filter(c=>c.portalUsername).length})
          </div>
          {clients.filter(c=>c.portalUsername).map(c => (
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:"1rem",padding:"0.75rem",background:t.surface2,borderRadius:6,marginBottom:"0.5rem",flexWrap:"wrap"}}>
              <Avatar name={c.name} size={30} t={t}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:t.text}}>{c.name}</div>
                <div style={{display:"flex",gap:12,marginTop:2,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>@{c.portalUsername}</span>
                  <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer}}>{c.email}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,
                  background:c.portalActive?`${t.teal}18`:`${t.red}18`,
                  color:c.portalActive?t.teal:t.red,
                  fontFamily:"'Fira Code',monospace",
                  border:`1px solid ${c.portalActive?t.teal+"40":t.red+"40"}`
                }}>{c.portalActive?"Active":"Suspended"}</span>
                <Btn variant="ghost" size="sm" t={t} onClick={()=>toggleAccess(c)}>
                  {c.portalActive?"Suspend":"Restore"}
                </Btn>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Grant/Edit Access Modal */}
      <Modal open={modal} onClose={()=>setModal(false)} title={form.username && clients.find(c=>String(c.id)===String(form.clientId))?.portalUsername ? "Manage Portal Access" : "Grant Portal Access"} t={t}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem"}}>
          <Field t={t} label="Username" value={form.username} onChange={F("username")} placeholder="e.g. tariq"/>
          <Field t={t} label="Password" value={form.password} onChange={F("password")} placeholder="auto-generated"/>
        </div>
        <div style={{padding:"0.85rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`,fontSize:15,color:t.textDim}}>
          Send these credentials to the client via email or WhatsApp. They log in at your OS URL using username + password.
        </div>
        <div style={{display:"flex",gap:"0.6rem",justifyContent:"flex-end"}}>
          <Btn variant="secondary" t={t} onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={saveAccess} disabled={!form.username||!form.password||!!working} icon="send">
            {working ? "Saving…" : "Grant Access"}
          </Btn>
        </div>
      </Modal>

      {/* Credentials reveal modal */}
      {credModal && createPortal(
        <div style={{position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:t.surface,borderRadius:10,width:"100%",maxWidth:420,border:`1px solid ${t.teal}40`,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
            <div style={{padding:"1.1rem 1.5rem",borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",gap:10}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.teal} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:t.text}}>Portal Access Granted</span>
            </div>
            <div style={{padding:"1.5rem",display:"flex",flexDirection:"column",gap:"1rem"}}>
              <div style={{padding:"0.75rem 1rem",background:`${t.amber}10`,border:`1px solid ${t.amber}30`,borderRadius:6,fontSize:12,color:t.amber,fontFamily:"'Space Grotesk',sans-serif"}}>
                ⚠ Save these credentials now — the password will not be shown again.
              </div>
              {[["Client", credModal.name],["Email", credModal.email],["Username", credModal.username],["Password", credModal.password]].map(([label, value]) => (
                <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.6rem 0.75rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`}}>
                  <span style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,textTransform:"uppercase",letterSpacing:"0.15em"}}>{label}</span>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:13,color:t.text,fontFamily:"'Fira Code',monospace",fontWeight:600}}>{value}</span>
                    <CopyBtn text={value} label="copy" t={t}/>
                  </div>
                </div>
              ))}
              <CopyBtn
                text={`Email: ${credModal.email}\nUsername: ${credModal.username}\nPassword: ${credModal.password}`}
                label="Copy All Credentials"
                t={t}
                style={{padding:"8px",width:"100%",justifyContent:"center",borderRadius:5,fontSize:12,fontWeight:600}}
              />
              <button onClick={()=>setCredModal(null)} style={{padding:"8px",borderRadius:5,background:t.teal,border:"none",color:"#050507",cursor:"pointer",fontSize:13,fontFamily:"'Space Grotesk',sans-serif",fontWeight:700}}>
                Done — I've saved these
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT PROJECT HUB — Drop-in replacement for ClientProjectView in Portal.js
//
// USAGE: Replace the existing `const ClientProjectView = ...` block (lines ~4510–4900)
// with this entire component. All helpers (apiFetch, Card, Modal, Btn, etc.)
// are already in Portal.js scope so no imports needed.
//
// NEW TABS ADDED vs old component:
//   Releases   — version history, grouped changelog, sign-off, download
//   Bugs       — bug reports with severity + admin replies (wired to /api/v1/bugs)
//   Requests   — feature requests (stored on project.featureRequests)
//   Messages   — project thread inline (was a separate page link)
//   Invoices   — project-scoped billing summary
//
// EXISTING TABS KEPT & IMPROVED:
//   Overview, Tasks, Deliverables
// ═══════════════════════════════════════════════════════════════════════════════

const ClientProjectView = ({
  projects, setProjects,
  invoices, setInvoices,
  proposals, setProposals,
  contracts, setContracts,
  messages, setMessages,
  deliverables, user, clients, t, setPage,
  team, onPreview,
  qtLicenses, qtVersions,
}) => {

  // ── Identity ────────────────────────────────────────────────────────────────
  const clientId = String(user?.id || user?.clientId || '');
  const myProjs  = projects.filter(p => String(p.clientId) === clientId);

  // ── State ───────────────────────────────────────────────────────────────────
  const [selProjId,  setSelProjId]  = React.useState(myProjs[0]?.id || null);
  const [tab,        setTab]        = React.useState('overview');
  const [relTab,     setRelTab]     = React.useState('all');       // releases sub-filter
  const [loading,    setLoading]    = React.useState({});
  const [signingOff, setSigningOff] = React.useState(null);       // releaseId being signed

  // Bug modal
  const [bugModal,   setBugModal]   = React.useState(false);
  const [bugForm,    setBugForm]    = React.useState({ title:'', description:'', stepsToRepro:'', severity:'MEDIUM' });

  // Feature request modal
  const [frModal,    setFrModal]    = React.useState(false);
  const [frForm,     setFrForm]     = React.useState({ title:'', description:'', priority:'Medium' });

  // Task request modal
  const [taskModal,  setTaskModal]  = React.useState(false);
  const [taskForm,   setTaskForm]   = React.useState({ title:'', description:'', priority:'Medium' });

  // Message compose
  const [msgDraft,    setMsgDraft]    = React.useState('');
  const [sendingMsg,  setSendingMsg]  = React.useState(false);
  const [msgThread,   setMsgThread]   = React.useState('admin');
  const msgEndRef   = React.useRef(null);
  const fileInputRef = React.useRef(null);

  // Pending attachments (selected but not yet sent)
  const [pendingFiles, setPendingFiles] = React.useState([]); // [{file, previewUrl, name, size, mimeType}]
  const [uploadingFiles, setUploadingFiles] = React.useState(false);

  // Message interactions
  const [hoveredMsg,  setHoveredMsg]  = React.useState(null);
  const [menuMsg,     setMenuMsg]     = React.useState(null);
  const [editingMsg,  setEditingMsg]  = React.useState(null);
  const [replyTo,     setReplyTo]     = React.useState(null);
  const [starredMsgs, setStarredMsgs] = React.useState(() => { try { return JSON.parse(''||'[]'); } catch { return []; } });

  // Release detail expand
  const [expandedRel, setExpandedRel] = React.useState(null);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const proj       = myProjs.find(p => p.id === selProjId) || myProjs[0];
  const projId     = proj?.id;

  const projMsgs   = (messages || [])
    .filter(m => m.projectId === projId && !m.private &&
      (msgThread === 'admin' ? (m.type === 'admin' || m.type === 'ADMIN' || !m.type) : (m.type === 'dev' || m.type === 'DEV')))
    .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));

  // ── Per-project invoices (project-scoped, not all client invoices) ──────────
  // projInvs: strictly for this project (or unlinked ones for this client)
  const projInvs   = (invoices || [])
    .filter(i => String(i.clientId) === clientId && String(i.projectId) === String(projId) && i.status !== 'DRAFT' && i.status !== 'Draft')
    .sort((a,b) => new Date(b.issuedAt||b.createdAt) - new Date(a.issuedAt||a.createdAt));

  // allProjInvs: all non-draft invoices for this client (used for global stats in header)
  const allProjInvs  = (invoices || []).filter(i => String(i.clientId) === clientId && i.status !== 'DRAFT' && i.status !== 'Draft');

  // Proposals — filter by projectId if present, else fall through to clientId only
  const myProposals  = (proposals || []).filter(p => {
    if (!['Draft','draft','DRAFT'].includes(p.status) === false) return false; // exclude drafts
    const matchClient = String(p.clientId) === clientId;
    if (!matchClient) return false;
    if (p.projectId) return String(p.projectId) === String(projId);
    return true; // no projectId set — show on all projects for this client
  }).filter(p => !['Draft','draft','DRAFT'].includes(p.status));

  // Contracts — filter by projectId if present, else by clientId
  const projContracts = (contracts || []).filter(c => {
    const matchClient = String(c.clientId) === clientId;
    if (!matchClient) return false;
    if (c.projectId) return String(c.projectId) === String(projId);
    return true; // no projectId — show on all projects for this client
  }).sort((a,b) => new Date(b.startDate||b.createdAt) - new Date(a.startDate||a.createdAt));

  const activeContracts = projContracts.filter(c => ['Signed','SIGNED','Active','ACTIVE'].includes(c.status));

  const safeDelivs  = deliverables || {};
  // Deliverables come directly from project (p.deliverables from API),
  // also include any locally-shared ones from the messages deliverables store
  const projDelivs  = [
    ...(proj?.deliverables || []),
    ...(safeDelivs[`${projId}-admin`] || []),
    ...(safeDelivs[`${projId}-dev`]   || []),
  ]
  .filter((d, idx, self) => self.findIndex(x => x.id === d.id) === idx) // dedupe
  .sort((a,b) => new Date(b.uploadedAt||b.createdAt) - new Date(a.uploadedAt||a.createdAt));

  // Project-level releases (stored on project.releases — array of release objects)
  const releases    = (proj?.releases || []).sort((a,b) => new Date(b.releasedAt) - new Date(a.releasedAt));

  // Bugs — client-reported, stored on project.bugs
  const bugs        = (proj?.bugs || []).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
  const openBugs    = bugs.filter(b => b.status !== 'Resolved' && b.status !== 'RESOLVED' && b.status !== 'WONT_FIX');

  // Feature requests stored on project.featureRequests
  const requests    = (proj?.featureRequests || []).sort((a,b) => (b.votes||0) - (a.votes||0));
  const openReqs    = requests.filter(r => r.status !== 'Completed' && r.status !== 'Declined');

  // Client tasks
  const clientTasks = proj?.clientTasks || [];
  const pendingTasks = clientTasks.filter(t => t.status !== 'Completed' && t.status !== 'COMPLETED');

  // Financial summary — project-scoped
  const paid        = projInvs.filter(i => i.status === 'Paid' || i.status === 'PAID').reduce((a,i) => a + (i.amount||0), 0);
  const outstanding = projInvs.filter(i => ['Sent','Pending','Overdue','SENT','PENDING','OVERDUE'].includes(i.status)).reduce((a,i) => a + (i.amount||0), 0);
  // Cross-project totals for the header stat cards
  const totalPaid  = allProjInvs.filter(i => i.status === 'Paid' || i.status === 'PAID').reduce((a,i) => a + (i.amount||0), 0);
  const totalOwed  = allProjInvs.filter(i => ['Sent','Pending','Overdue','SENT','PENDING','OVERDUE'].includes(i.status)).reduce((a,i) => a + (i.amount||0), 0);

  const unreadMsgs  = projMsgs.filter(m => !m.read && m.fromId !== `client-${clientId}`).length;

  // Unacknowledged releases
  const unackRels   = releases.filter(r => !r.acknowledgedAt);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const updateProj = (updater) =>
    setProjects(ps => ps.map(p => p.id === projId ? updater(p) : p));

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const severityColor = s => ({
    CRITICAL: t.red, Critical: t.red,
    HIGH: t.red,     High: t.red,
    MEDIUM: t.amber, Medium: t.amber,
    LOW: t.teal,     Low: t.teal,
  }[s] || t.textDim);

  const statusColor = s => ({
    Open: t.red,     OPEN: t.red,
    'In Progress': t.blue, IN_PROGRESS: t.blue,
    Resolved: t.teal, RESOLVED: t.teal,
    Requested: t.amber,
    Completed: t.teal,
    Done: t.teal,
    Pending: t.amber,
    Declined: t.textDimmer,
  }[s] || t.textDim);

  const priorityColor = s => ({
    High: t.red, Medium: t.amber, Low: t.teal,
    URGENT: t.red,
  }[s] || t.textDim);

  const relTypeColor = rt => ({
    MAJOR: t.red, Major: t.red,
    MINOR: t.blue, Minor: t.blue,
    PATCH: t.teal, Patch: t.teal,
    HOTFIX: t.amber, Hotfix: t.amber,
  }[rt] || t.textDim);

  const relTypeBg = rt => ({
    MAJOR: `${t.red}15`, Major: `${t.red}15`,
    MINOR: `${t.blue}15`, Minor: `${t.blue}15`,
    PATCH: `${t.teal}15`, Patch: `${t.teal}15`,
    HOTFIX: `${t.amber}15`, Hotfix: `${t.amber}15`,
  }[rt] || `${t.textDim}10`);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB',{ day:'numeric', month:'short', year:'numeric' }) : '—';
  const fmtRel = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB',{ day:'numeric', month:'long', year:'numeric' }) : '—';

  // ── Mark messages as read when messages tab is opened or thread changes ──────
  React.useEffect(() => {
    if (tab !== 'messages' || !projId) return;
    const myFromId = `client-${clientId}`;
    const unread = (messages || []).filter(m =>
      m.projectId === projId &&
      (m.type === msgThread || m.type === msgThread.toUpperCase()) &&
      !m.read &&
      m.fromId !== myFromId
    );
    if (unread.length === 0) return;
    setMessages(ms => ms.map(m =>
      m.projectId === projId &&
      (m.type === msgThread || m.type === msgThread.toUpperCase()) &&
      m.fromId !== myFromId
        ? { ...m, read: true }
        : m
    ));
    const tok = '' || '';
    unread.forEach(m => {
      if (m.id && typeof m.id === 'string' && m.id.length > 10) {
        apiFetch(`/api/v1/messages/${m.id}/read`, { method: 'PATCH' }, tok).catch(() => {});
      }
    });
  }, [tab, projId, msgThread]); // eslint-disable-line

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    if (tab === 'messages' && msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [tab, projMsgs.length]); // eslint-disable-line

  // ── Actions ──────────────────────────────────────────────────────────────────

  // Submit bug
  const submitBug = async () => {
    if (!bugForm.title.trim()) return;
    setLoad('bug', true);
    const tmp = {
      id: `tmp-${Date.now()}`,
      ...bugForm,
      status: 'Open',
      createdAt: new Date().toISOString(),
      _pending: true,
    };
    updateProj(p => ({ ...p, bugs: [...(p.bugs||[]), tmp] }));
    setBugForm({ title:'', description:'', stepsToRepro:'', severity:'MEDIUM' });
    setBugModal(false);
    try {
      const tok = '' || '';
      const res = await apiFetch(`/api/v1/projects/${projId}/bugs`, {
        method: 'POST',
        body: JSON.stringify({ title: bugForm.title, description: bugForm.description, stepsToRepro: bugForm.stepsToRepro, severity: bugForm.severity }),
      }, tok);
      if (res.bug?.id) {
        updateProj(p => ({ ...p, bugs: (p.bugs||[]).map(b => b.id === tmp.id ? res.bug : b) }));
      }
    } catch {
      updateProj(p => ({ ...p, bugs: (p.bugs||[]).map(b => b.id === tmp.id ? { ...b, _pending: false } : b) }));
    }
    setLoad('bug', false);
  };

  const submitRequest = async () => {
    if (!frForm.title.trim()) return;
    const tmp = {
      id: `fr-${Date.now()}`, ...frForm,
      status: 'Under Review', votes: 0, votedByMe: false,
      createdAt: new Date().toISOString(),
    };
    updateProj(p => ({ ...p, featureRequests: [...(p.featureRequests||[]), tmp] }));
    setFrForm({ title:'', description:'', priority:'Medium' });
    setFrModal(false);
    try {
      const tok = '' || '';
      const res = await apiFetch(`/api/v1/projects/${projId}/feature-requests`, {
        method: 'POST', body: JSON.stringify(frForm),
      }, tok);
      if (res.request?.id)
        updateProj(p => ({ ...p, featureRequests: (p.featureRequests||[]).map(r => r.id===tmp.id ? {...res.request, votedByMe:false} : r) }));
    } catch {}
  };

  // Vote on feature request
  const voteRequest = (reqId) => {
    updateProj(p => ({
      ...p,
      featureRequests: (p.featureRequests||[]).map(r =>
        r.id === reqId
          ? { ...r, votes: r.votedByMe ? (r.votes||1)-1 : (r.votes||0)+1, votedByMe: !r.votedByMe }
          : r
      ),
    }));
  };

  const submitTask = async () => {
    if (!taskForm.title.trim()) return;
    const tmp = {
      id: `ct-${Date.now()}`, ...taskForm,
      status: 'Requested', addedBy: 'client',
      createdAt: new Date().toISOString(),
    };
    updateProj(p => ({ ...p, clientTasks: [...(p.clientTasks||[]), tmp] }));
    setTaskForm({ title:'', description:'', priority:'Medium' });
    setTaskModal(false);
    try {
      const tok = '' || '';
      const res = await apiFetch(`/api/v1/projects/${projId}/client-tasks`, {
        method: 'POST', body: JSON.stringify(taskForm),
      }, tok);
      if (res.task?.id)
        updateProj(p => ({ ...p, clientTasks: (p.clientTasks||[]).map(t => t.id===tmp.id ? res.task : t) }));
    } catch {}
  };

  // Acknowledge (sign off on) a release
  const acknowledgeRelease = async (releaseId) => {
    setSigningOff(releaseId);
    updateProj(p => ({
      ...p,
      releases: (p.releases||[]).map(r =>
        r.id === releaseId
          ? { ...r, acknowledgedAt: new Date().toISOString(), acknowledgedBy: user.name }
          : r
      ),
    }));
    try {
      const tok = '' || '';
      await apiFetch(`/api/v1/projects/${projId}/releases/${releaseId}/acknowledge`, { method:'POST' }, tok);
    } catch {}
    setTimeout(() => setSigningOff(null), 1000);
  };

  // Send message
  // ── Message helpers ───────────────────────────────────────────────────────────
  const getMsgSenderName = (fromId) => {
    if (fromId === `client-${clientId}`) return user.name || 'You';
    const u = (team||[]).find(u => String(u.id) === String(fromId));
    if (u) return u.name;
    return 'Qire Team';
  };
  const fmtMsgTime = iso => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB',{day:'numeric',month:'short'}) + ' · ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
  };
  const toggleMsgStar = (id) => setStarredMsgs(prev => {
    const next = prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id];
    
    return next;
  });
  const deleteMsgClient = async (id) => {
    setMessages(ms => ms.filter(m => m.id !== id));
    setMenuMsg(null);
    const tok = '' || '';
    try { await apiFetch(`/api/v1/messages/${id}`, { method:'DELETE' }, tok); } catch {}
  };
  const saveEditClient = async (id, newBody) => {
    if (!newBody.trim()) return;
    setMessages(ms => ms.map(m => m.id===id ? {...m, body:newBody, edited:true} : m));
    setEditingMsg(null); setMenuMsg(null);
    const tok = '' || '';
    try { await apiFetch(`/api/v1/messages/${id}`, { method:'PATCH', body:JSON.stringify({body:newBody}) }, tok); } catch {}
  };
  const copyMsgClient = (body) => { navigator.clipboard?.writeText(body).catch(()=>{}); setMenuMsg(null); };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    const previews = files.map(f => ({
      file: f, name: f.name, size: f.size, mimeType: f.type,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPendingFiles(prev => [...prev, ...previews]);
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => {
      const next = [...prev];
      if (next[idx]?.previewUrl) URL.revokeObjectURL(next[idx].previewUrl);
      next.splice(idx, 1);
      return next;
    });
  };

  const sendMessage = async () => {
    if (!msgDraft.trim() && pendingFiles.length === 0) return;
    setSendingMsg(true);
    const tok = '' || '' || '';
    const replyData = replyTo ? { replyTo: { id:replyTo.id, body:replyTo.body, senderName:replyTo.senderName } } : {};

    const bodyText = msgDraft.trim();
    const tmp = {
      id: `m-${Date.now()}`, body: bodyText, projectId: projId,
      clientId, fromId: `client-${clientId}`,
      type: msgThread, read: false,
      createdAt: new Date().toISOString(), _pending: true,
      attachments: pendingFiles.map(pf => ({ fileName: pf.file.name, fileSize: pf.file.size, mimeType: pf.file.type })),
      ...replyData,
    };
    setMessages(ms => [...(ms||[]), tmp]);
    setMsgDraft('');
    setReplyTo(null);

    const filesToSend = [...pendingFiles];
    try {
      // Send files + message body together as multipart/form-data
      const fd = new FormData();
      fd.append('body', bodyText);
      fd.append('type', msgThread.toUpperCase());
      if (projId) fd.append('projectId', projId);
      if (replyTo) fd.append('replyTo', JSON.stringify({ id:replyTo.id, body:replyTo.body, senderName:replyTo.senderName }));
      filesToSend.forEach(pf => fd.append('files', pf.file));
      setPendingFiles([]);

      // Demo: message already in state via tmp, just clear pending flag
      setMessages(ms => (ms||[]).map(m => m.id===tmp.id ? { ...m, _pending:false } : m));
    } catch {
      // Network / parse error — mark failed and restore files so user can retry
      setMessages(ms => (ms||[]).map(m => m.id===tmp.id ? { ...m, _pending:false, _failed:true } : m));
      setPendingFiles(filesToSend);
    }
    setSendingMsg(false);
  };

  // ── Tab config ───────────────────────────────────────────────────────────────
  const pendingInvs = projInvs.filter(i => ['Sent','Pending','Overdue','SENT','PENDING','OVERDUE'].includes(i.status)).length;
  const tabs = [
    { id:'overview',     label:'Overview',     icon:'dashboard' },
    { id:'releases',     label:'Releases',     icon:'trending',  badge: unackRels.length, badgeColor: t.teal },
    { id:'bugs',         label:'Bugs',         icon:'alert',     badge: openBugs.length, badgeColor: openBugs.some(b=>['CRITICAL','HIGH','Critical','High'].includes(b.severity)) ? t.red : t.amber },
    { id:'requests',     label:'Requests',     icon:'proposal',  badge: openReqs.length, badgeColor: t.blue },
    { id:'tasks',        label:'Tasks',        icon:'check',     badge: pendingTasks.length, badgeColor: t.amber },
    { id:'files', label:'Project Files', icon:'folder', badge: projDelivs.length || null, badgeColor: t.teal },
    { id:'messages',     label:'Messages',     icon:'message',   badge: unreadMsgs || null, badgeColor: t.blue },
    { id:'proposals',    label:'Proposals',    icon:'proposal',  badge: myProposals.length||null, badgeColor: t.amber },
    { id:'contracts',    label:'Contracts',    icon:'contracts', badge: activeContracts.length||null, badgeColor: t.teal },
    { id:'invoices',     label:'Invoices',     icon:'invoices',  badge: pendingInvs||null, badgeColor: t.red },
  ];

  // ── Styles ───────────────────────────────────────────────────────────────────
  const tabBtn = (id) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 13px', borderRadius: 6, cursor: 'pointer',
    background: tab===id ? (t.id==='dark' ? t.teal : t.teal) : 'transparent',
    color: tab===id ? (t.id==='dark' ? '#050507' : '#fff') : t.textDim,
    border: tab===id ? 'none' : `1px solid ${t.border}`,
    fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", fontWeight: tab===id ? 600 : 400,
    transition: 'all 0.15s', whiteSpace: 'nowrap',
  });

  const projBtn = (id) => ({
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500,
    background: selProjId===id ? t.surface3 : 'transparent',
    color: selProjId===id ? t.text : t.textDim,
    border: selProjId===id ? `1px solid ${t.borderBright}` : `1px solid transparent`,
    transition: 'all 0.15s',
  });

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (myProjs.length === 0) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:320, gap:'1rem' }} className="fade-up">
      <div style={{ width:64, height:64, borderRadius:12, background:`${t.teal}15`, border:`1px solid ${t.teal}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <I n="projects" s={28} c={t.teal}/>
      </div>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:300, fontStyle:'italic', color:t.text, textAlign:'center' }}>No projects yet</div>
      <div style={{ fontSize:13, color:t.textDimmer, textAlign:'center', maxWidth:320, lineHeight:1.7 }}>
        Your project workspace will appear here once Qire Agency sets up your first project.
      </div>
    </div>
  );

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }} className="fade-up">

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:300, fontStyle:'italic', color:t.text, letterSpacing:'0.01em', lineHeight:1.1 }}>
            My Projects
          </div>
          <div style={{ width:28, height:1, background:t.teal, margin:'7px 0' }}/>
          <div style={{ fontSize:12, color:t.textDim }}>{myProjs.length} active project{myProjs.length!==1?'s':''} · Qire Agency Client Portal</div>
        </div>
        {/* Alert badges */}
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          {unackRels.length > 0 && (
            <div onClick={()=>{ setTab('releases'); }} style={{ cursor:'pointer', padding:'6px 12px', borderRadius:5, background:`${t.teal}15`, border:`1px solid ${t.teal}40`, fontSize:11, color:t.teal, display:'flex', alignItems:'center', gap:6 }}>
              <I n="trending" s={11} c={t.teal}/> {unackRels.length} release{unackRels.length>1?'s':''} to acknowledge
            </div>
          )}
          {unreadMsgs > 0 && (
            <div onClick={()=>setTab('messages')} style={{ cursor:'pointer', padding:'6px 12px', borderRadius:5, background:`${t.blue}15`, border:`1px solid ${t.blue}40`, fontSize:11, color:t.blue, display:'flex', alignItems:'center', gap:6 }}>
              <I n="message" s={11} c={t.blue}/> {unreadMsgs} unread
            </div>
          )}
        </div>
      </div>

      {/* ── Global stats (across all projects) ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:'0.6rem' }}>
        {[
          { l:'Projects',    v: myProjs.length,           c: t.teal },
          { l:'Total Paid',  v: fmtUSD(totalPaid),        c: t.teal },
          { l:'Outstanding', v: fmtUSD(totalOwed),        c: totalOwed>0 ? t.amber : t.textDimmer },
          { l:'Open Bugs',   v: bugs.filter(b=>b.status!=='Resolved'&&b.status!=='RESOLVED').length, c: openBugs.length ? t.red : t.textDimmer },
          { l:'Project Files',v: projDelivs.length,       c: t.blue },
        ].map(s => (
          <Card key={s.l} t={t} style={{ padding:'0.8rem', textAlign:'center' }}>
            <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", letterSpacing:'0.15em', color:t.textDim, textTransform:'uppercase', marginBottom:4 }}>{s.l}</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, fontStyle:"italic", color:s.c }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* ── Project selector (if multiple) ── */}
      {myProjs.length > 1 && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', padding:'0.65rem 0', borderBottom:`1px solid ${t.border}` }}>
          <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", color:t.textDimmer, textTransform:'uppercase', letterSpacing:'0.15em', display:'flex', alignItems:'center', marginRight:4 }}>Project:</div>
          {myProjs.map(p => {
            const pBugs = (p.bugs||[]).filter(b=>b.status!=='Resolved').length;
            const pUnack = (p.releases||[]).filter(r=>!r.acknowledgedAt).length;
            return (
              <button key={p.id} onClick={()=>{ setSelProjId(p.id); setTab('overview'); }} style={projBtn(p.id)}>
                <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background: p.status==='In_Progress'||p.status==='In Progress'?t.teal:p.status==='On_Hold'?t.amber:t.textDimmer, flexShrink:0 }}/>
                  {p.name}
                  {pBugs > 0 && <span style={{ fontSize:9, background:`${t.red}20`, color:t.red, borderRadius:8, padding:'0px 5px', fontFamily:"'Fira Code',monospace" }}>{pBugs}</span>}
                  {pUnack > 0 && <span style={{ fontSize:9, background:`${t.teal}20`, color:t.teal, borderRadius:8, padding:'0px 5px', fontFamily:"'Fira Code',monospace" }}>{pUnack}✓</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {proj && <>

        {/* ── Tab bar ── */}
        <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch', borderBottom:`1px solid ${t.border}`, paddingBottom:'0.75rem' }}>
          <div style={{ display:'flex', gap:5, minWidth:'max-content' }}>
          {tabs.map(tb => (
            <button key={tb.id} onClick={()=>setTab(tb.id)} style={tabBtn(tb.id)}>
              <I n={tb.icon} s={11} c="currentColor"/>
              {tb.label}
              {tb.badge > 0 && (
                <span style={{ fontSize:9, background: tab===tb.id ? 'rgba(0,0,0,0.25)' : (tb.badgeColor||t.teal), color: tab===tb.id ? (t.id==='dark'?'#050507':'#fff') : '#fff', borderRadius:8, padding:'0px 5px', fontFamily:"'Fira Code',monospace", fontWeight:700, minWidth:16, textAlign:'center' }}>
                  {tb.badge}
                </span>
              )}
            </button>
          ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            OVERVIEW TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'overview' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.85rem' }}>
            {/* Project header card */}
            <Card t={t} style={{ borderLeft:`3px solid ${t.teal}` }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                <div style={{ flex:1, minWidth:200 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:8 }}>
                    <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:18, color:t.text }}>{proj.name}</div>
                    <Badge label={proj.status} t={t}/>
                    <span style={{ fontSize:9, fontWeight:600, color:priorityColor(proj.priority), fontFamily:"'Fira Code',monospace", textTransform:'uppercase', background:`${priorityColor(proj.priority)}15`, padding:'2px 7px', borderRadius:3 }}>{proj.priority}</span>
                  </div>
                  {proj.description && <div style={{ fontSize:13, color:t.textDim, lineHeight:1.6, marginBottom:10 }}>{proj.description}</div>}
                  <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap' }}>
                    <div style={{ fontSize:11, color:t.textDim }}>
                      Deadline: <strong style={{ color:t.text }}>{proj.deadline ? fmt(proj.deadline) : '—'}</strong>
                    </div>
                    {proj.assigneeName && (
                      <div style={{ fontSize:11, color:t.textDim }}>
                        Dev: <strong style={{ color:t.text }}>{proj.assigneeName}</strong>
                      </div>
                    )}
                  </div>
                </div>
                {/* Circular progress */}
                {(() => {
                  const done = (proj.tasks||[]).filter(tk=>tk.done).length;
                  const total = (proj.tasks||[]).length;
                  const pct = total ? Math.round((done/total)*100) : 0;
                  const r=28, circ=2*Math.PI*r;
                  return (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <svg width={72} height={72} style={{ transform:'rotate(-90deg)' }}>
                        <circle cx={36} cy={36} r={r} fill="none" stroke={t.border} strokeWidth={5}/>
                        <circle cx={36} cy={36} r={r} fill="none" stroke={t.teal} strokeWidth={5}
                          strokeDasharray={circ} strokeDashoffset={circ*(1-pct/100)} strokeLinecap="round"
                          style={{ transition:'stroke-dashoffset 0.6s ease' }}/>
                      </svg>
                      <div style={{ marginTop:-58, fontSize:14, fontWeight:700, fontFamily:"'Space Grotesk',sans-serif", color:t.text, textAlign:'center', lineHeight:1 }}>{pct}%</div>
                      <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.textDimmer, marginTop:36 }}>progress</div>
                    </div>
                  );
                })()}
              </div>
            </Card>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'0.85rem' }}>

              {/* Latest release */}
              {releases.length > 0 && (() => {
                const latest = releases[0];
                return (
                  <Card t={t} style={{ cursor:'pointer' }} onClick={()=>{ setTab('releases'); setExpandedRel(latest.id); }}>
                    <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.18em', textTransform:'uppercase', color:t.textDim, marginBottom:'0.75rem' }}>Latest Release</div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:16, color:t.text }}>v{latest.version}</span>
                      <span style={{ fontSize:9, padding:'2px 8px', borderRadius:3, background:relTypeBg(latest.releaseType), color:relTypeColor(latest.releaseType), fontFamily:"'Fira Code',monospace", textTransform:'uppercase' }}>{latest.releaseType}</span>
                      {!latest.acknowledgedAt && <span style={{ fontSize:9, padding:'2px 8px', borderRadius:3, background:`${t.teal}15`, color:t.teal, fontFamily:"'Fira Code',monospace" }}>SIGN OFF NEEDED</span>}
                    </div>
                    <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6, marginBottom:8 }}>{latest.releaseNotes?.substring(0,120)}{latest.releaseNotes?.length>120?'…':''}</div>
                    <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.textDimmer }}>{fmtRel(latest.releasedAt)} · View full release →</div>
                  </Card>
                );
              })()}

              {/* Invoice summary */}
              <Card t={t}>
                <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.18em', textTransform:'uppercase', color:t.textDim, marginBottom:'0.75rem' }}>Billing · {proj.name}</div>
                <div style={{ display:'flex', gap:'1rem', marginBottom:'0.75rem' }}>
                  {[{ l:'Paid', v:fmtUSD(paid), c:t.teal }, { l:'Outstanding', v:fmtUSD(outstanding), c:outstanding>0?t.amber:t.textDimmer }].map(s=>(
                    <div key={s.l}>
                      <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', marginBottom:2 }}>{s.l}</div>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:22, fontStyle:"italic", color:s.c }}>{s.v}</div>
                    </div>
                  ))}
                </div>
                {projInvs.slice(0,3).map(inv => (
                  <div key={inv.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.4rem 0', borderBottom:`1px solid ${t.border}` }}>
                    <div style={{ fontSize:11, color:t.text }}>{inv.number}</div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <Badge label={inv.status} t={t}/>
                      <span style={{ fontSize:11, fontWeight:600, color:inv.status==='Paid'?t.teal:inv.status==='Overdue'?t.red:t.text, fontFamily:"'Space Grotesk',sans-serif" }}>{inv.currency} {inv.amount?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                <button onClick={()=>setTab('invoices')} style={{ marginTop:'0.5rem', fontSize:10, fontFamily:"'Fira Code',monospace", color:t.teal, background:'none', border:'none', cursor:'pointer', padding:0 }}>View all →</button>
              </Card>

              {/* Quick actions */}
              <Card t={t}>
                <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.18em', textTransform:'uppercase', color:t.textDim, marginBottom:'0.75rem' }}>Quick Actions</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {[
                    { label:'Report a Bug',        icon:'alert',    color:t.red,  action:()=>{ setTab('bugs');     setBugModal(true); }  },
                    { label:'Request a Feature',   icon:'proposal', color:t.blue, action:()=>{ setTab('requests'); setFrModal(true);  }  },
                    { label:'Submit Task Request', icon:'check',    color:t.amber,action:()=>{ setTab('tasks');    setTaskModal(true);}  },
                    { label:'Send a Message',      icon:'message',  color:t.teal, action:()=>setTab('messages')                         },
                  ].map(a => (
                    <button key={a.label} onClick={a.action} style={{
                      display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                      borderRadius:6, background:`${a.color}08`, border:`1px solid ${a.color}25`,
                      cursor:'pointer', color:a.color, fontSize:12,
                      fontFamily:"'Space Grotesk',sans-serif", fontWeight:500,
                      transition:'background 0.15s', textAlign:'left',
                    }}>
                      <I n={a.icon} s={13} c={a.color}/>
                      {a.label}
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            {/* Internal task progress */}
            {(proj.tasks||[]).length > 0 && (
              <Card t={t}>
                <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.18em', textTransform:'uppercase', color:t.textDim, marginBottom:'0.75rem' }}>
                  Work Progress · {proj.tasks.filter(tk=>tk.done).length}/{proj.tasks.length} tasks complete
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'0.4rem' }}>
                  {proj.tasks.map(task => (
                    <div key={task.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0' }}>
                      <div style={{ width:13, height:13, borderRadius:3, border:`1.5px solid ${task.done?t.teal:t.borderBright}`, background:task.done?t.teal:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {task.done && <I n="check" s={8} c={t.id==='dark'?'#050507':'#fff'}/>}
                      </div>
                      <span style={{ fontSize:12, color:task.done?t.textDimmer:t.text, textDecoration:task.done?'line-through':'none', lineHeight:1.4 }}>{task.title}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            RELEASES TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'releases' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.85rem' }}>
            {/* Sub-filter */}
            <div style={{ display:'flex', gap:5, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ fontSize:11, color:t.textDim, marginRight:4 }}>Filter:</div>
              {['all','MAJOR','MINOR','PATCH','HOTFIX'].map(f => (
                <button key={f} onClick={()=>setRelTab(f)} style={{
                  padding:'4px 10px', borderRadius:4, cursor:'pointer', fontSize:10,
                  fontFamily:"'Fira Code',monospace", textTransform:'uppercase',
                  background: relTab===f ? (f==='all'?t.teal:relTypeColor(f)) : 'transparent',
                  color: relTab===f ? (t.id==='dark'?'#050507':'#fff') : t.textDim,
                  border: relTab===f ? 'none' : `1px solid ${t.border}`,
                }}>
                  {f}
                </button>
              ))}
            </div>

            {releases.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2.5rem' }}>
                <I n="trending" s={28} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem', fontStyle:'italic' }}>No releases yet. Version history will appear here as work is delivered.</div>
              </Card>
            )}

            {releases
              .filter(r => relTab==='all' || r.releaseType===relTab || r.releaseType?.toUpperCase()===relTab)
              .map(rel => {
                const isExpanded = expandedRel === rel.id;
                const isAcked    = !!rel.acknowledgedAt;

                // Parse changelog groups if present
                const changelog = rel.changelog || {};
                const hasChangelog = (changelog.features?.length||0)+(changelog.fixes?.length||0)+(changelog.improvements?.length||0)+(changelog.security?.length||0) > 0;

                return (
                  <Card key={rel.id} t={t} style={{ borderLeft:`3px solid ${relTypeColor(rel.releaseType)}`, ...(isAcked ? {} : { boxShadow:`0 0 0 1px ${t.teal}30` }) }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                      <div style={{ flex:1, minWidth:200 }}>
                        {/* Release header */}
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:18, color:t.text, letterSpacing:'-0.02em' }}>v{rel.version}</span>
                          <span style={{ fontSize:9, padding:'2px 8px', borderRadius:3, background:relTypeBg(rel.releaseType), color:relTypeColor(rel.releaseType), fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.08em' }}>{rel.releaseType}</span>
                          {!isAcked && <span style={{ fontSize:9, padding:'2px 8px', borderRadius:3, background:`${t.teal}15`, color:t.teal, fontFamily:"'Fira Code',monospace", border:`1px solid ${t.teal}30` }}>AWAITING SIGN-OFF</span>}
                          {isAcked && <span style={{ fontSize:9, padding:'2px 8px', borderRadius:3, background:`${t.teal}08`, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>✓ ACKNOWLEDGED {fmt(rel.acknowledgedAt)}</span>}
                        </div>

                        <div style={{ fontSize:12, color:t.textDim, fontFamily:"'Fira Code',monospace", marginBottom:8 }}>
                          Released {fmtRel(rel.releasedAt)}
                        </div>

                        {/* Summary */}
                        <div style={{ fontSize:13, color:t.textDim, lineHeight:1.7, marginBottom: isExpanded ? '1rem' : 0 }}>
                          {isExpanded ? rel.releaseNotes : (rel.releaseNotes?.substring(0,160) + (rel.releaseNotes?.length > 160 ? '…' : ''))}
                        </div>

                        {/* Expanded: grouped changelog */}
                        {isExpanded && hasChangelog && (
                          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', marginTop:'0.75rem', padding:'0.85rem', background:t.surface2, borderRadius:6 }}>
                            {[
                              { key:'features',     icon:'✨', label:'Features',     color:t.teal  },
                              { key:'improvements', icon:'⚡', label:'Improvements', color:t.blue  },
                              { key:'fixes',        icon:'🐛', label:'Bug Fixes',    color:t.amber },
                              { key:'security',     icon:'🔒', label:'Security',     color:t.red   },
                            ].filter(g => (changelog[g.key]||[]).length > 0).map(g => (
                              <div key={g.key}>
                                <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.12em', color:g.color, marginBottom:6 }}>{g.icon} {g.label}</div>
                                {(changelog[g.key]||[]).map((item,i) => (
                                  <div key={i} style={{ display:'flex', gap:8, marginBottom:4 }}>
                                    <div style={{ width:4, height:4, borderRadius:'50%', background:g.color, marginTop:6, flexShrink:0 }}/>
                                    <div style={{ fontSize:12, color:t.textDim, lineHeight:1.5 }}>{item}</div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Media gallery */}
                        {isExpanded && (rel.media||[]).length > 0 && (
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:'0.75rem' }}>
                            {(rel.media||[]).map((m,i) => (
                              <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" style={{ display:'block', width:120, height:80, borderRadius:6, overflow:'hidden', border:`1px solid ${t.border}`, flexShrink:0 }}>
                                <img src={m.url} alt={m.caption||`screenshot-${i+1}`} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                              </a>
                            ))}
                          </div>
                        )}

                        {/* Expand/collapse */}
                        <button onClick={()=>setExpandedRel(isExpanded?null:rel.id)} style={{ marginTop:'0.5rem', fontSize:10, fontFamily:"'Fira Code',monospace", color:t.teal, background:'none', border:'none', cursor:'pointer', padding:0 }}>
                          {isExpanded ? '▲ Show less' : '▼ Show full release notes'}
                        </button>
                      </div>

                      {/* Right actions */}
                      <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end', flexShrink:0 }}>
                        {rel.fileName && (
                          <button
                            onClick={async () => {
                              try {
                                const tok = '' || '' || '';
                                alert('Demo mode — file downloads disabled.');
} catch {}
                            }}
                            style={{
                              display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:5,
                              border:`1px solid ${t.teal}40`, color:t.teal,
                              fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:500, cursor:'pointer', background:'none',
                            }}>
                            <I n="download" s={12} c={t.teal}/> {rel.fileName}
                          </button>
                        )}
                        {!isAcked && (
                          <button
                            onClick={()=>acknowledgeRelease(rel.id)}
                            disabled={signingOff===rel.id}
                            style={{
                              display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:5,
                              background: signingOff===rel.id ? `${t.teal}30` : t.teal,
                              border:'none', color: t.id==='dark'?'#050507':'#fff',
                              fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:600,
                              cursor: signingOff===rel.id ? 'not-allowed' : 'pointer',
                              transition:'all 0.15s',
                            }}>
                            <I n="check" s={12} c={t.id==='dark'?'#050507':'#fff'}/>
                            {signingOff===rel.id ? 'Signing…' : 'Acknowledge Release'}
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            }
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            BUGS TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'bugs' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <div style={{ fontSize:13, color:t.textDim }}>
                Report bugs or problems. We track every issue and keep you updated on fixes.
                {openBugs.length > 0 && <span style={{ color:t.red, marginLeft:8, fontFamily:"'Fira Code',monospace", fontSize:11 }}>{openBugs.length} open</span>}
              </div>
              <Btn icon="plus" t={t} onClick={()=>setBugModal(true)}>Report Bug</Btn>
            </div>

            {bugs.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="check" s={24} c={t.teal}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No bugs reported — all clear! 🎉</div>
              </Card>
            )}

            {['Open','In Progress','Resolved','WONT_FIX'].map(status => {
              const items = bugs.filter(b => b.status===status || (status==='Open'&&b.status==='OPEN') || (status==='In Progress'&&b.status==='IN_PROGRESS') || (status==='Resolved'&&b.status==='RESOLVED'));
              if (!items.length) return null;
              return (
                <div key={status}>
                  <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.2em', textTransform:'uppercase', color:statusColor(status), marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:statusColor(status) }}/>
                    {status.replace('_',' ')} · {items.length}
                  </div>
                  {items.map(bug => (
                    <Card key={bug.id} t={t} style={{ marginBottom:'0.5rem', borderLeft:`3px solid ${severityColor(bug.severity)}` }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                        <div style={{ flex:1, minWidth:180 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                            <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:13, color:t.text }}>{bug.title}</span>
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:`${severityColor(bug.severity)}18`, color:severityColor(bug.severity), fontFamily:"'Fira Code',monospace", border:`1px solid ${severityColor(bug.severity)}40`, textTransform:'uppercase' }}>{bug.severity}</span>
                            {bug._pending && <span style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>saving…</span>}
                          </div>
                          {bug.description && <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6, marginBottom:bug.stepsToRepro||bug.adminReply?8:0 }}>{bug.description}</div>}
                          {bug.stepsToRepro && (
                            <div style={{ fontSize:11, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginBottom:8, padding:'6px 10px', background:t.surface2, borderRadius:4 }}>
                              <div style={{ marginBottom:3, color:t.textDim, textTransform:'uppercase', letterSpacing:'0.1em', fontSize:9 }}>Steps to reproduce</div>
                              {bug.stepsToRepro}
                            </div>
                          )}
                          {/* Admin reply */}
                          {bug.adminReply && (
                            <div style={{ marginTop:8, padding:'8px 12px', background:`${t.teal}08`, border:`1px solid ${t.teal}20`, borderRadius:5 }}>
                              <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.teal, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>🛠 Qire Team Reply</div>
                              <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6 }}>{bug.adminReply}</div>
                              {bug.repliedAt && <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:4 }}>{fmt(bug.repliedAt)}</div>}
                            </div>
                          )}
                          <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:6 }}>Reported {fmt(bug.createdAt)}</div>
                        </div>
                        <Badge label={bug.status?.replace('_',' ')} t={t}/>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            FEATURE REQUESTS TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'requests' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <div style={{ fontSize:13, color:t.textDim }}>Suggest features or improvements. Vote on what matters most to you.</div>
              <Btn icon="plus" t={t} onClick={()=>setFrModal(true)}>New Request</Btn>
            </div>

            {requests.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="proposal" s={24} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No feature requests yet. Tell us what would make this product even better.</div>
              </Card>
            )}

            {['Under Review','Planned','In Progress','Completed','Declined'].map(status => {
              const items = requests.filter(r => r.status===status);
              if (!items.length) return null;
              return (
                <div key={status}>
                  <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.2em', textTransform:'uppercase', color:statusColor(status), marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:statusColor(status) }}/>
                    {status} · {items.length}
                  </div>
                  {items.map(req => (
                    <Card key={req.id} t={t} style={{ marginBottom:'0.5rem', borderLeft:`3px solid ${priorityColor(req.priority)}` }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:'0.85rem', flexWrap:'wrap' }}>
                        {/* Vote button */}
                        <button
                          onClick={()=>voteRequest(req.id)}
                          style={{
                            display:'flex', flexDirection:'column', alignItems:'center', gap:2,
                            padding:'8px 12px', borderRadius:6, cursor:'pointer', flexShrink:0,
                            background: req.votedByMe ? `${t.teal}20` : t.surface2,
                            border: `1px solid ${req.votedByMe ? t.teal : t.border}`,
                            color: req.votedByMe ? t.teal : t.textDim,
                            minWidth:44,
                          }}>
                          <span style={{ fontSize:12 }}>▲</span>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:13, color:req.votedByMe?t.teal:t.text }}>{req.votes||0}</span>
                        </button>
                        <div style={{ flex:1, minWidth:160 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                            <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:13, color:t.text }}>{req.title}</span>
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:`${priorityColor(req.priority)}18`, color:priorityColor(req.priority), fontFamily:"'Fira Code',monospace", border:`1px solid ${priorityColor(req.priority)}40`, textTransform:'uppercase' }}>{req.priority}</span>
                          </div>
                          {req.description && <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6 }}>{req.description}</div>}
                          <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:5 }}>{fmt(req.createdAt)}</div>
                        </div>
                        <Badge label={req.status} t={t}/>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TASKS TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'tasks' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <div style={{ fontSize:13, color:t.textDim }}>List what you need done. We'll pick these up and update the status as work progresses.</div>
              <Btn icon="plus" t={t} onClick={()=>setTaskModal(true)}>Request Task</Btn>
            </div>

            {clientTasks.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="check" s={24} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No task requests yet. Use the button above to tell us what you need done.</div>
              </Card>
            )}

            {['Requested','In Progress','Completed'].map(status => {
              const items = clientTasks.filter(tk => tk.status===status || (status==='In Progress'&&tk.status==='IN_PROGRESS') || (status==='Completed'&&tk.status==='COMPLETED'));
              if (!items.length) return null;
              return (
                <div key={status}>
                  <div style={{ fontSize:10, fontFamily:"'Fira Code',monospace", letterSpacing:'0.2em', textTransform:'uppercase', color:statusColor(status), marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:statusColor(status) }}/>
                    {status} · {items.length}
                  </div>
                  {items.map(task => (
                    <Card key={task.id} t={t} style={{ marginBottom:'0.5rem', borderLeft:`3px solid ${statusColor(task.status)}` }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:'0.85rem', flexWrap:'wrap' }}>
                        <div style={{ flex:1, minWidth:150 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                            <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:13, color:t.text }}>{task.title}</span>
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:`${priorityColor(task.priority)}18`, color:priorityColor(task.priority), fontFamily:"'Fira Code',monospace", border:`1px solid ${priorityColor(task.priority)}40`, textTransform:'uppercase' }}>{task.priority}</span>
                          </div>
                          {task.description && <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6 }}>{task.description}</div>}
                          <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", marginTop:5 }}>{fmt(task.createdAt)}</div>
                        </div>
                        <div style={{ display:'flex', gap:4, flexShrink:0, alignItems:'center' }}>
                          <Badge label={task.status} t={t}/>
                          {task.status==='Requested' && (
                            <button onClick={()=>updateProj(p=>({...p,clientTasks:(p.clientTasks||[]).filter(x=>x.id!==task.id)}))}
                              style={{ background:'none', border:'none', cursor:'pointer', color:t.textDimmer, padding:3 }}>
                              <I n="trash" s={12}/>
                            </button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            PROJECT FILES TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'files' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ fontSize:13, color:t.textDim }}>Design assets, strategy documents, reports and other project files from Qire Agency.</div>

            {projDelivs.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="download" s={28} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No project files yet. Design assets, strategy docs, reports and other files will appear here.</div>
              </Card>
            )}

            {projDelivs.map(d => (
              <Card key={d.id} t={t}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                  <div style={{ width:44, height:44, borderRadius:8, background:`${t.teal}15`, border:`1px solid ${t.teal}30`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <I n="download" s={18} c={t.teal}/>
                  </div>
                  <div style={{ flex:1, minWidth:150 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:14, color:t.text }}>{d.title||d.name}</span>
                      <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:`${t.teal}15`, color:t.teal, fontFamily:"'Fira Code',monospace", textTransform:'uppercase', letterSpacing:'0.06em' }}>{d.type||d.mimeType?.split('/')[1]||'FILE'}</span>
                    </div>
                    {(d.notes||d.description) && <div style={{ fontSize:12, color:t.textDim, lineHeight:1.7, marginBottom:6 }}>{d.notes||d.description}</div>}
                    <div style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace" }}>
                      Delivered {fmt(d.uploadedAt)}
                      {d.fileSize && ` · ${(d.fileSize/1024).toFixed(0)} KB`}
                    </div>
                  </div>
                  {d.fileName && (
                    <button onClick={async () => {
                      try {
                        const tok = '' || '' || '';
                        alert('Demo mode — file downloads disabled.');
} catch {}
                    }} style={{
                      display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:5,
                      background:`${t.teal}15`, color:t.teal,
                      fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:500, textDecoration:'none',
                      cursor:'pointer', border:'none', flexShrink:0,
                    }}>
                      <I n="download" s={12} c={t.teal}/> {d.fileName}
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}


        {/* ══════════════════════════════════════════════════════════════
            PROPOSALS TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'proposals' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ fontSize:13, color:t.textDim }}>
              Proposals from Qire Agency for <strong style={{ color:t.text }}>{proj.name}</strong>. Review, accept or decline below.
            </div>
            {myProposals.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="proposal" s={24} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No proposals for this project yet.</div>
              </Card>
            )}
            {myProposals.map(p => (
              <Card key={p.id} t={t} style={{ borderLeft:`3px solid ${['Accepted','ACCEPTED'].includes(p.status)?t.teal:['Declined','DECLINED'].includes(p.status)?t.red:t.amber}` }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                  <div style={{ flex:1, minWidth:180 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:14, color:t.text }}>{p.title}</span>
                      <Badge label={p.status} t={t}/>
                    </div>
                    {p.scope && <div style={{ fontSize:12, color:t.textDim, lineHeight:1.6, marginBottom:6 }}>{p.scope?.substring(0,180)}{p.scope?.length>180?'…':''}</div>}
                    <div style={{ fontSize:11, color:t.textDim }}>
                      Issued {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                      {p.validUntil && <span style={{ marginLeft:8 }}>· Valid until {new Date(p.validUntil).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end', flexShrink:0 }}>
                    {p.value > 0 && <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:24, fontStyle:"italic", color:t.teal }}>{p.currency||'USD'} {p.value?.toLocaleString()}</div>}
                    {onPreview && (
                      <button
                        onClick={()=>onPreview({html:buildProposalHTML&&buildProposalHTML(p, clients?.find(c=>String(c.id)===String(p.clientId))), title:`${p.title} · Qire Agency`, isProposal:true, proposalId:p.id, setProposals, initialAdminSig:p.adminSignature||'', initialClientSig:p.clientSignature||'', initialAdminSignedAt:p.adminSignedAt||'', initialClientSignedAt:p.clientSignedAt||p.signedAt||''})}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:5, background:`${t.amber}15`, border:`1px solid ${t.amber}40`, color:t.amber, cursor:'pointer', fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:500 }}
                      >
                        <I n="print" s={12} c={t.amber}/> Preview
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            CONTRACTS TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'contracts' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ fontSize:13, color:t.textDim }}>
              Contracts for <strong style={{ color:t.text }}>{proj.name}</strong>.
            </div>
            {projContracts.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="contracts" s={24} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No contracts for this project yet.</div>
              </Card>
            )}
            {projContracts.map(c => {
              const statusColor = ['Signed','SIGNED','Active','ACTIVE'].includes(c.status) ? t.teal : ['Expired','EXPIRED','Cancelled','CANCELLED'].includes(c.status) ? t.red : t.amber;
              return (
                <Card key={c.id} t={t} style={{ borderLeft:`3px solid ${statusColor}` }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                        <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:14, color:t.text }}>{c.title}</span>
                        <Badge label={c.status} t={t}/>
                        {c.type && <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, background:`${t.blue}15`, color:t.blue, fontFamily:"'Fira Code',monospace", textTransform:'uppercase' }}>{c.type}</span>}
                      </div>
                      <div style={{ fontSize:11, color:t.textDim }}>
                        {c.startDate && <span>From {new Date(c.startDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>}
                        {c.endDate && <span style={{ marginLeft:8 }}>→ {new Date(c.endDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>}
                        {!c.endDate && <span style={{ marginLeft:8, color:t.teal }}>· Ongoing</span>}
                      </div>
                      {c.notes && <div style={{ fontSize:11, color:t.textDimmer, marginTop:4, fontStyle:'italic' }}>{c.notes}</div>}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end', flexShrink:0 }}>
                      {c.value > 0 && <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:24, fontStyle:"italic", color:t.teal }}>{c.currency||'USD'} {c.value?.toLocaleString()}</div>}
                      {onPreview && (
                        <button
                          onClick={()=>onPreview({
                            html: buildContractHTML&&buildContractHTML(c, clients?.find(cl=>String(cl.id)===String(c.clientId))),
                            title:`${c.title} · Qire Agency`,
                            isContract:true, contractId:c.id, setContracts,
                            initialAdminSig: c.adminSignature||'',
                            initialClientSig: c.clientSignature||'',
                            initialAdminSignedAt: c.adminSignedAt||'',
                            initialClientSignedAt: c.clientSignedAt||c.signedAt||'',
                          })}
                          style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:5, background:`${t.teal}15`, border:`1px solid ${t.teal}40`, color:t.teal, cursor:'pointer', fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:500 }}
                        >
                          <I n="print" s={12} c={t.teal}/> Preview
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            MESSAGES TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'messages' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            {/* Thread */}
            <Card t={t} style={{ padding:0, overflow:'hidden', display:'flex', flexDirection:'column' }}>
              {/* Thread selector */}
              {(()=>{
                const devTeam=(team||[]).filter(u=>u.role==='developer'&&((proj.chatParticipants||proj.chatParticipantIds||[]).includes(u.id)||(proj.chatParticipants||proj.chatParticipantIds||[]).includes(Number(u.id))));
                return (
                  <div style={{padding:'0.75rem 1rem',borderBottom:`1px solid ${t.border}`,flexShrink:0}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:devTeam.length?8:0}}>
                      <div>
                        <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:15,color:t.text}}>{proj.name} · {msgThread==='admin'?'Qire Agency':'Dev Team'}</div>
                        <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",marginTop:2}}>{projMsgs.length} messages</div>
                      </div>
                      {unreadMsgs>0&&<span style={{fontSize:9,background:t.red,color:'#fff',borderRadius:8,padding:'2px 7px',fontFamily:"'Fira Code',monospace"}}>{unreadMsgs} unread</span>}
                    </div>
                    {(devTeam.length > 0 || true) && (
                      <div style={{display:'flex',gap:5}}>
                        {[{id:'admin',label:'Qire Agency',color:t.teal},{id:'dev',label: devTeam.length ? 'Dev Team · '+devTeam.map(u=>u.name.split(' ')[0]).join(', ') : 'Dev Team',color:t.blue}].map(th=>(
                          <button key={th.id} onClick={()=>setMsgThread(th.id)} style={{padding:'4px 12px',borderRadius:5,cursor:'pointer',fontSize:11,fontFamily:"'Space Grotesk',sans-serif",fontWeight:500,background:msgThread===th.id?th.color:'transparent',color:msgThread===th.id?(t.id==='dark'?'#050507':'#fff'):t.textDim,border:msgThread===th.id?'none':`1px solid ${t.border}`,transition:'all 0.15s',flexShrink:0}}>
                            {th.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ maxHeight:'50vh', overflowY:'auto', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.65rem' }} onClick={()=>setMenuMsg(null)}>
                {projMsgs.length === 0 && (
                  <div style={{ textAlign:'center', padding:'2rem', color:t.textDimmer, fontSize:13, fontStyle:'italic' }}>No messages yet. Start the conversation below.</div>
                )}
                {projMsgs.map((m, msgIdx) => {
                  const isMe      = m.fromId === `client-${clientId}`;
                  const isStarred = starredMsgs.includes(m.id);
                  const isHovered = hoveredMsg === m.id;
                  const menuOpen  = menuMsg === m.id;
                  const isEditing = editingMsg?.id === m.id;
                  const EDIT_LIMIT = 15 * 60 * 1000;
                  const ageMs     = m.createdAt ? Date.now() - new Date(m.createdAt).getTime() : Infinity;
                  const canEdit   = isMe && !m._pending && m.id && typeof m.id==='string' && m.id.length > 8 && ageMs < EDIT_LIMIT;
                  const tc        = msgThread === 'admin' ? t.teal : t.blue;
                  const isDelivMsg = m.body?.startsWith('📁') || m.body?.startsWith('📄') || m.body?.startsWith('📦');

                  // Grouping: same sender as previous message = grouped
                  const prev = projMsgs[msgIdx - 1];
                  const next = projMsgs[msgIdx + 1];
                  const isGrouped  = prev && prev.fromId === m.fromId;
                  const isLastInGroup = !next || next.fromId !== m.fromId;

                  // Date divider
                  const showDate = !prev || new Date(m.createdAt).toDateString() !== new Date(prev?.createdAt).toDateString();

                  const menuItems = [
                    { icon:'copy',    label:'Copy',                 action: ()=>copyMsgClient(m.body) },
                    { icon:'message', label:'Reply',                action: ()=>{ setReplyTo({ id:m.id, body:m.body, senderName:getMsgSenderName(m.fromId) }); setMenuMsg(null); } },
                    ...(canEdit ? [{ icon:'edit',  label:'Edit',   action: ()=>{ setEditingMsg({id:m.id,body:m.body}); setMenuMsg(null); } }] : []),
                    { icon:'star',    label:isStarred?'Unstar':'Star', action: ()=>{ toggleMsgStar(m.id); setMenuMsg(null); } },
                    ...(canEdit ? [{ icon:'trash', label:'Delete', action: ()=>deleteMsgClient(m.id), danger:true }] : []),
                  ];

                  const TickStatus = () => {
                    if (!isMe || m._pending || m._failed) return null;
                    const color = m.read ? tc : t.textDimmer;
                    return (
                      <span style={{display:'inline-flex',alignItems:'center',gap:1,verticalAlign:'middle'}}>
                        <svg width="14" height="9" viewBox="0 0 14 9" fill="none">
                          <path d="M1 4.5l2.5 2.5 4-6" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5 4.5l2.5 2.5 4-6" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    );
                  };

                  // Bubble corner radii — sharp corner towards consecutive messages from same sender
                  const br = isMe
                    ? `${isGrouped?'14px':'14px'} 4px ${isLastInGroup?'4px':'14px'} 14px`
                    : `4px ${isGrouped?'14px':'14px'} 14px ${isLastInGroup?'4px':'14px'}`;

                  return (
                    <React.Fragment key={m.id}>
                      {/* Date divider */}
                      {showDate && (
                        <div style={{display:'flex',alignItems:'center',gap:10,margin:'8px 0 4px'}}>
                          <div style={{flex:1,height:1,background:t.border}}/>
                          <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:'0.1em',textTransform:'uppercase',whiteSpace:'nowrap'}}>
                            {new Date(m.createdAt).toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'})}
                          </span>
                          <div style={{flex:1,height:1,background:t.border}}/>
                        </div>
                      )}

                      <div
                        style={{display:'flex', flexDirection:isMe?'row-reverse':'row', alignItems:'flex-end', gap:8,
                          marginTop: isGrouped ? 2 : 10,
                          opacity: m._pending ? 0.55 : 1, transition:'opacity 0.2s',
                        }}
                        onMouseEnter={()=>setHoveredMsg(m.id)}
                        onMouseLeave={()=>{ if(menuMsg!==m.id) setHoveredMsg(null); }}
                        onTouchStart={()=>setHoveredMsg(hoveredMsg===m.id?null:m.id)}
                      >
                        {/* Avatar — only on last in group */}
                        <div style={{width:26, flexShrink:0, alignSelf:'flex-end'}}>
                          {isLastInGroup ? (
                            <div style={{
                              width:26, height:26, borderRadius:'50%',
                              background: isMe ? `${tc}15` : t.id==='dark'?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.06)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:10, fontWeight:700, letterSpacing:'-0.02em',
                              color: isMe ? tc : t.textDim,
                              fontFamily:"'Space Grotesk',sans-serif",
                            }}>
                              {isMe ? (user.name?.[0]?.toUpperCase()||'C') : (m.sender?.name?.[0]?.toUpperCase()||'Q')}
                            </div>
                          ) : null}
                        </div>

                        {/* Bubble column */}
                        <div style={{maxWidth:'68%', display:'flex', flexDirection:'column', alignItems:isMe?'flex-end':'flex-start', position:'relative'}}>

                          {/* Sender name — only on first in group, non-me */}
                          {!isMe && !isGrouped && (
                            <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginBottom:3,textTransform:'uppercase',letterSpacing:'0.12em',paddingLeft:1}}>
                              {getMsgSenderName(m.fromId)}
                            </div>
                          )}

                          {/* Bubble wrapper — relative for corner action overlay */}
                          <div style={{position:'relative', display:'inline-block', maxWidth:'100%'}}>

                            {/* Corner action overlay — appears on hover */}
                            {(isHovered||menuOpen) && !isEditing && (
                              <div style={{
                                position:'absolute', top:6, [isMe?'left':'right']:6,
                                display:'flex', alignItems:'center', gap:1,
                                background: t.id==='dark' ? 'rgba(15,15,18,0.88)' : 'rgba(255,255,255,0.92)',
                                backdropFilter:'blur(6px)',
                                border:`1px solid ${t.border}`,
                                borderRadius:8, padding:'2px 3px',
                                boxShadow:'0 2px 8px rgba(0,0,0,0.12)',
                                zIndex:20,
                              }}
                              onMouseEnter={()=>setHoveredMsg(m.id)}
                              onMouseLeave={()=>{ if(menuMsg!==m.id) setHoveredMsg(null); }}>
                                {/* Reply */}
                                <button title="Reply" onClick={()=>{ setReplyTo({id:m.id,body:m.body,senderName:getMsgSenderName(m.fromId)}); setMenuMsg(null); }}
                                  style={{background:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:t.textDim,display:'flex',alignItems:'center'}}
                                  onMouseEnter={e=>e.currentTarget.style.color=tc}
                                  onMouseLeave={e=>e.currentTarget.style.color=t.textDim}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                                </button>
                                {/* Star */}
                                <button title={isStarred?'Unstar':'Star'} onClick={()=>{ toggleMsgStar(m.id); setMenuMsg(null); }}
                                  style={{background:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:isStarred?'#f59e0b':t.textDim,display:'flex',alignItems:'center'}}
                                  onMouseEnter={e=>e.currentTarget.style.color='#f59e0b'}
                                  onMouseLeave={e=>e.currentTarget.style.color=isStarred?'#f59e0b':t.textDim}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill={isStarred?'#f59e0b':'none'} stroke={isStarred?'#f59e0b':'currentColor'} strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                </button>
                                {/* Divider */}
                                <div style={{width:1,height:12,background:t.border,margin:'0 1px',flexShrink:0}}/>
                                {/* More */}
                                <button title="More" onClick={e=>{ e.stopPropagation(); setMenuMsg(menuOpen?null:m.id); }}
                                  style={{background:menuOpen?`${tc}15`:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:menuOpen?tc:t.textDim,display:'flex',alignItems:'center'}}
                                  onMouseEnter={e=>e.currentTarget.style.color=tc}
                                  onMouseLeave={e=>e.currentTarget.style.color=menuOpen?tc:t.textDim}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                                </button>
                              </div>
                            )}

                            {/* Context menu */}
                            {menuOpen && (
                              <div onClick={e=>e.stopPropagation()} style={{
                                position:'absolute',
                                ...(msgIdx < 3 ? {top:'100%',marginTop:4} : {bottom:'100%',marginBottom:4}),
                                [isMe?'right':'left']:0,
                                background: t.id==='dark' ? '#18181b' : '#fff',
                                border:`1px solid ${t.border}`,
                                borderRadius:8, padding:'4px', minWidth:144, zIndex:1000,
                                boxShadow: t.id==='dark'?'0 12px 32px rgba(0,0,0,0.55)':'0 8px 24px rgba(0,0,0,0.12)',
                              }}>
                                {menuItems.map((item,ii)=>(
                                  <React.Fragment key={item.label}>
                                    {ii>0&&item.danger&&<div style={{height:1,background:t.border,margin:'3px 6px'}}/>}
                                    <button onClick={item.action} style={{
                                      display:'flex',alignItems:'center',gap:8,width:'100%',
                                      padding:'6px 9px',borderRadius:5,border:'none',cursor:'pointer',
                                      background:'transparent',color:item.danger?t.red:t.text,
                                      fontSize:11,fontFamily:"'Space Grotesk',sans-serif",textAlign:'left',fontWeight:500,transition:'background 0.1s',
                                    }}
                                    onMouseEnter={e=>e.currentTarget.style.background=item.danger?`${t.red}10`:`${tc}0d`}
                                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                      <I n={item.icon} s={12} c={item.danger?t.red:t.textDim}/>{item.label}
                                    </button>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}

                            {/* The bubble */}
                            <div style={{
                              padding:'7px 11px',
                              borderRadius: br,
                              background: m._failed ? `${t.red}15`
                                : isStarred    ? `${t.amber||'#f59e0b'}0e`
                                : isDelivMsg   ? `${tc}0e`
                                : isMe         ? tc
                                : t.id==='dark' ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.042)',
                              border: m._failed  ? `1px solid ${t.red}40`
                                : isStarred      ? `1px solid ${t.amber||'#f59e0b'}30`
                                : isDelivMsg     ? `1px solid ${tc}30`
                                : isMe           ? 'none'
                                : `1px solid ${t.id==='dark'?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'}`,
                              color: isMe&&!isDelivMsg&&!m._failed ? (t.id==='dark'?'#050507':'#fff') : t.text,
                            }}>
                              {/* Reply quote */}
                              {m.replyTo && (
                                <div style={{
                                  borderLeft:`2px solid ${isMe?'rgba(255,255,255,0.3)':tc}`,
                                  background:isMe?'rgba(0,0,0,0.1)':t.id==='dark'?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)',
                                  borderRadius:'0 4px 4px 0',padding:'4px 8px',marginBottom:7,
                                }}>
                                  <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2,opacity:0.7,color:isMe?'inherit':tc}}>{m.replyTo.senderName}</div>
                                  <div style={{fontSize:11,lineHeight:1.5,overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',opacity:0.75}}>{m.replyTo.body}</div>
                                </div>
                              )}

                              {/* Edit mode */}
                              {isEditing ? (
                                <div>
                                  <textarea autoFocus defaultValue={editingMsg.body}
                                    onChange={e=>setEditingMsg(p=>({...p,body:e.target.value}))}
                                    onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();saveEditClient(m.id,editingMsg.body);}if(e.key==='Escape'){setEditingMsg(null);}}}
                                    style={{width:'100%',background:'transparent',border:`1px solid ${tc}50`,borderRadius:5,color:'inherit',fontSize:13,fontFamily:"'Space Grotesk',sans-serif",padding:'5px 7px',outline:'none',resize:'none',minHeight:38,lineHeight:1.5}}
                                    rows={2}
                                  />
                                  <div style={{display:'flex',gap:5,marginTop:5,justifyContent:'flex-end'}}>
                                    <button onClick={()=>setEditingMsg(null)} style={{fontSize:10,padding:'3px 9px',borderRadius:4,border:`1px solid ${t.border}`,background:'transparent',color:t.textDim,cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif"}}>Cancel</button>
                                    <button onClick={()=>saveEditClient(m.id,editingMsg.body)} style={{fontSize:10,padding:'3px 9px',borderRadius:4,border:'none',background:tc,color:t.id==='dark'?'#050507':'#fff',cursor:'pointer',fontWeight:600,fontFamily:"'Space Grotesk',sans-serif"}}>Save</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {m.body && <div style={{fontSize:13,lineHeight:1.65,whiteSpace:'pre-wrap'}}>{m.body}</div>}
                                  {(m.files||m.attachments||[]).length > 0 && (
                                    <div style={{marginTop: m.body ? 7 : 0}}>
                                      {(m.files||m.attachments||[]).map((att,ai)=>(
                                        <MsgAttachment key={ai} att={att} isMe={isMe} tc={tc} t={t}/>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}

                              {/* Footer — time + ticks, only on last in group or if failed */}
                              {!isEditing && (isLastInGroup || m._failed || m._pending) && (
                                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",marginTop:4,display:'flex',alignItems:'center',justifyContent:'flex-end',gap:3,opacity:0.45}}>
                                  {m._failed
                                    ? <span style={{display:'flex',alignItems:'center',gap:5,opacity:1}}>
                                        <span style={{color:t.red}}>⚠ Failed</span>
                                        <button onClick={()=>{setMessages(ms=>ms.filter(x=>x.id!==m.id));setMsgDraft(m.body);}}
                                          style={{background:t.red,border:'none',borderRadius:3,color:'#fff',fontSize:9,padding:'2px 7px',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontWeight:600}}>Resend</button>
                                      </span>
                                    : m._pending
                                      ? <span>Sending…</span>
                                      : <span style={{display:'flex',alignItems:'center',gap:3}}>
                                          {fmtMsgTime(m.createdAt)}{m.edited&&<span>· edited</span>}<TickStatus/>
                                        </span>
                                  }
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Star indicator */}
                          {isStarred && (
                            <div style={{fontSize:9,marginTop:2,paddingLeft:isMe?0:2,paddingRight:isMe?2:0,alignSelf:isMe?'flex-end':'flex-start',opacity:0.6}}>⭐</div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={msgEndRef}/>
              </div>

              {/* Reply preview bar */}
              {replyTo && (
                <div style={{ borderTop:`1px solid ${t.border}`, padding:'6px 0.75rem', display:'flex', alignItems:'center', gap:8, background:t.surface2, flexShrink:0 }}>
                  <div style={{ flex:1, borderLeft:`3px solid ${t.teal}`, paddingLeft:8 }}>
                    <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", color:t.teal, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:1 }}>Replying to {replyTo.senderName}</div>
                    <div style={{ fontSize:11, color:t.textDim, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:280 }}>{replyTo.body}</div>
                  </div>
                  <button onClick={()=>setReplyTo(null)} style={{ background:'none', border:'none', cursor:'pointer', color:t.textDimmer, padding:2, flexShrink:0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              )}

              {/* Pending file chips */}
              {pendingFiles.length > 0 && (
                <div style={{ borderTop:`1px solid ${t.border}`, padding:'8px 0.75rem', display:'flex', flexWrap:'wrap', gap:6, background:t.surface2, flexShrink:0 }}>
                  {pendingFiles.map((pf, idx) => (
                    <div key={idx} style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 8px', borderRadius:6, background:t.surface3, border:`1px solid ${t.border}`, maxWidth:180 }}>
                      {pf.previewUrl
                        ? <img src={pf.previewUrl} alt="" style={{ width:22, height:22, borderRadius:3, objectFit:'cover', flexShrink:0 }}/>
                        : <span style={{ fontSize:14 }}>{fileTypeIcon(pf.mimeType)}</span>
                      }
                      <span style={{ fontSize:10, color:t.text, fontFamily:"'Space Grotesk',sans-serif", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>{pf.name}</span>
                      <span style={{ fontSize:9, color:t.textDimmer, fontFamily:"'Fira Code',monospace", flexShrink:0 }}>{fmtFileSize(pf.size)}</span>
                      <button onClick={()=>removePendingFile(idx)} style={{ background:'none', border:'none', cursor:'pointer', color:t.textDimmer, padding:0, flexShrink:0, marginLeft:2 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                  {uploadingFiles && <span style={{ fontSize:10, color:t.textDimmer, alignSelf:'center', fontFamily:"'Fira Code',monospace" }}>Uploading…</span>}
                </div>
              )}

              {/* Compose */}
              <div style={{ borderTop:`1px solid ${t.border}`, padding:'0.65rem 0.75rem', display:'flex', gap:6, alignItems:'flex-end', flexShrink:0 }} className="q-msg-input-row">
                {/* Hidden file input */}
                <input ref={fileInputRef} type="file" multiple accept="*/*" style={{ display:'none' }} onChange={handleFilePick}/>
                {/* Paperclip button */}
                <button
                  onClick={()=>fileInputRef.current?.click()}
                  title="Attach file"
                  style={{ background:'none', border:`1px solid ${t.border}`, borderRadius:6, cursor:'pointer', padding:'7px 8px', color:t.textDim, flexShrink:0, display:'flex', alignItems:'center', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=t.teal;e.currentTarget.style.color=t.teal;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=t.border;e.currentTarget.style.color=t.textDim;}}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <textarea
                  value={msgDraft}
                  onChange={e=>setMsgDraft(e.target.value)}
                  onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); sendMessage(); } }}
                  placeholder="Type your message… (Enter to send)"
                  rows={2}
                  style={{ flex:1, background:t.surface2, border:`1px solid ${t.border}`, borderRadius:6, padding:'8px 10px', color:t.text, fontSize:13, fontFamily:"'Space Grotesk',sans-serif", resize:'none', outline:'none', lineHeight:1.5 }}
                />
                <button onClick={sendMessage} disabled={(!msgDraft.trim()&&pendingFiles.length===0)||sendingMsg||uploadingFiles} style={{
                  padding:'9px 14px', borderRadius:6, background:t.teal, border:'none',
                  color:t.id==='dark'?'#050507':'#fff', cursor:'pointer', flexShrink:0,
                  opacity:(!msgDraft.trim()&&pendingFiles.length===0)||sendingMsg||uploadingFiles?0.4:1, transition:'opacity 0.15s',
                }}>
                  {uploadingFiles
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/></svg>
                    : <I n="send" s={14} c={t.id==='dark'?'#050507':'#fff'}/>
                  }
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            INVOICES TAB
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'invoices' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:'0.25rem' }}>
              <div style={{ fontSize:12, color:t.textDim }}>Invoices for <strong style={{ color:t.text }}>{proj.name}</strong></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.6rem', marginBottom:'0.25rem' }}>
              {[
                { l:'Paid',          v:fmtUSD(paid),        c:t.teal  },
                { l:'Outstanding',   v:fmtUSD(outstanding), c:outstanding>0?t.amber:t.textDimmer },
                { l:'This Project',  v:projInvs.length,     c:t.textDim },
              ].map(s=>(
                <Card key={s.l} t={t} style={{ padding:'0.8rem', textAlign:'center' }}>
                  <div style={{ fontSize:9, fontFamily:"'Fira Code',monospace", letterSpacing:'0.15em', color:t.textDim, textTransform:'uppercase', marginBottom:4 }}>{s.l}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:400, fontStyle:"italic", color:s.c }}>{s.v}</div>
                </Card>
              ))}
            </div>

            {projInvs.length === 0 && (
              <Card t={t} style={{ textAlign:'center', padding:'2rem' }}>
                <I n="invoices" s={24} c={t.textDimmer}/>
                <div style={{ fontSize:13, color:t.textDimmer, marginTop:'0.75rem' }}>No invoices for this project yet.</div>
              </Card>
            )}

            {projInvs.map(inv => (
              <Card key={inv.id} t={t} style={{ borderLeft:`3px solid ${inv.status==='Paid'||inv.status==='PAID'?t.teal:inv.status==='Overdue'||inv.status==='OVERDUE'?t.red:t.border}` }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
                  <div style={{ flex:1, minWidth:180 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:600, fontSize:14, color:t.text }}>{inv.number}</span>
                      <Badge label={inv.status} t={t}/>
                    </div>
                    <div style={{ fontSize:11, color:t.textDim }}>
                      Issued {fmt(inv.issuedAt)}
                      {inv.dueAt && <span style={{ marginLeft:8 }}>· Due {fmt(inv.dueAt)}</span>}
                      {inv.paidAt && <span style={{ marginLeft:8, color:t.teal }}>· Paid {fmt(inv.paidAt)}</span>}
                    </div>
                    {inv.notes && <div style={{ fontSize:11, color:t.textDimmer, marginTop:4 }}>{inv.notes}</div>}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:400, fontSize:24, fontStyle:"italic", color:inv.status==='Paid'||inv.status==='PAID'?t.teal:inv.status==='Overdue'||inv.status==='OVERDUE'?t.red:t.text }}>
                      {inv.currency} {inv.amount?.toLocaleString()}
                    </div>
                    {onPreview && (
                      <button
                        onClick={()=>onPreview({
                          html: buildInvoiceHTML(inv, clients?.find(c=>String(c.id)===String(inv.clientId)), proj),
                          title: `${inv.number} · Qire Agency`,
                          isInvoice: true, invoiceId: inv.id, setInvoices,
                          initialAdminSig: inv.adminSignature||'',
                          initialClientSig: inv.clientSignature||'',
                          initialAdminSignedAt: inv.adminSignedAt||'',
                          initialClientSignedAt: inv.clientSignedAt||'',
                        })}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:5, background:`${t.teal}15`, border:`1px solid ${t.teal}40`, color:t.teal, cursor:'pointer', fontSize:11, fontFamily:"'Space Grotesk',sans-serif", fontWeight:500, flexShrink:0 }}
                      >
                        <I n="print" s={12} c={t.teal}/> Preview / Print
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      </>}

      {/* ══════════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════════ */}

      {/* Bug Report Modal */}
      <Modal open={bugModal} onClose={()=>setBugModal(false)} title="Report a Bug" t={t}>
        <div style={{ fontSize:12, color:t.textDim, marginBottom:'0.75rem' }}>Found something broken? Tell us exactly what happened so we can fix it fast.</div>
        <Field t={t} label="Bug Title" required value={bugForm.title} onChange={v=>setBugForm(f=>({...f,title:v}))} placeholder="e.g. Login fails with correct password, Export button doesn't work…"/>
        <Field t={t} label="Description" textarea value={bugForm.description} onChange={v=>setBugForm(f=>({...f,description:v}))} placeholder="What did you expect to happen? What actually happened?" rows={3}/>
        <Field t={t} label="Steps to Reproduce" textarea value={bugForm.stepsToRepro} onChange={v=>setBugForm(f=>({...f,stepsToRepro:v}))} placeholder="1. Go to Settings&#10;2. Click Export&#10;3. See error" rows={3}/>
        <Field t={t} label="Severity" options={['LOW','MEDIUM','HIGH','CRITICAL']} value={bugForm.severity} onChange={v=>setBugForm(f=>({...f,severity:v}))}/>
        <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end' }}>
          <Btn variant="secondary" t={t} onClick={()=>setBugModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={submitBug} disabled={!bugForm.title.trim()||loading.bug} icon="send">{loading.bug?'Submitting…':'Submit Bug Report'}</Btn>
        </div>
      </Modal>

      {/* Feature Request Modal */}
      <Modal open={frModal} onClose={()=>setFrModal(false)} title="Request a Feature" t={t}>
        <div style={{ fontSize:12, color:t.textDim, marginBottom:'0.75rem' }}>What would make this product better for you? The more detail, the better we can plan it.</div>
        <Field t={t} label="Feature Title" required value={frForm.title} onChange={v=>setFrForm(f=>({...f,title:v}))} placeholder="e.g. Add dark mode, Export to PDF, SMS notifications…"/>
        <Field t={t} label="Description" textarea value={frForm.description} onChange={v=>setFrForm(f=>({...f,description:v}))} placeholder="Describe the feature and the problem it solves for you." rows={4}/>
        <Field t={t} label="Priority" options={['Low','Medium','High']} value={frForm.priority} onChange={v=>setFrForm(f=>({...f,priority:v}))}/>
        <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end' }}>
          <Btn variant="secondary" t={t} onClick={()=>setFrModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={submitRequest} disabled={!frForm.title.trim()} icon="send">Submit Request</Btn>
        </div>
      </Modal>

      {/* Task Request Modal */}
      <Modal open={taskModal} onClose={()=>setTaskModal(false)} title="Request a Task" t={t}>
        <div style={{ fontSize:12, color:t.textDim, marginBottom:'0.75rem' }}>Describe what you need done. We'll review and start working on it.</div>
        <Field t={t} label="Task Title" required value={taskForm.title} onChange={v=>setTaskForm(f=>({...f,title:v}))} placeholder="e.g. Update pricing on the menu, Add new employee to POS, Redesign checkout flow…"/>
        <Field t={t} label="Description" textarea value={taskForm.description} onChange={v=>setTaskForm(f=>({...f,description:v}))} placeholder="Describe in detail what you need. The more context the better." rows={4}/>
        <Field t={t} label="Priority" options={['Low','Medium','High']} value={taskForm.priority} onChange={v=>setTaskForm(f=>({...f,priority:v}))}/>
        <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end' }}>
          <Btn variant="secondary" t={t} onClick={()=>setTaskModal(false)}>Cancel</Btn>
          <Btn t={t} onClick={submitTask} disabled={!taskForm.title.trim()} icon="send">Submit Request</Btn>
        </div>
      </Modal>

    </div>
  );
};
// ─── FIXED: ClientInvoices — Space Grotesk 700 for amounts (was Syne) ─────────
const ClientInvoices = ({ invoices, setInvoices, clients, projects, t, onPreview }) => (
  <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
    <SectionHeader eyebrow="Billing" title="Your Invoices" sub={`${invoices.length} invoice${invoices.length!==1?"s":""}`} t={t}/>
    {/* Cormorant Garamond stat cards — single letterSpacing */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
      {[
        {l:"Paid",        v:invoices.filter(i=>i.status==="Paid").reduce((a,i)=>a+i.amount,0),  c:t.teal},
        {l:"Outstanding", v:invoices.filter(i=>i.status!=="Paid").reduce((a,i)=>a+i.amount,0), c:t.amber},
        {l:"Invoices",    v:invoices.length,                                                      c:t.blue},
      ].map(s=><Card key={s.l} t={t} style={{padding:"1rem",textAlign:"center"}}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginBottom:5}}>{s.l}</div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontWeight:400,letterSpacing:"0.02em",fontStyle:"italic",color:s.c}}>{typeof s.v==="number"&&s.l!=="Invoices"?fmtUSD(s.v):s.v}</div>
      </Card>)}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {invoices.length===0&&<div style={{color:t.textDimmer,fontSize:15,padding:"2rem",textAlign:"center",fontStyle:"italic"}}>No invoices yet.</div>}
      {invoices.map(inv=>(
        <Card key={inv.id} t={t}>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                <span style={{fontFamily:"'Fira Code',monospace",fontSize:11,fontWeight:500,color:t.textDim}}>{inv.number}</span>
                <Badge label={inv.status} t={t}/>
              </div>
              <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>Issued {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'} · Due {inv.dueAt ? new Date(inv.dueAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}</div>
              {inv.notes&&<div style={{fontSize:11,color:t.textDimmer,marginTop:2,fontStyle:"italic",fontWeight:400}}>{inv.notes}</div>}
            </div>
            {/* FIXED: invoice amount — Space Grotesk 700 instead of Syne */}
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400,fontSize:26,fontStyle:"italic",color:inv.status==="Paid"?t.teal:inv.status==="Overdue"?t.red:t.text,flexShrink:0}}>
              {inv.currency} {inv.amount?.toLocaleString()}
            </div>
            <Btn variant="ghost" size="sm" t={t} onClick={()=>onPreview({
              html: buildInvoiceHTML(inv, clients.find(c=>c.id===inv.clientId), projects.find(p=>p.id===inv.projectId)),
              title: `${inv.number} · Qire Agency`,
              isInvoice: true, invoiceId: inv.id, setInvoices,
              initialAdminSig: inv.adminSignature||'',
              initialClientSig: inv.clientSignature||'',
            })} icon="print">{""}</Btn>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

// ─── MODULE: DEV PAYSLIPS ─────────────────────────────────────────────────────
const DevPayslips = ({ payroll, setPayroll, user, team, timeEntries, projects, t, onPreview }) => {

  // ── Enrich a payslip exactly like the admin Payroll view does ────────────────
  const enrichPayslip = (p) => {
    const member = team.find(u => String(u.id)===String(p.recipientId) || u.name===p.recipient);
    const ratePerHour = Number(member?.ratePerHour || p.ratePerHour || 0);

    // Time entries for this period
    const userId = member?.id || user.id;
    const periodEntries = p.period ? timeEntries.filter(e =>
      (String(e.userId)===String(userId) || e.userName===p.recipient) &&
      (e.date||"").slice(0,7) === p.period
    ) : [];
    const totalHrs    = periodEntries.reduce((a,e) => a + Number(e.hours), 0);
    const billableHrs = periodEntries.filter(e=>e.billable===true||e.billable==="true").reduce((a,e) => a + Number(e.hours), 0);

    // Auto breakdown from time entries grouped by project
    let autoBreakdown = [];
    if (ratePerHour && periodEntries.length > 0) {
      const byProject = {};
      periodEntries.forEach(e => {
        const proj = projects.find(pr => String(pr.id)===String(e.projectId));
        const key  = String(e.projectId);
        if (!byProject[key]) byProject[key] = { projectName: proj?.name || "Unknown Project", hours: 0, ratePerHour, amount: 0, _auto: true };
        byProject[key].hours  = Math.round((byProject[key].hours + Number(e.hours)) * 100) / 100;
        byProject[key].amount = Math.round(byProject[key].hours * ratePerHour);
      });
      autoBreakdown = Object.values(byProject);
    }

    // Manual rows stored in DB (not auto-generated)
    const storedBreakdown = Array.isArray(p.projectBreakdown) ? p.projectBreakdown : [];
    const manualRows = storedBreakdown.filter(r => !r._auto);

    // Auto rows first, manual additions on top
    const projectBreakdown = [...autoBreakdown, ...manualRows];

    const computedGross = projectBreakdown.reduce((a,b) => a + Number(b.amount||0), 0);
    const gross = computedGross || Number(p.gross) || 0;
    const rawDeductions = Number(p.deductions||0);

    // Deductions: stored manual list takes priority, else auto-calculate
    const storedDeductions = Array.isArray(p.deductionsList) && p.deductionsList.length > 0
      ? p.deductionsList : null;

    let deductionsList = storedDeductions || [];
    if (!storedDeductions) {
      if (member?.contractType==="employee") {
        const nssf = 420;
        const shif = Math.round(gross * 0.0275);
        const paye = gross > 24000 ? Math.round((gross - 24000) * 0.1) : 0;
        if (nssf) deductionsList.push({ label:"NSSF (Pension)",             amount: nssf });
        if (shif) deductionsList.push({ label:"SHA/SHIF (Health Insurance)", amount: shif });
        if (paye) deductionsList.push({ label:"PAYE (Income Tax)",           amount: paye });
      } else if (member?.withholdingTax && gross > 24000) {
        deductionsList.push({ label:"Withholding Tax (5% — WHT, remit to KRA)", amount: Math.round(gross * 0.05) });
      } else if (rawDeductions > 0) {
        deductionsList.push({ label:"Deductions", amount: rawDeductions });
      }
    }

    const totalDeductions = deductionsList.reduce((a,d) => a + d.amount, 0);
    return {
      ...p,
      ratePerHour,
      projectBreakdown,
      gross: computedGross || p.gross,
      deductions: totalDeductions || rawDeductions,
      deductionsList,
      net: gross - totalDeductions,
      totalHrs, billableHrs,
    };
  };

  const rawSlips  = payroll.filter(p => String(p.recipientId) === String(user.id) || p.recipient === user.name);
  const mySlips   = rawSlips.map(enrichPayslip);
  const totalPaid = mySlips.filter(p=>p.status==="Paid").reduce((a,p)=>a+p.net,0);
  const periods   = [...new Set(mySlips.map(p=>p.period))].sort().reverse();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader eyebrow="My Pay" title="Payslips" sub={`${mySlips.length} payslips · ${fmtKES(totalPaid)} total received`} t={t}/>
      {mySlips.length===0&&(
        <Card t={t}><div style={{color:t.textDimmer,fontSize:13,fontStyle:"italic",textAlign:"center",padding:"2rem"}}>No payslips yet. Your payslips will appear here once admin processes your pay.</div></Card>
      )}
      {periods.map(period=>{
        const slips=mySlips.filter(p=>p.period===period);
        return (
          <div key={period}>
            <div style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginBottom:"0.5rem",letterSpacing:"0.15em",textTransform:"uppercase"}}>{period}</div>
            {slips.map(p=>(
              <Card key={p.id} t={t} style={{marginBottom:"0.5rem"}}>
                <div style={{display:"flex",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                      <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>{p.period}</span>
                      <Badge label={p.status} t={t}/>
                    </div>
                    <div style={{fontSize:15,color:t.textDim}}>
                      Gross: {fmtKES(p.gross)} · Deductions: {fmtKES(p.deductions)} · Net: <strong style={{color:t.teal}}>{fmtKES(p.net)}</strong>
                    </div>
                    {/* Project breakdown pills */}
                    {p.projectBreakdown.length > 0 && (
                      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:5}}>
                        {p.projectBreakdown.map((br,i)=>(
                          <span key={i} style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.teal}15`,color:t.teal,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em"}}>
                            {br.projectName} · {br.hours}h · {fmtKES(br.amount)}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Deductions breakdown */}
                    {p.deductionsList.length > 0 && (
                      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>
                        {p.deductionsList.map((d,i)=>(
                          <span key={i} style={{fontSize:9,padding:"2px 7px",borderRadius:3,background:`${t.red}12`,color:t.red,fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em"}}>
                            {d.label}: -{fmtKES(d.amount)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{display:"flex",gap:12,marginTop:4,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>via {p.method}</span>
                      {p.totalHrs > 0 && <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{p.totalHrs.toFixed(1)}h logged · {p.ratePerHour ? `${fmtKES(p.ratePerHour)}/hr` : ""}</span>}
                      {p.paidAt && <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>Paid {new Date(p.paidAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>}
                    </div>
                  </div>
                  <Btn variant="secondary" size="sm" t={t} onClick={()=>onPreview({
                    html: buildPayslipHTML(p),
                    title: `Payslip · ${p.recipient} · ${p.period}`,
                    isPayslip: true, payslipId: p.id, setPayroll,
                    initialAdminSig: p.adminSignature||'',
                    initialAdminSignedAt: p.adminSignedAt||'',
                  })} icon="print">Download</Btn>
                </div>
              </Card>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ─── MODULE: DEV EARNINGS ─────────────────────────────────────────────────────
const DevEarnings = ({ payroll, timeEntries, projects, user, t }) => {
  const mySlips   = payroll.filter(p => String(p.recipientId) === String(user.id) || p.recipient === user.name);
  const myTime    = timeEntries.filter(e=>e.userId===user.id||e.userId===Number(user.id));
  const totalPaid = mySlips.filter(p=>p.status==="Paid").reduce((a,p)=>a+p.net,0);
  const pending   = mySlips.filter(p=>p.status==="Pending").reduce((a,p)=>a+p.net,0);
  const totalHrs  = myTime.reduce((a,e)=>a+e.hours,0);
  const byProject = projects.map(p=>{
    const hrs=myTime.filter(e=>e.projectId===p.id||e.projectId===Number(p.id)).reduce((a,e)=>a+e.hours,0);
    return {...p,hrs};
  }).filter(p=>p.hrs>0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <SectionHeader eyebrow="My Finances" title="Earnings" sub="Pay history and time summary" t={t}/>
      {/* FIXED: DevEarnings stat cards — Space Grotesk 700 instead of Syne */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"0.75rem"}}>
        {[
          {l:"Total Received", v:fmtKES(totalPaid), c:t.teal,  i:"finance"},
          {l:"Pending",        v:fmtKES(pending),   c:t.amber, i:"clock"},
          {l:"Hours Logged",   v:`${totalHrs.toFixed(1)}h`,    c:t.blue,  i:"timer"},
          {l:"Payslips",       v:String(mySlips.length),       c:t.textDim,i:"payroll"},
        ].map(s=>(
          <Card key={s.l} t={t} style={{padding:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",color:t.textDim,textTransform:"uppercase"}}>{s.l}</span>
              <I n={s.i} s={12} c={s.c}/>
            </div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:400,fontStyle:"italic",color:s.c}}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Pay history */}
      <Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Pay History</div>
        {mySlips.length===0&&<div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No pay records yet.</div>}
        {[...mySlips].sort((a,b)=>b.period.localeCompare(a.period)).map(p=>(
          <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.75rem 0",borderBottom:`1px solid ${t.border}`,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:t.text}}>{p.period}</div>
              <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{p.method}{p.paidAt?` · ${p.paidAt?toDateInput(p.paidAt):"—"}`:""}</div>
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <Badge label={p.status} t={t}/>
              {/* FIXED: pay amount — Space Grotesk 700 instead of Syne */}
              <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:15,color:p.status==="Paid"?t.teal:t.amber}}>{fmtKES(p.net)}</span>
            </div>
          </div>
        ))}
      </Card>

      {/* Hours by project */}
      {byProject.length>0&&<Card t={t}>
        <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>My Hours by Project</div>
        {byProject.map(p=>(
          <div key={p.id} style={{marginBottom:"0.85rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:14,color:t.text}}>{p.name}</span>
              <span style={{fontSize:11,fontFamily:"'Fira Code',monospace",color:t.teal}}>{p.hrs.toFixed(1)}h</span>
            </div>
            <ProgressBar pct={Math.round((p.hrs/totalHrs)*100)} t={t}/>
          </div>
        ))}
      </Card>}
    </div>
  );
};
// ─── FIXED: Messages — all Syne → Space Grotesk 700 ──────────────────────────
const Messages = ({ messages, setMessages, projects, setProjects, clients, letters, deliverables, setDeliverables, user, team, t, socket, typingUsers, onlineUsers }) => {
  const isAdmin  = user.role==="admin";
  const isClient = user.role==="client";
  const isDev    = user.role==="developer";

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const [selected,    setSelected]    = useState(null);
  const [newMsg,      setNewMsg]      = useState("");
  const [activeTab,   setActiveTab]   = useState("messages");
  const [showLetters, setShowLetters] = useState(false);
  const [addDeliv,    setAddDeliv]    = useState(false);
  const [delivForm,   setDelivForm]   = useState({title:"",type:"Document",link:"",notes:"",clientVisible:true});
  const [mobileView,  setMobileView]  = useState("threads");
  const messagesEndRef = useRef(null);
  const msgFileInputRef = useRef(null);
  const [hoveredMsg,    setHoveredMsg]   = useState(null);
  const [menuMsg,       setMenuMsg]      = useState(null);
  const [editingMsg,    setEditingMsg]   = useState(null);
  const [starredMsgs,   setStarredMsgs]  = useState(() => { try { return JSON.parse(''||'[]'); } catch{return[];} });
  const [replyTo,       setReplyTo]      = useState(null);
  // Attachments
  const [pendingMsgFiles,  setPendingMsgFiles]  = useState([]);
  const [uploadingMsgFiles,setUploadingMsgFiles] = useState(false);

  // ── Socket: join/leave thread room when selected thread changes ──────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    /* demo: no socket */
  }, [selected?.projectId, selected?.type]);

  // ── Typing: emit when user types, stop after 2s idle ────────────────────
  const typingTimer = useRef(null);
  const emitTyping = (isTyping) => {
    if (!selected) return;
    const room = selected.projectId ? `project-${selected.projectId}-${selected.type||'admin'}` : null;
    /* demo: no socket */
  };

  const handleMsgInput = (val) => {
    setNewMsg(val);
    emitTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => emitTyping(false), 2000);
  };

  // ── Get typing users for current room ────────────────────────────────────
  const currentRoom = selected?.projectId ? `project-${selected.projectId}-${selected.type||'admin'}` : null;
  const currentTypers = (typingUsers || {})[currentRoom] || [];
  const currentOnline = (onlineUsers || {})[currentRoom] || [];

  const clientAcc = isClient ? { clientId: user.id, username: user.portalUsername || user.username } : null;

  const threads = (() => {
    if(isAdmin){
      const list=[];
      projects.forEach(p=>{
        const client=clients.find(c=>c.id===p.clientId);
        ["admin","dev"].forEach(type=>{
          if(type==="dev"){
            const devs=team.filter(u=>u.role==="developer"&&((p.chatParticipants||[]).includes(u.id)||(p.chatParticipants||[]).includes(Number(u.id))));
            if(devs.length===0&&messages.filter(m=>m.projectId===p.id&&m.type==="dev").length===0) return;
          }
          const msgs=messages.filter(m=>m.projectId===p.id&&m.type===type);
          const last=msgs[msgs.length-1];
          const unread=msgs.filter(m=>!m.read&&m.fromId!==String(user.id)).length;
          list.push({projectId:p.id,type,name:p.name,sub:type==="admin"?`${client?.name||""} · Direct`:`Dev Team · ${team.filter(u=>u.role==="developer"&&(p.chatParticipants||[]).includes(u.id)).map(u=>u.name.split(" ")[0]).join(", ")||"No devs yet"}`,last,unread,proj:p});
        });
      });
      return list;
    }
    if(isClient){
      const myProjs=projects.filter(p=>String(p.clientId)===String(clientAcc?.clientId));
      return myProjs.flatMap(p=>{
        const results=[];
        const adminMsgs=messages.filter(m=>m.projectId===p.id&&m.type==="admin");
        results.push({projectId:p.id,type:"admin",name:p.name,sub:"Qire Agency · Direct",last:adminMsgs[adminMsgs.length-1],unread:adminMsgs.filter(m=>!m.read&&m.fromId!==`client-${clientAcc?.clientId}`).length});
        const devs=team.filter(u=>u.role==="developer"&&((p.chatParticipants||[]).includes(u.id)||(p.chatParticipants||[]).includes(Number(u.id))));
        const devMsgs=messages.filter(m=>m.projectId===p.id&&m.type==="dev");
        results.push({projectId:p.id,type:"dev",name:p.name,sub:devs.length?`Dev Team · ${devs.map(u=>u.name.split(" ")[0]).join(", ")}`:"Dev Team",last:devMsgs[devMsgs.length-1],unread:devMsgs.filter(m=>!m.read&&m.fromId!==`client-${clientAcc?.clientId}`).length});
        return results;
      });
    }
    // Developer: only assigned projects, dev thread only
    if(isDev){
      const myProjs=projects.filter(p=>
        p.assignee === user.id||
        (p.chatParticipants||[]).includes(user.id)||
        (p.chatParticipants||[]).includes(Number(user.id))
      );
      return myProjs.map(p=>{
        const devMsgs=messages.filter(m=>m.projectId===p.id&&m.type==="dev");
        return {projectId:p.id,type:"dev",name:p.name,sub:"Dev Team Thread",last:devMsgs[devMsgs.length-1],unread:devMsgs.filter(m=>!m.read&&m.fromId!==String(user.id)).length};
      });
    }
    // Sales / Support / Marketing: see all client-facing (admin) threads
    const list=[];
    projects.forEach(p=>{
      const client=clients.find(c=>c.id===p.clientId);
      const adminMsgs=messages.filter(m=>m.projectId===p.id&&m.type==="admin");
      const unread=adminMsgs.filter(m=>!m.read&&m.fromId!==String(user.id)).length;
      list.push({projectId:p.id,type:"admin",name:p.name,sub:`${client?.name||""} · Direct`,last:adminMsgs[adminMsgs.length-1],unread,proj:p});
    });
    return list;
  })();

  const threadKey = th => th?`${th.projectId}-${th.type}`:null;
  const selectedThread = threads.find(th=>threadKey(th)===threadKey(selected));
  const selectedProject = selected ? projects.find(p=>p.id===selected.projectId) : null;

  const threadMessages = selected
    ? messages.filter(m=>m.projectId===selected.projectId&&m.type===selected.type)
    : [];

  const delivKey = selected ? `${selected.projectId}-${selected.type}` : null;
  const threadDeliverables = delivKey ? (deliverables[delivKey]||[]) : [];

  const getSenderName = fromId => {
    // Check team first (covers real DB ids), fallback for legacy local id 1
    if(fromId===1||fromId==="1") return "Amon (Qire)";
    // Team member
    const u=team.find(u=>String(u.id)===String(fromId));
    if(u) return u.name;
    // Client — fromId stored as "client-{id}", strip prefix before lookup
    const rawId = String(fromId).startsWith('client-') ? String(fromId).slice(7) : String(fromId);
    const cl=clients.find(c=>String(c.id)===rawId);
    if(cl) return cl.name;
    return "Unknown";
  };

  const toggleStar = (id) => {
    setStarredMsgs(prev => {
      const next = prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id];
      
      return next;
    });
  };

  const deleteMessage = async (id) => {
    setMessages(ms => ms.filter(m => m.id !== id));
    setMenuMsg(null);
    try { await apiFetch(`/api/v1/messages/${id}`, { method:'DELETE' }); } catch{}
  };

  const saveEdit = async (id, newBody) => {
    if (!newBody.trim()) return;
    setMessages(ms => ms.map(m => m.id===id ? {...m, body:newBody, edited:true} : m));
    setEditingMsg(null);
    setMenuMsg(null);
    try { await apiFetch(`/api/v1/messages/${id}`, { method:'PATCH', body:JSON.stringify({body:newBody}) }); } catch{}
  };

  const copyMessage = (body) => {
    navigator.clipboard?.writeText(body).catch(()=>{});
    setMenuMsg(null);
  };

  const handleMsgFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    const previews = files.map(f => ({
      file: f, name: f.name, size: f.size, mimeType: f.type,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPendingMsgFiles(prev => [...prev, ...previews]);
  };

  const removePendingMsgFile = (idx) => {
    setPendingMsgFiles(prev => {
      const next = [...prev];
      if (next[idx]?.previewUrl) URL.revokeObjectURL(next[idx].previewUrl);
      next.splice(idx, 1);
      return next;
    });
  };

  const sendMessage = async (body) => {
    const text = body||newMsg;
    if(!text.trim() && pendingMsgFiles.length === 0) return;
    if(!selected) return;
    const fromId = isClient?`client-${clientAcc?.clientId}`:String(user.id);
    const token = isClient
      ? ('' || '' || '')
      : ('' || '' || '');

    const tmpId = Date.now();
    const tmp = {
      id: tmpId, projectId: selected.projectId, type: selected.type,
      fromId, body: text.trim(), createdAt: new Date().toISOString(), read: false,
      _sending: true,
      attachments: pendingMsgFiles.map(pf => ({ fileName: pf.file.name, fileSize: pf.file.size, mimeType: pf.file.type })),
      ...(replyTo ? { replyTo: { id: replyTo.id, body: replyTo.body, senderName: replyTo.senderName } } : {}),
    };
    setMessages(ms=>[...ms,tmp]);
    setNewMsg("");
    setReplyTo(null);

    try {
      // Send files + message body together as multipart/form-data
      const endpoint = isClient ? '/api/v1/messages/send' : '/api/v1/messages';
      const fd = new FormData();
      fd.append('body', text.trim());
      fd.append('type', (selected.type||'admin').toUpperCase());
      if (selected.projectId) fd.append('projectId', String(selected.projectId));
      if (replyTo) fd.append('replyTo', JSON.stringify({ id:replyTo.id, body:replyTo.body, senderName:replyTo.senderName }));
      pendingMsgFiles.forEach(pf => fd.append('files', pf.file));
      setPendingMsgFiles([]);

      // Demo: message already in state via tmpId, just clear sending flag
      setMessages(ms=>ms.map(m=>m.id===tmpId ? {...m,_sending:false} : m));
    } catch(e) {
      // API failed — keep the message visible but mark as failed so user knows
      console.warn('Message send failed:', e.message);
      setMessages(ms=>ms.map(m=>m.id===tmpId ? {...m,_sending:false,_failed:true} : m));
    }
  };

  const sendLetter = (letter) => {
    setShowLetters(false);
    sendMessage(`📄 Letter attached: "${letter.subject}"\n\n${letter.body}`);
  };

  const addDeliverable = () => {
    if(!delivForm.title||!delivKey) return;
    const entry={...delivForm, id:Date.now(), uploadedBy:user.id, uploadedAt:new Date().toISOString()};
    setDeliverables(d=>({...d,[delivKey]:[...(d[delivKey]||[]),entry]}));
    sendMessage(`📁 Project file shared: "${delivForm.title}"${delivForm.link?`\nLink: ${delivForm.link}`:""}`);
    setDelivForm({title:"",type:"Document",link:"",notes:"",clientVisible:true});
    setAddDeliv(false);
  };

  const removeDeliverable = (id) => {
    setDeliverables(d=>({...d,[delivKey]:(d[delivKey]||[]).filter(x=>x.id!==id)}));
  };

  const toggleParticipant = (devId) => {
    if(!selectedProject) return;
    const current = selectedProject.chatParticipants||[];
    const updated  = current.includes(devId)||current.includes(Number(devId))
      ? current.filter(id=>id!==devId&&id!==Number(devId))
      : [...current, devId];
    setProjects(ps=>ps.map(p=>p.id===selectedProject.id?{...p,chatParticipants:updated}:p));
  };

  useEffect(()=>{ messagesEndRef.current?.scrollIntoView({behavior:"smooth"}); },[threadMessages.length,selected]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if(!selected) return;
    const myFromId = isClient ? `client-${clientAcc?.clientId}` : String(user.id);
    // Only mark as read messages that were NOT sent by me
    setMessages(ms=>ms.map(m=>
      m.projectId===selected.projectId && m.type===selected.type && m.fromId!==myFromId
        ? {...m,read:true}
        : m
    ));
  },[selected]);
  useEffect(()=>{ setActiveTab("messages"); setAddDeliv(false); },[selected?.projectId,selected?.type]);

  // Mark messages as read on backend when a thread is opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selected) return;
    const unread = messages.filter(m =>
      m.projectId === selected.projectId &&
      m.type === selected.type &&
      !m.read &&
      m.fromId !== (isClient ? `client-${clientAcc?.clientId}` : String(user.id))
    );
    if (unread.length === 0) return;
    // Mark locally immediately
    setMessages(ms => ms.map(m =>
      m.projectId === selected.projectId && m.type === selected.type ? { ...m, read: true } : m
    ));
    // Persist to backend — fire and forget, no need to await
    const token = isClient ? '' : null;
    const readEndpoint = isClient ? 'read-client' : 'read';
    unread.forEach(m => {
      if (m.id && typeof m.id === 'string' && m.id.length > 10) { // real DB id, not tmp
        apiFetch(`/api/v1/messages/${m.id}/${readEndpoint}`, { method: 'PATCH' }, token).catch(() => {});
      }
    });
  }, [selected?.projectId, selected?.type]);

  const handleSelectThread = (th) => {
    setSelected({projectId:th.projectId,type:th.type});
    setMobileView("chat");
  };

  const fmtTime = iso => {
    if(!iso) return "";
    const d=new Date(iso);
    return d.toLocaleDateString("en-GB",{day:"numeric",month:"short"})+" · "+d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  };

  const delivTypes=["Document","Design","Source Code","Build / APK","Report","Invoice","Letter","Other"];
  const typeIcon={Document:"letters",Design:"edit","Source Code":"projects","Build / APK":"download",Report:"chart",Invoice:"invoices",Letter:"mail",Other:"receipt"};

  return (
    <div style={{
      display:"flex",
      flexDirection: isMobile ? "column" : "row",
      ...(isMobile && mobileView==="chat" ? {
        flex:1, minHeight:0,
      } : isMobile ? {
        flex:1, minHeight:0,
        overflow: "hidden",
      } : {
        height: "calc(100vh - 4rem)",
      }),
      background: t.surface,
      borderRadius: isMobile ? 0 : 8,
      border: isMobile ? "none" : `1px solid ${t.border}`,
      overflow: "hidden",
    }} className="fade-up">

      {/* ── Thread list ── */}
      {(!isMobile || mobileView==="threads") && (
      <div style={{width: isMobile ? "100%" : 250, flexShrink:0, borderRight: isMobile ? "none" : `1px solid ${t.border}`, borderBottom: isMobile ? `1px solid ${t.border}` : "none", display:"flex", flexDirection:"column", flex: isMobile ? 1 : "unset"}}>
        <div style={{padding:"1rem",borderBottom:`1px solid ${t.border}`,flexShrink:0}}>
          <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>Messages</div>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginTop:2}}>
            {isAdmin?"All project threads":isClient?"Your threads":"Your dev threads"}
          </div>
        </div>
        <div style={{flex:1, overflowY:"auto"}}>
          {threads.length===0&&<div style={{padding:"1rem",color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No threads yet.</div>}
          {[
            {type:"admin", label:"Direct", color:t.teal},
            {type:"dev",   label:"Dev Team", color:t.blue},
          ].map(group => {
            const groupThreads = threads.filter(th => th.type === group.type);
            if (groupThreads.length === 0) return null;
            return (
              <div key={group.type}>
                {/* Section header */}
                <div style={{padding:"0.5rem 1rem 0.3rem",display:"flex",alignItems:"center",gap:8,position:"sticky",top:0,background:t.surface,zIndex:1}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:group.color,flexShrink:0}}/>
                  <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.15em",textTransform:"uppercase",color:group.color,fontWeight:600}}>{group.label}</span>
                  <div style={{flex:1,height:1,background:t.border}}/>
                  {groupThreads.reduce((a,th)=>a+(th.unread||0),0)>0&&(
                    <span style={{fontSize:9,background:t.red,color:"#fff",borderRadius:8,padding:"1px 5px",fontFamily:"'Fira Code',monospace"}}>
                      {groupThreads.reduce((a,th)=>a+(th.unread||0),0)}
                    </span>
                  )}
                </div>
                {groupThreads.map(th=>{
                  const key=threadKey(th);
                  const isActive=threadKey(selected)===key;
                  const tc=group.color;
                  return (
                    <div key={key} onClick={()=>handleSelectThread(th)}
                      style={{padding:"0.75rem 1rem",cursor:"pointer",background:isActive?`${tc}12`:"transparent",borderBottom:`1px solid ${t.border}`,borderLeft:isActive?`3px solid ${tc}`:"3px solid transparent",transition:"all 0.15s"}}
                      onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=`${tc}06`;}}
                      onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                        <span style={{fontSize:12,fontWeight:600,color:isActive?tc:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,paddingRight:4}}>{th.name}</span>
                        {th.unread>0&&<span style={{fontSize:9,background:t.red,color:"#fff",borderRadius:8,padding:"1px 5px",flexShrink:0,fontFamily:"'Fira Code',monospace"}}>{th.unread}</span>}
                      </div>
                      <div style={{fontSize:10,color:t.textDimmer,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:th.last?2:0}}>{th.sub}</div>
                      {th.last&&<div style={{fontSize:10,color:t.textDimmer,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontStyle:"italic"}}>{th.last.body}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ── Main area ── */}
      {(!isMobile || mobileView==="chat") && (
        !selected ? (
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"0.75rem"}}>
            <I n="message" s={28} c={t.textDimmer}/>
            <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text}}>Select a thread</div>
            <div style={{fontSize:14,color:t.textDimmer,fontWeight:400}}>Choose a project thread from the left</div>
          </div>
        ) : (
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,minHeight:0,height:"100%",overflow:"hidden"}}>

          {/* Thread header */}
          <div style={{padding: isMobile ? "0.5rem 0.75rem" : "0.85rem 1.25rem", borderBottom:`1px solid ${t.border}`,flexShrink:0}}>
            {isMobile ? (
              /* Mobile: single compact row — back | title+badge | tabs */
              <div style={{display:"flex",alignItems:"center",gap:6,overflow:"hidden"}}>
                <button onClick={()=>setMobileView("threads")} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:"2px 4px 2px 0",flexShrink:0,display:"flex",alignItems:"center"}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                  <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedThread?.name}</div>
                </div>
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,flexShrink:0,whiteSpace:"nowrap",
                  background:selected.type==="admin"?`${t.teal}18`:`${t.blue}18`,
                  color:selected.type==="admin"?t.teal:t.blue,
                  fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase",
                  border:`1px solid ${selected.type==="admin"?t.teal+"40":t.blue+"40"}`}}>
                  {selected.type==="admin"?"Direct":"Dev"}
                </span>
                {[
                  {id:"messages", label:"Messages", icon:"message"},
                  {id:"files", label:"Files", icon:"folder"},
                  ...(isAdmin&&selected.type==="dev"?[{id:"participants",label:"Manage",icon:"team"}]:[]),
                ].map(tab=>(
                  <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
                    display:"flex",alignItems:"center",gap:4,
                    padding:"4px 8px",borderRadius:5,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,
                    background:activeTab===tab.id?t.teal:"transparent",
                    color:activeTab===tab.id?(t.id==="dark"?"#050507":"#fff"):t.textDim,
                    border:activeTab===tab.id?"none":`1px solid ${t.border}`,
                    fontSize:10,fontFamily:"'Space Grotesk',sans-serif",fontWeight:activeTab===tab.id?600:400,
                  }}>
                    <I n={tab.icon} s={10} c="currentColor"/>
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : (
              /* Desktop: original two-row layout */
              <>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:14,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedThread?.name}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2,flexWrap:"nowrap",overflow:"hidden"}}>
                      <span style={{fontSize:9,padding:"2px 7px",borderRadius:3,flexShrink:0,whiteSpace:"nowrap",
                        background:selected.type==="admin"?`${t.teal}18`:`${t.blue}18`,
                        color:selected.type==="admin"?t.teal:t.blue,
                        fontFamily:"'Fira Code',monospace",letterSpacing:"0.06em",textTransform:"uppercase",
                        border:`1px solid ${selected.type==="admin"?t.teal+"40":t.blue+"40"}`}}>
                        {selected.type==="admin"?"Direct":"Dev"}
                      </span>
                      {selected.type==="dev"&&selectedProject&&(
                        <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {team.filter(u=>u.role==="developer"&&((selectedProject.chatParticipants||[]).includes(u.id)||(selectedProject.chatParticipants||[]).includes(Number(u.id)))).map(u=>u.name.split(" ")[0]).join(", ")||"No devs yet"}
                        </span>
                      )}
                      {selected.type==="admin"&&(
                        <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          Admin ↔ Client
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:2}}>
                  {[
                    {id:"messages",    label:"Messages",    icon:"message"},
                    {id:"files",label:"Project Files",icon:"folder"},
                    ...(isAdmin&&selected.type==="dev"?[{id:"participants",label:"Manage",icon:"team"}]:[]),
                  ].map(tab=>(
                    <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
                      display:"flex",alignItems:"center",gap:5,
                      padding:"5px 10px",borderRadius:5,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,
                      background:activeTab===tab.id?t.teal:"transparent",
                      color:activeTab===tab.id?(t.id==="dark"?"#050507":"#fff"):t.textDim,
                      border:activeTab===tab.id?"none":`1px solid ${t.border}`,
                      fontSize:11,fontFamily:"'Space Grotesk',sans-serif",fontWeight:activeTab===tab.id?600:400,
                    }}>
                      <I n={tab.icon} s={11} c="currentColor"/>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── MESSAGES TAB ── */}
          {activeTab==="messages"&&<>
            <div style={{flex:1,overflowY:"auto",padding:"1rem",display:"flex",flexDirection:"column",gap:"0.75rem"}} onClick={()=>setMenuMsg(null)}>
              {threadMessages.length===0&&(
                <div style={{textAlign:"center",color:t.textDimmer,fontSize:12,fontStyle:"italic",marginTop:"2rem"}}>No messages yet. Start the conversation!</div>
              )}
              {threadMessages.map((m, msgIdx)=>{
                const isMe=isAdmin?(m.fromId===String(user.id)||m.fromId==="1"):isClient?m.fromId===`client-${String(clientAcc?.clientId)}`:m.fromId===String(user.id);
                const tc=selected.type==="admin"?t.teal:t.blue;
                const isDelivMsg=m.body.startsWith("📦")||m.body.startsWith("📄");
                const isStarred=starredMsgs.includes(m.id);
                const isHovered=hoveredMsg===m.id;
                const menuOpen=menuMsg===m.id;
                const EDIT_LIMIT_MS = 15 * 60 * 1000;
                const ageMs = m.createdAt ? Date.now() - new Date(m.createdAt).getTime() : Infinity;
                const canEdit = isMe && !m._sending && m.id && typeof m.id==='string' && ageMs < EDIT_LIMIT_MS;
                const isEditing=editingMsg?.id===m.id;

                // Grouping
                const prev = threadMessages[msgIdx - 1];
                const next = threadMessages[msgIdx + 1];
                const isGrouped = prev && prev.fromId === m.fromId;
                const isLastInGroup = !next || next.fromId !== m.fromId;
                const showDate = !prev || new Date(m.createdAt).toDateString() !== new Date(prev?.createdAt).toDateString();

                const TickStatus = () => {
                  if(!isMe||m._sending||m._failed) return null;
                  const readByOther = !!m.read;
                  const color = readByOther ? t.teal : t.textDimmer;
                  return (
                    <span style={{display:'inline-flex',alignItems:'center',gap:1,marginLeft:2,verticalAlign:'middle'}}>
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                        <path d="M1 5l3 3 5-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 5l3 3 5-7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  );
                };

                const menuItems = [
                  { icon:'copy',    label:'Copy',   action:()=>copyMessage(m.body) },
                  { icon:'message', label:'Reply',  action:()=>{ setReplyTo({id:m.id, body:m.body, senderName:getSenderName(m.fromId)}); setMenuMsg(null); } },
                  ...(canEdit ? [{ icon:'edit', label:'Edit', action:()=>{setEditingMsg({id:m.id,body:m.body});setMenuMsg(null);} }] : []),
                  { icon:'star',    label:isStarred?'Unstar':'Star', action:()=>{toggleStar(m.id);setMenuMsg(null);} },
                  ...(canEdit ? [{ icon:'trash', label:'Delete', action:()=>deleteMessage(m.id), danger:true }] : []),
                ];

                const br = isMe
                  ? `${isGrouped?'14px':'14px'} 4px ${isLastInGroup?'4px':'14px'} 14px`
                  : `4px ${isGrouped?'14px':'14px'} 14px ${isLastInGroup?'4px':'14px'}`;

                return (
                  <React.Fragment key={m.id}>
                    {/* Date divider */}
                    {showDate && (
                      <div style={{display:'flex',alignItems:'center',gap:10,margin:'8px 0 4px'}}>
                        <div style={{flex:1,height:1,background:t.border}}/>
                        <span style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:'0.1em',textTransform:'uppercase',whiteSpace:'nowrap'}}>
                          {new Date(m.createdAt).toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'})}
                        </span>
                        <div style={{flex:1,height:1,background:t.border}}/>
                      </div>
                    )}

                    <div
                      style={{display:'flex',flexDirection:isMe?'row-reverse':'row',alignItems:'flex-end',gap:8,
                        marginTop:isGrouped?2:10,
                        opacity:m._sending?0.55:1,transition:'opacity 0.2s',
                      }}
                      onMouseEnter={()=>setHoveredMsg(m.id)}
                      onMouseLeave={()=>{ if(menuMsg!==m.id) setHoveredMsg(null); }}
                      onTouchStart={()=>setHoveredMsg(hoveredMsg===m.id?null:m.id)}
                    >
                      {/* Avatar — only on last in group */}
                      <div style={{width:26,flexShrink:0,alignSelf:'flex-end'}}>
                        {isLastInGroup ? (
                          <div style={{
                            width:26,height:26,borderRadius:'50%',
                            background:isMe?`${tc}15`:t.id==='dark'?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.06)',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:10,fontWeight:700,letterSpacing:'-0.02em',
                            color:isMe?tc:t.textDim,
                            fontFamily:"'Space Grotesk',sans-serif",
                          }}>
                            {isMe?(user.name?.[0]?.toUpperCase()||'A'):(m.sender?.name?.[0]?.toUpperCase()||getSenderName(m.fromId)?.[0]?.toUpperCase()||'Q')}
                          </div>
                        ) : null}
                      </div>

                      {/* Bubble column */}
                      <div style={{maxWidth:'68%',display:'flex',flexDirection:'column',alignItems:isMe?'flex-end':'flex-start',position:'relative'}}>

                        {/* Sender name — first in group, non-me only */}
                        {!isMe&&!isGrouped&&(
                          <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.textDimmer,marginBottom:3,textTransform:'uppercase',letterSpacing:'0.12em',paddingLeft:1}}>
                            {getSenderName(m.fromId)}
                          </div>
                        )}

                        {/* Bubble wrapper */}
                        <div style={{position:'relative',display:'inline-block',maxWidth:'100%'}}>

                          {/* Corner action overlay */}
                          {(isHovered||menuOpen)&&!isEditing&&(
                            <div style={{
                              position:'absolute',top:6,[isMe?'left':'right']:6,
                              display:'flex',alignItems:'center',gap:1,
                              background:t.id==='dark'?'rgba(15,15,18,0.88)':'rgba(255,255,255,0.92)',
                              backdropFilter:'blur(6px)',
                              border:`1px solid ${t.border}`,
                              borderRadius:8,padding:'2px 3px',
                              boxShadow:'0 2px 8px rgba(0,0,0,0.12)',
                              zIndex:20,
                            }}
                            onMouseEnter={()=>setHoveredMsg(m.id)}
                            onMouseLeave={()=>{ if(menuMsg!==m.id) setHoveredMsg(null); }}>
                              <button title="Reply" onClick={()=>{setReplyTo({id:m.id,body:m.body,senderName:getSenderName(m.fromId)});setMenuMsg(null);}}
                                style={{background:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:t.textDim,display:'flex',alignItems:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.color=tc}
                                onMouseLeave={e=>e.currentTarget.style.color=t.textDim}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                              </button>
                              <button title={isStarred?'Unstar':'Star'} onClick={()=>{toggleStar(m.id);setMenuMsg(null);}}
                                style={{background:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:isStarred?'#f59e0b':t.textDim,display:'flex',alignItems:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.color='#f59e0b'}
                                onMouseLeave={e=>e.currentTarget.style.color=isStarred?'#f59e0b':t.textDim}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill={isStarred?'#f59e0b':'none'} stroke={isStarred?'#f59e0b':'currentColor'} strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                              </button>
                              <div style={{width:1,height:12,background:t.border,margin:'0 1px',flexShrink:0}}/>
                              <button title="More" onClick={e=>{e.stopPropagation();setMenuMsg(menuOpen?null:m.id);}}
                                style={{background:menuOpen?`${tc}15`:'none',border:'none',cursor:'pointer',padding:'3px 5px',borderRadius:5,color:menuOpen?tc:t.textDim,display:'flex',alignItems:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.color=tc}
                                onMouseLeave={e=>e.currentTarget.style.color=menuOpen?tc:t.textDim}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                              </button>
                            </div>
                          )}

                          {/* Context menu */}
                          {menuOpen&&(
                            <div onClick={e=>e.stopPropagation()} style={{
                              position:'absolute',
                              ...(msgIdx<3?{top:'100%',marginTop:4}:{bottom:'100%',marginBottom:4}),
                              [isMe?'right':'left']:0,
                              background:t.id==='dark'?'#18181b':'#fff',
                              border:`1px solid ${t.border}`,
                              borderRadius:8,padding:'4px',minWidth:144,zIndex:1000,
                              boxShadow:t.id==='dark'?'0 12px 32px rgba(0,0,0,0.55)':'0 8px 24px rgba(0,0,0,0.12)',
                            }}>
                              {menuItems.map((item,ii)=>(
                                <React.Fragment key={item.label}>
                                  {ii>0&&item.danger&&<div style={{height:1,background:t.border,margin:'3px 6px'}}/>}
                                  <button onClick={item.action} style={{
                                    display:'flex',alignItems:'center',gap:8,width:'100%',
                                    padding:'6px 9px',borderRadius:5,border:'none',cursor:'pointer',
                                    background:'transparent',color:item.danger?t.red:t.text,
                                    fontSize:11,fontFamily:"'Space Grotesk',sans-serif",textAlign:'left',fontWeight:500,transition:'background 0.1s',
                                  }}
                                  onMouseEnter={e=>e.currentTarget.style.background=item.danger?`${t.red}10`:`${tc}0d`}
                                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                    <I n={item.icon} s={12} c={item.danger?t.red:t.textDim}/>{item.label}
                                  </button>
                                </React.Fragment>
                              ))}
                            </div>
                          )}

                          {/* Bubble */}
                          <div style={{
                            padding:'7px 11px',
                            borderRadius:br,
                            background:m._failed?`${t.red}15`:isStarred?`${t.amber||'#f59e0b'}0e`:isDelivMsg?`${tc}0e`:isMe?tc:t.id==='dark'?'rgba(255,255,255,0.055)':'rgba(0,0,0,0.042)',
                            border:m._failed?`1px solid ${t.red}40`:isStarred?`1px solid ${t.amber||'#f59e0b'}30`:isDelivMsg?`1px solid ${tc}30`:isMe?'none':`1px solid ${t.id==='dark'?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'}`,
                            color:isMe&&!isDelivMsg&&!m._failed?(t.id==='dark'?'#050507':'#fff'):t.text,
                          }}>
                            {/* Reply quote */}
                            {m.replyTo&&(
                              <div style={{
                                borderLeft:`2px solid ${isMe?'rgba(255,255,255,0.3)':tc}`,
                                background:isMe?'rgba(0,0,0,0.1)':t.id==='dark'?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)',
                                borderRadius:'0 4px 4px 0',padding:'4px 8px',marginBottom:7,
                              }}>
                                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2,opacity:0.7,color:isMe?'inherit':tc}}>{m.replyTo.senderName}</div>
                                <div style={{fontSize:11,lineHeight:1.5,overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',opacity:0.75}}>{m.replyTo.body}</div>
                              </div>
                            )}

                            {/* Edit mode */}
                            {isEditing ? (
                              <div>
                                <textarea autoFocus defaultValue={editingMsg.body}
                                  onChange={e=>setEditingMsg(prev=>({...prev,body:e.target.value}))}
                                  onKeyDown={e=>{
                                    if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();saveEdit(m.id,editingMsg.body);}
                                    if(e.key==='Escape'){setEditingMsg(null);}
                                  }}
                                  style={{width:'100%',background:'transparent',border:`1px solid ${tc}50`,borderRadius:5,color:'inherit',fontSize:13,fontFamily:"'Space Grotesk',sans-serif",padding:'5px 7px',outline:'none',resize:'none',minHeight:38,lineHeight:1.5}}
                                  rows={2}
                                />
                                <div style={{display:'flex',gap:5,marginTop:5,justifyContent:'flex-end'}}>
                                  <button onClick={()=>setEditingMsg(null)} style={{fontSize:10,padding:'3px 9px',borderRadius:4,border:`1px solid ${t.border}`,background:'transparent',color:t.textDim,cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif"}}>Cancel</button>
                                  <button onClick={()=>saveEdit(m.id,editingMsg.body)} style={{fontSize:10,padding:'3px 9px',borderRadius:4,border:'none',background:tc,color:t.id==='dark'?'#050507':'#fff',cursor:'pointer',fontWeight:600,fontFamily:"'Space Grotesk',sans-serif"}}>Save</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {m.body ? <div style={{fontSize:13,lineHeight:1.65,whiteSpace:"pre-wrap",wordBreak:"break-word",overflowWrap:"anywhere"}}>{m.body}</div> : null}
                                {(m.files||m.attachments||[]).length > 0 && (
                                  <div style={{marginTop: m.body ? 7 : 0}}>
                                    {(m.files||m.attachments||[]).map((att, ai) => (
                                      <MsgAttachment key={ai} att={att} isMe={isMe} tc={tc} t={t}/>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}

                            {/* Timestamp — only last in group */}
                            {!isEditing&&(isLastInGroup||m._failed||m._sending)&&(
                              <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",marginTop:4,display:'flex',alignItems:'center',justifyContent:'flex-end',gap:3,opacity:0.45}}>
                                {m._failed
                                  ? <span style={{display:'flex',alignItems:'center',gap:5,opacity:1}}>
                                      <span style={{color:t.red}}>⚠ Failed</span>
                                      <button onClick={()=>{setMessages(ms=>ms.filter(x=>x.id!==m.id));sendMessage(m.body);}}
                                        style={{background:t.red,border:'none',borderRadius:3,color:'#fff',fontSize:9,padding:'2px 7px',cursor:'pointer',fontFamily:"'Space Grotesk',sans-serif",fontWeight:600}}>Resend</button>
                                    </span>
                                  : m._sending
                                    ? <span>Sending…</span>
                                    : <span style={{display:'flex',alignItems:'center',gap:3}}>
                                        {fmtTime(m.createdAt)}{m.edited&&<span>· edited</span>}<TickStatus/>
                                      </span>
                                }
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Star indicator */}
                        {isStarred&&(
                          <div style={{fontSize:9,marginTop:2,paddingLeft:isMe?0:2,paddingRight:isMe?2:0,alignSelf:isMe?'flex-end':'flex-start',opacity:0.6}}>⭐</div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef}/>
            </div>

            {/* Input */}
            <div style={{padding:"0.65rem 0.75rem",borderTop:`1px solid ${t.border}`,flexShrink:0}}>
              {isAdmin&&showLetters&&(
                <div style={{marginBottom:8,background:t.surface2,borderRadius:6,border:`1px solid ${t.border}`,padding:"0.75rem",maxHeight:180,overflowY:"auto"}}>
                  <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",color:t.textDimmer,letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:8}}>Attach a Letter</div>
                  {(letters||[]).length===0&&<div style={{fontSize:14,color:t.textDimmer,fontStyle:"italic"}}>No letters saved yet.</div>}
                  {(letters||[]).map(l=>(
                    <div key={l.id} onClick={()=>sendLetter(l)} style={{padding:"0.5rem 0.65rem",borderRadius:5,cursor:"pointer",marginBottom:4,background:t.surface3,fontSize:14,color:t.text}} onMouseEnter={e=>e.currentTarget.style.background=`${t.teal}15`} onMouseLeave={e=>e.currentTarget.style.background=t.surface3}>
                      <div style={{fontWeight:600}}>{l.subject}</div>
                      <div style={{fontSize:10,color:t.textDimmer}}>To: {(l.to||"").split("\n")[0]} · {l.date}</div>
                    </div>
                  ))}
                </div>
              )}
              {replyTo&&(
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:t.surface2,borderRadius:6,marginBottom:6,border:`1px solid ${t.border}`}}>
                  <div style={{flex:1,borderLeft:`3px solid ${t.teal}`,paddingLeft:8}}>
                    <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",color:t.teal,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:1}}>Replying to {replyTo.senderName}</div>
                    <div style={{fontSize:11,color:t.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:280}}>{replyTo.body}</div>
                  </div>
                  <button onClick={()=>setReplyTo(null)} style={{background:'none',border:'none',cursor:'pointer',color:t.textDimmer,padding:2,flexShrink:0}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              )}
              {/* Pending file chips */}
              {pendingMsgFiles.length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
                  {pendingMsgFiles.map((pf,idx)=>(
                    <div key={idx} style={{display:'flex',alignItems:'center',gap:5,padding:'4px 8px',borderRadius:6,background:t.surface3,border:`1px solid ${t.border}`,maxWidth:200}}>
                      {pf.previewUrl
                        ? <img src={pf.previewUrl} alt="" style={{width:22,height:22,borderRadius:3,objectFit:'cover',flexShrink:0}}/>
                        : <span style={{fontSize:14}}>{fileTypeIcon(pf.mimeType)}</span>
                      }
                      <span style={{fontSize:10,color:t.text,fontFamily:"'Space Grotesk',sans-serif",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0}}>{pf.name}</span>
                      <span style={{fontSize:9,color:t.textDimmer,fontFamily:"'Fira Code',monospace",flexShrink:0}}>{fmtFileSize(pf.size)}</span>
                      <button onClick={()=>removePendingMsgFile(idx)} style={{background:'none',border:'none',cursor:'pointer',color:t.textDimmer,padding:0,marginLeft:2,flexShrink:0}}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                  {uploadingMsgFiles&&<span style={{fontSize:10,color:t.textDimmer,alignSelf:'center',fontFamily:"'Fira Code',monospace"}}>Uploading…</span>}
                </div>
              )}
              <div style={{display:"flex",gap:6,alignItems:"flex-end"}} className="q-msg-input-row">
                {/* Hidden file input */}
                <input ref={msgFileInputRef} type="file" multiple accept="*/*" style={{display:'none'}} onChange={handleMsgFilePick}/>
                {/* Letters button (admin only) */}
                {isAdmin&&<button onClick={()=>setShowLetters(!showLetters)} title="Attach letter" style={{background:"none",border:`1px solid ${showLetters?t.teal:t.border}`,borderRadius:5,cursor:"pointer",color:showLetters?t.teal:t.textDim,padding:"7px 8px",flexShrink:0,transition:"all 0.15s"}}>
                  <I n="mail" s={14} c="currentColor"/>
                </button>}
                {/* Paperclip button */}
                <button
                  onClick={()=>msgFileInputRef.current?.click()}
                  title="Attach file"
                  style={{background:'none',border:`1px solid ${t.border}`,borderRadius:5,cursor:'pointer',color:t.textDim,padding:'7px 8px',flexShrink:0,transition:'all 0.15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=t.teal;e.currentTarget.style.color=t.teal;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=t.border;e.currentTarget.style.color=t.textDim;}}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <textarea value={newMsg} onChange={e=>handleMsgInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();emitTyping(false);sendMessage();}}}
                  placeholder="Message… (Enter to send)"
                  rows={2}
                  style={{flex:1,background:t.surface2,color:t.text,border:`1px solid ${t.border}`,borderRadius:6,padding:"8px 12px",fontSize:14,fontFamily:"'Space Grotesk',sans-serif",outline:"none",resize:"none",minWidth:0}}
                />
                <Btn t={t} onClick={()=>{emitTyping(false);sendMessage();}} disabled={!newMsg.trim()&&pendingMsgFiles.length===0} icon="send">{""}</Btn>
              </div>
              {/* Typing indicator */}
              {currentTypers.length > 0 && (
                <div style={{padding:"2px 1rem 6px",display:"flex",alignItems:"center",gap:6}}>
                  <div style={{display:"flex",gap:3,alignItems:"center"}}>
                    {[0,1,2].map(i=>(
                      <div key={i} style={{width:5,height:5,borderRadius:"50%",background:t.teal,opacity:0.7,animation:`typing-dot 1.2s ${i*0.2}s ease-in-out infinite`}}/>
                    ))}
                  </div>
                  <span style={{fontSize:10,color:t.textDimmer,fontStyle:"italic",fontFamily:"'Fira Code',monospace"}}>
                    {currentTypers.map(u=>u.userName.split(" ")[0]).join(", ")} {currentTypers.length===1?"is":"are"} typing…
                  </span>
                  <style>{`@keyframes typing-dot{0%,80%,100%{transform:scale(0.8);opacity:0.4}40%{transform:scale(1.2);opacity:1}}`}</style>
                </div>
              )}
              {/* Online presence */}
              {currentOnline.length > 1 && (
                <div style={{padding:"0 1rem 4px",display:"flex",alignItems:"center",gap:4}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"#22c55e"}}/>
                  <span style={{fontSize:9,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>
                    {currentOnline.filter(u => {
                      const myId = isClient ? `client-${user.id}` : String(user.id);
                      return u.userId !== myId;
                    }).map(u=>u.userName.split(" ")[0]).join(", ")} online
                  </span>
                </div>
              )}
            </div>
          </>}

          {/* ── PROJECT FILES TAB ── */}
          {activeTab==="files"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{flex:1,overflowY:"auto",padding:"1rem"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
                  <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase"}}>
                    {threadDeliverables.length} file{threadDeliverables.length!==1?"s":""} · {selected.type==="admin"?"Admin + Client":"Dev + Client"}
                  </div>
                  {(isAdmin||isDev)&&<Btn size="sm" t={t} icon="plus" onClick={()=>setAddDeliv(!addDeliv)}>Add Project File</Btn>}
                </div>

                {addDeliv&&(
                  <div style={{background:t.surface2,borderRadius:8,padding:"1rem",marginBottom:"1rem",border:`1px solid ${t.border}`}} className="fade-up">
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.65rem",marginBottom:"0.65rem"}}>
                      <div style={{gridColumn:"1/-1"}}>
                        <Field t={t} label="Title" required value={delivForm.title} onChange={v=>setDelivForm(f=>({...f,title:v}))} placeholder="MVP Build v1.0, Design Mockups, Final EA…"/>
                      </div>
                      <Field t={t} label="Type" options={delivTypes} value={delivForm.type} onChange={v=>setDelivForm(f=>({...f,type:v}))}/>
                      <Field t={t} label="Link / Reference" value={delivForm.link} onChange={v=>setDelivForm(f=>({...f,link:v}))} placeholder="https://drive.google.com/… or GitHub link"/>
                    </div>
                    <Field t={t} label="Notes" textarea value={delivForm.notes} onChange={v=>setDelivForm(f=>({...f,notes:v}))} placeholder="What's included, version notes, instructions…" rows={2}/>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"0.65rem",flexWrap:"wrap",gap:8}}>
                      <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:15,color:t.textDim}}>
                        <div onClick={()=>setDelivForm(f=>({...f,clientVisible:!f.clientVisible}))} style={{width:16,height:16,borderRadius:3,border:`1.5px solid ${delivForm.clientVisible?t.teal:t.borderBright}`,background:delivForm.clientVisible?t.teal:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                          {delivForm.clientVisible&&<I n="check" s={9} c={t.id==="dark"?"#050507":"#fff"}/>}
                        </div>
                        Visible to client
                      </label>
                      <div style={{display:"flex",gap:6}}>
                        <Btn variant="secondary" size="sm" t={t} onClick={()=>setAddDeliv(false)}>Cancel</Btn>
                        <Btn size="sm" t={t} onClick={addDeliverable} disabled={!delivForm.title} icon="send">Share</Btn>
                      </div>
                    </div>
                  </div>
                )}

                {threadDeliverables.length===0&&!addDeliv&&(
                  <div style={{textAlign:"center",color:t.textDimmer,fontSize:12,fontStyle:"italic",marginTop:"3rem"}}>No project files yet. Add one above to share files, builds, or documents with the client.</div>
                )}
                {[...threadDeliverables].reverse().map(d=>{
                  const uploader=team.find(u=>u.id===d.uploadedBy||u.id===Number(d.uploadedBy));
                  const ic=typeIcon[d.type]||"receipt";
                  const visible=isClient?d.clientVisible:true;
                  if(!visible) return null;
                  return (
                    <div key={d.id} style={{background:t.surface2,borderRadius:8,padding:"1rem",marginBottom:"0.65rem",border:`1px solid ${t.border}`}}>
                      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                        <div style={{width:38,height:38,borderRadius:6,background:`${t.teal}15`,border:`1px solid ${t.teal}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <I n={ic} s={15} c={t.teal}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                            {/* FIXED: deliverable title in thread — Space Grotesk 700 instead of Syne */}
                            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:13,color:t.text}}>{d.title}</span>
                            <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.teal}15`,color:t.teal,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>{d.type}</span>
                            {!d.clientVisible&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,background:`${t.amber}15`,color:t.amber,fontFamily:"'Fira Code',monospace"}}>Internal</span>}
                          </div>
                          {d.notes&&<div style={{fontSize:12,color:t.textDim,marginBottom:5,fontWeight:400}}>{d.notes}</div>}
                          {d.link&&<a href={d.link} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:t.blue,fontFamily:"'Fira Code',monospace",wordBreak:"break-all"}}>{d.link}</a>}
                          <div style={{fontSize:9,color:t.textDimmer,marginTop:6,fontFamily:"'Fira Code',monospace"}}>
                            {uploader?.name||"Unknown"} · {new Date(d.uploadedAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                          </div>
                        </div>
                        {(isAdmin||isDev)&&<button onClick={()=>removeDeliverable(d.id)} style={{background:"none",border:"none",cursor:"pointer",color:t.textDimmer,padding:4,flexShrink:0}}><I n="trash" s={13}/></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── PARTICIPANTS TAB ── */}
          {activeTab==="participants"&&isAdmin&&selected.type==="dev"&&(
            <div style={{flex:1,overflowY:"auto",padding:"1rem"}}>
              <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>
                Manage Dev Thread Participants
              </div>
              <div style={{fontSize:12,color:t.textDim,marginBottom:"1rem",fontWeight:400}}>
                Toggle developers on or off for this project's client-facing dev thread. Changes take effect immediately.
              </div>
              {team.filter(u=>u.role==="developer"&&u.active!==false).map(u=>{
                const current=selectedProject?.chatParticipants||[];
                const included=current.includes(u.id)||current.includes(Number(u.id));
                return (
                  <div key={u.id} style={{display:"flex",alignItems:"center",gap:"1rem",padding:"0.85rem",borderRadius:7,background:included?`${t.teal}10`:t.surface2,border:`1px solid ${included?t.teal+"40":t.border}`,marginBottom:"0.5rem",transition:"all 0.2s"}}>
                    <Avatar name={u.name} size={36} t={t}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:t.text}}>{u.name}</div>
                      <div style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>@{u.username} · {u.title||"Developer"}</div>
                    </div>
                    <button onClick={()=>toggleParticipant(u.id)} style={{
                      padding:"6px 14px",borderRadius:5,cursor:"pointer",fontSize:11,
                      fontFamily:"'Space Grotesk',sans-serif",fontWeight:500,
                      background:included?`${t.red}15`:`${t.teal}15`,
                      color:included?t.red:t.teal,
                      border:`1px solid ${included?t.red+"40":t.teal+"40"}`,
                      transition:"all 0.15s",
                    }}>
                      {included?"Remove from thread":"Invite to thread"}
                    </button>
                  </div>
                );
              })}
              {team.filter(u=>u.role==="developer"&&u.active!==false).length===0&&(
                <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No active developers on the team yet.</div>
              )}
            </div>
          )}
        </div>
        )
      )}
    </div>
  );
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const TIER_META = {
  STARTER: { color: "#6b8cba", label: "Starter", accounts: 1, price: "$49/mo" },
  PRO:     { color: "#00c9a7", label: "Pro",      accounts: 5, price: "$99/mo" },
  ELITE:   { color: "#c9a84c", label: "Elite",    accounts: "∞", price: "$199/mo" },
};

const SEVERITY_COLOR = (s, t) => ({
  CRITICAL: t.red, HIGH: t.red, MEDIUM: t.amber, LOW: t.teal
}[s] || t.textDim);

const BUG_STATUS_COLOR = (s, t) => ({
  OPEN: t.red, INVESTIGATING: t.amber, IN_PROGRESS: t.blue,
  RESOLVED: t.teal, WONT_FIX: t.textDimmer, DUPLICATE: t.textDimmer,
}[s] || t.textDim);

const TICKET_STATUS_COLOR = (s, t) => ({
  OPEN: t.red, IN_PROGRESS: t.blue, WAITING_CLIENT: t.amber,
  RESOLVED: t.teal, CLOSED: t.textDimmer,
}[s] || t.textDim);

const RELEASE_TYPE_COLOR = (r, t) => ({
  MAJOR: t.red, MINOR: t.teal, PATCH: t.blue, HOTFIX: t.amber
}[r] || t.textDim);

const getDaysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) + " · " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const maskKey = (key) => {
  if (!key) return "";
  const parts = key.split("-");
  if (parts.length < 2) return key;
  return parts[0] + "-" + parts.slice(1, -1).map(() => "****").join("-") + "-" + parts[parts.length - 1];
};

const renderMD = (md, t) => {
  if (!md) return null;
  return md.split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <div key={i} style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: t.text, marginTop: i > 0 ? 12 : 0, marginBottom: 4 }}>{line.slice(3)}</div>;
    if (line.startsWith("**") && line.endsWith("**")) return <div key={i} style={{ fontWeight: 600, color: t.text, fontSize: 12, marginTop: 6 }}>{line.slice(2, -2)}</div>;
    if (line.startsWith("- ")) return <div key={i} style={{ fontSize: 12, color: t.textDim, paddingLeft: 12, marginBottom: 2, display: "flex", gap: 6 }}><span style={{ color: t.teal, flexShrink: 0 }}>·</span>{line.slice(2)}</div>;
    if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
    return <div key={i} style={{ fontSize: 12, color: t.textDim, lineHeight: 1.7 }}>{line}</div>;
  });
};


// ─── TIER BADGE ──────────────────────────────────────────────────────────────
const TierBadge = ({ tier, t, large }) => {
  const meta = TIER_META[tier] || TIER_META.STARTER;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: large ? "5px 14px" : "3px 9px",
      borderRadius: 4,
      background: `${meta.color}18`,
      border: `1px solid ${meta.color}50`,
      color: meta.color,
      fontFamily: "'Fira Code',monospace",
      fontSize: large ? 12 : 10,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
      {meta.label}
    </span>
  );
};

// ─── ACCOUNT SLOTS ────────────────────────────────────────────────────────────
const AccountSlots = ({ license, t, onAddAccount, onRemoveAccount, isAdmin, canRemove = false, canAdd = false }) => {
  const [addForm, setAddForm] = useState({ accountNumber: '', broker: '' });
  const [addOpen, setAddOpen] = useState(false);
  const accounts = license.boundAccounts || [];
  const maxSlots = license.maxAccounts === 999 ? null : license.maxAccounts;
  const atLimit = maxSlots !== null && accounts.length >= maxSlots;
  const showAdd = (isAdmin || canAdd) && !atLimit;

  const handleAdd = () => {
    if (!addForm.accountNumber.trim()) return;
    onAddAccount(license.id, { accountNumber: addForm.accountNumber.trim(), broker: addForm.broker.trim() || 'Unknown' });
    setAddForm({ accountNumber: '', broker: '' });
    setAddOpen(false);
  };

  const fld = { width: '100%', background: t.surface, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: '6px 10px', fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", outline: 'none' };

  return (
    <div>
      <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: '0.18em', color: t.textDim, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Bound MT5 Accounts</span>
        <span style={{ color: atLimit ? t.red : t.teal }}>{accounts.length}/{maxSlots === null ? '∞' : maxSlots}</span>
      </div>
      {accounts.length === 0 ? (
        <div style={{ fontSize: 12, color: t.textDimmer, fontStyle: 'italic', padding: '8px 0' }}>No accounts bound yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: t.surface, borderRadius: 6, border: `1px solid ${t.border}` }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: acc.lastSeenAt ? t.teal : t.textDimmer, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 12, fontWeight: 600, color: t.text }}>{acc.accountNumber}</div>
                <div style={{ fontSize: 10, color: t.textDimmer }}>{acc.broker}{acc.lastSeenAt ? ` · Last seen ${fmtDate(acc.lastSeenAt)}` : ' · Not yet validated'}</div>
              </div>
              {(isAdmin || canRemove) && (
                <button onClick={() => { if (window.confirm(`Remove account ${acc.accountNumber}? The EA will stop trading on this account immediately.`)) onRemoveAccount(license.id, acc.id); }}
                  style={{ background: `${t.red}12`, border: `1px solid ${t.red}30`, borderRadius: 4, cursor: 'pointer', color: t.red, padding: '3px 8px', fontSize: 10, fontFamily: "'Fira Code',monospace" }}>Remove</button>
              )}
            </div>
          ))}
        </div>
      )}
      {showAdd && (
        !addOpen ? (
          <button onClick={() => setAddOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 5, background: `${t.teal}12`, border: `1px solid ${t.teal}35`, color: t.teal, cursor: 'pointer', fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Account
          </button>
        ) : (
          <div style={{ background: t.surface, borderRadius: 6, padding: '10px 12px', border: `1px solid ${t.teal}35`, display: 'flex', flexDirection: 'column', gap: 7 }} className="fade-in">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <div style={{ fontSize: 10, color: t.textDim, marginBottom: 3, fontFamily: "'Fira Code',monospace", textTransform: 'uppercase', letterSpacing: '0.1em' }}>Account Number *</div>
                <input style={fld} placeholder="e.g. 12345678" value={addForm.accountNumber} onChange={e => setAddForm(f => ({ ...f, accountNumber: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: t.textDim, marginBottom: 3, fontFamily: "'Fira Code',monospace", textTransform: 'uppercase', letterSpacing: '0.1em' }}>Broker</div>
                <input style={fld} placeholder="e.g. ICMarkets" value={addForm.broker} onChange={e => setAddForm(f => ({ ...f, broker: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: t.textDim, padding: '5px 8px', background: `${t.teal}08`, borderRadius: 4, border: `1px solid ${t.teal}20` }}>
              Account will show as "Not yet validated" until the EA connects with this license key.
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddOpen(false)} style={{ padding: '5px 12px', borderRadius: 5, background: 'none', border: `1px solid ${t.border}`, color: t.textDim, cursor: 'pointer', fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
              <button onClick={handleAdd} disabled={!addForm.accountNumber.trim()} style={{ padding: '5px 14px', borderRadius: 5, background: addForm.accountNumber.trim() ? t.teal : t.border, border: 'none', color: addForm.accountNumber.trim() ? '#050506' : t.textDimmer, cursor: addForm.accountNumber.trim() ? 'pointer' : 'not-allowed', fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>Bind Account</button>
            </div>
          </div>
        )
      )}
      {(isAdmin || canAdd) && atLimit && (
        <div style={{ fontSize: 11, color: t.textDim, padding: '6px 0', fontStyle: 'italic' }}>
          Limit reached ({maxSlots}/{maxSlots}).
        </div>
      )}
      <div style={{ padding: '8px 10px', background: `${t.teal}08`, border: `1px solid ${t.teal}20`, borderRadius: 5, fontSize: 11, color: t.textDim, marginTop: accounts.length > 0 ? 8 : 0 }}>
        <strong style={{ color: t.teal }}>How to connect:</strong> Open MetaTrader 5 → load Q-Trader EA → paste your license key. Your account binds automatically on first validation.
        {atLimit && <div style={{ marginTop: 5, color: t.amber }}>⚠ Slot limit reached. Remove an account above to free up a slot, then run the EA on your new account.</div>}
      </div>
    </div>
  );
};
// ─── LICENSE CARD ─────────────────────────────────────────────────────────────
const LicenseCard = ({ license, t, onRevoke, onExtend, onEditTier, isAdmin, clientName, onAddAccount, onRemoveAccount }) => {
  const [expanded, setExpanded] = useState(false);
  const days = getDaysRemaining(license.expiresAt);
  const isExpiringSoon = days !== null && days <= 14;
  const activeAccounts = license.boundAccounts || [];
  const statusColor = license.status === "ACTIVE" ? t.teal : license.status === "REVOKED" ? t.red : t.amber;

  return (
    <div style={{
      background: t.surface, borderRadius: 8,
      border: `1px solid ${isExpiringSoon ? t.amber + "60" : t.border}`,
      overflow: "hidden", transition: "all 0.2s",
    }}>
      <div style={{ padding: "1.1rem 1.25rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{
          width: 44, height: 44, borderRadius: 8, flexShrink: 0,
          background: `${TIER_META[license.tier]?.color || t.teal}15`,
          border: `1.5px solid ${TIER_META[license.tier]?.color || t.teal}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={TIER_META[license.tier]?.color || t.teal} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span className="q-license-key" style={{ fontFamily: "'Fira Code',monospace", fontSize: 12, fontWeight: 600, color: t.text, letterSpacing: "0.08em" }}>
              {isAdmin ? license.licenseKey : maskKey(license.licenseKey)}
            </span>
            <TierBadge tier={license.tier} t={t} />
            <span style={{
              fontSize: 9, padding: "2px 7px", borderRadius: 3,
              background: `${statusColor}18`, color: statusColor,
              fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase",
              border: `1px solid ${statusColor}40`,
            }}>{license.status}</span>
          </div>
          <div style={{ fontSize: 12, color: t.textDim, display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
            {isAdmin && clientName && <span>👤 {clientName}</span>}
            <span>{activeAccounts.length} / {license.maxAccounts === 999 ? "∞" : license.maxAccounts} accounts</span>
            <span style={{ color: isExpiringSoon ? t.amber : t.textDim }}>
              {license.expiresAt
                ? isExpiringSoon ? `⚠ Expires in ${days}d` : `Expires ${fmtDate(license.expiresAt)}`
                : "Lifetime"}
            </span>
            <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 10 }}>{license.billingCycle}</span>
          </div>
        </div>

        <div style={{ width: 80, flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, marginBottom: 4, textAlign: "right" }}>
            {activeAccounts.length}/{license.maxAccounts === 999 ? "∞" : license.maxAccounts}
          </div>
          {license.maxAccounts !== 999 && (
            <div style={{ height: 4, background: t.border, borderRadius: 4, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${Math.min(100, (activeAccounts.length / license.maxAccounts) * 100)}%`,
                background: activeAccounts.length >= license.maxAccounts ? t.red : t.teal,
                borderRadius: 4,
              }} />
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 4, flexShrink: 0, flexWrap: "wrap" }}>
          <button onClick={() => setExpanded(!expanded)}
            style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, cursor: "pointer", color: t.textDim, padding: "5px 9px", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            {expanded ? "Less" : "Details"}
          </button>
          {isAdmin && (
            <>
              {onEditTier && <button onClick={() => onEditTier(license)} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, cursor: "pointer", color: t.textDim, padding: "5px 9px", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>Edit</button>}
              {onExtend && <button onClick={() => onExtend(license)} style={{ background: `${t.teal}15`, border: `1px solid ${t.teal}40`, borderRadius: 5, cursor: "pointer", color: t.teal, padding: "5px 9px", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>Extend</button>}
              {onRevoke && license.status === "ACTIVE" && <button onClick={() => onRevoke(license.id)} style={{ background: `${t.red}12`, border: `1px solid ${t.red}40`, borderRadius: 5, cursor: "pointer", color: t.red, padding: "5px 9px", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>Revoke</button>}
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${t.border}`, padding: "1rem 1.25rem", background: t.surface2 }} className="fade-in">
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 1fr", gap: "1rem" }}>
            <div>
              <AccountSlots license={license} t={t} isAdmin={isAdmin} onAddAccount={onAddAccount} onRemoveAccount={onRemoveAccount} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase", marginBottom: 8 }}>License Details</div>
              {[
                ["License Key", isAdmin ? license.licenseKey : maskKey(license.licenseKey)],
                ["Tier", TIER_META[license.tier]?.label || license.tier],
                ["Billing", license.billingCycle],
                ["Activated", fmtDate(license.activatedAt)],
                ["Expires", license.expiresAt ? fmtDate(license.expiresAt) : "Never (Lifetime)"],
                ...(isAdmin && license.adminNotes ? [["Notes", license.adminNotes]] : []),
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 11, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>{k}</span>
                  <span style={{ fontSize: 11, color: t.text, fontWeight: 500, textAlign: "right", wordBreak: "break-all" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          {!isAdmin && (
            <div style={{ marginTop: "1rem", display: "flex", gap: 8 }}>
              <CopyBtn text={license.licenseKey} label="Copy License Key" t={t} />
              {TIER_META[license.tier]?.label !== "Elite" && (
                <div style={{ padding: "7px 14px", borderRadius: 5, background: `${t.amber}15`, border: `1px solid ${t.amber}40`, color: t.amber, fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500 }}>
                  ↑ Upgrade to {license.tier === "STARTER" ? "Pro" : "Elite"}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── VERSION CARD ─────────────────────────────────────────────────────────────
const VersionCard = ({ version, t, isAdmin, onSetLatest, onToggleMandatory, onDelete, canDownload }) => {
  const [showNotes, setShowNotes] = useState(false);
  const rtc = RELEASE_TYPE_COLOR(version.releaseType, t);

  return (
    <div style={{ background: t.surface, borderRadius: 8, border: `1px solid ${version.isLatest ? t.teal + "50" : t.border}`, overflow: "hidden" }}>
      <div style={{ padding: "1rem 1.25rem", display: "flex", alignItems: "flex-start", gap: "1rem" }}>
        {/* Version badge - fixed width, never shrinks */}
        <div style={{
          minWidth: 72, width: 72, textAlign: "center", padding: "8px 10px",
          background: version.isLatest ? `${t.teal}15` : t.surface2,
          borderRadius: 6, border: `1px solid ${version.isLatest ? t.teal + "40" : t.border}`, flexShrink: 0,
        }}>
          <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 15, fontWeight: 600, color: version.isLatest ? t.teal : t.text, letterSpacing: "0.05em" }}>
            v{version.version}
          </div>
          {version.isLatest && <div style={{ fontSize: 8, color: t.teal, fontFamily: "'Fira Code',monospace", textTransform: "uppercase", letterSpacing: "0.15em", marginTop: 2 }}>Latest</div>}
        </div>

        {/* Info + actions column */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: badges + filename */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: `${rtc}18`, color: rtc, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${rtc}40`, whiteSpace: "nowrap", flexShrink: 0 }}>
              {version.releaseType}
            </span>
            {version.isMandatory && (
              <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: `${t.red}18`, color: t.red, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${t.red}40`, whiteSpace: "nowrap", flexShrink: 0 }}>
                Mandatory
              </span>
            )}
            <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{version.fileName}</span>
          </div>
          {/* Row 2: published date + size - always on one line */}
          <div style={{ fontSize: 11, color: t.textDimmer, fontFamily: "'Fira Code',monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 10 }}>
            Published {fmtDate(version.publishedAt)}{version.fileSize ? ` · ${(version.fileSize / 1024).toFixed(0)} KB` : ""}
          </div>
          {/* Row 3: action buttons */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => setShowNotes(!showNotes)}
              style={{ padding: "5px 10px", borderRadius: 5, background: "none", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textDim, fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", whiteSpace: "nowrap" }}>
              {showNotes ? "Hide Notes" : "Release Notes"}
            </button>
            {canDownload && (
              <button
                onClick={() => qtDownload(version.id, version.fileName)}
                style={{ padding: "5px 12px", borderRadius: 5, background: `${t.teal}15`, color: t.teal, fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download
              </button>
            )}
            {isAdmin && (
              <>
                {!version.isLatest && onSetLatest && <button onClick={() => onSetLatest(version.id)} style={{ padding: "5px 10px", borderRadius: 5, background: `${t.teal}12`, border: `1px solid ${t.teal}40`, cursor: "pointer", color: t.teal, fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", whiteSpace: "nowrap" }}>Set Latest</button>}
                {onToggleMandatory && <button onClick={() => onToggleMandatory(version.id)} style={{ padding: "5px 10px", borderRadius: 5, background: version.isMandatory ? `${t.red}12` : "none", border: `1px solid ${version.isMandatory ? t.red + "40" : t.border}`, cursor: "pointer", color: version.isMandatory ? t.red : t.textDim, fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", whiteSpace: "nowrap" }}>{version.isMandatory ? "Un-mandatory" : "Force Update"}</button>}
                {onDelete && <button onClick={() => onDelete(version.id)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textDimmer, padding: "5px 6px", flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>}
              </>
            )}
          </div>
        </div>
      </div>
      {showNotes && (
        <div style={{ borderTop: `1px solid ${t.border}`, padding: "1rem 1.25rem", background: t.surface2 }} className="fade-in">
          <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase", marginBottom: 10 }}>Release Notes</div>
          <div>{renderMD(version.releaseNotes, t)}</div>
        </div>
      )}
    </div>
  );
};


// ─── BUG ROW ──────────────────────────────────────────────────────────────────
const BugRow = ({ bug, t, isAdmin, onReply, clients }) => {
  const [showReply, setShowReply] = useState(false);
  const [reply, setReply] = useState(bug.adminReply || "");
  const [status, setStatus] = useState(bug.status);
  const sc = BUG_STATUS_COLOR(bug.status, t);
  const sevc = SEVERITY_COLOR(bug.severity, t);
  const clientName = clients?.find(c => c.id === bug.clientId)?.name || "Client";

  const save = () => {
    onReply && onReply(bug.id, reply, status);
    setShowReply(false);
  };

  return (
    <div style={{ background: t.surface, borderRadius: 8, border: `1px solid ${t.border}`, overflow: "hidden" }}>
      <div style={{ padding: "1rem 1.25rem", display: "flex", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: sevc, flexShrink: 0, minHeight: 40 }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: t.text }}>{bug.title}</span>
            <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: `${sevc}18`, color: sevc, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${sevc}40` }}>{bug.severity}</span>
            <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: `${sc}18`, color: sc, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${sc}40` }}>{bug.status}</span>
          </div>
          <div style={{ fontSize: 12, color: t.textDim, lineHeight: 1.6, marginBottom: 6 }}>{bug.description}</div>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {isAdmin && <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>👤 {clientName}</span>}
            {bug.eaVersion && <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>v{bug.eaVersion}</span>}
            {bug.broker && <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>{bug.broker}</span>}
            {bug.symbol && <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>{bug.symbol}</span>}
            <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>{fmtDate(bug.createdAt)}</span>
          </div>
          {bug.adminReply && (
            <div style={{ marginTop: 8, padding: "8px 10px", background: `${t.teal}08`, borderRadius: 5, border: `1px solid ${t.teal}25` }}>
              <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.teal, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Qire Response · {fmtDate(bug.repliedAt)}</div>
              <div style={{ fontSize: 12, color: t.text, lineHeight: 1.6 }}>{bug.adminReply}</div>
            </div>
          )}
        </div>
        {isAdmin && (
          <button onClick={() => setShowReply(!showReply)}
            style={{ padding: "5px 10px", borderRadius: 5, background: showReply ? `${t.teal}15` : "none", border: `1px solid ${showReply ? t.teal + "40" : t.border}`, cursor: "pointer", color: showReply ? t.teal : t.textDim, fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>
            {bug.adminReply ? "Edit Reply" : "Reply"}
          </button>
        )}
      </div>
      {isAdmin && showReply && (
        <div style={{ borderTop: `1px solid ${t.border}`, padding: "1rem 1.25rem", background: t.surface2 }} className="fade-in">
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.65rem", marginBottom: "0.65rem" }}>
            <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase" }}>Admin Reply</div>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ background: t.surface, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "4px 8px", fontSize: 11, fontFamily: "'Fira Code',monospace", cursor: "pointer", outline: "none" }}>
              {["OPEN", "INVESTIGATING", "IN_PROGRESS", "RESOLVED", "WONT_FIX", "DUPLICATE"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Write a response visible to the client…" rows={3}
            style={{ width: "100%", background: t.surface, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "8px 12px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", resize: "vertical", marginBottom: "0.5rem" }} />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button onClick={() => setShowReply(false)} style={{ padding: "6px 14px", borderRadius: 5, background: "none", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textDim, fontSize: 12, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
            <button onClick={save} style={{ padding: "6px 14px", borderRadius: 5, background: t.teal, color: "#050507", cursor: "pointer", fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, border: "none" }}>Save Reply</button>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── TICKET THREAD ────────────────────────────────────────────────────────────
const TicketThread = ({ ticket, t, isAdmin, onReply, onUpdateStatus, clientName }) => {
  const [reply, setReply] = useState("");
  const [expanded, setExpanded] = useState(ticket.status !== "CLOSED");
  const sc = TICKET_STATUS_COLOR(ticket.status, t);
  const lastMsg = ticket.messages?.[ticket.messages.length - 1];

  const send = () => {
    if (!reply.trim()) return;
    onReply && onReply(ticket.id, reply, isAdmin);
    setReply("");
  };

  return (
    <div style={{ background: t.surface, borderRadius: 8, border: `1px solid ${t.border}`, overflow: "hidden" }}>
      <div onClick={() => setExpanded(!expanded)} style={{ padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "1rem", cursor: "pointer", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: t.text }}>{ticket.subject}</span>
            <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, background: `${sc}18`, color: sc, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${sc}40` }}>{ticket.status}</span>
            {isAdmin && ticket.messages?.some(m => !m.isFromAdmin) && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: `${t.red}18`, color: t.red, fontFamily: "'Fira Code',monospace", border: `1px solid ${t.red}40` }}>New reply</span>}
          </div>
          <div style={{ fontSize: 11, color: t.textDimmer, fontFamily: "'Fira Code',monospace" }}>
            {isAdmin && `${clientName} · `}{ticket.messages?.length || 0} message{(ticket.messages?.length || 0) !== 1 ? "s" : ""} · {fmtDate(ticket.createdAt)}
          </div>
          {!expanded && lastMsg && (
            <div style={{ fontSize: 11, color: t.textDim, marginTop: 3, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {lastMsg.isFromAdmin ? "↩ " : ""}{lastMsg.body}
            </div>
          )}
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textDimmer} strokeWidth="2" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${t.border}` }} className="fade-in">
          <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.65rem", maxHeight: 340, overflowY: "auto" }}>
            {(ticket.messages || []).map(msg => (
              <div key={msg.id} style={{ display: "flex", flexDirection: msg.isFromAdmin ? "row-reverse" : "row", gap: 8, alignItems: "flex-end" }}>
                <div style={{
                  maxWidth: "80%", padding: "0.6rem 0.85rem",
                  borderRadius: msg.isFromAdmin ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                  background: msg.isFromAdmin ? t.teal : t.surface2,
                  color: msg.isFromAdmin ? "#050507" : t.text,
                  border: msg.isFromAdmin ? "none" : `1px solid ${t.border}`,
                }}>
                  {!msg.isFromAdmin && <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.08em" }}>{clientName}</div>}
                  {msg.isFromAdmin && <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: "#050507", opacity: 0.6, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.08em" }}>Qire Agency</div>}
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>{msg.body}</div>
                  <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", opacity: 0.6, marginTop: 3 }}>{fmtDateTime(msg.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>

          {ticket.status !== "CLOSED" && (
            <div style={{ borderTop: `1px solid ${t.border}`, padding: "0.85rem 1.25rem", background: t.surface2 }}>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6, marginBottom: "0.5rem", flexWrap: "wrap" }}>
                  {["IN_PROGRESS", "WAITING_CLIENT", "RESOLVED", "CLOSED"].map(s => (
                    <button key={s} onClick={() => onUpdateStatus && onUpdateStatus(ticket.id, s)}
                      style={{ padding: "3px 8px", borderRadius: 4, background: ticket.status === s ? `${TICKET_STATUS_COLOR(s, t)}18` : "none", border: `1px solid ${ticket.status === s ? TICKET_STATUS_COLOR(s, t) + "40" : t.border}`, cursor: "pointer", color: ticket.status === s ? TICKET_STATUS_COLOR(s, t) : t.textDimmer, fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <textarea value={reply} onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={isAdmin ? "Reply to client… (Enter to send)" : "Reply… (Enter to send)"}
                  rows={2}
                  style={{ flex: 1, background: t.surface, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "7px 10px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", resize: "none" }} />
                <button onClick={send} disabled={!reply.trim()}
                  style={{ padding: "7px 14px", borderRadius: 5, background: reply.trim() ? t.teal : t.border, color: reply.trim() ? "#050507" : t.textDimmer, border: "none", cursor: reply.trim() ? "pointer" : "not-allowed", fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN MODULE
// ═══════════════════════════════════════════════════════════════════════════════

const QTraderAdmin = ({ licenses, setLicenses, versions, setVersions, bugs, setBugs, tickets, setTickets, clients, products, apiToken, apiLoading, t }) => {
  const [tab, setTab] = useState("overview");
  const [newLicModal, setNewLicModal] = useState(false);
  const [newVerModal, setNewVerModal] = useState(false);
  const [extendModal, setExtendModal] = useState(null);
  const [editLicModal, setEditLicModal] = useState(null);
  const [licForm, setLicForm] = useState({ clientId: "", tier: "PRO", billingCycle: "ANNUAL", adminNotes: "" });
  const [verForm, setVerForm] = useState({ version: "", releaseType: "PATCH", releaseNotes: "", fileName: "", isMandatory: false, isLatest: true });
  const LF = k => v => setLicForm(f => ({ ...f, [k]: v }));
  const VF = k => v => setVerForm(f => ({ ...f, [k]: v }));

  // Use first product from DB, or fall back to any product id available
  const QT_PRODUCT_ID = (products && products.length > 0) ? products[0].id : null;
  const genKey = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const seg = () => Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    return `QTRDR-${seg()}-${seg()}-${seg()}-${seg()}`;
  };
  const getMaxAccounts = tier => ({ STARTER: 1, PRO: 5, ELITE: 999 }[tier] || 1);
  const getExpiry = cycle => {
    const d = new Date();
    if (cycle === "MONTHLY") d.setMonth(d.getMonth() + 1);
    else if (cycle === "ANNUAL") d.setFullYear(d.getFullYear() + 1);
    else return null;
    return d.toISOString().slice(0, 10);
  };

  const createLicense = async () => {
    // Optimistic local update (instant UI feedback)
    const newLic = {
      id: Date.now(), licenseKey: genKey(), clientId: Number(licForm.clientId),
      tier: licForm.tier, status: "ACTIVE", billingCycle: licForm.billingCycle,
      maxAccounts: getMaxAccounts(licForm.tier),
      activatedAt: new Date().toISOString().slice(0, 10),
      expiresAt: getExpiry(licForm.billingCycle),
      adminNotes: licForm.adminNotes, boundAccounts: [],
    };
    setLicenses(ls => [...ls, newLic]);
    setNewLicModal(false);
    setLicForm({ clientId: "", tier: "PRO", billingCycle: "ANNUAL", adminNotes: "" });

    // Sync to API in background
// Add this constant near the top of QTraderAdmin component:

// Then fix the createLicense API call:
if (apiToken) {
  try {
    const res = await qtFetch('/api/v1/licenses', {  // ✓ removed trailing slash
      method: 'POST',
      body: JSON.stringify({
        clientId:    licForm.clientId,  // ✓ variable, not string
        productId:   QT_PRODUCT_ID,      // ✓ real constant
        tier:        licForm.tier,
        billingCycle: licForm.billingCycle,
        adminNotes:  licForm.adminNotes,
      }),
    }, apiToken);
        // Replace temp local entry with real API entry (has real ID)
        if (res.license) {
          setLicenses(ls => ls.map(l => l.id === newLic.id ? res.license : l));
        }
      } catch (err) {
        console.warn('License sync failed:', err.message);
      }
    }
  };

  const revokeLicense = async (id) => {
    setLicenses(ls => ls.map(l => l.id === id ? { ...l, status: "REVOKED" } : l));
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/licenses/${id}/revoke`, { method: 'POST' }, apiToken);
      } catch (err) {
        console.warn('revoke sync failed:', err.message);
      }
    }
  };

  const addAccount = async (licId, acc) => {
    if (apiToken) {
      try {
        const res = await qtFetch(`/api/v1/licenses/${licId}/accounts`, {
          method: 'POST',
          body: JSON.stringify({ accountNumber: acc.accountNumber, broker: acc.broker }),
        }, apiToken);
        if (res.account) {
          // Replace with real DB entry
          setLicenses(ls => ls.map(l => l.id !== licId ? l : {
            ...l, boundAccounts: [...(l.boundAccounts || []), res.account],
          }));
        }
      } catch (err) {
        console.warn('addAccount failed:', err.message);
        alert('Failed to bind account: ' + err.message);
      }
    } else {
      // No token — local state only (offline mode)
      setLicenses(ls => ls.map(l => l.id !== licId ? l : {
        ...l, boundAccounts: [...(l.boundAccounts || []), { ...acc, id: Date.now() }],
      }));
    }
  };

  const removeAccount = async (licId, accId) => {
    setLicenses(ls => ls.map(l => l.id !== licId ? l : { ...l, boundAccounts: (l.boundAccounts || []).filter(a => a.id !== accId) }));
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/licenses/${licId}/accounts/${accId}`, { method: 'DELETE' }, apiToken);
      } catch (err) {
        console.warn('removeAccount sync failed:', err.message);
      }
    }
  };

  const extendLicense = async (id, months) => {
    setLicenses(ls => ls.map(l => {
      if (l.id !== id) return l;
      const base = l.expiresAt ? new Date(l.expiresAt) : new Date();
      base.setMonth(base.getMonth() + months);
      return { ...l, expiresAt: base.toISOString().slice(0, 10), status: "ACTIVE" };
    }));
    setExtendModal(null);
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/licenses/${id}/extend`, {
          method: 'POST',
          body: JSON.stringify({ months }),
        }, apiToken);
      } catch (err) {
        console.warn('extendLicense sync failed:', err.message);
      }
    }
  };

  const updateLicenseTier = async (id, tier) => {
    setLicenses(ls => ls.map(l => l.id === id ? { ...l, tier, maxAccounts: getMaxAccounts(tier) } : l));
    setEditLicModal(null);
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/licenses/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ tier }),
        }, apiToken);
      } catch (err) {
        console.warn('updateLicenseTier sync failed:', err.message);
      }
    }
  };

  const createVersion = async () => {
    if (!verForm.version || !verForm.releaseNotes) return;
    if (!QT_PRODUCT_ID) { alert('No product found in database. Please create a product first via /api/v1/admin/products.'); return; }
    // Optimistic local update
    if (verForm.isLatest) setVersions(vs => vs.map(v => ({ ...v, isLatest: false })));
    const tempVer = { id: Date.now(), ...verForm, fileSize: verForm.file?.size || 0, downloadUrl: "#", publishedAt: new Date().toISOString().slice(0, 10) };
    setVersions(vs => [...vs, tempVer]);
    setNewVerModal(false);
    setVerForm({ version: "", releaseType: "PATCH", releaseNotes: "", fileName: "", file: null, isMandatory: false, isLatest: true });

    if (apiToken) {
      try {
        const formData = new FormData();
        formData.append('productId', QT_PRODUCT_ID);
        formData.append('version', verForm.version);
        formData.append('releaseType', verForm.releaseType);
        formData.append('releaseNotes', verForm.releaseNotes);
        formData.append('isMandatory', String(verForm.isMandatory));
        formData.append('isLatest', String(verForm.isLatest));
        if (verForm.file) formData.append('file', verForm.file);

        const res = { ok: false, json: async () => ({}) };
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (data.version) {
          setVersions(vs => vs.map(v => v.id === tempVer.id ? data.version : v));
        }
      } catch (err) {
        console.warn('createVersion sync failed:', err.message);
      }
    }
  };

  const replyToBug = async (bugId, reply, status) => {
    setBugs(bs => bs.map(b => b.id === bugId ? { ...b, adminReply: reply, status, repliedAt: new Date().toISOString().slice(0, 10) } : b));
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/bugs/${bugId}`, {
          method: 'PATCH',
          body: JSON.stringify({ adminReply: reply, status }),
        }, apiToken);
      } catch (err) {
        console.warn('bug reply sync failed:', err.message);
      }
    }
  };
  const replyToTicket = async (ticketId, body, isFromAdmin) => {
    setTickets(ts => ts.map(tk => tk.id !== ticketId ? tk : {
      ...tk,
      status: isFromAdmin ? "WAITING_CLIENT" : "OPEN",
      messages: [...(tk.messages || []), { id: Date.now(), body, isFromAdmin, createdAt: new Date().toISOString() }],
    }));
    if (apiToken && isFromAdmin) {
      try {
        await qtFetch(`/api/v1/tickets/${ticketId}/reply`, {
          method: 'POST',
          body: JSON.stringify({ message: body }),
        }, apiToken);
      } catch (err) {
        console.warn('ticket reply sync failed:', err.message);
      }
    }
  };
  const updateTicketStatus = async (ticketId, status) => {
    setTickets(ts => ts.map(tk => tk.id === ticketId ? { ...tk, status } : tk));
    if (apiToken) {
      try {
        await qtFetch(`/api/v1/tickets/${ticketId}`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        }, apiToken);
      } catch (err) {
        console.warn('updateTicketStatus sync failed:', err.message);
      }
    }
  };

  const activeLicenses = licenses.filter(l => l.status === "ACTIVE").length;
  const expiringCount = licenses.filter(l => { const d = getDaysRemaining(l.expiresAt); return d !== null && d <= 14 && l.status === "ACTIVE"; }).length;
  const openBugs = bugs.filter(b => b.status === "OPEN" || b.status === "INVESTIGATING").length;
  const openTickets = tickets.filter(tk => tk.status === "OPEN" || tk.status === "IN_PROGRESS").length;
  const totalAccounts = licenses.reduce((a, l) => a + (l.boundAccounts?.length || 0), 0);

  const tabStyle = id => ({
    padding: "6px 14px", borderRadius: 5, cursor: "pointer", fontSize: 11,
    fontFamily: "'Space Grotesk',sans-serif", fontWeight: tab === id ? 600 : 400,
    background: tab === id ? t.teal : "transparent",
    color: tab === id ? (t.id === "dark" ? "#050507" : "#fff") : t.textDim,
    border: tab === id ? "none" : `1px solid ${t.border}`,
    display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s",
  });

  const fieldStyle = { width: "100%", background: t.surface2, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "7px 10px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", minHeight: 36 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} className="fade-up">
{/* API status banner */}
      {apiLoading && (
        <div style={{ padding: "0.65rem 1rem", borderRadius: 6, background: `${t.teal}10`, border: `1px solid ${t.teal}30`, fontSize: 11, color: t.teal, fontFamily: "'Fira Code',monospace", letterSpacing: "0.08em" }}>
          ⟳ Loading live data from Railway API…
        </div>
      )}
      {!apiLoading && !apiToken && (
        <div style={{ padding: "0.65rem 1rem", borderRadius: 6, background: `${t.amber}10`, border: `1px solid ${t.amber}30`, fontSize: 11, color: t.amber, fontFamily: "'Fira Code',monospace" }}>
          ⚠ Not connected to API — showing local state only. Check your Railway URL and credentials.
        </div>
      )}
      {!apiLoading && apiToken && (
        <div style={{ padding: "0.65rem 1rem", borderRadius: 6, background: `${t.teal}08`, border: `1px solid ${t.teal}20`, fontSize: 11, color: t.teal, fontFamily: "'Fira Code',monospace", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.teal, display: "inline-block" }}/>
          Live — connected to Railway API
        </div>
      )}
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: t.teal, marginBottom: 6 }}>
            Q-Trader EA <span style={{ display: "inline-block", height: 1, width: 40, background: t.border, verticalAlign: "middle", marginLeft: 8 }} />
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 400, fontStyle: "italic", color: t.text, letterSpacing: "0.025em", lineHeight: 1.1 }}>License Manager</div>
          <div style={{ fontSize: 13, color: t.textDim, marginTop: 4 }}>Manage licenses, EA versions, bug reports, and support tickets</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setNewLicModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New License
          </button>
          <button onClick={() => setNewVerModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 6, background: "transparent", color: t.textDim, border: `1px solid ${t.borderBright}`, cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload Version
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: "0.75rem" }}>
        {[
          { l: "Active Licenses", v: activeLicenses, c: t.teal },
          { l: "Bound Accounts", v: totalAccounts, c: t.blue },
          { l: "Expiring Soon", v: expiringCount, c: expiringCount > 0 ? t.amber : t.textDimmer },
          { l: "Open Bugs", v: openBugs, c: openBugs > 0 ? t.red : t.textDimmer },
          { l: "Open Tickets", v: openTickets, c: openTickets > 0 ? t.amber : t.textDimmer },
          { l: "EA Versions", v: versions.length, c: t.textDim },
        ].map(s => (
          <div key={s.l} style={{ background: t.surface, borderRadius: 7, padding: "1rem", border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase", marginBottom: 6 }}>{s.l}</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 400, fontStyle: "italic", color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {[
          { id: "overview", label: "Licenses" },
          { id: "versions", label: "Versions" },
          { id: "bugs", label: "Bug Reports", badge: openBugs || null },
          { id: "tickets", label: "Support", badge: openTickets || null },
        ].map(tab2 => (
          <button key={tab2.id} onClick={() => setTab(tab2.id)} style={tabStyle(tab2.id)}>
            {tab2.label}
            {tab2.badge > 0 && <span style={{ fontSize: 9, background: tab === tab2.id ? "#050507" : t.red, color: "#fff", borderRadius: 8, padding: "0px 5px", fontFamily: "'Fira Code',monospace" }}>{tab2.badge}</span>}
          </button>
        ))}
      </div>

      {/* Licenses Tab */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          {licenses.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No licenses yet. Create one above.</div>}
          {licenses.map(lic => (
            <LicenseCard key={lic.id} license={lic} t={t} isAdmin
              clientName={clients.find(c => c.id === lic.clientId)?.name}
              onRevoke={revokeLicense} onExtend={() => setExtendModal(lic)} onEditTier={() => setEditLicModal(lic)}
              onAddAccount={addAccount} onRemoveAccount={removeAccount} />
          ))}
        </div>
      )}

      {/* Versions Tab */}
      {tab === "versions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          {versions.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No versions uploaded yet.</div>}
          {[...versions].sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || "")).map(ver => (
            <VersionCard key={ver.id} version={ver} t={t} isAdmin
              onSetLatest={id => setVersions(vs => vs.map(v => ({ ...v, isLatest: v.id === id })))}
              onToggleMandatory={id => setVersions(vs => vs.map(v => v.id === id ? { ...v, isMandatory: !v.isMandatory } : v))}
              onDelete={id => setVersions(vs => vs.filter(v => v.id !== id))} />
          ))}
        </div>
      )}

      {/* Bugs Tab */}
      {tab === "bugs" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "0.5rem" }}>
            {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map(sev => {
              const count = bugs.filter(b => b.severity === sev).length;
              return count > 0 ? <span key={sev} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 3, background: `${SEVERITY_COLOR(sev, t)}15`, color: SEVERITY_COLOR(sev, t), fontFamily: "'Fira Code',monospace", border: `1px solid ${SEVERITY_COLOR(sev, t)}40` }}>{sev}: {count}</span> : null;
            })}
          </div>
          {bugs.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No bug reports yet.</div>}
          {[...bugs].sort((a, b) => ({ CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }[a.severity] - { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }[b.severity])).map(bug => (
            <BugRow key={bug.id} bug={bug} t={t} isAdmin clients={clients} onReply={replyToBug} />
          ))}
        </div>
      )}

      {/* Tickets Tab */}
      {tab === "tickets" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          {tickets.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No support tickets yet.</div>}
          {[...tickets].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(ticket => (
            <TicketThread key={ticket.id} ticket={ticket} t={t} isAdmin
              clientName={clients.find(c => c.id === ticket.clientId)?.name || "Client"}
              onReply={replyToTicket} onUpdateStatus={updateTicketStatus} />
          ))}
        </div>
      )}

      {/* Modal: New License */}
      <Modal open={newLicModal} onClose={() => setNewLicModal(false)} title="Create License" t={t}>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Client *</label>
                <select value={licForm.clientId} onChange={e => LF("clientId")(e.target.value)} style={{ ...fieldStyle }}>
                  <option value="">Select a client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem" }}>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Tier</label>
                  <select value={licForm.tier} onChange={e => LF("tier")(e.target.value)} style={{ ...fieldStyle }}>
                    <option value="STARTER">Starter — 1 account</option>
                    <option value="PRO">Pro — 5 accounts</option>
                    <option value="ELITE">Elite — Unlimited</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Billing</label>
                  <select value={licForm.billingCycle} onChange={e => LF("billingCycle")(e.target.value)} style={{ ...fieldStyle }}>
                    <option value="MONTHLY">Monthly</option>
                    <option value="ANNUAL">Annual</option>
                    <option value="LIFETIME">Lifetime</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Admin Notes</label>
                <input value={licForm.adminNotes} onChange={e => LF("adminNotes")(e.target.value)} placeholder="Internal notes…" style={{ ...fieldStyle }} />
              </div>
              <div style={{ padding: "0.75rem", background: t.surface2, borderRadius: 5, border: `1px solid ${t.border}`, fontSize: 11, color: t.textDim }}>
                License key will be auto-generated · Tier: <strong style={{ color: t.text }}>{TIER_META[licForm.tier]?.label}</strong>
              </div>
              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
                <button onClick={() => setNewLicModal(false)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
                <button onClick={createLicense} disabled={!licForm.clientId} style={{ padding: "7px 16px", borderRadius: 6, background: licForm.clientId ? t.teal : t.border, color: licForm.clientId ? "#050507" : t.textDimmer, border: "none", cursor: licForm.clientId ? "pointer" : "not-allowed", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>Create License</button>
              </div>
      </Modal>

      {/* Modal: Extend License */}
      <Modal open={!!extendModal} onClose={() => setExtendModal(null)} title="Extend License" t={t}>
      {extendModal && <>
              <div style={{ fontSize: 13, color: t.textDim }}>
                Current expiry: <strong style={{ color: t.text }}>{fmtDate(extendModal.expiresAt)}</strong> · {getDaysRemaining(extendModal.expiresAt)} days remaining
              </div>
              <div style={{ fontSize: 12, color: t.textDim }}>Extend by:</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[1, 3, 6, 12].map(m => (
                  <button key={m} onClick={() => extendLicense(extendModal.id, m)}
                    style={{ flex: 1, minWidth: 70, padding: "10px 12px", borderRadius: 6, background: `${t.teal}15`, border: `1px solid ${t.teal}40`, cursor: "pointer", color: t.teal, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, textAlign: "center" }}>
                    {m} mo
                  </button>
                ))}
              </div>
              <button onClick={() => setExtendModal(null)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", alignSelf: "flex-end" }}>Cancel</button>
      </>}
      </Modal>

      {/* Modal: Edit Tier */}
      <Modal open={!!editLicModal} onClose={() => setEditLicModal(null)} title="Change Tier" t={t}>
      {editLicModal && <>
              <div style={{ fontSize: 12, color: t.textDim }}>Current: <TierBadge tier={editLicModal.tier} t={t} /></div>
              {["STARTER", "PRO", "ELITE"].map(tier => (
                <button key={tier} onClick={() => updateLicenseTier(editLicModal.id, tier)}
                  style={{ padding: "10px 14px", borderRadius: 6, background: editLicModal.tier === tier ? `${TIER_META[tier].color}20` : t.surface2, border: `1px solid ${editLicModal.tier === tier ? TIER_META[tier].color + "60" : t.border}`, cursor: "pointer", color: t.text, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600 }}>{TIER_META[tier].label}</span>
                  <span style={{ fontSize: 11, color: TIER_META[tier].color, fontFamily: "'Fira Code',monospace" }}>{TIER_META[tier].accounts} accts · {TIER_META[tier].price}</span>
                </button>
              ))}
              <button onClick={() => setEditLicModal(null)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.border}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", alignSelf: "flex-end" }}>Cancel</button>
      </>}
      </Modal>

      {/* Modal: Upload Version */}
      <Modal open={newVerModal} onClose={() => setNewVerModal(false)} title="Upload EA Version" t={t}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem" }}>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Version Number *</label>
                  <input value={verForm.version} onChange={e => VF("version")(e.target.value)} placeholder="2.7.0" style={{ ...fieldStyle }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Release Type</label>
                  <select value={verForm.releaseType} onChange={e => VF("releaseType")(e.target.value)} style={{ ...fieldStyle }}>
                    <option value="PATCH">Patch — Bug fixes</option>
                    <option value="MINOR">Minor — New features</option>
                    <option value="MAJOR">Major — Breaking changes</option>
                    <option value="HOTFIX">Hotfix — Critical fix</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>File Name</label>
                <input value={verForm.fileName} onChange={e => VF("fileName")(e.target.value)} placeholder="QTrader_v2.7.0.ex5" style={{ ...fieldStyle }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Release Notes (Markdown)</label>
                <textarea value={verForm.releaseNotes} onChange={e => VF("releaseNotes")(e.target.value)} placeholder={"## What's New\n\n- Feature 1\n- Fix 2\n\n## Bug Fixes\n\n- Fixed issue with..."} rows={8} style={{ ...fieldStyle, resize: "vertical", minHeight: 160 }} />
              </div>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: t.textDim }}>
                  <div onClick={() => VF("isLatest")(!verForm.isLatest)} style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${verForm.isLatest ? t.teal : t.borderBright}`, background: verForm.isLatest ? t.teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    {verForm.isLatest && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={t.id === "dark" ? "#050507" : "#fff"} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  Mark as Latest Version
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: t.textDim }}>
                  <div onClick={() => VF("isMandatory")(!verForm.isMandatory)} style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${verForm.isMandatory ? t.red : t.borderBright}`, background: verForm.isMandatory ? t.red : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    {verForm.isMandatory && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  Force Update (Mandatory)
                </label>
              </div>
              <div style={{ padding: "0.75rem", background: `${t.amber}08`, borderRadius: 5, border: `1px solid ${t.amber}25`, fontSize: 11, color: t.textDim }}>
                <div style={{ marginBottom: 8, color: t.amber, fontWeight: 600 }}>Upload .ex5 / .ex4 / .zip file</div>
                <input
                  type="file"
                  accept=".ex5,.ex4,.zip,.pdf"
                  onChange={e => VF("file")(e.target.files[0] || null)}
                  style={{ fontSize: 12, color: t.textDim, fontFamily: "'Fira Code',monospace", width: "100%" }}
                />
                {verForm.file && (
                  <div style={{ marginTop: 6, color: t.teal, fontSize: 11 }}>
                    ✓ {verForm.file.name} ({(verForm.file.size / 1024).toFixed(0)} KB)
                  </div>
                )}
                {!verForm.file && (
                  <div style={{ marginTop: 6, color: t.textDimmer, fontSize: 11 }}>
                    No file selected — metadata will be saved without a download link.
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
                <button onClick={() => setNewVerModal(false)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
                <button onClick={createVersion} disabled={!verForm.version || !verForm.releaseNotes} style={{ padding: "7px 16px", borderRadius: 6, background: (verForm.version && verForm.releaseNotes) ? t.teal : t.border, color: (verForm.version && verForm.releaseNotes) ? "#050507" : t.textDimmer, border: "none", cursor: (verForm.version && verForm.releaseNotes) ? "pointer" : "not-allowed", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>Publish Version</button>
              </div>
      </Modal>
    </div>
  );
};
const LicenseCardClient = ({ lic, t, addAccount, removeAccount }) => {
  const [expanded, setExpanded] = React.useState(false);
  const tc = TIER_META[lic.tier]?.color || t.teal;
  const daysLeft = lic.expiresAt
    ? Math.ceil((new Date(lic.expiresAt) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const activeAccounts = lic.boundAccounts || [];
  const statusColor = lic.status === "ACTIVE" ? t.teal : lic.status === "REVOKED" ? t.red : t.amber;
  const isExpiringSoon = daysLeft !== null && daysLeft <= 14;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

      {/* ── HERO CREDIT CARD ── */}
      <div style={{
        borderRadius: 10,
        background: `linear-gradient(135deg, ${tc}18 0%, ${t.surface} 100%)`,
        border: `1px solid ${tc}40`,
        padding: "1.5rem",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Decorative circle */}
        <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: `${tc}08`, pointerEvents: "none" }} />
        {/* Second decorative circle */}
        <div style={{ position: "absolute", bottom: -30, left: "40%", width: 160, height: 160, borderRadius: "50%", background: `${tc}05`, pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          {/* Left side */}
          <div>
            <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.25em", textTransform: "uppercase", color: tc, marginBottom: 6 }}>
              Q-Trader EA · Active License
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300, fontStyle: "italic", color: t.text, lineHeight: 1, marginBottom: 8 }}>
              {TIER_META[lic.tier]?.label || lic.tier}
            </div>
            <div className="q-license-key" style={{ fontFamily: "'Fira Code',monospace", fontSize: 13, color: t.textDim, letterSpacing: "0.08em", marginBottom: 16 }}>
              {maskKey(lic.licenseKey)}
            </div>
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 3 }}>Activated</div>
                <div style={{ fontSize: 12, color: t.text, fontWeight: 500 }}>{fmtDate(lic.activatedAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 3 }}>Expires</div>
                <div style={{ fontSize: 12, color: isExpiringSoon ? t.amber : t.text, fontWeight: 500 }}>
                  {lic.expiresAt ? fmtDate(lic.expiresAt) : "Lifetime"}
                </div>
              </div>
              {daysLeft !== null && (
                <div>
                  <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 3 }}>Days Left</div>
                  <div style={{ fontSize: 12, color: daysLeft <= 30 ? t.amber : t.teal, fontWeight: 600 }}>{daysLeft}d</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 3 }}>Accounts</div>
                <div style={{ fontSize: 12, color: t.text, fontWeight: 500 }}>{activeAccounts.length} / {lic.maxAccounts === 999 ? "∞" : lic.maxAccounts}</div>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            <span style={{
              display: "inline-block", padding: "4px 14px", borderRadius: 4,
              background: `${statusColor}20`, color: statusColor,
              fontFamily: "'Fira Code',monospace", fontSize: 11,
              textTransform: "uppercase", letterSpacing: "0.1em",
              border: `1px solid ${statusColor}50`,
            }}>{lic.status}</span>

            {/* Account usage bar */}
            {lic.maxAccounts !== 999 && (
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, marginBottom: 4, textAlign: "right" }}>
                  {activeAccounts.length}/{lic.maxAccounts}
                </div>
                <div style={{ height: 4, background: `${tc}25`, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min(100, (activeAccounts.length / lic.maxAccounts) * 100)}%`,
                    background: activeAccounts.length >= lic.maxAccounts ? t.red : tc,
                    borderRadius: 4,
                    transition: "width 0.3s",
                  }} />
                </div>
              </div>
            )}

            <CopyBtn text={lic.licenseKey} label="Copy License Key" t={t} />
          </div>
        </div>
      </div>

      {/* ── EXPAND PANEL ── */}
      <div style={{
        background: t.surface,
        borderRadius: 8,
        border: `1px solid ${isExpiringSoon ? t.amber + "60" : t.border}`,
        overflow: "hidden",
      }}>
        {/* Toggle row */}
        <div style={{ padding: "0.75rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: t.textDim, display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
            <span>{activeAccounts.length} / {lic.maxAccounts === 999 ? "∞" : lic.maxAccounts} accounts</span>
            <span style={{ color: isExpiringSoon ? t.amber : t.textDim }}>
              {lic.expiresAt
                ? isExpiringSoon ? `⚠ Expires in ${daysLeft}d` : `Expires ${fmtDate(lic.expiresAt)}`
                : "Lifetime"}
            </span>
            <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 10 }}>{lic.billingCycle}</span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, cursor: "pointer", color: t.textDim, padding: "5px 9px", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
            {expanded ? "Less" : "Details"}
          </button>
        </div>

        {/* Expanded two-column panel */}
        {expanded && (
          <div style={{ borderTop: `1px solid ${t.border}`, padding: "1rem 1.25rem", background: t.surface2 }} className="fade-in">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>

              {/* Left — Bound accounts */}
              <div>
                <AccountSlots
                  license={lic}
                  t={t}
                  isAdmin={false}
                  canRemove={true}
                  canAdd={true}
                  onAddAccount={addAccount}
                  onRemoveAccount={removeAccount}
                />
              </div>

              {/* Right — License details */}
              <div>
                <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase", marginBottom: 8 }}>
                  License Details
                </div>
                {[
                  ["License Key", maskKey(lic.licenseKey)],
                  ["Tier", TIER_META[lic.tier]?.label || lic.tier],
                  ["Billing", lic.billingCycle],
                  ["Activated", fmtDate(lic.activatedAt)],
                  ["Expires", lic.expiresAt ? fmtDate(lic.expiresAt) : "Never (Lifetime)"],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 11, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.1em" }}>{k}</span>
                    <span style={{ fontSize: 11, color: t.text, fontWeight: 500, textAlign: "right", maxWidth: "60%" }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom action buttons */}
            <div style={{ marginTop: "1rem", display: "flex", gap: 8 }}>
              <CopyBtn text={lic.licenseKey} label="Copy License Key" t={t} />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT MODULE
// ═══════════════════════════════════════════════════════════════════════════════


// ─── COPY BUTTON with feedback ───────────────────────────────────────────────
const CopyBtn = ({ text, label = "Copy", t, style = {} }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    const trigger = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(trigger).catch(() => {
        // fallback
        const el = document.createElement('textarea');
        el.value = text; el.style.position = 'fixed'; el.style.opacity = '0';
        document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el); trigger();
      });
    } else {
      const el = document.createElement('textarea');
      el.value = text; el.style.position = 'fixed'; el.style.opacity = '0';
      document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el); trigger();
    }
  };
  return (
    <button onClick={handleCopy} style={{
      display: "flex", alignItems: "center", gap: 5,
      padding: "6px 12px", borderRadius: 5,
      background: copied ? `${t.teal}25` : `${t.teal}15`,
      border: `1px solid ${copied ? t.teal : t.teal + "40"}`,
      color: copied ? t.teal : t.teal,
      cursor: "pointer", fontSize: 11,
      fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500,
      transition: "all 0.15s",
      ...style,
    }}>
      {copied
        ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Copied!</>
        : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> {label}</>
      }
    </button>
  );
};

// ─── SUBSCRIPTION REQUEST MODAL ───────────────────────────────────────────────
const SubRequestModal = ({ mode, currentTier, clientName, clientEmail, onSubmit, onClose, t }) => {
  const tiers = mode === "upgrade"
    ? Object.entries(TIER_META).filter(([k]) => {
        const order = { STARTER: 0, PRO: 1, ELITE: 2 };
        return order[k] > order[currentTier];
      })
    : Object.entries(TIER_META);

  const [selectedTier, setSelectedTier] = React.useState(tiers[0]?.[0] || "STARTER");
  const [notes, setNotes] = React.useState("");

  const MPESA_INFO = {
    paybill: "522533",
    account: "QIRE-LICENSE",
    phone: "+254 XXX XXX XXX",
  };

  const selected = TIER_META[selectedTier];

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ background: t.surface, borderRadius: 10, width: "100%", maxWidth: 540, border: `1px solid ${t.borderBright}`, boxShadow: "0 32px 80px rgba(0,0,0,0.5)", overflow: "hidden" }}>
        
        {/* Header */}
        <div style={{ padding: "1.1rem 1.5rem", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, letterSpacing: "0.25em", color: t.teal, textTransform: "uppercase", marginBottom: 3 }}>Q-Trader EA</div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 16, color: t.text }}>
              {mode === "upgrade" ? "Request Upgrade" : "Get Your License"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t.textDim, fontSize: 18 }}>✕</button>
        </div>

        <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

          {/* Tier selector */}
          <div>
            <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", marginBottom: 10 }}>Select Plan</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tiers.map(([key, meta]) => (
                <div key={key} onClick={() => setSelectedTier(key)} style={{
                  padding: "12px 16px", borderRadius: 8, cursor: "pointer",
                  background: selectedTier === key ? `${meta.color}15` : t.surface2,
                  border: `1.5px solid ${selectedTier === key ? meta.color + "60" : t.border}`,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  transition: "all 0.15s",
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: selectedTier === key ? meta.color : t.text, fontFamily: "'Space Grotesk',sans-serif" }}>
                      {meta.label}
                    </div>
                    <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>
                      {meta.accounts === "∞" ? "Unlimited MT5 accounts" : `Up to ${meta.accounts} MT5 account${meta.accounts > 1 ? "s" : ""}`}
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 18, fontWeight: 700, color: meta.color }}>
                    {meta.price}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment instructions */}
          <div style={{ background: `${t.teal}08`, border: `1px solid ${t.teal}25`, borderRadius: 8, padding: "1rem 1.25rem" }}>
            <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.teal, textTransform: "uppercase", marginBottom: 10 }}>Payment — M-Pesa</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                ["Amount", selected.price + " / month"],
                ["Paybill", MPESA_INFO.paybill],
                ["Account No.", MPESA_INFO.account],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, color: t.text, fontWeight: 600 }}>{value}</span>
                    {(label === "Paybill" || label === "Account No.") && <CopyBtn text={value} label="Copy" t={t} />}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: t.textDim, lineHeight: 1.5 }}>
              Send payment via M-Pesa then submit this request. We'll activate your license within 24 hours after payment confirmation.
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. M-Pesa transaction ID, any questions..."
              rows={2}
              style={{ width: "100%", background: t.surface2, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "7px 10px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", resize: "vertical" }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
            <button onClick={() => onSubmit(selectedTier, notes)} style={{ padding: "8px 20px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 }}>
              Submit Request →
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── SUBSCRIPTIONS ADMIN TAB ─────────────────────────────────────────────────
const Subscriptions = ({ subRequests, setSubRequests, clients, t }) => {
  const statusColor = s => ({ Pending: t.amber, Approved: t.teal, Rejected: t.red }[s] || t.textDim);
  const [rejectModal, setRejectModal] = React.useState(null); // { id, clientName }
  const [rejectReason, setRejectReason] = React.useState("");

  const approve = (id) => {
    setSubRequests(rs => rs.map(r => r.id === id ? {
      ...r, status: "Approved",
      adminNote: "Your license is being activated. You will see it shortly after logging in.",
      updatedAt: new Date().toISOString().slice(0, 10),
    } : r));
  };

  const openReject = (r) => { setRejectModal(r); setRejectReason(""); };

  const confirmReject = () => {
    if (!rejectReason.trim()) return;
    setSubRequests(rs => rs.map(r => r.id === rejectModal.id ? {
      ...r, status: "Rejected",
      adminNote: rejectReason.trim(),
      updatedAt: new Date().toISOString().slice(0, 10),
    } : r));
    setRejectModal(null);
    setRejectReason("");
  };

  const pending = subRequests.filter(r => r.status === "Pending");
  const others  = subRequests.filter(r => r.status !== "Pending");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} className="fade-up">
      <SectionHeader
        eyebrow="Q-Trader"
        title="Subscription Requests"
        sub={`${pending.length} pending · ${subRequests.length} total`}
        t={t}
      />

      {subRequests.length === 0 && (
        <div style={{ textAlign: "center", padding: "4rem", color: t.textDimmer, fontSize: 13, fontStyle: "italic" }}>
          No subscription requests yet. They'll appear here when clients request a license or upgrade.
        </div>
      )}

      {pending.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.amber, textTransform: "uppercase", marginBottom: 10 }}>Pending ({pending.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pending.map(r => (
              <SubRequestRow key={r.id} r={r} t={t} statusColor={statusColor}
                onApprove={() => approve(r.id)}
                onReject={() => openReject(r)}
              />
            ))}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", marginBottom: 10 }}>History ({others.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...others].sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).map(r => (
              <SubRequestRow key={r.id} r={r} t={t} statusColor={statusColor} />
            ))}
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejectModal && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div className="fade-up" style={{ background: t.surface, borderRadius: 10, width: "100%", maxWidth: 420, border: `1px solid ${t.red}40`, boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
            <div style={{ padding: "1.1rem 1.5rem", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, letterSpacing: "0.2em", color: t.red, textTransform: "uppercase", marginBottom: 3 }}>Decline Request</div>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 15, color: t.text }}>{rejectModal.clientName}</div>
              </div>
              <button onClick={() => setRejectModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textDim, fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ fontSize: 13, color: t.textDim }}>
                Provide a reason so the client knows why their request was declined. This will be shown to them on their Q-Trader page.
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Payment not confirmed. Please send M-Pesa and resubmit."
                  rows={3}
                  autoFocus
                  style={{ width: "100%", background: t.surface2, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "8px 10px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", resize: "vertical" }}
                />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setRejectModal(null)} style={{ padding: "8px 18px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
                <button onClick={confirmReject} disabled={!rejectReason.trim()} style={{ padding: "8px 18px", borderRadius: 6, background: rejectReason.trim() ? t.red : t.border, color: rejectReason.trim() ? "#fff" : t.textDimmer, border: "none", cursor: rejectReason.trim() ? "pointer" : "not-allowed", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 }}>
                  Decline Request
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const SubRequestRow = ({ r, t, statusColor, onApprove, onReject }) => {
  const sc = statusColor(r.status);
  const tierMeta = TIER_META[r.tier] || TIER_META.STARTER;
  return (
    <div style={{ background: t.surface, borderRadius: 8, border: `1px solid ${r.status === "Pending" ? t.amber + "40" : t.border}`, padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: t.text, fontFamily: "'Space Grotesk',sans-serif", marginBottom: 2 }}>{r.clientName}</div>
        <div style={{ fontSize: 11, color: t.textDim, fontFamily: "'Fira Code',monospace" }}>{r.clientEmail}</div>
        {r.notes && <div style={{ fontSize: 11, color: t.textDimmer, marginTop: 4, fontStyle: "italic" }}>"{r.notes}"</div>}
        {r.adminNote && r.status !== "Pending" && (
          <div style={{ fontSize: 11, marginTop: 4, color: r.status === "Approved" ? t.teal : t.red, display: "flex", alignItems: "flex-start", gap: 5 }}>
            <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 1, flexShrink: 0 }}>{r.status === "Approved" ? "Note:" : "Reason:"}</span>
            <span>{r.adminNote}</span>
          </div>
        )}
        <div style={{ fontSize: 10, color: t.textDimmer, marginTop: 4 }}>{r.type === "upgrade" ? `Upgrade request` : "New license request"} · {r.createdAt?toDateInput(r.createdAt):"—"}{r.updatedAt && r.updatedAt !== r.createdAt ? ` · Updated ${toDateInput(r.updatedAt)}` : ""}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>Tier</div>
          <div style={{ fontWeight: 700, fontSize: 13, color: tierMeta.color, fontFamily: "'Space Grotesk',sans-serif" }}>{tierMeta.label}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", color: t.textDimmer, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>Price</div>
          <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 13, color: t.text, fontWeight: 600 }}>{tierMeta.price}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <span style={{ padding: "3px 10px", borderRadius: 4, background: `${sc}20`, color: sc, fontFamily: "'Fira Code',monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", border: `1px solid ${sc}40` }}>{r.status}</span>
          {r.status === "Pending" && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={onApprove} style={{ padding: "5px 12px", borderRadius: 5, background: `${t.teal}20`, border: `1px solid ${t.teal}50`, color: t.teal, cursor: "pointer", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>✓ Approve</button>
              <button onClick={onReject} style={{ padding: "5px 12px", borderRadius: 5, background: `${t.red}15`, border: `1px solid ${t.red}40`, color: t.red, cursor: "pointer", fontSize: 11, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>✕ Reject</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── REQUEST STATUS TRACKER ──────────────────────────────────────────────────
const RequestStatusTracker = ({ req, hasLicense, licenses, t, onResubmit, onOpenTicket }) => {
  if (!req) return null;

  const tierMeta = TIER_META[req.tier] || TIER_META.STARTER;

  // If approved AND the license exists with the right tier — it's fully done, hide tracker
  const licenseActivated = hasLicense && licenses.some(l =>
    l.tier === req.tier && (l.status === "ACTIVE" || l.status === "TRIALING")
  );
  if (req.status === "Approved" && licenseActivated) return null;

  // Steps definition
  const steps = [
    {
      key: "submitted",
      label: "Request Submitted",
      detail: `${req.type === "upgrade" ? "Upgrade to" : "New license:"} ${tierMeta.label} · ${tierMeta.price}`,
      date: req.createdAt,
      done: true,
      active: req.status === "Pending",
    },
    {
      key: "review",
      label: "Under Review",
      detail: req.status === "Pending" ? "Qire Agency is reviewing your request." : null,
      date: null,
      done: req.status !== "Pending",
      active: req.status === "Pending",
    },
    {
      key: "decision",
      label: req.status === "Approved" ? "Approved ✓" : req.status === "Rejected" ? "Declined" : "Decision",
      detail: req.adminNote || null,
      date: req.updatedAt || null,
      done: req.status !== "Pending",
      active: req.status !== "Pending",
    },
    ...(req.status === "Approved" ? [{
      key: "activation",
      label: "License Activation",
      detail: "Your license is being set up. Log out and back in if it doesn't appear.",
      date: null,
      done: licenseActivated,
      active: !licenseActivated,
    }] : []),
  ];

  const borderColor = req.status === "Rejected" ? t.red
    : req.status === "Approved" ? t.teal
    : t.amber;

  return (
    <div style={{
      background: t.surface, borderRadius: 10,
      border: `1px solid ${borderColor}35`,
      overflow: "hidden",
    }} className="fade-in">

      {/* Header bar */}
      <div style={{
        padding: "0.85rem 1.25rem",
        background: `${borderColor}10`,
        borderBottom: `1px solid ${borderColor}25`,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: req.status === "Pending" ? t.amber : req.status === "Approved" ? t.teal : t.red,
            boxShadow: req.status === "Pending" ? `0 0 0 3px ${t.amber}30` : "none",
          }} />
          <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: borderColor }}>
            {req.status === "Pending" ? "Request Pending Review" : req.status === "Approved" ? "Request Approved" : "Request Declined"}
          </span>
        </div>
        <span style={{
          padding: "2px 10px", borderRadius: 4,
          background: `${borderColor}20`, color: borderColor,
          fontFamily: "'Fira Code',monospace", fontSize: 10,
          textTransform: "uppercase", letterSpacing: "0.1em",
          border: `1px solid ${borderColor}40`,
        }}>{tierMeta.label} · {tierMeta.price}</span>
      </div>

      {/* Timeline */}
      <div style={{ padding: "1.25rem 1.5rem" }}>
        <div style={{ position: "relative" }}>
          {/* Vertical line */}
          <div style={{
            position: "absolute", left: 9, top: 18, bottom: 18,
            width: 1, background: t.border,
          }} />

          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            {steps.map((step, i) => {
              const dotColor = step.done ? (step.key === "decision" && req.status === "Rejected" ? t.red : t.teal)
                : step.active ? borderColor : t.textDimmer;
              return (
                <div key={step.key} style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  {/* Dot */}
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                    background: step.done ? dotColor : "transparent",
                    border: `2px solid ${dotColor}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 1, position: "relative",
                    boxShadow: step.active && !step.done ? `0 0 0 4px ${dotColor}20` : "none",
                    transition: "all 0.2s",
                  }}>
                    {step.done && (
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#050506" strokeWidth="3.5">
                        {step.key === "decision" && req.status === "Rejected"
                          ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                          : <polyline points="20 6 9 17 4 12"/>}
                      </svg>
                    )}
                    {!step.done && step.active && (
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, paddingBottom: i < steps.length - 1 ? 4 : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 13, fontWeight: 600,
                        fontFamily: "'Space Grotesk',sans-serif",
                        color: step.done || step.active ? t.text : t.textDimmer,
                      }}>{step.label}</span>
                      {step.date && (
                        <span style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.textDimmer }}>{step.date}</span>
                      )}
                    </div>
                    {step.detail && (
                      <div style={{
                        fontSize: 12, marginTop: 3, lineHeight: 1.5,
                        color: step.key === "decision" && req.status === "Rejected" ? t.red
                          : step.key === "decision" && req.status === "Approved" ? t.teal
                          : t.textDim,
                      }}>{step.detail}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons for rejected */}
        {req.status === "Rejected" && (
          <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: `1px solid ${t.border}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={onResubmit} style={{
              padding: "8px 18px", borderRadius: 6,
              background: t.teal, color: "#050507",
              border: "none", cursor: "pointer",
              fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
            }}>↺ Resubmit Request</button>
            <button onClick={onOpenTicket} style={{
              padding: "8px 18px", borderRadius: 6,
              background: "none", color: t.textDim,
              border: `1px solid ${t.border}`, cursor: "pointer",
              fontSize: 12, fontFamily: "'Space Grotesk',sans-serif",
            }}>Open Support Ticket</button>
          </div>
        )}

        {/* Pending — reassurance note */}
        {req.status === "Pending" && (
          <div style={{ marginTop: "1rem", padding: "0.75rem 1rem", background: `${t.amber}08`, borderRadius: 6, border: `1px solid ${t.amber}20`, fontSize: 11, color: t.textDim, lineHeight: 1.6 }}>
            <strong style={{ color: t.amber }}>Payment reminder:</strong> Make sure you've sent the M-Pesa payment. Requests without payment confirmation may take longer or be declined.
          </div>
        )}
      </div>
    </div>
  );
};

const QTraderClient = ({ licenses, setLicenses, versions, bugs, setBugs, tickets, setTickets, clientId, clientName, clientEmail, subRequests, setSubRequests, t }) => {
  const [tab, setTab] = useState("license");
  const [bugModal, setBugModal] = useState(false);
  const [ticketModal, setTicketModal] = useState(false);
  const [bugForm, setBugForm] = useState({ title: "", description: "", stepsToRepro: "", eaVersion: "", broker: "", symbol: "", timeframe: "", severity: "MEDIUM" });
  const [ticketForm, setTicketForm] = useState({ subject: "", message: "" });
  const [subModal, setSubModal] = useState(null); // null | "new" | "upgrade"

  const latestVersion = versions.find(v => v.isLatest);
  const myLicense = licenses[0];
  const hasLicense = !!myLicense;

  // Most recent non-superseded request for this client
  const myRequest = [...subRequests]
    .filter(r => r.clientEmail === clientEmail && r.status !== "Superseded")
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null;
  const hasPendingRequest = myRequest?.status === "Pending";

  // Account management — client updates global licenses state
  const addAccount = async (licId, acc) => {
    const clientToken = '';
    if (clientToken) {
      try {
        const res = await qtFetch(`/api/v1/licenses/${licId}/accounts/self`, {
          method: 'POST',
          body: JSON.stringify({ accountNumber: acc.accountNumber, broker: acc.broker }),
        }, clientToken);
        // Use the real DB record returned from the API
        if (res.account) {
          setLicenses(ls => ls.map(l => l.id !== licId ? l : {
            ...l, boundAccounts: [...(l.boundAccounts || []), res.account],
          }));
        }
      } catch (err) {
        // Show the API error message to the client (e.g. "Account limit reached")
        alert(err.message);
      }
    } else {
      // No token yet — optimistic local only (fallback)
      setLicenses(ls => ls.map(l => l.id !== licId ? l : {
        ...l, boundAccounts: [...(l.boundAccounts || []), { ...acc, id: Date.now() }],
      }));
    }
  };

  const removeAccount = async (licId, accId) => {
    // Optimistic update
    setLicenses(ls => ls.map(l => l.id !== licId ? l : { ...l, boundAccounts: (l.boundAccounts || []).filter(a => a.id !== accId) }));
    // Sync to API — removes from DB immediately, EA stops on that account
    const clientToken = '';
    if (clientToken) {
      try {
        await qtFetch(`/api/v1/licenses/${licId}/accounts/${accId}/self`, { method: 'DELETE' }, clientToken);
      } catch (err) {
        console.warn('removeAccount API call failed:', err.message);
      }
    }
  };

  // Product ID resolved at runtime from versions list
  const QT_PRODUCT_ID = versions && versions.length > 0 ? versions[0].productId : null;

const submitBug = async () => {
  if (!bugForm.title || !bugForm.description) return;

  // Optimistic UI update
  const newBug = {
    id: Date.now(), clientId,
    productId: QT_PRODUCT_ID, // ✓ correct field
    ...bugForm, status: 'OPEN', adminReply: '',
    repliedAt: '', createdAt: new Date().toISOString().slice(0, 10),
  };
  setBugs(bs => [...bs, newBug]);
  setBugForm({ title: '', description: '', stepsToRepro: '', eaVersion: '',
    broker: '', symbol: '', timeframe: '', severity: 'MEDIUM' });
  setBugModal(false);

  // Sync to API
  const clientToken = '';
  if (clientToken) {
    try {
      const res = await qtFetch('/api/v1/bugs', {
        method: 'POST',
        body: JSON.stringify({
          productId:   QT_PRODUCT_ID, // ✓ correct — not licenseId
          title:       bugForm.title,
          description: bugForm.description,
          severity:    bugForm.severity,
          stepsToRepro: bugForm.stepsToRepro,
          eaVersion:   bugForm.eaVersion,
          broker:      bugForm.broker,
          symbol:      bugForm.symbol,
          timeframe:   bugForm.timeframe,
        }),
      }, clientToken);
      // Replace temp bug with real one from API
      if (res.report) {
        setBugs(bs => bs.map(b => b.id === newBug.id ? res.report : b));
      }
    } catch (err) {
      console.warn('Bug submit failed:', err.message);
    }
  }
};

  const openTicket = async () => {
    if (!ticketForm.subject || !ticketForm.message) return;
    setTickets(ts => [...ts, { id: Date.now(), clientId, subject: ticketForm.subject, status: "OPEN", createdAt: new Date().toISOString().slice(0, 10), messages: [{ id: Date.now(), body: ticketForm.message, isFromAdmin: false, createdAt: new Date().toISOString() }] }]);
    setTicketForm({ subject: "", message: "" });
    setTicketModal(false);
    setTab("support");

    const clientToken = '';
    if (clientToken) {
      try {
        await qtFetch('/api/v1/tickets', {
          method: 'POST',
          body: JSON.stringify({ subject: ticketForm.subject, message: ticketForm.message }),
        }, clientToken);
      } catch (err) {
        console.warn('ticket open sync failed:', err.message);
      }
    }
  };

  const replyToTicket = async (ticketId, body) => {
    setTickets(ts => ts.map(tk => tk.id !== ticketId ? tk : {
      ...tk,
      status: "OPEN",
      messages: [...(tk.messages || []), { id: Date.now(), body, isFromAdmin: false, createdAt: new Date().toISOString() }],
    }));
    const clientToken = '';
    if (clientToken) {
      try {
        await qtFetch(`/api/v1/tickets/${ticketId}/messages`, {
          method: 'POST',
          body: JSON.stringify({ message: body }),
        }, clientToken);
      } catch (err) {
        console.warn('replyToTicket sync failed:', err.message);
      }
    }
  };

  const submitSubRequest = (tier, notes) => {
    const req = {
      id: Date.now(),
      clientName: clientName || "Client",
      clientEmail: clientEmail || "",
      tier,
      notes,
      type: subModal,
      status: "Pending",
      createdAt: new Date().toISOString().slice(0, 10),
    };
    // Mark any previous rejected request from this client as superseded
    setSubRequests(rs => [
      ...rs.map(r => r.clientEmail === (clientEmail || "") && r.status === "Rejected"
        ? { ...r, status: "Superseded" }
        : r
      ),
      req,
    ]);
    setSubModal(null);
  };

  const fieldStyle = { width: "100%", background: t.surface2, color: t.text, border: `1px solid ${t.border}`, borderRadius: 5, padding: "7px 10px", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", outline: "none", minHeight: 36 };
  const tabStyle = id => ({
    padding: "6px 14px", borderRadius: 5, cursor: "pointer", fontSize: 11,
    fontFamily: "'Space Grotesk',sans-serif", fontWeight: tab === id ? 600 : 400,
    background: tab === id ? t.teal : "transparent",
    color: tab === id ? (t.id === "dark" ? "#050507" : "#fff") : t.textDim,
    border: tab === id ? "none" : `1px solid ${t.border}`,
    transition: "all 0.15s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} className="fade-up">

      {/* Header */}
      <div>
        <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: t.teal, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
          Q-Trader EA <span style={{ display: "inline-block", height: 1, width: 40, background: t.border, verticalAlign: "middle" }} />
        </div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 400, fontStyle: "italic", color: t.text, letterSpacing: "0.025em", lineHeight: 1.1 }}>Your License</div>
        <div style={{ fontSize: 13, color: t.textDim, marginTop: 4 }}>Manage your Q-Trader EA license, downloads, and support</div>
      </div>
{/* Quick stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: "0.75rem" }}>
        {[
          { l: "License Status", v: myLicense ? myLicense.status : "—", c: myLicense?.status === "ACTIVE" ? t.teal : t.red },
          { l: "EA Version", v: latestVersion ? `v${latestVersion.version}` : "—", c: t.blue },
          { l: "Bound Accounts", v: myLicense?.boundAccounts?.length ?? 0, c: t.textDim },
          { l: "Open Tickets", v: tickets.filter(tk => tk.status !== "CLOSED" && tk.status !== "RESOLVED").length, c: tickets.filter(tk => tk.status !== "CLOSED" && tk.status !== "RESOLVED").length > 0 ? t.amber : t.textDimmer },
          { l: "Bug Replies", v: bugs.filter(b => b.adminReply && b.adminReply !== "").length, c: bugs.filter(b => b.adminReply && b.adminReply !== "").length > 0 ? t.teal : t.textDimmer },
        ].map(s => (
          <div key={s.l} style={{ background: t.surface, borderRadius: 7, padding: "1rem", border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 9, fontFamily: "'Fira Code',monospace", letterSpacing: "0.18em", color: t.textDim, textTransform: "uppercase", marginBottom: 6 }}>{s.l}</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 400, fontStyle: "italic", color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Expiry warning */}
      {myLicense && (() => {
        const d = getDaysRemaining(myLicense.expiresAt);
        return d !== null && d <= 14 && myLicense.status === "ACTIVE" ? (
          <div style={{ padding: "0.85rem 1.1rem", borderRadius: 7, background: `${t.amber}10`, border: `1px solid ${t.amber}40`, fontSize: 12, color: t.amber, display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Your license expires in <strong style={{ marginLeft: 3 }}>{d} day{d !== 1 ? "s" : ""}</strong>. Contact support to renew.
          </div>
        ) : null;
      })()}
      {/* Request Status Tracker */}
      <RequestStatusTracker
        req={myRequest}
        hasLicense={hasLicense}
        licenses={licenses}
        t={t}
        onResubmit={() => setSubModal(hasLicense ? "upgrade" : "new")}
        onOpenTicket={() => { setTab("support"); setTimeout(() => setTicketModal(true), 50); }}
      />

      {/* Support tab always available — even without a license (e.g. after rejection) */}
      {!hasLicense && (
        <div style={{ display: "flex", gap: 5, marginTop: "0.5rem" }}>
          <button onClick={() => setTab("support")} style={{
            padding: "6px 14px", borderRadius: 5, cursor: "pointer", fontSize: 11,
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: tab === "support" ? 600 : 400,
            background: tab === "support" ? t.teal : "transparent",
            color: tab === "support" ? (t.id === "dark" ? "#050507" : "#fff") : t.textDim,
            border: tab === "support" ? "none" : `1px solid ${t.border}`,
            transition: "all 0.15s",
          }}>
            Support
            {tickets.filter(tk => tk.status !== "CLOSED" && tk.status !== "RESOLVED").length > 0 && (
              <span style={{ fontSize: 9, background: t.amber, color: "#050507", borderRadius: 8, padding: "0px 5px", fontFamily: "'Fira Code',monospace", marginLeft: 5 }}>
                {tickets.filter(tk => tk.status !== "CLOSED" && tk.status !== "RESOLVED").length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Support tab content for no-license clients */}
      {!hasLicense && tab === "support" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={() => setTicketModal(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Ticket
            </button>
          </div>
          {tickets.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No support tickets yet. Have a question about your request? Open a ticket!</div>}
          {[...tickets].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(ticket => (
            <TicketThread key={ticket.id} ticket={ticket} t={t} isAdmin={false} clientName="You" onReply={replyToTicket} />
          ))}
        </div>
      )}

      {/* No license purchase state */}
      {!hasLicense && tab !== "support" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ background: t.surface, borderRadius: 8, padding: "2rem", textAlign: "center", border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🔑</div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 17, color: t.text, marginBottom: 6 }}>Get Your Q-Trader EA License</div>
            <div style={{ fontSize: 13, color: t.textDim, marginBottom: 24 }}>Choose a plan below. Pay via M-Pesa and we'll activate within 24hrs.</div>
            <div style={{ display: "grid", gridTemplateColumns: window.innerWidth <= 480 ? "1fr" : "repeat(3, 1fr)", gap: 12, maxWidth: 560, margin: "0 auto", width: "100%" }}>
              {["STARTER", "PRO", "ELITE"].map(tier => {
                const meta = TIER_META[tier];
                return (
                  <div key={tier} onClick={() => !hasPendingRequest && setSubModal("new")} style={{
                    padding: "1.25rem 1rem", borderRadius: 8, cursor: hasPendingRequest ? "not-allowed" : "pointer",
                    background: hasPendingRequest ? `${t.border}20` : `${meta.color}10`,
                    border: `1.5px solid ${hasPendingRequest ? t.border : meta.color + "35"}`,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    transition: "all 0.15s",
                    opacity: hasPendingRequest ? 0.45 : 1,
                  }}
                  onMouseEnter={e => { if (!hasPendingRequest) { e.currentTarget.style.background = `${meta.color}20`; e.currentTarget.style.borderColor = `${meta.color}70`; }}}
                  onMouseLeave={e => { if (!hasPendingRequest) { e.currentTarget.style.background = `${meta.color}10`; e.currentTarget.style.borderColor = `${meta.color}35`; }}}>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 15, color: hasPendingRequest ? t.textDimmer : meta.color }}>{meta.label}</div>
                    <div style={{ fontSize: 11, color: t.textDim }}>{meta.accounts === "∞" ? "Unlimited accounts" : `${meta.accounts} account${meta.accounts > 1 ? "s" : ""}`}</div>
                    <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 20, fontWeight: 700, color: t.text }}>{meta.price}</div>
                    <div style={{ padding: "6px 14px", borderRadius: 5, background: hasPendingRequest ? t.border : meta.color, color: hasPendingRequest ? t.textDimmer : "#050506", fontSize: 12, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, width: "100%", textAlign: "center" }}>
                      {hasPendingRequest ? "Request Pending" : `Get ${meta.label}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {hasLicense && (
        <>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[
              { id: "license", label: "License" },
              { id: "downloads", label: "Downloads" },
              { id: "bugs", label: "Bug Reports", badge: bugs.filter(b => b.status === "OPEN").length || null },
              { id: "support", label: "Support", badge: tickets.filter(tk => tk.status === "OPEN").length || null },
            ].map(tab2 => (
              <button key={tab2.id} onClick={() => setTab(tab2.id)} style={tabStyle(tab2.id)}>
                {tab2.label}
                {tab2.badge > 0 && <span style={{ fontSize: 9, background: tab === tab2.id ? "#050507" : t.red, color: "#fff", borderRadius: 8, padding: "0px 5px", fontFamily: "'Fira Code',monospace" }}>{tab2.badge}</span>}
              </button>
            ))}
          </div>

          {/* License Tab */}
{tab === "license" && (
  <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>

    {licenses.map(lic => (
      <LicenseCardClient
        key={lic.id}
        lic={lic}
        t={t}
        addAccount={addAccount}
        removeAccount={removeAccount}
      />
    ))}

    {/* Latest version banner */}
    {latestVersion && (
      <div style={{ background: `${t.teal}08`, borderRadius: 8, padding: "1rem 1.25rem", border: `1px solid ${t.teal}25`, display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", color: t.teal, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>Latest Version Available</div>
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 15, color: t.text }}>Q-Trader v{latestVersion.version}</div>
          <div style={{ fontSize: 12, color: t.textDim, marginTop: 2 }}>Published {fmtDate(latestVersion.publishedAt)}</div>
        </div>
        <button
          onClick={() => qtDownload(latestVersion.id, latestVersion.fileName)}
          style={{ padding: "8px 18px", borderRadius: 6, background: t.teal, color: "#050507", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download Latest
        </button>
      </div>
    )}

    {/* Upgrade prompt */}
    {myLicense && myLicense.tier !== "ELITE" && !hasPendingRequest && (
      <div style={{ padding: "1.25rem 1.5rem", borderRadius: 8, background: `${t.teal}08`, border: `1px solid ${t.teal}25`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: t.text, marginBottom: 4 }}>Ready to scale?</div>
          <div style={{ fontSize: 12, color: t.textDim }}>
            {myLicense.tier === "STARTER" ? "Upgrade to Pro for 5 accounts, or Elite for unlimited." : "Upgrade to Elite for unlimited accounts."}
          </div>
        </div>
        <button onClick={() => setSubModal("upgrade")} style={{ padding: "9px 20px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 }}>
          ↑ Upgrade Plan
        </button>
      </div>
    )}

  </div>
)}

          {/* Downloads Tab */}
          {tab === "downloads" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              <div style={{ fontSize: 13, color: t.textDim, marginBottom: 4 }}>All available EA versions for your license tier.</div>
              {versions.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No versions available yet.</div>}
              {[...versions].sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || "")).map(ver => (
                <VersionCard key={ver.id} version={ver} t={t} isAdmin={false} canDownload={true} />
              ))}
            </div>
          )}

          {/* Bug Reports Tab */}
          {tab === "bugs" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => setBugModal(true)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Report Bug
                </button>
              </div>
              {bugs.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No bug reports yet. Found an issue? Let us know!</div>}
              {[...bugs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(bug => (
                <BugRow key={bug.id} bug={bug} t={t} isAdmin={false} />
              ))}
            </div>
          )}

          {/* Support Tab */}
          {tab === "support" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => setTicketModal(true)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 6, background: t.teal, color: "#050507", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  New Ticket
                </button>
              </div>
              {tickets.length === 0 && <div style={{ textAlign: "center", color: t.textDimmer, fontSize: 13, padding: "3rem", fontStyle: "italic" }}>No support tickets yet. Need help? Open a ticket!</div>}
              {[...tickets].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(ticket => (
                <TicketThread key={ticket.id} ticket={ticket} t={t} isAdmin={false} clientName="You" onReply={replyToTicket} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal: Report Bug */}
      <Modal open={bugModal} onClose={() => setBugModal(false)} title="Report a Bug" t={t} wide>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Title *</label>
                <input value={bugForm.title} onChange={e => setBugForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief description of the issue…" style={{ ...fieldStyle }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Description *</label>
                <textarea value={bugForm.description} onChange={e => setBugForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened? What did you expect?" rows={3} style={{ ...fieldStyle, resize: "vertical" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Steps to Reproduce</label>
                <textarea value={bugForm.stepsToRepro} onChange={e => setBugForm(f => ({ ...f, stepsToRepro: e.target.value }))} placeholder={"1. Attach EA to chart\n2. Set input X to Y\n3. Wait for Z\n4. Bug occurs"} rows={4} style={{ ...fieldStyle, resize: "vertical" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem" }}>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>EA Version</label>
                  <select value={bugForm.eaVersion} onChange={e => setBugForm(f => ({ ...f, eaVersion: e.target.value }))} style={{ ...fieldStyle }}>
                    <option value="">Select…</option>
                    {versions.map(v => <option key={v.id} value={v.version}>v{v.version}{v.isLatest ? " (latest)" : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Severity</label>
                  <select value={bugForm.severity} onChange={e => setBugForm(f => ({ ...f, severity: e.target.value }))} style={{ ...fieldStyle }}>
                    <option value="LOW">Low — Minor inconvenience</option>
                    <option value="MEDIUM">Medium — Feature broken</option>
                    <option value="HIGH">High — Major functionality affected</option>
                    <option value="CRITICAL">Critical — EA not trading / data loss</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: "0.65rem" }}>
                {[["broker", "Broker", "ICMarkets-Live"], ["symbol", "Symbol", "EURUSD"], ["timeframe", "Timeframe", "M15"]].map(([key, label, ph]) => (
                  <div key={key}>
                    <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>{label}</label>
                    <input value={bugForm[key]} onChange={e => setBugForm(f => ({ ...f, [key]: e.target.value }))} placeholder={ph} style={{ ...fieldStyle }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
                <button onClick={() => setBugModal(false)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
                <button onClick={submitBug} disabled={!bugForm.title || !bugForm.description}
                  style={{ padding: "7px 16px", borderRadius: 6, background: bugForm.title && bugForm.description ? t.teal : t.border, color: bugForm.title && bugForm.description ? "#050507" : t.textDimmer, border: "none", cursor: bugForm.title && bugForm.description ? "pointer" : "not-allowed", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
                  Submit Report
                </button>
              </div>
      </Modal>

      {/* Modal: Subscription Request */}
      {subModal && (
        <SubRequestModal
          mode={subModal}
          currentTier={myLicense?.tier || "STARTER"}
          clientName={clientName}
          clientEmail={clientEmail}
          onSubmit={submitSubRequest}
          onClose={() => setSubModal(null)}
          t={t}
        />
      )}

      {/* Modal: New Ticket */}
      <Modal open={ticketModal} onClose={() => setTicketModal(false)} title="Open Support Ticket" t={t}>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Subject *</label>
                <input value={ticketForm.subject} onChange={e => setTicketForm(f => ({ ...f, subject: e.target.value }))} placeholder="What do you need help with?" style={{ ...fieldStyle }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontFamily: "'Fira Code',monospace", letterSpacing: "0.2em", color: t.textDim, textTransform: "uppercase", display: "block", marginBottom: 5 }}>Message *</label>
                <textarea value={ticketForm.message} onChange={e => setTicketForm(f => ({ ...f, message: e.target.value }))} placeholder="Describe your question or issue in detail…" rows={4} style={{ ...fieldStyle, resize: "vertical" }} />
              </div>
              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
                <button onClick={() => setTicketModal(false)} style={{ padding: "7px 16px", borderRadius: 6, background: "none", border: `1px solid ${t.borderBright}`, cursor: "pointer", color: t.textDim, fontSize: 13, fontFamily: "'Space Grotesk',sans-serif" }}>Cancel</button>
                <button onClick={openTicket} disabled={!ticketForm.subject || !ticketForm.message}
                  style={{ padding: "7px 16px", borderRadius: 6, background: ticketForm.subject && ticketForm.message ? t.teal : t.border, color: ticketForm.subject && ticketForm.message ? "#050507" : t.textDimmer, border: "none", cursor: ticketForm.subject && ticketForm.message ? "pointer" : "not-allowed", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>
                  Open Ticket
                </button>
              </div>
      </Modal>

    </div>
  );
};                   
// ─── ROLE DASHBOARD: SALES ───────────────────────────────────────────────────
// ─── MODULE: SALES DASHBOARD ─────────────────────────────────────────────────
const SalesDashboard = ({ leads, clients, proposals, invoices, t, setPage }) => {
  const newLeads     = leads.filter(l => l.status === "NEW");
  const active       = leads.filter(l => !["Won","Lost","Junk"].includes(l.status));
  const won          = leads.filter(l => l.status === "Won");
  const pipeline     = active.reduce((a,l) => a + (l.budget||0), 0);
  const calls        = leads.flatMap(l => (l.calls||[]).filter(c => c.status === "Scheduled"));
  const recentLeads  = [...leads].sort((a,b) => (b.id||0)-(a.id||0)).slice(0,5);
  const quickActions = [
    { label:"Add Lead",       icon:"plus",     page:"sales",     color:"#4a9eff" },
    { label:"View Pipeline",  icon:"funnel",   page:"sales",     color:"#00c9a7" },
    { label:"Proposals",      icon:"proposal", page:"proposals", color:"#a78bfa" },
    { label:"CRM",            icon:"clients",  page:"clients",   color:"#c9a84c" },
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,fontWeight:300,fontStyle:"italic",color:t.text,letterSpacing:"0.01em",lineHeight:1.1}}>Sales Overview</div>
        <div style={{width:32,height:1,background:t.teal,margin:"8px 0"}}/>
        <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{newLeads.length} new lead{newLeads.length!==1?"s":""} waiting · {calls.length} call{calls.length!==1?"s":""} scheduled</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.85rem"}}>
        <StatCard label="New Leads"      value={newLeads.length}          sub="uncontacted"      icon="waitlist"  color="#4a9eff"   t={t}/>
        <StatCard label="Active"         value={active.length}            sub="in pipeline"      icon="funnel"    color={t.teal}    t={t}/>
        <StatCard label="Won"            value={won.length}               sub="converted"        icon="check"     color="#22c55e"   t={t}/>
        <StatCard label="Pipeline Value" value={`$${pipeline.toLocaleString()}`} sub="est. revenue" icon="finance" color={t.amber} t={t}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.65rem"}}>
        {quickActions.map(q => (
          <Card key={q.label} t={t} onClick={()=>setPage(q.page)} style={{padding:"1rem",textAlign:"center",cursor:"pointer"}}>
            <div style={{marginBottom:6}}><I n={q.icon} s={18} c={q.color}/></div>
            <div style={{fontSize:11,fontWeight:500,color:t.text,fontFamily:"'Space Grotesk',sans-serif"}}>{q.label}</div>
          </Card>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.85rem"}}>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Recent Leads</div>
          {recentLeads.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No leads yet.</div>}
          {recentLeads.map(l => (
            <div key={l.id} onClick={()=>setPage("sales")} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${t.border}`,cursor:"pointer"}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:STAGE_COLOR(l.status),flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div>
                <div style={{fontSize:15,color:t.textDimmer}}>{l.product||"—"} · {l.source}</div>
              </div>
              <div style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:`${STAGE_COLOR(l.status)}18`,color:STAGE_COLOR(l.status),fontFamily:"'Fira Code',monospace",textTransform:"uppercase",flexShrink:0}}>{l.status}</div>
            </div>
          ))}
        </Card>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Upcoming Calls</div>
          {calls.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No calls scheduled.</div>}
          {calls.slice(0,5).map(c => (
            <div key={c.id} style={{padding:"8px 0",borderBottom:`1px solid ${t.border}`}}>
              <div style={{fontSize:13,fontWeight:600,color:t.text}}>{c.leadName}</div>
              <div style={{fontSize:11,color:t.teal,fontFamily:"'Fira Code',monospace"}}>{new Date(c.scheduledAt).toLocaleString()}</div>
              {c.notes && <div style={{fontSize:11,color:t.textDimmer,fontStyle:"italic",marginTop:2}}>{c.notes}</div>}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ─── MODULE: SUPPORT DASHBOARD ────────────────────────────────────────────────
const SupportDashboard = ({ qtTickets, setQtTickets, qtBugs, setQtBugs, clients, qtClients, qtToken, t, setPage }) => {
  const openTickets  = (qtTickets||[]).filter(tk => tk.status !== "CLOSED" && tk.status !== "Resolved");
  const openBugs     = (qtBugs||[]).filter(b => b.status !== "CLOSED" && b.status !== "Resolved");
  const urgent       = openTickets.filter(tk => (tk.priority||"").toUpperCase() === "HIGH" || (tk.priority||"").toUpperCase() === "URGENT");
  const recent       = [...(qtTickets||[])].sort((a,b) => new Date(b.createdAt||0)-new Date(a.createdAt||0)).slice(0,6);

  const statusColor = s => ({
    OPEN:"#4a9eff", PENDING:"#c9a84c", IN_PROGRESS:"#a78bfa",
    CLOSED:t.textDimmer, Resolved:t.teal, New:"#4a9eff",
  }[s] || t.textDim);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,fontWeight:300,fontStyle:"italic",color:t.text,letterSpacing:"0.01em",lineHeight:1.1}}>Support Queue</div>
        <div style={{width:32,height:1,background:t.teal,margin:"8px 0"}}/>
        <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{openTickets.length} open ticket{openTickets.length!==1?"s":""} · {urgent.length} urgent · {openBugs.length} bug{openBugs.length!==1?"s":""}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.85rem"}}>
        <StatCard label="Open Tickets"  value={openTickets.length}  sub="need attention"  icon="message"  color="#4a9eff"  t={t}/>
        <StatCard label="Urgent"        value={urgent.length}       sub="high priority"   icon="alert"    color={t.red}    t={t}/>
        <StatCard label="Open Bugs"     value={openBugs.length}     sub="reported issues" icon="alert"    color={t.amber}  t={t}/>
        <StatCard label="Total Tickets" value={(qtTickets||[]).length} sub="all time"     icon="check"    color={t.teal}   t={t}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.85rem"}}>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Recent Tickets</div>
          {recent.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No tickets.</div>}
          {recent.map(tk => (
            <div key={tk.id} onClick={()=>setPage("qtrader")} style={{padding:"8px 0",borderBottom:`1px solid ${t.border}`,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:2}}>
                <span style={{fontSize:13,fontWeight:600,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{tk.subject||tk.title||"Ticket"}</span>
                <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:`${statusColor(tk.status)}18`,color:statusColor(tk.status),fontFamily:"'Fira Code',monospace",textTransform:"uppercase",flexShrink:0}}>{tk.status}</span>
              </div>
              <div style={{fontSize:15,color:t.textDimmer}}>{tk.clientName||tk.client?.name||"Client"} · {(tk.createdAt||"").slice(0,10)}</div>
            </div>
          ))}
        </Card>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Recent Bug Reports</div>
          {(qtBugs||[]).length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No bugs reported.</div>}
          {[...(qtBugs||[])].sort((a,b) => new Date(b.createdAt||0)-new Date(a.createdAt||0)).slice(0,6).map(bug => (
            <div key={bug.id} onClick={()=>setPage("qtrader")} style={{padding:"8px 0",borderBottom:`1px solid ${t.border}`,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:2}}>
                <span style={{fontSize:13,fontWeight:600,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{bug.title||bug.subject||"Bug"}</span>
                <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:`${statusColor(bug.status)}18`,color:statusColor(bug.status),fontFamily:"'Fira Code',monospace",textTransform:"uppercase",flexShrink:0}}>{bug.status||"Open"}</span>
              </div>
              <div style={{fontSize:15,color:t.textDimmer}}>{bug.clientName||bug.client?.name||"Client"} · {(bug.createdAt||"").slice(0,10)}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ─── MODULE: MARKETING DASHBOARD ─────────────────────────────────────────────
const MarketingDashboard = ({ ads, waitlist, leads, clients, t, setPage }) => {
  const activeAds    = (ads||[]).filter(a => a.status === "Active");
  const totalSpend   = (ads||[]).reduce((a,x) => a+(x.spent||0), 0);
  const totalLeadsAd = (ads||[]).reduce((a,x) => a+(x.leads||0), 0);
  const waiting      = (waitlist||[]).filter(w => w.status === "Waiting");
  const cpl          = totalLeadsAd > 0 ? Math.round(totalSpend / totalLeadsAd) : 0;
  const recentWait   = [...(waitlist||[])].sort((a,b) => (b.id||0)-(a.id||0)).slice(0,5);
  const adsByPlatform = (ads||[]).reduce((acc,a) => {
    if(!acc[a.platform]) acc[a.platform] = { spend:0, leads:0, count:0 };
    acc[a.platform].spend  += a.spent||0;
    acc[a.platform].leads  += a.leads||0;
    acc[a.platform].count  += 1;
    return acc;
  }, {});
  const quickActions = [
    { label:"New Campaign",   icon:"ads",      page:"ads",      color:t.teal  },
    { label:"Waitlist",       icon:"waitlist", page:"waitlist", color:"#a78bfa" },
    { label:"Sales Pipeline", icon:"funnel",   page:"sales",    color:"#4a9eff" },
    { label:"CRM",            icon:"clients",  page:"clients",  color:t.amber  },
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,fontWeight:300,fontStyle:"italic",color:t.text,letterSpacing:"0.01em",lineHeight:1.1}}>Marketing</div>
        <div style={{width:32,height:1,background:t.teal,margin:"8px 0"}}/>
        <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{activeAds.length} active campaign{activeAds.length!==1?"s":""} · {waiting.length} waitlist signup{waiting.length!==1?"s":""} pending</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.85rem"}}>
        <StatCard label="Ad Spend"     value={`KES ${totalSpend.toLocaleString()}`} sub="total spent"     icon="finance"  color={t.red}   t={t}/>
        <StatCard label="Leads Generated" value={totalLeadsAd}                      sub="from campaigns"  icon="funnel"   color="#4a9eff" t={t}/>
        <StatCard label="Cost Per Lead" value={cpl > 0 ? `KES ${cpl}` : "—"}       sub="avg across ads"  icon="trending" color={t.amber} t={t}/>
        <StatCard label="Waitlist"      value={waiting.length}                       sub="awaiting notify" icon="waitlist" color="#a78bfa" t={t}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.65rem"}}>
        {quickActions.map(q => (
          <Card key={q.label} t={t} onClick={()=>setPage(q.page)} style={{padding:"1rem",textAlign:"center",cursor:"pointer"}}>
            <div style={{marginBottom:6}}><I n={q.icon} s={18} c={q.color}/></div>
            <div style={{fontSize:11,fontWeight:500,color:t.text,fontFamily:"'Space Grotesk',sans-serif"}}>{q.label}</div>
          </Card>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.85rem"}}>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Campaigns by Platform</div>
          {Object.keys(adsByPlatform).length === 0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No campaigns yet.</div>}
          {Object.entries(adsByPlatform).map(([platform,data]) => (
            <div key={platform} style={{padding:"8px 0",borderBottom:`1px solid ${t.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:13,fontWeight:600,color:t.text}}>{platform}</span>
                <span style={{fontSize:11,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{data.count} ad{data.count!==1?"s":""}</span>
              </div>
              <div style={{display:"flex",gap:16}}>
                <span style={{fontSize:13,color:t.red}}>KES {data.spend.toLocaleString()} spent</span>
                <span style={{fontSize:11,color:"#4a9eff"}}>{data.leads} leads</span>
              </div>
            </div>
          ))}
        </Card>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>Recent Waitlist</div>
          {recentWait.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>Waitlist empty.</div>}
          {recentWait.map(w => (
            <div key={w.id} onClick={()=>setPage("waitlist")} style={{padding:"8px 0",borderBottom:`1px solid ${t.border}`,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.name}</div>
                  <div style={{fontSize:15,color:t.textDimmer}}>{w.product||"—"} · {w.country||w.email||"—"}</div>
                </div>
                <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,background:w.status==="Notified"?`${t.teal}18`:`${t.textDim}18`,color:w.status==="Notified"?t.teal:t.textDim,fontFamily:"'Fira Code',monospace",textTransform:"uppercase",flexShrink:0}}>{w.status}</span>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ─── MODULE: DEVELOPER DASHBOARD (enhanced from old generic Dashboard) ─────────
const DevDashboard = ({ user, projects, timeEntries, payroll, messages, clients, t, setPage }) => {
  const myProjects   = projects.filter(p => p.assignee === user.id || p.chatParticipants?.includes(user.id));
  const active       = myProjects.filter(p => p.status === "In Progress");
  const myTime       = (timeEntries||[]).filter(e => e.userId === user.id || e.userName === user.username || e.userId === user.username);
  const hoursThisMonth = myTime.filter(e => (e.date||e.createdAt||"").slice(0,7) === new Date().toISOString().slice(0,7)).reduce((a,e) => a+(e.hours||0), 0);
  const myPay        = (payroll||[]).filter(p => p.recipient === user.name).slice(-3);
  const unread       = (messages||[]).filter(m => !m.read && m.fromId !== String(user.id)).length;
  const overdue      = myProjects.filter(p => p.deadline && new Date(p.deadline) < new Date() && p.status !== "Completed" && p.status !== "COMPLETED");

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.5rem"}} className="fade-up">
      <div>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,fontWeight:300,fontStyle:"italic",color:t.text,letterSpacing:"0.01em",lineHeight:1.1}}>Good day, {user.name?.split(" ")[0]}</div>
        <div style={{width:32,height:1,background:t.teal,margin:"8px 0"}}/>
        <div style={{fontSize:15,color:t.textDim,fontWeight:400}}>{active.length} active project{active.length!==1?"s":""}{unread>0?` · ${unread} unread message${unread!==1?"s":""}`:""}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(145px,1fr))",gap:"0.85rem"}}>
        <StatCard label="Active Projects" value={active.length}           sub="in progress"     icon="projects" color={t.teal}    t={t}/>
        <StatCard label="Tasks Done"      value={myProjects.reduce((a,p)=>a+p.tasks.filter(tk=>tk.done).length,0)} sub="total completed" icon="check" color="#22c55e" t={t}/>
        <StatCard label="Hours (Month)"   value={hoursThisMonth.toFixed(1)} sub="logged this month" icon="timer"  color={t.amber}  t={t}/>
        <StatCard label="Messages"        value={unread||0}               sub="unread"          icon="message"  color={unread>0?t.red:t.textDimmer} t={t}/>
      </div>
      {overdue.length > 0 && (
        <div style={{padding:"10px 14px",background:`${t.red}10`,border:`1px solid ${t.red}30`,borderRadius:6,display:"flex",alignItems:"center",gap:8}}>
          <I n="alert" s={14} c={t.red}/>
          <span style={{fontSize:12,color:t.red,fontWeight:500}}>{overdue.length} project{overdue.length!==1?"s":""} past deadline: {overdue.map(p=>p.name).join(", ")}</span>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,340px),1fr))",gap:"0.85rem"}}>
        <Card t={t}>
          <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"1rem"}}>My Projects</div>
          {myProjects.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No projects assigned.</div>}
          {myProjects.map(p => {
            const done = p.tasks.filter(tk=>tk.done).length;
            const pct  = p.tasks.length ? Math.round((done/p.tasks.length)*100) : 0;
            return (
              <div key={p.id} onClick={()=>setPage("projects")} style={{marginBottom:"1rem",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6,gap:8}}>
                  <span style={{fontSize:15,color:t.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{p.name}</span>
                  <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace",flexShrink:0}}>{pct}%</span>
                </div>
                <ProgressBar pct={pct} t={t}/>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:5}}>
                  <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>{done}/{p.tasks.length} tasks</span>
                  <Badge label={p.status} t={t}/>
                </div>
              </div>
            );
          })}
        </Card>
        <div style={{display:"flex",flexDirection:"column",gap:"0.85rem"}}>
          <Card t={t} style={{padding:"1rem"}}>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"0.75rem"}}>Quick Actions</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {[
                { label:"Log Time",  icon:"timer",   page:"time",     color:t.teal  },
                { label:"Messages",  icon:"message", page:"messages", color:"#4a9eff"},
                { label:"Payslips",  icon:"payroll", page:"payslips", color:t.amber },
                { label:"Earnings",  icon:"finance", page:"earnings", color:"#22c55e"},
              ].map(q => (
                <div key={q.label} onClick={()=>setPage(q.page)} style={{padding:"0.75rem 0.5rem",background:t.surface2,borderRadius:5,border:`1px solid ${t.border}`,cursor:"pointer",textAlign:"center"}}>
                  <I n={q.icon} s={18} c={q.color}/>
                  <div style={{fontSize:11,color:t.text,fontFamily:"'Space Grotesk',sans-serif",marginTop:5,fontWeight:500}}>{q.label}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card t={t} style={{padding:"1rem"}}>
            <div style={{fontSize:10,fontFamily:"'Fira Code',monospace",letterSpacing:"0.2em",color:t.textDim,textTransform:"uppercase",marginBottom:"0.75rem"}}>Recent Payslips</div>
            {myPay.length===0 && <div style={{color:t.textDimmer,fontSize:12,fontStyle:"italic"}}>No payslips yet.</div>}
            {myPay.map(p => (
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{fontSize:12,color:t.textDim,fontFamily:"'Fira Code',monospace"}}>{p.period}</span>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:13,fontWeight:700,color:t.teal,fontFamily:"'Space Grotesk',sans-serif"}}>KES {Number(p.net||0).toLocaleString()}</div>
                  <Badge label={p.status} t={t}/>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
};

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function QireOS({ externalWaitlist, setExternalWaitlist }) {
  const [theme, setTheme]       = useState("light");
  const [user,  setUser]        = useState(null);  
  const [team, setTeam] = useState(INITIAL_TEAM);                
  const [page,  setPage]        = useState("dashboard");

  const [leads,       setLeads]       = useState(SEED.leads || []);
  const [clients,     setClients]     = useState(SEED.clients);
  const [deliverables, setDeliverables] = useState({});
  const [projects,    setProjects]    = useState(SEED.projects);
  const [invoices,    setInvoices]    = useState(SEED.invoices);
  const [expenses,    setExpenses]    = useState(SEED.expenses);
  const [taxRecords,  setTaxRecords]  = useState(SEED.taxRecords);
  const [letters,     setLetters]     = useState(SEED.letters);
  const [ads,         setAds]         = useState(SEED.ads);
const [internalWaitlist, setInternalWaitlist] = useState(SEED.waitlist);
const waitlist = [...internalWaitlist, ...(externalWaitlist||[])];
const setWaitlist = (updater) => {
  // Run the updater on the full combined list
  const current = [...internalWaitlist, ...(externalWaitlist||[])];
  const updated = typeof updater === "function" ? updater(current) : updater;
  
  // Split the result back into internal vs external by checking IDs
  const internalIds = new Set(internalWaitlist.map(w => w.id));
  const externalIds = new Set((externalWaitlist||[]).map(w => w.id));
  
  setInternalWaitlist(updated.filter(w => internalIds.has(w.id)));
  setExternalWaitlist(updated.filter(w => externalIds.has(w.id)));
};

  const [pdfPreview, setPdfPreview] = useState(null);
  const [payroll,   setPayroll]   = useState(SEED.payroll);
  const [contracts, setContracts] = useState(SEED.contracts);
  const [timeEntries, setTimeEntries] = useState(SEED.timeEntries);
  const [showNotifications, setShowNotifications] = useState(false);
  const [splashDone, setSplashDone] = useState(false);
  const [splashFading, setSplashFading] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setSplashFading(true), 1800);
    const t2 = setTimeout(() => setSplashDone(true), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [proposals,      setProposals]      = useState(SEED.proposals);
  const [messages,       setMessages]       = useState(SEED.messages);

  // ── Real-time Socket.io ───────────────────────────────────────────────────
  const socketRef       = useRef(null);
  const [typingUsers,   setTypingUsers]   = useState({}); // { [room]: [{userId, userName}] }
  const [onlineUsers,   setOnlineUsers]   = useState({}); // { [room]: [{userId, userName}] }
  const typingTimers    = useRef({});  // clear typing after 3s of silence
  const [qtLicenses,  setQtLicenses]  = useState(QT_SEED.licenses);
  const [qtVersions,  setQtVersions]  = useState(QT_SEED.versions);
  const [qtProducts,  setQtProducts]  = useState(QT_SEED.products);
  const [qtBugs,      setQtBugs]      = useState(QT_SEED.bugs);
  const [qtTickets,   setQtTickets]   = useState(QT_SEED.tickets);
  const [qtToken,     setQtToken]     = useState('demo-token');
  const [qtLoading,   setQtLoading]   = useState(false);
  const [apiError,    setApiError]    = useState(null);
  const [qtClients,   setQtClients]   = useState(QT_SEED.clients);
  const [subRequests,  setSubRequests]  = useState(QT_SEED.subRequests);

  // ── DEMO: all load functions are no-ops — data lives in SEED ───────────────
  const loadAdminData       = () => {};
  const loadClientData      = () => {};
  const loadAgencyData      = () => {};
  const loadClientAgencyData= () => {};

  // ── Normalisers — map API shape to Portal.js shape ────────────────────────
  const normalizeClients = (arr) => arr.map(c => ({
    ...c,
    deal: c.deal || 0,
    tags: Array.isArray(c.tags) ? c.tags : [],
    notes: c.notes || '',
    status: c.status || 'Active',
    source: c.source || 'Direct',
    currency: c.currency || 'USD',
    company: c.company || '',
    phone: c.phone || '',
  }));
  const normalizeProjects = (arr) => arr.map(p => ({
    ...p,
    tasks: (p.tasks || []).map(tk => ({ ...tk, done: tk.done || false })),
    clientTasks: p.clientTasks || [],
    clientIssues: p.clientIssues || [],
    // API returns status/priority in UPPERCASE — normalize to title case for UI
    status:   p.status   ? p.status.charAt(0).toUpperCase()   + p.status.slice(1).toLowerCase().replace('_progress','_progress').replace(/_([a-z])/g,(_,c)=>'_'+c.toUpperCase())   : 'Planning',
    priority: p.priority ? p.priority.charAt(0).toUpperCase() + p.priority.slice(1).toLowerCase() : 'Medium',
    // assignee from API is an object {id, name, avatar} — flatten to id string
    assignee:       (typeof p.assignee === 'object' && p.assignee) ? p.assignee.id   : (p.assigneeId || p.assignee || ''),
    assigneeName:   (typeof p.assignee === 'object' && p.assignee) ? p.assignee.name : (p.assigneeName || ''),
    assigneeAvatar: (typeof p.assignee === 'object' && p.assignee) ? p.assignee.avatar : null,
    chatParticipants: p.chatParticipantIds || p.chatParticipants || [],
  }));
  // Messages from API use senderId — Portal.js uses fromId internally
  const normalizeMessages = (arr) => arr.map(m => ({
    ...m,
    fromId: m.senderId
      ? String(m.senderId)
      : m.clientId
        ? `client-${m.clientId}`
        : 'unknown',
    type: (m.type || 'ADMIN').toLowerCase(),
    body: m.body || '',
    read: m.read || false,
    attachments: m.attachments || [],
  }));

  const normalizeTeam = (arr) => arr.map(u => ({
    ...u,
    // API returns isActive & UPPERCASE role — normalize both
    active:   u.isActive !== undefined ? u.isActive : (u.active !== false),
    isActive: u.isActive !== undefined ? u.isActive : true,
    role:     (u.role || '').toLowerCase(),
  }));
  const normalizePayroll = (arr) => arr.map(p => ({
    ...p,
    // recipient from API is a User object {id,name,role} — flatten
    recipient:     (p.recipient && typeof p.recipient === 'object') ? p.recipient.name : (p.recipient || ''),
    recipientId:   (p.recipient && typeof p.recipient === 'object') ? p.recipient.id   : (p.recipientId || ''),
    recipientRole: (p.recipient && typeof p.recipient === 'object') ? (p.recipient.role||'').toLowerCase() : (p.recipientRole || p.role || ''),
    // API returns UPPERCASE status — normalize for UI comparisons
    status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Pending',
  }));
  const normalizeProposals = (arr) => arr.map(p => ({
    ...p,
    // items is a Json field — Prisma may return it as string, object, or array
    items: Array.isArray(p.items) ? p.items : (typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items); } catch { return []; } })() : []),
    // normalize UPPERCASE status to title case
    status: p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Draft',
  }));
  const normalizeInvoices = (arr) => arr.map(i => ({
    ...i,
    items: Array.isArray(i.items) ? i.items : (typeof i.items === 'string' ? (() => { try { return JSON.parse(i.items); } catch { return []; } })() : []),
    amount: i.amount || 0,
    kesAmount: i.kesAmount || 0,
    // normalize UPPERCASE status to title case for UI
    status: i.status ? i.status.charAt(0).toUpperCase() + i.status.slice(1).toLowerCase() : 'Draft',
  }));
  const normalizeTax = (arr) => arr.map(r => ({
    ...r,
    turnover: r.turnover || 0,
    taxDue: r.taxDue || 0,
    taxPaid: r.taxPaid || 0,
    status: r.status || 'Due',
  }));
  // API returns type as 'EXPENSE'/'INCOME' — UI checks lowercase 'expense'/'income'
  const normalizeExpenses = (arr) => arr.map(e => ({
    ...e,
    type:       (e.type || 'EXPENSE').toLowerCase(),
    deductible: e.deductible === true || e.deductible === 'true',
    date:       e.date ? (typeof e.date === 'string' ? e.date.split('T')[0] : e.date) : '',
  }));

  // ── DEMO: no API calls on login — SEED data already loaded ─────────────────
  useEffect(() => { /* demo: nothing to load */ }, [user]);

  // ─────────────────────────────────────────────────────────────────────────
  const notifications = useNotifications({
    invoices, taxRecords,
    contracts: contracts||[],
    payroll:   payroll||[],
    projects,
    waitlist,
    user,
  });

  // All data loaded from API on login — no local storage (device-agnostic)

  const t = theme === "dark" ? DARK : LIGHT;

  const NAV_ADMIN = [
    { id:"dashboard",  label:"Dashboard",  icon:"dashboard"  },
    { id:"reports",    label:"Reports",    icon:"reports"    },
    { id:"sales",      label:"Sales",      icon:"funnel"     },
    { id:"clients",    label:"CRM",        icon:"clients"    },
    { id:"projects",   label:"Projects",   icon:"projects"   },
    { id:"proposals",  label:"Proposals",  icon:"proposal"   },
    { id:"invoices",   label:"Invoices",   icon:"invoices"   },
    { id:"contracts",  label:"Contracts",  icon:"contracts"  },
    { id:"finance",    label:"Finance",    icon:"finance"    },
    { id:"tax",        label:"Tax / KRA",  icon:"tax"        },
    { id:"payroll",    label:"Payroll",    icon:"payroll"    },
    { id:"time",       label:"Time",       icon:"timer"      },
    { id:"letters",    label:"Letters",    icon:"letters"    },
    { id:"ads",        label:"Ads",        icon:"ads"        },
    { id:"waitlist",   label:"Waitlist",   icon:"waitlist"   },
    { id:"portal",     label:"Portal",     icon:"portal"     },
    { id:"messages",   label:"Messages",   icon:"message"    },
    { id:"team",       label:"Team",       icon:"team"       },
  { id:"qtrader",       label:"Q-Trader",     icon:"shield"   },
  { id:"subscriptions", label:"Subscriptions", icon:"invoices" },
  ];
   
  const NAV_CLIENT = [
    { id:"dashboard",  label:"Dashboard",  icon:"dashboard"  },
    { id:"myproject",  label:"My Project", icon:"projects"   },
    { id:"proposals",  label:"Proposals",  icon:"proposal"   },
    { id:"invoices",   label:"Invoices",   icon:"invoices"   },
    { id:"messages",   label:"Messages",   icon:"message"    },
  { id:"qtrader", label:"Q-Trader EA", icon:"shield" },
  ];

const NAV_DEV = [
  { id:"dashboard", label:"Dashboard", icon:"dashboard" },
  { id:"projects",  label:"Projects",  icon:"projects"  },
  { id:"messages",  label:"Messages",  icon:"message"   },
  { id:"time",      label:"Time",      icon:"timer"     },
  { id:"payslips",  label:"Payslips",  icon:"payroll"   },
  { id:"earnings",  label:"Earnings",  icon:"finance"   },
];

const NAV_SALES = [
  { id:"dashboard",  label:"Dashboard", icon:"dashboard" },
  { id:"sales",      label:"Pipeline",  icon:"funnel"    },
  { id:"clients",    label:"CRM",       icon:"clients"   },
  { id:"proposals",  label:"Proposals", icon:"proposal"  },
  { id:"messages",   label:"Messages",  icon:"message"   },
];

const NAV_SUPPORT = [
  { id:"dashboard", label:"Dashboard", icon:"dashboard" },
  { id:"qtrader",   label:"Tickets",   icon:"message"   },
  { id:"clients",   label:"Clients",   icon:"clients"   },
  { id:"messages",  label:"Messages",  icon:"message"   },
];

const NAV_MARKETING = [
  { id:"dashboard", label:"Dashboard", icon:"dashboard" },
  { id:"ads",       label:"Campaigns", icon:"ads"       },
  { id:"waitlist",  label:"Waitlist",  icon:"waitlist"  },
  { id:"sales",     label:"Pipeline",  icon:"funnel"    },
  { id:"clients",   label:"CRM",       icon:"clients"   },
  { id:"messages",  label:"Messages",  icon:"message"   },
];

  const role      = (user?.role||"").toLowerCase();
  const isAdmin   = role === "admin";
  const isSales   = role === "sales";
  const isSupport = role === "support";
  const isMarketing = role === "marketing";
  const isDev     = role === "developer";
  const isTeam    = isAdmin || isSales || isSupport || isMarketing || isDev;
  const nav = role==="admin"
    ? NAV_ADMIN
    : role==="client"
    ? NAV_CLIENT
    : role==="developer"
    ? NAV_DEV
    : role==="sales"
    ? NAV_SALES
    : role==="support"
    ? NAV_SUPPORT
    : role==="marketing"
    ? NAV_MARKETING
    : NAV_DEV; // fallback for any future roles

  // Badges for nav items
  const badges = {
    invoices:      invoices.filter(i=>i.status==="Overdue").length || null,
    tax:           taxRecords.filter(r=>r.status==="Due").length   || null,
    waitlist:      waitlist.filter(w=>w.status==="Waiting").length || null,
    subscriptions: subRequests.filter(r=>r.status==="Pending").length || null,
    sales:         leads.filter(l=>l.status==="New").length        || null,
    qtrader:       (qtTickets||[]).filter(tk=>tk.status==="OPEN"||tk.status==="New").length || null,
    messages: (() => {
      const uid  = String(user?.id || '');
      const role = (user?.role||'').toLowerCase();

      if (role === 'client') {
        // Client sees only their project threads — count unread not sent by them
        const myFromId = `client-${uid}`;
        return (messages||[]).filter(m => !m.read && m.fromId !== myFromId).length || null;
      }

      if (role === 'developer') {
        // Dev only sees DEV-type threads on assigned/participant projects
        const myProjectIds = projects
          .filter(p => String(p.assignee)===uid || (p.chatParticipants||[]).some(id=>String(id)===uid))
          .map(p => p.id);
        return (messages||[]).filter(m =>
          !m.read &&
          m.fromId !== uid &&
          m.type === 'dev' &&
          myProjectIds.includes(m.projectId)
        ).length || null;
      }

      if (role === 'admin') {
        // Admin sees ALL threads — count unread not sent by this admin
        return (messages||[]).filter(m =>
          !m.read && m.fromId !== uid
        ).length || null;
      }

      // Sales / Support / Marketing — see only ADMIN-type (client-facing) threads
      return (messages||[]).filter(m =>
        !m.read &&
        m.fromId !== uid &&
        m.type === 'admin'
      ).length || null;
    })(),
  };

  // Sync body classes for theme + portal scoping — all in one effect so cleanup is guaranteed
  useEffect(() => {
    document.body.classList.add("portal-page");
    document.body.classList.toggle("qireos-dark",  theme === "dark");
    document.body.classList.toggle("qireos-light", theme === "light");
    // Prevent black gap on mobile when browser chrome collapses
    const bg = theme === "dark" ? "#050507" : "#f5f4f2";
    document.body.style.background = bg;
    document.documentElement.style.background = bg;
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";
    return () => {
      document.body.classList.remove("portal-page");
      document.body.classList.remove("qireos-dark");
      document.body.classList.remove("qireos-light");
      document.body.style.background = "";
      document.documentElement.style.background = "";
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overscrollBehavior = "";
    };
  }, [theme]);

  // ── Pull-to-refresh ───────────────────────────────────────────────────────
  const [pullY,          setPullY]          = React.useState(0);
  const [pullRefreshing, setPullRefreshing] = React.useState(false);
  const pullStartY  = React.useRef(null);
  const pullMainRef = React.useRef(null);
  const PULL_THRESHOLD = 72;

  const onPullTouchStart = React.useCallback((e) => {
    const el = pullMainRef.current;
    if (el && el.scrollTop === 0) pullStartY.current = e.touches[0].clientY;
  }, []);

  const onPullTouchMove = React.useCallback((e) => {
    if (pullStartY.current === null || pullRefreshing) return;
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta > 0) {
      e.preventDefault();
      setPullY(Math.min(delta * 0.45, PULL_THRESHOLD + 20));
    }
  }, [pullRefreshing]);

  const onPullTouchEnd = React.useCallback(() => {
    if (pullStartY.current === null) return;
    pullStartY.current = null;
    if (pullY >= PULL_THRESHOLD && !pullRefreshing) {
      setPullRefreshing(true);
      setPullY(PULL_THRESHOLD);
      setTimeout(() => { window.location.reload(); }, 700);
    } else {
      setPullY(0);
    }
  }, [pullY, pullRefreshing]);

  // Splash renders before login check so it shows on first app load
  if(!splashDone) return (
    <div className={`q-splash${splashFading ? ' fade-out' : ''}`}>
      <div className="q-splash-logo">
        <div className="q-splash-ring" />
        <div className="q-splash-ring2" />
        <div className="q-splash-mark"><LogoMark size={80} /></div>
      </div>
      <div className="q-splash-wordmark">Qire<span>OS</span></div>
      <div className="q-splash-bar" />
      <div className="q-splash-label">Initializing workspace</div>
    </div>
  );

  if(!user) return <LoginPage t={t} team={team} onLogin={(u)=>{setUser(u);setPage("dashboard");}}/>;

  const isDark  = t.id === "dark";

  // ── Sidebar shared styles ──────────────────────────────────────────────────
  const col  = sidebarCollapsed;
  // ── Sidebar palette ──────────────────────────────────────────────────────
  // Dark mode: near-black surface, subtle border
  // Light mode: deep slate — creates intentional contrast with white content area
  //             (same pattern as Linear, Vercel, Notion, Superhuman)
  // eslint-disable-next-line no-unused-vars
  const isDarkSb = true; // sidebar is ALWAYS dark — brand consistency
  const sbBg         = t.id==="light" ? "#1a1714" : t.surface;
  const sbBorder     = t.id==="light" ? `1px solid #2a2520` : `1px solid ${t.border}`;
  const sbText       = t.id==="light" ? "#f0ede8" : t.text;
  const sbTextDim    = t.id==="light" ? "#9a948c" : t.textDim;
  // eslint-disable-next-line no-unused-vars
  const sbTextDimmer = t.id==="light" ? "#4a4540" : t.textDimmer;
  // eslint-disable-next-line no-unused-vars
  const sbActiveBg   = t.id==="light" ? `rgba(0,122,101,0.15)` : `${t.teal}12`;
  const sbHoverBg    = t.id==="light" ? "rgba(240,237,232,0.05)" : `${t.teal}08`;
  const sbDivider    = t.id==="light" ? "#2a2520" : t.border;

  // Shared nav button renderer used by both desktop and mobile
  const NavBtn = ({ item, onClick: onClickOverride }) => {
    const active = page === item.id;
    const badge  = badges[item.id];
    return (
      <button
        key={item.id}
        data-tip={item.label}
        className={col ? "sidebar-tooltip" : ""}
        onClick={() => { setPage(item.id); if(onClickOverride) onClickOverride(); }}
        style={{
          display:"flex", alignItems:"center",
          gap: col ? 0 : "0.6rem",
          justifyContent: col ? "center" : "flex-start",
          padding: col ? "9px 0" : "7px 9px",
          borderRadius:5,
          background: active ? `${t.teal}0d` : "transparent",
          border: "none",
          borderLeft: col ? "none" : `2px solid ${active ? t.teal : "transparent"}`,
          paddingLeft: col ? undefined : active ? "7px" : "9px",
          cursor:"pointer", width:"100%", textAlign:"left",
          transition:"all 0.15s",
          color: active ? t.teal : sbTextDim,
          position:"relative",
        }}
        onMouseEnter={e=>{if(!active){e.currentTarget.style.background=sbHoverBg;e.currentTarget.style.color=sbText;}}}
        onMouseLeave={e=>{if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.color=sbTextDim;}}}>
        <div style={{position:"relative",flexShrink:0}}>
          <I n={item.icon} s={14} c="currentColor"/>
          {col && badge > 0 && (
            <span style={{position:"absolute",top:-5,right:-5,fontSize:9,fontWeight:700,fontFamily:"'Fira Code',monospace",background:t.red,color:"#fff",borderRadius:"50%",width:14,height:14,display:"flex",alignItems:"center",justifyContent:"center"}}>{badge > 9 ? "9+" : badge}</span>
          )}
        </div>
        {!col && <span style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:12,fontWeight:active?600:400,flex:1,letterSpacing:active?"0.01em":"0"}}>{item.label}</span>}
        {!col && badge > 0 && <span style={{fontSize:9,fontWeight:600,fontFamily:"'Fira Code',monospace",background:t.red,color:"#fff",borderRadius:8,padding:"1px 5px",minWidth:14,textAlign:"center"}}>{badge}</span>}
      </button>
    );
  };

  // Shared bottom action button
  const SbAction = ({ icon, label, onClick, color, border }) => (
    <button
      data-tip={col ? label : undefined}
      className={col ? "sidebar-tooltip" : ""}
      onClick={onClick}
      style={{
        display:"flex", alignItems:"center",
        gap: col ? 0 : "0.6rem",
        justifyContent: col ? "center" : "flex-start",
        padding: col ? "9px 0" : "7px 9px",
        borderRadius:6,
        background:"transparent",
        border: border || "none",
        cursor:"pointer",
        color: color || sbTextDim,
        fontSize:11, fontFamily:"'Space Grotesk',sans-serif",
        transition:"all 0.15s", width:"100%",
      }}
      onMouseEnter={e=>{e.currentTarget.style.color=t.teal; if(border) e.currentTarget.style.borderColor=t.teal;}}
      onMouseLeave={e=>{e.currentTarget.style.color=color||sbTextDim; if(border) e.currentTarget.style.borderColor=border.includes("amber")?t.amber:t.border;}}>
      <div style={{position:"relative",flexShrink:0}}>
        <I n={icon} s={13} c="currentColor"/>
        {icon==="bell" && notifications.length>0 && (
          <div style={{position:"absolute",top:-4,right:-4,width:7,height:7,borderRadius:"50%",background:t.red,animation:"portalPulse 2s infinite"}}/>
        )}
      </div>
      {!col && <span style={{flex:1}}>{label}</span>}
      {!col && icon==="bell" && notifications.length>0 && (
        <span style={{fontSize:9,fontWeight:600,fontFamily:"'Fira Code',monospace",background:t.red,color:"#fff",borderRadius:8,padding:"1px 5px"}}>{notifications.length}</span>
      )}
    </button>
  );

  return (
    <div style={{position:"fixed", inset:0, display:"flex", background:t.bg, fontFamily:"'Space Grotesk',sans-serif", overflow:"hidden"}}>

      {/* ── MOBILE OVERLAY ── */}
      {mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(3px)",zIndex:998}}
        />
      )}

      {/* ── SIDEBAR (desktop: inline | mobile: fixed drawer) ── */}
      <aside id="qireos-desktop-sidebar" style={{
        width: col ? 56 : 210,
        flexShrink: 0,
        background: sbBg,
        borderRight: sbBorder,
        display:"flex", flexDirection:"column", overflow:"hidden",
        transition:"width 0.22s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Wordmark / collapse row */}
        <div style={{padding: col ? "1rem 0" : "1.1rem 1.1rem 0.9rem", borderBottom:`1px solid ${t.border}`,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent: col ? "center" : "space-between"}}>
            {!col && (
              <div style={{display:"flex",alignItems:"center",gap:"0.65rem"}}>
                <LogoMark size={28}/>
                <div>
                  <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14,color:sbText,letterSpacing:"-0.02em",lineHeight:1}}>Qire<span style={{color:t.teal}}>OS</span></div>
                  <div style={{fontFamily:"'Fira Code',monospace",fontSize:9,letterSpacing:"0.18em",color:sbTextDim,textTransform:"uppercase",marginTop:2}}>Agency System</div>
                </div>
              </div>
            )}
            {col && <LogoMark size={26}/>}
            <button
              onClick={() => setSidebarCollapsed(!col)}
              style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4,marginLeft: col ? 0 : 4,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}
              title={col ? "Expand sidebar" : "Collapse sidebar"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {col
                  ? <><polyline points="9 18 15 12 9 6"/></>
                  : <><polyline points="15 18 9 12 15 6"/></>
                }
              </svg>
            </button>
          </div>
        </div>

        {/* User card */}
        {!col ? (
          <div style={{padding:"0.75rem 1.1rem",borderBottom:`1px solid ${sbDivider}`,flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:"0.65rem"}}>
              <Avatar name={user.name} size={28} t={t}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:sbText,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.name.split(" ")[0]}</div>
                <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.12em",textTransform:"uppercase",color:isAdmin?t.teal:t.amber,marginTop:1}}>{user.role}</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{padding:"0.6rem 0",borderBottom:`1px solid ${sbDivider}`,flexShrink:0,display:"flex",justifyContent:"center"}} title={user.name}>
            <Avatar name={user.name} size={26} t={t}/>
          </div>
        )}

        {/* Nav */}
        <nav style={{flex:1,overflow:"auto",padding: col ? "0.6rem 0.3rem" : "0.6rem 0.65rem",display:"flex",flexDirection:"column",gap:2}}>
          {nav.map(item => <NavBtn key={item.id} item={item} />)}
        </nav>

        {/* Bottom actions */}
        <div style={{padding: col ? "0.5rem 0.3rem" : "0.65rem",borderTop:`1px solid ${sbDivider}`,flexShrink:0,display:"flex",flexDirection:"column",gap:2}}>
          <SbAction
            icon="bell" label={`Alerts${notifications.length>0?" ("+notifications.length+")":""}`}
            onClick={() => setShowNotifications(true)}
            color={notifications.length>0?t.amber:sbTextDim}
            border={`1px solid ${notifications.length>0?t.amber:t.border}`}
          />
          <SbAction
            icon={isDark?"sun":"moon"} label={isDark?"Light Mode":"Dark Mode"}
            onClick={() => setTheme(isDark?"light":"dark")}
            border={`1px solid ${t.border}`}
          />
          <SbAction
            icon="logout" label="Sign Out"
            onClick={() => { setUser(null); setPage("dashboard"); }}
          />
        </div>
      </aside>

      {/* ── MOBILE SIDEBAR DRAWER (separate fixed element) ── */}
      <div id="qireos-mobile-sidebar" style={{
        position:"fixed", top:0, left:0, bottom:0,
        width:220,
        background: sbBg,
        borderRight: sbBorder,
        display:"flex", flexDirection:"column",
        zIndex:999,
        transform: mobileNavOpen ? "translateX(0)" : "translateX(-100%)",
        transition:"transform 0.25s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Mobile header */}
        <div style={{padding:"1.1rem 1.1rem 0.9rem",borderBottom:`1px solid ${t.border}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.65rem"}}>
            <LogoMark size={28}/>
            <div>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:14,color:sbText,letterSpacing:"-0.02em",lineHeight:1}}>Qire<span style={{color:t.teal}}>OS</span></div>
              <div style={{fontFamily:"'Fira Code',monospace",fontSize:9,letterSpacing:"0.18em",color:t.textDim,textTransform:"uppercase",marginTop:2}}>Agency System</div>
            </div>
          </div>
          <button onClick={() => setMobileNavOpen(false)} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        {/* Mobile user */}
        <div style={{padding:"0.75rem 1.1rem",borderBottom:`1px solid ${t.border}`,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.65rem"}}>
            <Avatar name={user.name} size={28} t={t}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:sbText,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.name}</div>
              <div style={{fontSize:9,fontFamily:"'Fira Code',monospace",letterSpacing:"0.12em",textTransform:"uppercase",color:isAdmin?t.teal:t.amber,marginTop:1}}>{user.role}</div>
            </div>
          </div>
        </div>
        {/* Mobile nav */}
        <nav style={{flex:1,overflow:"auto",padding:"0.6rem 0.65rem",display:"flex",flexDirection:"column",gap:2}}>
          {nav.map(item => <NavBtn key={item.id} item={item} onClick={() => setMobileNavOpen(false)} />)}
        </nav>
        {/* Mobile bottom */}
        <div style={{padding:"0.65rem",borderTop:`1px solid ${t.border}`,flexShrink:0,display:"flex",flexDirection:"column",gap:2}}>
          <SbAction icon="bell" label="Alerts" onClick={() => { setShowNotifications(true); setMobileNavOpen(false); }} color={notifications.length>0?t.amber:sbTextDim} border={`1px solid ${notifications.length>0?t.amber:t.border}`}/>
          <SbAction icon={isDark?"sun":"moon"} label={isDark?"Light Mode":"Dark Mode"} onClick={() => setTheme(isDark?"light":"dark")} border={`1px solid ${t.border}`}/>
          <SbAction icon="logout" label="Sign Out" onClick={() => { setUser(null); setPage("dashboard"); }}/>
        </div>
      </div>

      {/* ── MAIN ── */}
      <main
        ref={pullMainRef}
        onTouchStart={onPullTouchStart}
        onTouchMove={onPullTouchMove}
        onTouchEnd={onPullTouchEnd}
        style={{flex:1, overflowY:"auto", overflowX:"hidden", background:t.bg, display:"flex", flexDirection:"column", minWidth:0, minHeight:0}}>
        {/* Pull-to-refresh indicator */}
        <div style={{
          height: pullY,
          overflow:"hidden",
          display:"flex", alignItems:"center", justifyContent:"center",
          transition: pullStartY.current ? "none" : "height 0.3s ease",
          flexShrink:0,
        }}>
          {pullY > 10 && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{
                width:28, height:28, borderRadius:"50%",
                border:`2px solid ${t.teal}`,
                borderTopColor:"transparent",
                animation: pullRefreshing ? "spin 0.7s linear infinite" : "none",
                transform: !pullRefreshing ? `rotate(${Math.min(pullY/PULL_THRESHOLD,1)*270}deg)` : undefined,
                transition: pullRefreshing ? "none" : "transform 0.05s linear",
              }}/>
              <span style={{fontSize:10,color:t.textDimmer,fontFamily:"'Fira Code',monospace"}}>
                {pullRefreshing ? "Refreshing…" : pullY >= PULL_THRESHOLD ? "Release to refresh" : "Pull to refresh"}
              </span>
            </div>
          )}
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        {/* Mobile top bar — shown/hidden via index.css media query on #qireos-mobile-bar */}
        <div style={{
          display:"none",
          alignItems:"center", justifyContent:"space-between",
          padding:"0.65rem 1rem",
          background: sbBg,
          borderBottom:`1px solid ${t.border}`,
          flexShrink:0,
          position:"sticky",
          top:0,
          zIndex:100,
        }} id="qireos-mobile-bar">
          <button onClick={() => setMobileNavOpen(true)} style={{background:"none",border:"none",cursor:"pointer",color:t.textDim,padding:4,display:"flex",alignItems:"center",gap:8}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <div style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
            <LogoMark size={22}/>
            <span style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:13,color:sbText}}>Qire<span style={{color:t.teal}}>OS</span></span>
          </div>
          <button onClick={() => setShowNotifications(true)} style={{background:"none",border:"none",cursor:"pointer",color:notifications.length>0?t.amber:t.textDim,padding:4,position:"relative"}}>
            <I n="bell" s={16} c="currentColor"/>
            {notifications.length>0&&<div style={{position:"absolute",top:2,right:2,width:7,height:7,borderRadius:"50%",background:t.red,animation:"portalPulse 2s infinite"}}/>}
          </button>
        </div>

  <div style={{padding:"2rem", paddingBottom:"3rem", maxWidth:1800, margin:"0 auto", width:"100%", ...(page==="messages" ? {padding:0, flex:1, minHeight:0, display:"flex", flexDirection:"column"} : {})}} className={`qireos-main-padding${page==="messages"?" page-messages":""}`} key={page}>
          {/* API connection banner */}
          {apiError && (
            <div style={{marginBottom:"1rem",padding:"10px 14px",background:"rgba(192,57,78,0.1)",border:"1px solid rgba(192,57,78,0.3)",borderRadius:6,fontSize:12,color:"#c0394e",display:"flex",alignItems:"center",gap:8}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              API: {apiError} — running on local data
              <button onClick={()=>setApiError(null)} style={{marginLeft:"auto",background:"none",border:"none",color:"inherit",cursor:"pointer",fontSize:13}}>×</button>
            </div>
          )}
          {page==="dashboard" && isAdmin     && <Dashboard data={{clients,projects,invoices,expenses,taxRecords,waitlist}} t={t} user={user} setPage={setPage}/>}
          {page==="dashboard" && isDev       && <DevDashboard user={user} projects={projects} timeEntries={timeEntries} payroll={payroll} messages={messages} clients={clients} t={t} setPage={setPage}/>}
          {page==="dashboard" && isSales     && <SalesDashboard leads={leads} clients={clients} proposals={proposals} invoices={invoices} t={t} setPage={setPage}/>}
          {page==="dashboard" && isSupport   && <SupportDashboard qtTickets={qtTickets} setQtTickets={setQtTickets} qtBugs={qtBugs} setQtBugs={setQtBugs} clients={clients} qtClients={qtClients} qtToken={qtToken} t={t} setPage={setPage}/>}
          {page==="dashboard" && isMarketing && <MarketingDashboard ads={ads} waitlist={waitlist} leads={leads} clients={clients} t={t} setPage={setPage}/>}
          {page==="dashboard" && role==="client" && (
            <ClientDashboard
              user={user}
              clients={clients}
              projects={projects}
              invoices={invoices}
              setInvoices={setInvoices}
              contracts={contracts}
              setContracts={setContracts}
              messages={messages}
              proposals={proposals}
              setProposals={setProposals}
              qtLicenses={qtLicenses}
              qtVersions={qtVersions}
              qtTickets={qtTickets}
              t={t}
              setPage={setPage}
              onPreview={setPdfPreview}
            />
          )}
          {page==="clients" && (isAdmin||isSales||isSupport||isMarketing) && <CRM clients={clients} setClients={setClients} qtToken={qtToken} t={t}/>}
          {page==="sales" && (isAdmin||isSales) && <SalesPipeline leads={leads} setLeads={setLeads} clients={clients} setClients={setClients} proposals={proposals} setProposals={setProposals} t={t} setPage={setPage}/>}
          {page==="projects" && (isTeam) && <Projects projects={projects} setProjects={setProjects} clients={clients} team={team} t={t} user={user}/>}
          {page==="invoices" && isAdmin && 
  <Invoices invoices={invoices} setInvoices={setInvoices} clients={clients} projects={projects} t={t} onPreview={setPdfPreview} setExpenses={setExpenses}/>}
{page==="invoices" && user.role==="client" && (() => {
  const acc = user ? { clientId: user.id, username: user.portalUsername || user.username, active: user.isActive !== false } : null;
  const myInvs=invoices.filter(i=>i.clientId===acc?.clientId&&i.status!=="Draft");
  return <ClientInvoices invoices={myInvs} setInvoices={setInvoices} clients={clients} projects={projects} t={t} onPreview={setPdfPreview}/>;
})()}
          {page==="finance"   && isAdmin && <Finance  expenses={expenses}  setExpenses={setExpenses}   t={t}/>}
          {page==="tax"       && isAdmin && <TaxTracker taxRecords={taxRecords} setTaxRecords={setTaxRecords} expenses={expenses} invoices={invoices} t={t}/>}
          {page==="letters"   && isAdmin && <Letters  letters={letters}    setLetters={setLetters}     t={t} onPreview={setPdfPreview}/>}
          {page==="ads"       && (isAdmin||isMarketing) && <Ads      ads={ads}            setAds={setAds}             t={t}/>}
          {page==="waitlist"  && (isAdmin||isMarketing) && <Waitlist waitlist={waitlist}  setWaitlist={setWaitlist}   t={t}/>}
          {page==="team" && isAdmin && <Team projects={projects} team={team} setTeam={setTeam} timeEntries={timeEntries} payroll={payroll} t={t}/>}
          {page==="payroll" && isAdmin && <Payroll payroll={payroll} setPayroll={setPayroll} setExpenses={setExpenses} team={team} timeEntries={timeEntries} projects={projects} t={t} onPreview={setPdfPreview}/>}
          {page==="contracts" && isAdmin && <Contracts contracts={contracts} setContracts={setContracts} clients={clients} projects={projects} t={t} onPreview={setPdfPreview}/>}
          {page==="reports" && isAdmin && <Reports invoices={invoices} expenses={expenses} clients={clients} projects={projects} payroll={payroll} contracts={contracts} t={t}/>}
          {page==="time" && isAdmin && 
  <TimeTracker timeEntries={timeEntries} setTimeEntries={setTimeEntries} projects={projects} team={team} t={t}/>}

          {page==="proposals" && (isAdmin||isSales||role==="client") && 
            <Proposals proposals={proposals} setProposals={setProposals} clients={clients} user={user} t={t} onPreview={setPdfPreview}/>}
          {page==="portal"    && isAdmin && 
            <ClientPortalManager clients={clients} setClients={setClients} t={t}/>}
          
{page==="payslips" && isDev &&
  <DevPayslips payroll={payroll} setPayroll={setPayroll} user={user} team={team} timeEntries={timeEntries} projects={projects} t={t} onPreview={setPdfPreview}/>}
{page==="earnings" && isDev &&
  <DevEarnings payroll={payroll} timeEntries={timeEntries} projects={projects} user={user} t={t}/>}
{page==="time" && isDev && 
  <TimeTracker timeEntries={timeEntries} setTimeEntries={setTimeEntries} projects={projects.filter(p=>p.assignee === user.id)} team={team} t={t}/>}
{page==="messages" && (isTeam||role==="client") && 
  <Messages messages={messages} setMessages={setMessages} projects={projects} setProjects={setProjects} deliverables={deliverables} setDeliverables={setDeliverables} clients={clients} letters={letters} user={user} team={team} t={t} socket={socketRef.current} typingUsers={typingUsers} onlineUsers={onlineUsers}/>}

  {page==="qtrader" && (isAdmin||isSupport) &&
    <QTraderAdmin
      licenses={qtLicenses}   setLicenses={setQtLicenses}
      versions={qtVersions}   setVersions={setQtVersions}
              products={qtProducts}
      bugs={qtBugs}           setBugs={setQtBugs}
      tickets={qtTickets}     setTickets={setQtTickets}
      clients={qtClients}
      apiToken={qtToken}
      apiLoading={qtLoading}
      t={t}
    />
  }
  {page==="qtrader" && user.role==="client" && (() => {
    // Filter Q-Trader data to just this client's records by matching email
    const clientEmail = user.email || "";
    const myLics    = qtLicenses.filter(l => l.email === clientEmail || l.clientName === user.name);
    const myBugs    = qtBugs.filter(b => b.clientName === user.name);
    const myTickets = qtTickets.filter(tk => tk.clientName === user.name);
    const mySubs    = subRequests.filter(r => r.clientName === user.name);
    return (
      <QTraderClient
        licenses={myLics}
        setLicenses={setQtLicenses}
        versions={qtVersions}
        bugs={myBugs}         setBugs={setQtBugs}
        tickets={myTickets}   setTickets={setQtTickets}
        clientId={user.id}
        clientName={user.name || user.username}
        clientEmail={clientEmail}
        subRequests={mySubs}
        setSubRequests={setSubRequests}
        t={t}
      />
    );
  })()}

{page==="subscriptions" && isAdmin && <Subscriptions subRequests={subRequests} setSubRequests={setSubRequests} clients={clients} t={t}/>}
{page==="myproject" && user.role==="client" && 
<ClientProjectView projects={projects} setProjects={setProjects} invoices={invoices} setInvoices={setInvoices}
  proposals={proposals} setProposals={setProposals} messages={messages} setMessages={setMessages}
  deliverables={deliverables} contracts={contracts} setContracts={setContracts} user={user} clients={clients} t={t} setPage={setPage}
  team={team} onPreview={setPdfPreview}/>}
          {/*!isAdmin && page!=="dashboard" && page!=="projects" && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:"1rem"}}>
              <I n="lock" s={32} c={t.textDimmer}/>
              <div style={{fontFamily:"'Syne',sans-serif",fontSize:18,fontWeight:700,color:t.text}}>Admin Access Required</div>
              <div style={{fontSize:13,color:t.textDimmer,fontWeight:400 }}>This section is restricted to admin users.</div>
            </div>
          )*/}
        </div>
      </main>


      {/* PDF Preview Overlay */}
      <PDFPreview
        html={pdfPreview?.html}
        title={pdfPreview?.title || "Qire Agency Document"}
        onClose={()=>setPdfPreview(null)}
        t={t}
        user={user}
        isContract={pdfPreview?.isContract || false}
        contractId={pdfPreview?.contractId || null}
        setContracts={setContracts}
        isProposal={pdfPreview?.isProposal || false}
        proposalId={pdfPreview?.proposalId || null}
        setProposals={setProposals}
        isInvoice={pdfPreview?.isInvoice || false}
        invoiceId={pdfPreview?.invoiceId || null}
        setInvoices={setInvoices}
        isLetter={pdfPreview?.isLetter || false}
        letterId={pdfPreview?.letterId || null}
        setLetters={setLetters}
        isPayslip={pdfPreview?.isPayslip || false}
        payslipId={pdfPreview?.payslipId || null}
        setPayroll={setPayroll}
        initialAdminSig={pdfPreview?.initialAdminSig || ''}
        initialClientSig={pdfPreview?.initialClientSig || ''}
        initialAdminSignedAt={pdfPreview?.initialAdminSignedAt || ''}
        initialClientSignedAt={pdfPreview?.initialClientSignedAt || ''}
      />
<NotificationPanel
  notes={showNotifications ? notifications : null}
  onClose={()=>setShowNotifications(false)}
  onNavigate={(page)=>setPage(page)}
  t={t}
/>
    </div>
  );
}